// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O mapa — grelha virtualizada de 250 viaturas × 365 dias
// ============================================================================
//
// A ESTRUTURA É REQUISITO; A ESTÉTICA É NOSSA (decisão do cliente, 2026-08-04)
//   Viaturas em linhas, dias em colunas, três bandas por viatura, sigla do cliente
//   visível, dia de rotação evidente. Isto não muda: é o que as planeadoras
//   conhecem, e a fonte 6 valoriza explicitamente a gestão da mudança.
//
// AS TRÊS BANDAS
//   1 · barra do itinerário — cliente, escrito UMA vez por corrida
//   2 · localidade do dia   — muda todos os dias, por isso é por dia
//   3 · barra do motorista  — nome, escrito UMA vez por corrida
//
//   ⚠️ As bandas têm **calha** entre si e a linha tem **separador**. O cliente
//   apontou que «os motoristas sobrepõem as restantes linhas»: as bandas estavam
//   encostadas e a barra de motorista de uma linha ficava colada à barra de
//   itinerário da seguinte, pelo que o olho as agrupava como a mesma viatura.
//
//   As corridas de itinerário e de motorista **não coincidem**, e é isso que a
//   banda própria mostra: a troca a meio do itinerário (`trsf`) parte uma e não a
//   outra. É o modelo operacional do negócio — a viatura fica fora e os motoristas
//   rodam.
//
// COR EM TUDO POR OMISSÃO
//   A pedido do cliente. A contrapartida está exposta: com ~40 clientes a cor
//   **não identifica** — a sigla identifica. A cor serve para varrer o mapa. O
//   filtro tem tudo ligado; o que se desliga fica **esbatido**, não escondido,
//   para nunca parecer que a viatura está livre quando não está.
//
// ÍCONES DE COR ÚNICA, NUNCA EMOJI
//   Ver o cabeçalho de `icones.tsx`. Emoji trazem cor própria para dentro de um
//   mapa onde a cor tem significado, mudam de forma por sistema, e desalinham em
//   texto de 9 px.
//
// VIRTUALIZAÇÃO — decidida por MEDIÇÃO, não por opinião (ADR-001)
//   273 750 células de uma vez são ~97× mais lentas do que a janela visível.
//
// PORQUE NÃO HÁ SINCRONIZAÇÃO DE SCROLL
//   Um só contentor com scroll e cabeçalhos `position: sticky`. O padrão dos
//   quatro painéis com `scrollLeft` copiado à mão introduz um atraso de um quadro
//   entre cabeçalho e corpo, visível como tremor.
// ============================================================================

import { seccoesDoDetalhe } from './seccoes-do-detalhe.js';
import {
  BANDEIRA,
  outrasViaturasDaViagem,
  paragemInferida,
  TITULO_DESTINO_PRESUMIDO,
  type CelulaCompacta,
  type DadosMapa,
} from '@xlv-gva-mpt/dominio';
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type PointerEvent as EventoPonteiro,
  type MouseEvent as EventoRato,
} from 'react';
import { CONFIGURACAO_OMISSAO, type ConfiguracaoMapa } from './configuracao.js';
// ---- ⚠️⚠️ AS JANELAS QUE SÓ ABREM A PEDIDO — carregamento diferido (peça do «bundle») -------
//
//   📌 **O `import(...)` fica AQUI, no sítio de sempre**, e não junto de cada uso: o resto do
//   ficheiro continua a escrever `<Pedidos .../>` exactamente como escrevia quando `Pedidos`
//   era um import estático. `janelaDiferida` (definida logo a seguir aos imports) é que embrulha
//   o `React.lazy` + `Suspense` — e é o que torna esta troca cirúrgica: nem uma linha muda nos
//   ~40 sítios onde estas janelas se desenham lá em baixo.
//
//   ⚠️ **O mapa e a primeira vista NÃO estão aqui** — ficam import estático, porque são o que o
//   planeador vê antes de clicar em nada.
// B1/C: o «Planear» aberto do mapa recebe um pedido inteiro — o MESMO tipo que a lista usa.
import { razaoParaNaoPlanear, type Pedido, type PontoPedido } from './pedido.js';
// ⚠️ `diaDoAnoDoPlano` vem daqui e **não é uma formulação nova**: é a que ancora no ano do
//    PLANO e devolve `−1` fora dele, já provada como tabela em `dia-do-ano.a5.test.ts`.
//    ⚠️⚠️ **Deixou de ser import ESTÁTICO** — misturar um import estático com o `import()`
//    dinâmico de `Consultas` (abaixo) obrigava o Rollup a pôr o módulo inteiro (83 kB) num
//    pedaço carregado logo no arranque, porque um import estático exige o valor disponível de
//    imediato. Lê-se do mesmo módulo, à mesma, mas só quando é preciso — ver o único uso, em
//    `aoVerNoMapa`.
// Quem tem acesso a que plano (fonte 5.2). So o superadmin ve a entrada no menu.
// 🔴 «P» do §11 — imprimir e exportar. ⚠️ Ela importa a `Linha`, o `Cabecalho` e a `Legenda`
//    DESTE ficheiro: é o que a impede de ser uma segunda formulação do desenho do mapa.
// Cancelar a partir da celula. 📗 «cancelar ou reagendar» — o pedido resolve-se por
// clique, e nao por um campo em cada uma das 91 250 celulas.
// 🔴 R · as viagens de uma linha, para o «mover período» apanhar várias de uma vez.
import { viagensDaLinha } from './viagens-da-linha.js';
// 📗 §4.6 «split de itinerarios»: partir uma viagem em duas a partir de um dia.
// ⚠️ «Organizar em troços…» e «Juntar os troços…» vivem NESTE ficheiro (definidas mais abaixo)
// e não em ficheiros próprios como `PartirViagem` — a fronteira desta tarefa só permite tocar
// em `Mapa.tsx` do lado do ecrã. Ver o relatório do trabalho.
import { useEscape, useJanelaAoCentro } from './janela.js';
import { escreverNaRede } from './rede.js';
import { Kpis } from './Kpis.js';
// ⚠️⚠️ «Voltar atrás» — 📗 *«no mapa ter algo tipo voltar atrás como tem no Word»*. Vive na barra
//    de topo e **não** numa das 28 janelas: quem erra não sabe em qual errou.
import { Desfazer } from './Desfazer.js';
// ⚠️⚠️ §5.6 exigência 3 do desenho: «Abre-se da célula do mapa — tudo o que aconteceu a ESTA
//    VIAGEM. É assim que a pergunta nasce.» O componente é o MESMO que a barra de topo abre;
//    o que muda é o `alvo`. Ver `alvoDoDiario`, abaixo.
// ⚠️⚠️ **NÃO É JANELA DIFERIDA, ao contrário das outras — medido, não presumido.** O `vite build`
//    avisa: «`Diario.tsx` is dynamically imported by `Mapa.tsx` but also statically imported by
//    `ConflitoAoGravar.tsx`, `Desfazer.tsx`, `Detalhe.tsx`, `PlanoMudou.tsx`». Estes quatro fazem
//    parte da barra de topo, sempre montada — logo `Diario.tsx` (11,3 kB) entra sempre no pedaço
//    do arranque de qualquer forma, e um `import()` aqui só acrescentava um `Suspense` a
//    suspender por nada (um ciclo de re-render a mais, para um módulo já carregado). Fica import
//    estático — e é a razão de nenhum outro `import type` desta secção estar acompanhado de um
//    aviso destes: só este módulo tem um caminho estático concorrente.
import { Diario, type AlvoDoDiarioNoEcra } from './Diario.js';
import { LigacaoAoMapa } from './ligacao-ao-mapa.js';
// ⚠️⚠️ §12 do desenho, peça L — «a selecção como objecto», e as setas a movê-la. As três
//    decisões (que tecla, que linha a seguir com linhas escondidas, quanto deslocar) vivem num
//    módulo PURO: dentro do componente só se provavam através do ecrã.
import {
  deslocarParaCaber,
  linhaDaPagina,
  linhasPorPagina,
  oFocoEstaLivre,
  passoDaTecla,
  proximaLinha,
  proximoDia,
  teclaDeSalto,
} from './navegar-no-mapa.js';
// ⚠️⚠️ O₁ — «o plano mudou noutro sítio». Vive na faixa das métricas do topo, e a razão de não
//    ser uma caixa por cima está no cabeçalho do próprio ficheiro.
import { PlanoMudou } from './PlanoMudou.js';
// ⚠️ O₂ · quem MAIS tem este plano aberto. Ver o cabeçalho do componente para o que ele NÃO
//    sabe dizer — e é a parte que decide as palavras.
import { QuemEstaNoPlano } from './QuemEstaNoPlano.js';
import { RecomendacaoDoIntervalo } from './RecomendacaoDoIntervalo.js';
// ⚠️⚠️ **A PALETA NÃO É DIFERIDA — é a porta de entrada do teclado.** O planeador carrega em
//    `Ctrl+K` e escreve logo o número da viatura; com a paleta num pedaço à parte, as teclas
//    escritas antes de o pedaço chegar e a caixa existir **perdiam-se**, sem erro nenhum. Custa
//    pouco no arranque e é o gesto mais frequente de todos (§12). As outras janelas ficaram
//    diferidas porque o primeiro gesto delas é um clique, que espera sem perder nada.
import { PaletaDeComandos, type ComandoDaPaleta, type VerboDaPaleta } from './PaletaDeComandos.js';
// 🧹 REMOVÍVEL · formação — o botão e o comando de entrada na página «Formação».
import { BotaoDaFormacao, COMANDO_DA_FORMACAO } from './entrada-na-formacao.js';
// §12.3 · o `Ctrl+K` contextual (peça H): que verbos existem e qual se aplica a esta selecção.
import {
  MARCA_NAO_DA,
  marcaDoVerbo,
  verbosDaSeleccao,
  type ChaveDoVerbo,
  type ContextoDaSeleccao,
} from './verbos-da-seleccao.js';
import { barrasDaLinhaSub, extentoDoServico } from './barras-do-subcontratado.js';
import {
  formaDaSeccaoSub,
  linhaDoServicoSub,
  linhasDaEspera,
  linhasDosSubcontratados,
  type LinhaDeSubcontratado,
  type LinhaEmEspera,
} from './linhas-do-subcontratado.js';
// 🔴 Peça F §6.3 · os dois sentidos do mesmo gesto. Em ficheiro PRÓPRIO, ao contrário do
//    «Organizar em troços…» acima — aqui a fronteira do trabalho permite-o, e é o padrão da casa.
// §11 · M · esticar/encurtar uma viagem arrastando a ponta da barra. A aritmética é módulo
//    próprio (`pega-da-barra.ts`, sem DOM); a janela de confirmação é a MESMA `DatasDoPedido`
//    que `Detalhe.tsx` já usa — regra 8, nenhuma escrita nova.
import { pegaDaBarra, novoIntervaloAposArrasto, type PontaDaBarra } from './pega-da-barra.js';
// K · o «por planear» vê-se no mapa, no tempo — módulo puro, ver o seu cabeçalho para o
//    porquê de não reutilizar `linhasDaEspera`/`CelulaCompacta`.
import {
  filaPorPlanearNoMapa,
  indicePorSiglaDeCliente,
  notaDaFilaPorPlanear,
  type ItemPorPlanear,
} from './por-planear-no-mapa.js';
// 🔴 Peça F §6.5 · a mesma viagem num SEGUNDO autocarro, e o inverso que a tira de lá.
import type { OutroAutocarroDaViagem } from './CopiarPeriodo.js';
import type { PedidoParaDuplicar } from './NovoPedido.js';
import {
  arrastoDoPedido,
  eArrastoDeEspera,
  eArrastoDePedido,
  eArrastoDeServico,
  esperaArrastadaDe,
  iniciarArrasto,
  iniciarArrastoDeEspera,
  iniciarArrastoDeServico,
  pedidoArrastadoDe,
  servicoArrastadoDe,
  type PedidoArrastado,
  type ServicoArrastado,
} from './arrasto-pedido.js';
import {
  detalhe,
  detalheSub,
  meioComIcone,
  resumo,
  resumoRotacao,
  codigoLocal,
  nomeOuCodigoDoItinerario,
  rotuloItinerario,
  rotuloMotorista,
  // ⚠️ B1 · a formulação ÚNICA do nome de um condutor — é ela que decide o `?` do inactivo.
  nomeDoMotorista,
  nomeLocal,
} from './celula.js';
import {
  ALTURA_CABECALHO,
  ALTURA_LINHA_SUB,
  ALTURA_TITULO_SUB,
  ALTURA_LINHA,
  ALTURA_LINHA_MAX,
  ALTURA_LINHA_MIN,
  ALTURA_PAINEL,
  ALTURA_PAINEL_MAX,
  ALTURA_PAINEL_MIN,
  DIAS_SEMANA,
  LARGURA_DIA,
  LARGURA_DIA_MAX,
  LARGURA_DIA_MIN,
  LARGURA_TIRA,
  LARGURA_VIATURA,
  LARGURA_VIATURA_MAX,
  LARGURA_VIATURA_MIN,
  MESES,
  SEPARADOR_LINHA,
  acumular,
  bandas,
  calcularJanela,
  indiceEm,
  mesmaJanela,
  tamanho,
  type Janela,
} from './dimensoes.js';
import {
  IconeChegada,
  IconeCaixaEntrada,
  IconeEnvelope,
  IconeSino,
  IconeChevron,
  IconeEngrenagem,
  IconeFunil,
  IconeComboio,
  IconeDecoracao,
  IconeDupla,
  IconeFechar,
  IconeFerry,
  IconeInfo,
  IconeOficina,
  IconeOutroPlano,
  IconePaleta,
  IconePartida,
  IconePessoa,
  IconePesquisa,
  IconeRotacao,
  IconeVazio,
  IconeVisto,
  IconeVoo,
  IconeLista,
} from './icones.js';
import { viagensDoSubcontratado } from './viagem-do-subcontratado.js';
// ---- B1/C · os subcontratos: trocar de empresa, e criar um à mão --------------------
//
// 📗 *«tem de dar para alterar os subcontratos para uns outros»* e *«tem de dar para criar
//    subcontratos manualmente no mapa»*. ⚠️ Nenhum dos dois estreia caminho de escrita: o
//    primeiro usa o `/api/plano/executor` que já existe, e o segundo **não escreve nada** —
//    abre o «Planear» com a empresa garantida.
import { ESTADO, OUTRO_PLANO, corCliente, corDoCliente, tintaSobre, type Tema } from './paleta.js';
import { segmentos, segmentosMotorista } from './segmentos.js';
// ⚠️⚠️ **A CIDADE DE UM DIA — uma só decisão para a FROTA, os PARCEIROS e a ESPERA.** Ver o
//    cabeçalho de `local-do-dia.ts`: as quatro saídas (`nada`/`falta`/`repetido`/`local`)
//    estavam escritas à mão aqui dentro, e ao dar banda de localidade às outras duas secções
//    passariam a existir três cópias da mesma regra. `natureza` e `rotuloDia` deixaram de ser
//    importados aqui **por consequência disso**: quem os chama agora é o módulo.
import {
  TITULO_LOCAL_EM_FALTA,
  TITULO_LOCAL_INFERIDO,
  diasDoPedido,
  localDoDia,
  textoDoLocal,
  type LocalDoDia,
  type PontoDoPedidoNoMapa,
} from './local-do-dia.js';
import { iniciosDeSemana } from './semana.js';
import { carregarComRecarga, RedeDaJanela } from './janela-diferida.js';
import { dataDoIndice, inicioEmMs, janelaDoAno } from '@xlv-gva-mpt/dominio';
import { dataUtcDoIndice, diaCurto, janelaDe, nomeDoExecutor, nomesDosExecutoresDoMapa, periodoCurto } from '@xlv-gva-mpt/dominio';
import type { JanelaDoMapa } from '@xlv-gva-mpt/dominio';
import { diaDaJanelaEmPortugal } from '@xlv-gva-mpt/dominio';
import { NavegarNaFita, decidirIrPara } from './NavegarNaFita.js';
import { chaveDaJanela } from './chave-da-janela.js';
import { dentroDaJanela, hojeEmPortugal, indiceDaData, inicioDaFitaCentradaEm, inicioDeslocado } from '@xlv-gva-mpt/dominio';

// ============================================================================
// AS JANELAS QUE SÓ ABREM A PEDIDO — carregadas quando abrem, não no arranque
// ============================================================================
//
// 📌 O *build* avisava a cada corrida: «Some chunks are larger than 500 kB after
//    minification» — medido em ~1 006 kB num só pedaço, com o mapa e umas quatro
//    dezenas de janelas (Gestão, Consultas, Caixa de entrada, Diário, Imprimir…)
//    tudo interpretado antes da primeira célula. O `PainelPlanear` e o
//    `Consultas` sozinhos eram 85 kB e 83 kB — mais do que o mapa em si.
//
// ⚠️⚠️ **O `Suspense` fica AQUI, uma vez, e não à volta de cada `<Janela .../>`
//    lá em baixo.** `janelaDiferida` devolve um componente que já o traz consigo,
//    logo nem uma linha muda nos ~40 sítios onde estas janelas se desenham — o
//    que muda é só a forma como entram no módulo. Cada janela tem o SEU PRÓPRIO
//    limite de suspensão: se estivessem todas dentro de um único `<Suspense>`
//    partilhado, abrir uma segunda janela enquanto a primeira já está montada
//    fá-la-ia desaparecer (o limite esconde TODOS os filhos enquanto um deles
//    carrega) — e isto é possível, são janelas `position: fixed` arrastáveis e
//    várias podem estar abertas ao mesmo tempo.
//
// ⚠️ `fallback={null}` de propósito: um `Suspense` que desenhasse um spinner
//    acrescentava um nó à árvore que o orçamento de desempenho do mapa (R1) não
//    contabiliza, e nenhuma destas janelas faz parte da grelha virtualizada.
//
// ⚠️⚠️ **Nada que já era import de TIPO (`import type { … }`) mudou** — é erased
//    em tempo de compilação e não tem custo de bundle nenhum; só os valores (os
//    componentes) passam a `import()`. E onde um módulo tinha um valor sempre
//    necessário a par do componente (`Consultas.js` exportava também
//    `diaDoAnoDoPlano`), esse valor teve de deixar de ser import estático — um
//    módulo importado estaticamente E dinamicamente ao mesmo tempo obriga o
//    Rollup a incluí-lo no pedaço do arranque de qualquer forma. Ver o único uso
//    de `diaDoAnoDoPlano`, mais abaixo, onde passou a `import()` também.
function janelaDiferida<T>(carregar: () => Promise<{ default: T }>): T {
  // ⚠️ **`T`, e não `ComponentType<P>` derivado por inferência** — a primeira versão inferia
  //    `P` através de `ComponentType<P>` dentro de um `Promise<{ default: … }>` e o TypeScript
  //    devolvia `never` para as propriedades em alguns dos componentes (a inferência através de
  //    `FunctionComponent<PropsWithChildren<P>>` degrada-se). Deixar `T` ser o PRÓPRIO tipo do
  //    componente original — o que `lazy` já faz — e só fazer o `as unknown as T` no fim (o
  //    wrapper tem exactamente a mesma assinatura, só que devolvida por outro componente) resolve
  //    a inferência sem perder verificação nenhuma nos sítios de uso: `<Pedidos ... />` continua
  //    a exigir as props verdadeiras de `Pedidos`.
  // 🔴 2026-10-23 · o ecrã branco: ver `janela-diferida.tsx` — a falha do pedaço recarrega uma vez,
  //    e a rede fica NA janela em vez de desmontar a aplicação inteira.
  const Diferida = lazy(
    carregarComRecarga(carregar) as unknown as () => Promise<{ default: ComponentType<unknown> }>,
  );
  function ComSuspenso(props: Record<string, unknown>) {
    return (
      <RedeDaJanela>
        <Suspense fallback={null}>
          <Diferida {...props} />
        </Suspense>
      </RedeDaJanela>
    );
  }
  return ComSuspenso as unknown as T;
}

const Pedidos = janelaDiferida(() => import('./Pedidos.js').then((m) => ({ default: m.Pedidos })));
const LargarPedido = janelaDiferida(() =>
  import('./LargarPedido.js').then((m) => ({ default: m.LargarPedido })),
);
const EditarAlocacao = janelaDiferida(() =>
  import('./EditarAlocacao.js').then((m) => ({ default: m.EditarAlocacao })),
);
const NotaDaCelula = janelaDiferida(() =>
  import('./NotaDaCelula.js').then((m) => ({ default: m.NotaDaCelula })),
);
const CorDaCorrida = janelaDiferida(() =>
  import('./CorDaCorrida.js').then((m) => ({ default: m.CorDaCorrida })),
);
const EditarCelula = janelaDiferida(() =>
  import('./EditarCelula.js').then((m) => ({ default: m.EditarCelula })),
);
const Consultas = janelaDiferida(() =>
  import('./Consultas.js').then((m) => ({ default: m.Consultas })),
);
const Gestao = janelaDiferida(() => import('./Gestao.js').then((m) => ({ default: m.Gestao })));
const Sessao = janelaDiferida(() => import('./Sessao.js').then((m) => ({ default: m.Sessao })));
const FecharServico = janelaDiferida(() =>
  import('./FecharServico.js').then((m) => ({ default: m.FecharServico })),
);
const RegrasNegocio = janelaDiferida(() =>
  import('./RegrasNegocio.js').then((m) => ({ default: m.RegrasNegocio })),
);
const Contas = janelaDiferida(() => import('./Contas.js').then((m) => ({ default: m.Contas })));
const FolhaDeImpressao = janelaDiferida(() =>
  import('./FolhaDeImpressao.js').then((m) => ({ default: m.FolhaDeImpressao })),
);
const CancelarServico = janelaDiferida(() =>
  import('./CancelarServico.js').then((m) => ({ default: m.CancelarServico })),
);
const RetirarDoMapa = janelaDiferida(() =>
  import('./RetirarDoMapa.js').then((m) => ({ default: m.RetirarDoMapa })),
);
const MoverBloco = janelaDiferida(() =>
  import('./MoverBloco.js').then((m) => ({ default: m.MoverBloco })),
);
const Alfinete = janelaDiferida(() => import('./Alfinete.js').then((m) => ({ default: m.Alfinete })));
const PartirViagem = janelaDiferida(() =>
  import('./PartirViagem.js').then((m) => ({ default: m.PartirViagem })),
);
const Substituir = janelaDiferida(() =>
  import('./Substituir.js').then((m) => ({ default: m.Substituir })),
);
const Indisponibilidade = janelaDiferida(() =>
  import('./Indisponibilidade.js').then((m) => ({ default: m.Indisponibilidade })),
);
const Paragem = janelaDiferida(() => import('./Paragem.js').then((m) => ({ default: m.Paragem })));
const PorFechar = janelaDiferida(() =>
  import('./PorFechar.js').then((m) => ({ default: m.PorFechar })),
);
const PorConfirmar = janelaDiferida(() =>
  import('./PorConfirmar.js').then((m) => ({ default: m.PorConfirmar })),
);
const Atencao = janelaDiferida(() => import('./Atencao.js').then((m) => ({ default: m.Atencao })));
const CaixaDeEntrada = janelaDiferida(() =>
  import('./CaixaDeEntrada.js').then((m) => ({ default: m.CaixaDeEntrada })),
);
const ValidarProposta = janelaDiferida(() =>
  import('./ValidarProposta.js').then((m) => ({ default: m.ValidarProposta })),
);
const TrocarSubcontratado = janelaDiferida(() =>
  import('./TrocarSubcontratado.js').then((m) => ({ default: m.TrocarSubcontratado })),
);
const SubcontratarAMao = janelaDiferida(() =>
  import('./SubcontratarAMao.js').then((m) => ({ default: m.SubcontratarAMao })),
);
const PainelPlanear = janelaDiferida(() =>
  import('./PainelPlanear.js').then((m) => ({ default: m.PainelPlanear })),
);
const CopiarPeriodo = janelaDiferida(() =>
  import('./CopiarPeriodo.js').then((m) => ({ default: m.CopiarPeriodo })),
);
const RetirarCopia = janelaDiferida(() =>
  import('./CopiarPeriodo.js').then((m) => ({ default: m.RetirarCopia })),
);
const NovoPedido = janelaDiferida(() =>
  import('./NovoPedido.js').then((m) => ({ default: m.NovoPedido })),
);
const PorEmEspera = janelaDiferida(() =>
  import('./EmEspera.js').then((m) => ({ default: m.PorEmEspera })),
);
const TrazerDeEspera = janelaDiferida(() =>
  import('./EmEspera.js').then((m) => ({ default: m.TrazerDeEspera })),
);
const DatasDoPedido = janelaDiferida(() =>
  import('./DatasDoPedido.js').then((m) => ({ default: m.DatasDoPedido })),
);

interface Posicao {
  readonly v: number;
  readonly d: number;
}

/**
 * O que o filtro esconde — ou melhor, **esbate**.
 *
 * Três eixos, porque foram os três que o cliente pediu: cliente, motorista e
 * itinerário. Um conjunto vazio significa «nenhum activo»; `null` significa
 * «todos», que é o estado inicial e o mais comum. Distinguir os dois evita ter de
 * enumerar 40 clientes, 312 motoristas ou 2 679 rotas só para dizer «tudo ligado».
 */
// ⚠️ **Exportado desde 2026-09-16 pela FOLHA DE IMPRESSÃO** (melhoria P do §11): ela desenha as
//    linhas com a **mesma** `Linha`, e uma `Linha` precisa do filtro. 📌 Exportar o tipo é o que
//    impede a segunda formulação — sem ele a folha teria de inventar um filtro seu, e o papel
//    passava a esbater coisas diferentes do ecrã.
export interface Filtro {
  readonly clientes: ReadonlySet<number> | null;
  readonly motoristas: ReadonlySet<number> | null;
  readonly rotas: ReadonlySet<number> | null;
  /**
   * ⚠️⚠️ **O QUARTO EIXO — A CIDADE (V05, 2026-08-30).**
   *
   * 📗 Cliente (pág. 14-16): *«filtro por cidade no mapa»*.
   *
   * 📌 **Medido antes de construir:** a base tem **47** localidades usadas e **87,2 %** dos
   * dias-viatura estão em **dez** delas. É a mesma escala dos outros três eixos — logo o mesmo
   * painel serve, sem paginacão nem desenho novo.
   *
   * ⚠️ **A cidade é do DIA e não da corrida**, ao contrário dos outros três: uma viagem passa
   * por várias. Uma barra fica activa se **algum** dos seus dias tem uma cidade escolhida —
   * exigir que TODOS a tenham esbatia toda a viagem por causa de um dia de passagem, e a
   * pergunta que se faz é *«quem passa por Madrid?»*.
   */
  readonly localidades: ReadonlySet<number> | null;
}

/**
 * O rótulo da barra de um dia **em trânsito** — 📗 «esses dias têm de aparecer … em trânsito».
 *
 * ⚠️⚠️ **UMA formulação, DOIS consumidores: a barra da grelha e a amostra da legenda.** É a
 * lição do `--mascara-cadeado` e da `--trama-sem-trabalho`, escrita nos dois: *«com duas, a
 * legenda ensina um sinal que o mapa não usa, e a que divergir é a que ninguém corrige»*.
 *
 * 📌 **E não é hipotético — foi exactamente o que aconteceu.** O rótulo `A»B` saiu da barra a
 * 2026-09-03 (a origem **não existe em coluna nenhuma**; ver o comentário longo no ramo do
 * trânsito, em `Linha`) e a amostra da legenda ficou a ensinar `BCN»SERZ` durante seis dias,
 * com o portão verde — porque o guarda do `mapa.css.test.ts` só cobria o `outline` do
 * alfinete. Para onde a viatura vai continua a dizer-se, no `title` da barra, que é o único
 * dos dois que a informação sustenta.
 */
export const ROTULO_TRANSITO = 'em trânsito';

const SEM_FILTRO: Filtro = {
  clientes: null,
  motoristas: null,
  rotas: null,
  localidades: null,
};

function activo(conjunto: ReadonlySet<number> | null, i: number): boolean {
  return conjunto === null || i < 0 || conjunto.has(i);
}

/**
 * ---- ⚠️⚠️ A9 · O FILTRO DE CONDUTOR CONTRADIZIA A BANDA 3 -----------------------------
 *
 * `true` quando o condutor filtrado conduz **qualquer** dia desta barra.
 *
 * ⚠️⚠️ **O condutor é DO DIA, e o filtro lia o PRIMEIRO dia da corrida.** Uma viagem de oito
 *   dias em que X só conduz os dias 4-8 ficava **esbatida** com o filtro em X — enquanto a
 *   **banda 3 mostrava a barra dele acesa** na mesma linha. **Medido: 1 910 de 7 099 corridas
 *   do `data/sintetico` (26,9 %) têm mais do que um condutor**, logo o ecrã contradizia-se em
 *   pouco mais de um quarto das viagens.
 *   ⚠️ Ressalva do denominador: o número foi medido em `alocacao.motorista_id` do CSV, e o
 *   `c[2]` vem de `alocacao_condutor` — no sintético coincidem porque o semeador deriva a
 *   segunda da primeira, no `staging` isso **não está provado**.
 *
 * 📌 **É a mesma regra que os subcontratados já aplicam** («QUALQUER dia da barra, e não o
 *   primeiro») e que o eixo da cidade escolheu de propósito. A banda 3 continua a dizer
 *   **quais** dias — é para isso que existe; não se filtra (esbater o pedaço do condutor Y
 *   fazia a viagem parecer parcialmente sem tripulação).
 *
 * ⚠️⚠️ **SÃO QUATRO CAMPOS, e não um** — é o que a banda 3 lê (`segmentos.ts`):
 *   `c[2]` o condutor do dia · `c[6]` o 2.º condutor SIMULTÂNEO (tripulação dupla) ·
 *   `c[14]` o condutor da viagem que **PARTE** num dia de rotação · `c[15]` a tripulação
 *   dupla dessa. Um condutor que só apareça como 2.º de dupla nunca casava o filtro, e a
 *   barra dele estava acesa na banda 3: a mesma contradição por outra porta.
 *
 * ⚠️⚠️ **NÃO se pode envolver `activo()` num `some` por dia.** `activo` devolve **`true`**
 *   para `i < 0`, logo um `some` assim acendia **toda** a barra que tivesse **um** dia sem
 *   condutor — o filtro ao contrário. Por isso se recolhe primeiro o **conjunto** dos
 *   condutores da barra (só os `>= 0`) e só depois se pergunta:
 *     · conjunto **vazio** → acesa, que é exactamente o que o `i < 0` do `activo` dava hoje;
 *     · conjunto **não vazio** → acesa se **algum** deles estiver no filtro.
 *   ⚠️ **Zero das 7 099 corridas do `data/sintetico` têm um dia sem condutor**, logo nenhuma
 *   corrida real exercita esta inversão — a prova dela é um *fixture* à mão.
 *
 * ⚠️ **`d < s.dia1`: o `dia1` do segmento é EXCLUSIVO.** E o segmento já vem recortado à
 *   janela visível, logo uma corrida cortada pela borda do buffer é avaliada só pelo pedaço
 *   desenhado. ⚠️⚠️ **E esta frase dizia «é a mesma propriedade que o eixo da cidade aceitou»
 *   — não tinha aceitado**: o eixo da cidade fazia `d <= s.dia1` e lia um dia para lá da barra.
 *   Corrigido a 2026-09-04; ver `cidadeNaBarra`, logo abaixo.
 */
export function condutorNaBarra(
  conjunto: ReadonlySet<number> | null,
  linha: readonly (CelulaCompacta | null)[],
  dia0: number,
  dia1: number,
): boolean {
  if (conjunto === null) return true;
  let algum = false;
  for (let d = dia0; d < dia1; d++) {
    const c = linha[d];
    if (c == null) continue;
    for (const i of [c[2], c[6], c[14] ?? -1, c[15] ?? -1]) {
      if (i < 0) continue;
      algum = true;
      if (conjunto.has(i)) return true;
    }
  }
  return !algum;
}

/**
 * O eixo da **cidade**: esta barra passa por alguma das localidades filtradas?
 *
 * ---- 🔴🔴 O `dia1` É EXCLUSIVO, e este eixo lia um dia PARA LÁ DA BARRA (2026-09-04) --------
 *
 * 📄 Dívida §5.0-bis (a) da FILA. Era `for (let d = s.dia0; d <= s.dia1; d++)` escrito à mão
 * dentro do `render`, e `Segmento.dia1` é **exclusivo** (está escrito no tipo). 📌 Lia o
 * **primeiro dia da barra seguinte** — logo uma viagem acendia por causa de uma cidade por onde
 * **nunca passou**.
 *
 * ⚠️ **Medido em `data/sintetico`** (barras = corridas contíguas na linha da viatura):
 * **2 248 de 7 099 (31,7 %)** têm no dia a seguir à barra uma cidade que **nenhum** dos seus
 * dias tem. A viatura 100, dias 34–39, passa por `PAR,RNS,LIO,BOD,MAD` e acendia em `SERZ`.
 *
 * ⚠️ **Extraída para se poder provar**, tal como o `condutorNaBarra` foi no A9: a expressão
 * vivia numa IIFE dentro do JSX, onde nenhum teste lhe chegava sem montar o mapa inteiro.
 *
 * ⚠️ **A regra fica a mesma em tudo o resto:** basta **UM** dia (exigir todos esbatia a viagem
 * por causa de um dia de passagem, e o filtro respondia «ninguém passa por Madrid» sobre um mapa
 * cheio de viagens que passam); e uma barra **sem localidade nenhuma** fica esbatida — que é o
 * comportamento de hoje, e é **diferente** do eixo do condutor, onde o conjunto vazio acende.
 * 📌 Não se uniformizou: são perguntas diferentes, e nenhuma delas foi acusada.
 */
export function cidadeNaBarra(
  conjunto: ReadonlySet<number> | null,
  linha: readonly (CelulaCompacta | null)[],
  dia0: number,
  dia1: number,
): boolean {
  if (conjunto === null) return true;
  for (let d = dia0; d < dia1; d++) {
    const loc = linha[d]?.[1] ?? -1;
    if (loc >= 0 && conjunto.has(loc)) return true;
  }
  return false;
}

function contaFiltros(f: Filtro, dados: DadosMapa): number {
  let n = 0;
  if (f.clientes !== null && f.clientes.size !== dados.clientes.length) n++;
  if (f.motoristas !== null && f.motoristas.size !== dados.motoristas.length) n++;
  if (f.rotas !== null && f.rotas.size !== dados.itinerarios.length) n++;
  if (f.localidades !== null && f.localidades.size !== dados.localidades.length) n++;
  return n;
}

function classes(...partes: readonly (string | false | undefined)[]): string {
  return partes.filter((p): p is string => typeof p === 'string').join(' ');
}

function IconeDoMeio({ meio }: { meio: string }) {
  const k = meioComIcone(meio);
  if (k === 'voo') return <IconeVoo em={0.95} className="ic" />;
  if (k === 'comboio') return <IconeComboio em={0.95} className="ic" />;
  if (k === 'ferry') return <IconeFerry em={0.95} className="ic" />;
  return null;
}

/**
 * Os dias que a **corrida** do dia `d` ocupa nesta linha.
 *
 * ⚠️ **PORQUE NÃO É O DIA CLICADO.** Um serviço de 6 dias muda de viatura **por inteiro**: mover 3
 * dos 6 parte a corrida em duas viaturas. Isso **acontece de facto** — 43 % das referências reais
 * servem a mesma viagem em dois autocarros (F-22) — mas tem de ser uma **decisão** e não a
 * omissão de um clique.
 *
 * ⚠️ A chave é a **corrida** e cai na rota quando não há corrida — a mesma regra de `mapa.ts`. E
 * exige-se a mesma chave nos dois lados: sem isso, dois serviços seguidos com a mesma rota
 * apareciam como um.
 */
/**
 * ⚠️⚠️ **A LINHA FIXADA DESENHA-SE? (B32 · «comparar facilmente»)**
 *
 * `true` só quando há linha fixada **e** ela está fora da janela virtualizada.
 *
 * 📌 **Extraída de propósito, e a razão é que sem isto a regra não se prova.** No `jsdom`
 * não há layout, logo a janela contém sempre todas as linhas do *fixture* e o caso «a linha
 * está fora do ecrã» — que é a razão de a funcionalidade existir — **nunca acontece num teste
 * de componente**. Um critério que só exercita o ramo negativo passa com a funcionalidade
 * morta, e este projecto tem quatro dessas registadas.
 *
 * ⚠️ **E o ramo negativo importa tanto como o positivo:** duas cópias da mesma viatura no
 * ecrã fazem comparar uma linha consigo mesma, e as duas respondem ao clique — incluindo a
 * que o planeador não está a olhar.
 *
 * ⚠️ **Devolve o ÍNDICE e não um booleano**, e não é estilo: com um booleano o compilador
 * não estreita `number | null`, e quem desenha tinha de escrever `as number` — que apaga
 * exactamente a garantia que esta função existe para dar.
 */
export function linhaFixadaADesenhar(
  linhaFixada: number | null,
  janela: { readonly viatura0: number; readonly viatura1: number },
): number | null {
  if (linhaFixada === null) return null;
  const fora = linhaFixada < janela.viatura0 || linhaFixada >= janela.viatura1;
  return fora ? linhaFixada : null;
}

export function extentoDaCorrida(
  linha: readonly (CelulaCompacta | null)[],
  d: number,
): { readonly dia0: number; readonly dia1: number } {
  const c = linha[d];
  if (c == null) return { dia0: d, dia1: d };
  const chave = (x: CelulaCompacta): number => (x[10] >= 0 ? x[10] : x[5]);
  const igual = (x: CelulaCompacta | null | undefined): boolean =>
    x != null && x[7] < 0 && c[7] < 0 && chave(x) === chave(c);
  let a0 = d;
  while (a0 > 0 && igual(linha[a0 - 1])) a0--;
  let a1 = d;
  while (a1 + 1 < linha.length && igual(linha[a1 + 1])) a1++;
  return { dia0: a0, dia1: a1 };
}

/**
 * ⚠️⚠️ **Até onde o CONDUTOR desta viagem vai, contando os dias de trânsito a seguir.**
 *
 * 📗 Cliente (bugs_duvidasv1.pdf, pág. 18): *«alterei o condutor do último dia da viagem, o que
 * aconteceu no dia de trânsito pôs o motorista antigo — o que faz zero sentido».*
 *
 * ⚠️⚠️ **A causa:** `extentoDaCorrida` agrupa pela corrida (campo 10) ou pelo itinerário (campo 5),
 * e um dia em trânsito **não tem nenhum dos dois** — é uma linha `vazio` com a bandeira
 * `EM_TRANSITO`. Logo o intervalo parava antes dele, a omissão do selector do condutor era a
 * viagem, e o dia de trânsito ficava de fora do gesto sem ninguém o notar.
 *
 * 📌 **Os selectores JÁ alcançavam `dia1 + 2`** — o planeador podia esticar à mão. O que
 * faltava era a **omissão**: ninguém tem razão para saber que tem de esticar. Quem leva o
 * autocarro de volta é, por omissão, quem acabou a viagem.
 *
 * ⚠️ **Só para a FRENTE e só dias de trânsito.** Um dia parado normal não entra: aí a viatura
 * está estacionada e não há ninguém a conduzi-la. E pára no primeiro dia com serviço real —
 * esse já tem o seu condutor, e escrever por cima era mexer na viagem de outra pessoa.
 *
 * Devolve `dia1` quando não há trânsito nenhum a seguir — e aí o comportamento é o de sempre,
 * palavra por palavra.
 */
export function ateAoFimDoTransito(
  linha: readonly (CelulaCompacta | null)[],
  dia1: number,
): number {
  let f = dia1;
  while (f + 1 < linha.length) {
    const c = linha[f + 1];
    if (c == null) break;
    if ((c[4] & BANDEIRA.EM_TRANSITO) === 0) break;
    // ⚠️ Um dia que já tem serviço real não é trânsito nosso, mesmo com a bandeira.
    if (c[10] >= 0 || c[5] >= 0 || c[0] >= 0) break;
    f += 1;
  }
  return f;
}

/**
 * O intervalo da **viagem que PARTE** num dia de rotação — `null` quando não há segunda viagem.
 *
 * 📗 Cliente, 2026-08-14: os detalhes de um dia de rotação têm de servir **as duas** viagens.
 *
 * ⚠️⚠️ **PORQUE É QUE `extentoDaCorrida` NÃO SERVE:** ele agrupa pela corrida do campo 10, que num
 * dia de rotação é a viagem que **ACABA**. A que parte vive no campo 13 **só naquele dia** — a
 * partir do dia seguinte é ela o campo 10. 📌 Era esta a peça que faltava para o painel poder agir
 * na segunda viagem: sem o intervalo dela, enviar a referência de uma com os dias da outra **movia
 * os dias errados**.
 */
export function extentoDaViagemQueParte(
  linha: readonly (CelulaCompacta | null)[],
  d: number,
): { readonly dia0: number; readonly dia1: number } | null {
  const c = linha[d];
  if (c == null || c[13] < 0) return null;
  let a1 = d;
  while (a1 + 1 < linha.length) {
    const x = linha[a1 + 1];
    // Pára na oficina, no vazio, e quando a corrida deixa de ser a nossa.
    if (x == null || x[7] >= 0 || x[10] !== c[13]) break;
    a1++;
  }
  return { dia0: d, dia1: a1 };
}

/**
 * ---- 🔴🔴 A VIAGEM INTEIRA, INCLUINDO O DIA DE ROTAÇÃO EM QUE ELA PARTE (2026-09-29) ------
 *
 * 📗 Cliente, com duas capturas: *«falta um dia na viagem trocada… dado que o dia de rotação
 * também pertence à segunda viagem… tem de aparecer mais um dia antes»*. A viagem IGT começava
 * no dia de rotação em OPO; movida para outro autocarro, ficou SEM esse dia.
 *
 * ⚠️⚠️ **A causa era o intervalo que o gesto enviava.** `extentoDaCorrida` agrupa pelo campo 10,
 * e no dia de rotação o campo 10 é a viagem que ACABA — a que PARTE vive lá só no campo 13 (ver
 * `extentoDaViagemQueParte`). Logo, aberto num dia qualquer da viagem que parte, o gesto
 * enviava o intervalo **a partir do dia seguinte**, e o servidor movia exactamente o que lhe
 * pediram: tudo menos o primeiro dia, que ficava no autocarro de origem como uma `partida`
 * sozinha da viagem que já lá não estava.
 *
 * ✅ Estende um dia para trás quando a véspera do intervalo tem **esta** viagem no campo 13 — é
 * o primeiro dia dela. `partePelaRotacao` diz que o intervalo passou a incluir um dia com DUAS
 * viagens, e quem escreve tem de nomear a sua (`referencia`): sem isso o servidor recusa com
 * 409 `rotacao_exige_servico`, e com razão.
 *
 * 📌 **É a função dos gestos que escrevem sobre «a viagem toda»** — mover, pôr em espera,
 * copiar, retirar a cópia. A guarda `gesto-sobre-a-viagem-toda.test.ts` exige-a a cada um, e
 * nomeia com razão os que ficam com `extentoDaCorrida` (os que não enviam referência, e para os
 * quais incluir o dia de rotação era mexer também na OUTRA viagem).
 */
export function extentoDaViagem(
  linha: readonly (CelulaCompacta | null)[],
  d: number,
): { readonly dia0: number; readonly dia1: number; readonly partePelaRotacao: boolean } {
  const ext = extentoDaCorrida(linha, d);
  const c = linha[d];
  // ⚠️ `>= 0` afirmativo: com o campo ausente, `undefined < 0` é FALSO e lia-se presença.
  const nossa = c != null && c[10] >= 0 ? c[10] : -1;
  const vespera = ext.dia0 > 0 ? linha[ext.dia0 - 1] : null;
  if (nossa >= 0 && vespera != null && vespera[13] === nossa) {
    return { dia0: ext.dia0 - 1, dia1: ext.dia1, partePelaRotacao: true };
  }
  return { ...ext, partePelaRotacao: false };
}

/** O que «Viatura e condutor…» recebe — a forma do estado `aEditar`. */
export interface AlvoDaEdicao {
  readonly viatura: number;
  readonly dia0: number;
  readonly dia1: number;
  readonly referenciaAlvo?: string;
  readonly diaDePartida?: number;
  readonly condAte?: number;
}

/**
 * 2026-10-27 · O ALVO DE «VIATURA E CONDUTOR…» — UMA formulação para as TRÊS portas.
 *
 * 📌 Havia três cópias: o «editar» do painel, o menu de contexto e o «editar a viagem que parte».
 * Só a primeira levava `condAte` (B03: o condutor leva os dias de trânsito a seguir), logo pelo
 * clique direito o dia de trânsito ficava com o condutor antigo — o defeito que a outra porta já
 * tinha corrigido. É a regra 8: «duas portas, uma fechada».
 *
 * ⚠️ A VIAGEM INTEIRA e não o dia clicado (`extentoDaViagem`). O dia de rotação em que a viagem
 * parte vai à parte (`diaDePartida`) e o `dia0` do painel é o dia seguinte: a escrita do condutor
 * é por papel, e um intervalo a começar no dia de rotação mandava o papel `partida` para a viagem
 * inteira — os dias seguintes (`principal`) não recebiam o condutor.
 */
export function alvoDaEdicao(
  linha: readonly (CelulaCompacta | null)[],
  corridas: readonly string[],
  v: number,
  d: number,
): AlvoDaEdicao {
  const ext = extentoDaViagem(linha, d);
  const ref = corridas[linha[d]?.[10] ?? -1];
  return {
    viatura: v,
    dia0: ext.partePelaRotacao ? ext.dia0 + 1 : ext.dia0,
    dia1: ext.dia1,
    ...(ext.partePelaRotacao && ref !== undefined ? { referenciaAlvo: ref, diaDePartida: ext.dia0 } : {}),
    condAte: ateAoFimDoTransito(linha, ext.dia1),
  };
}

/**
 * 2026-10-27 · O alvo para a viagem que PARTE num dia de rotação — `null` sem segunda viagem.
 *
 * O intervalo é o DELA (`extentoDaViagemQueParte`), do dia de rotação até ao fim dela, com a
 * referência explícita: o painel abre com «a que PARTE» marcada (`papel-inicial-do-painel.test.tsx`).
 *
 * 📗 Decisão do cliente, 2026-10-27: esta porta muda o condutor da VIAGEM TODA — o dia de rotação
 * (linha `partida`) e os dias seguintes dela (linhas `principal` com a referência dela). Quem o faz
 * no servidor é `linhasDoPapelSql` (`condutor.ts`); antes, com o papel `partida`, só o dia de
 * rotação mudava e o ecrã dizia «Condutor actualizado». Escolher no selector só o dia de rotação
 * continua a mudar só esse dia.
 * ✅ Por isso o `condAte` (B03) passa a fazer sentido também aqui: o dia de estrada a seguir é uma
 * linha sem viagem, que a mesma condição apanha. A MESMA conta de `alvoDaEdicao` (`ateAoFimDoTransito`).
 */
export function alvoDaEdicaoQueParte(
  linha: readonly (CelulaCompacta | null)[],
  corridas: readonly string[],
  v: number,
  d: number,
): AlvoDaEdicao | null {
  const ext = extentoDaViagemQueParte(linha, d);
  if (ext === null) return null;
  const ref = corridas[linha[d]?.[13] ?? -1];
  if (ref === undefined) return null;
  return {
    viatura: v,
    dia0: ext.dia0,
    dia1: ext.dia1,
    referenciaAlvo: ref,
    condAte: ateAoFimDoTransito(linha, ext.dia1),
  };
}

/**
 * As corridas candidatas a esticar/encurtar a partir do dia `d` desta linha (§11 · M) — em
 * geral uma só; num dia de rotação, a que ACABA e a que PARTE.
 *
 * ⚠️⚠️ **SÓ ENTRA QUEM TEM PEDIDO** (campo 16 ≥ 0). O arrasto reutiliza `/api/pedidos/alterar`
 *    (regra 8 — nenhuma escrita nova), e essa rota exige um `pedidoId`. Sem pedido não há para
 *    onde escrever a mudança, e oferecer a pega sobre um serviço assim era um punho que carrega
 *    a janela e não tem o que lhe mandar — pior do que não a oferecer.
 *
 * ⚠️ **O pedido da viagem que PARTE lê-se do dia SEGUINTE ao de rotação**, não deste: no dia de
 *    rotação ela só existe no campo 13 (ver `extentoDaViagemQueParte`); a partir do dia seguinte
 *    é ela a corrida `principal` da própria linha, com o seu próprio campo 16. Uma partida de
 *    UM dia só (acaba no mesmo dia em que começa) não tem «dia seguinte que ainda é dela» —
 *    fica sem candidata, e é uma limitação declarada e não um defeito: rara, e sem ela não há
 *    pedido nenhum a alterar.
 */
export function candidatasDaBarra(
  linha: readonly (CelulaCompacta | null)[],
  d: number,
): readonly { readonly dia0: number; readonly dia1: number; readonly pedidoIndice: number }[] {
  const candidatas: { dia0: number; dia1: number; pedidoIndice: number }[] = [];
  const c = linha[d];
  if (c != null && c[0] >= 0 && c[16] >= 0) {
    candidatas.push({ ...extentoDaCorrida(linha, d), pedidoIndice: c[16] });
  }
  const partida = extentoDaViagemQueParte(linha, d);
  if (partida !== null && partida.dia1 > d) {
    const pedidoIndice = linha[d + 1]?.[16] ?? -1;
    if (pedidoIndice >= 0) candidatas.push({ ...partida, pedidoIndice });
  }
  return candidatas;
}

// ---------------------------------------------------------------------------
// Uma linha de SUBCONTRATADO
// ---------------------------------------------------------------------------

/**
 * **UMA LINHA POR SERVIÇO**, agrupadas sob o nome da empresa, em secção própria abaixo da frota.
 *
 * ⚠️ **PORQUE É QUE A SECÇÃO É PRÓPRIA**, e a razão é do domínio e não de desenho: uma linha da
 * grelha da frota representa **um objecto físico** que só pode estar num sítio de cada vez — é
 * isso que dá sentido a «uma célula por dia» e é isso que o índice único da base impõe. Um
 * subcontratado **não é um autocarro**: é uma empresa com frota própria, e pode legitimamente
 * ter nove serviços no mesmo dia (D-002).
 *
 * ⚠️⚠️ **C₂ — E A SAÍDA É DAR-LHE TANTAS LINHAS QUANTOS OS SERVIÇOS SIMULTÂNEOS.**
 *
 * 📗 Cliente (§4.3): *«Os subcontratados têm de dar para ver os dias igualmente como se fosse um
 * autocarro normal; a única diferença é que não se indica o motorista.»*
 *
 * 📌 **Isto NÃO revoga D-002 — cumpre-o melhor.** A empresa continua a ser uma empresa: o que
 * muda é que ela ocupa tantas linhas quantos os serviços do seu pior dia do ano, e **cada linha
 * é um serviço**. É isso que faz a selecção, o realce, o cartão, o painel e as setas
 * funcionarem sem um único caso especial — todos passam a apontar para **um** serviço.
 *
 * ⚠️ **O que saiu com o C₂ foram as BANDAS de B28** — e com elas o tecto de 4 e a calha dos
 * escondidos. Um tecto era a parte que ainda falhava: na folha real duas empresas passam-no
 * (9 e 8 serviços no mesmo dia) e os serviços acima dele eram contados, não desenhados.
 *
 * ⚠️ **O contra-facto de B28 continua a pagar-se:** 9 de 16 empresas nunca passam de UM serviço
 * no mesmo dia em todo o ano, logo ficam com **uma** linha — exactamente o que tinham.
 *
 * Não tem banda de motorista: o motorista é **deles**, e nós não o conhecemos.
 */
/**
 * ⚠️⚠️ **A BANDA DAS CIDADES DE UMA LINHA SEM CÉLULAS DE DIA — os PARCEIROS e a ESPERA.**
 *
 * 📗 Cliente, 2026-09-16: *«Subcontratados com UI muito parecido no mapa aos pedidos
 * convencionais, com as cidades por dias seguindo a mesma estrutura. Para além disso, quando
 * movo um pedido para o final ficando pendente quero que também guarde a UI pelas cidades por
 * dia e não aparecer apenas a linha única entre várias outras.»* — e já o tinha pedido em
 * §4.3: *«têm de dar para ver os dias igualmente como se fosse um autocarro normal»*.
 *
 * ⚠️ **UM componente para as DUAS secções, e não um por secção.** É a regra 8: a cidade de um
 * dia é a mesma coisa nos parceiros e na espera, e a segunda cópia seria a que ninguém corre.
 * 📌 O que continua separado é o **vocabulário das barras** (`.sub-barra` contra
 * `.espera-barra`), porque aí as coisas **são** diferentes — uma viagem em espera não é um
 * parceiro. A cidade não: é cidade nas duas.
 *
 * ---------------------------------------------------------------------------
 * ⚠️⚠️ PORQUE É QUE ISTO NÃO É A CÉLULA `.dia` DA FROTA — e o número que o decide
 * ---------------------------------------------------------------------------
 *
 *   A frota mete a cidade **dentro da célula `.dia`**, que existe porque é o alvo do rato.
 *   Estas secções não têm células de dia: têm barras em absoluto sobre a faixa. Medido na
 *   folha real (`sonda-cidades-por-dia-no-subcontratado.mjs`, pior janela de 40 dias):
 *
 *   | | nós acrescentados | o mapa inteiro (3 246) |
 *   |---|---|---|
 *   | copiar a célula `.dia` à letra | **+1 400** só de células VAZIAS, mais as cidades | +63 % |
 *   | a marcação da frota: `span` + `-txt` (+1 286) e as setas (+304) | **+1 590** | +49 % |
 *   | **um nó por dia ocupado** ← isto | **+643** | **+19,8 %** |
 *
 *   ⚠️ O mapa mede **3 246 nós** com p95 29,5–45,2 ms (ADR-001, risco R1), e este ficheiro já
 *   recusa um ícone em 19 % das barras porque *«~2 nós cada em 600 dias visíveis, e o p95
 *   nota-o»*. **Entre as duas marcações vão +947 nós, 29 % do mapa inteiro.**
 *
 *   📌 **O que se perde, declarado:** o embarque vai no MESMO nó (`MAD/LIS`) em vez de num
 *   `<span>` mais fraco; a seta de chegada/partida é um CARÁCTER no mesmo nó (`↓`/`↑`, desde
 *   2026-10-08) e não o ícone — como ícone custaria **+304** nós, 23,6 % dos dias; não há ícone
 *   de meio. ⚠️ **Nada disto é informação que existisse e tenha
 *   desaparecido** — esta secção não tinha banda nenhuma, logo o saldo é só de adição.
 *
 * ⚠️ **E «a cidade só quando MUDA» foi medido e recusado:** poupa **142 nós (22,1 %)** e
 * contradiz uma decisão do cliente — a frota só suprime repetições num dia **parado**, e os
 * dias de um parceiro são viagem. 📗 *«tens de colocar a pernoita do dia, para quem olhar para
 * o mapa e perceber onde o autocarro vai pernoitar»*. 22 % não paga isso.
 *
 * ⚠️ **`pointer-events: none`**, no CSS: hoje tudo o que não é barra nesta linha é área morta
 * para o rato, e uma banda que engolisse o ponteiro **tirava** o cartão do cursor em vez de o
 * dar. Dar-lhe `data-sl` é o passo seguinte e não é este — o ouvinte delegado lê também
 * `data-d1`, que uma cidade não tem.
 */
const BandaDeLocais = memo(function BandaDeLocais({
  dados,
  linha,
  janela,
  offsetsDias,
}: {
  readonly dados: DadosMapa;
  /** O ano inteiro da linha: `linha[d]` tem **zero ou um** serviço (C₂). */
  readonly linha: readonly (readonly CelulaCompacta[])[];
  readonly janela: Janela;
  readonly offsetsDias: readonly number[];
}) {
  const { dia0, dia1 } = janela;
  const nos = [];
  for (let d = dia0; d < dia1; d++) {
    // ⚠️ **`anterior` é `null` no primeiro dia da janela** — a mesma excepção da frota, e pela
    //    mesma razão: sem ela a coluna da esquerda ficava sem contexto ao deslocar o mapa.
    const r = localDoDia(
      dados,
      (linha[d] ?? [])[0] ?? null,
      d > dia0 ? ((linha[d - 1] ?? [])[0] ?? null) : null,
    );
    if (r.tipo === 'nada' || r.tipo === 'repetido') continue;
    // `null`: as horas destas linhas não vêm no contrato do mapa — não é «por definir».
    nos.push(noDoLocalSolto(d, r, offsetsDias, null));
  }
  return <>{nos}</>;
});

/**
 * ---- 🔴 O NÓ de uma cidade na banda solta — UM, para as três secções do fundo (2026-10-08) ----
 *
 * 📗 Cliente, 2026-10-08: *«as cidades também e todos os outros detalhes como acontece nas
 * alocações normais»*. Saiu de `BandaDeLocais` para a fila «por planear» o usar sem uma segunda
 * cópia das classes e dos `title` (regra 8).
 *
 * ⚠️⚠️ **A SETA de chegada/partida entra no MESMO nó, como texto (`↓`/`↑`) — e é o desempenho
 * que o decide.** O cabeçalho de `BandaDeLocais` recusou-a como ícone por **+304 nós** (23,6 % dos
 * dias); como carácter custa **zero** nós, e diz o mesmo que o ícone da frota (que substituiu
 * estes mesmos dois glifos). A hora, quando se sabe, vai no `title`.
 */
function noDoLocalSolto(
  d: number,
  r: LocalDoDia,
  offsetsDias: readonly number[],
  /** `null` = a hora não se conhece AQUI (não vem no contrato); `''` = o pedido não a tem. */
  hora: string | null,
) {
  const inferido = r.tipo === 'local' && r.inferido;
  const seta = r.tipo !== 'local' ? '' : r.sentido === 'chegada' ? '↓' : r.sentido === 'partida' ? '↑' : '';
  const doSentido =
    r.tipo !== 'local' || r.sentido === null
      ? ''
      : `${r.sentido === 'chegada' ? 'chegada do grupo (in)' : 'partida do grupo (out)'}${
          hora === null ? '' : hora === '' ? ' — hora por definir' : ` às ${hora}`
        }`;
  const explicacao =
    r.tipo === 'falta'
      ? TITULO_LOCAL_EM_FALTA
      : r.tipo === 'local' && r.presumido
        ? TITULO_DESTINO_PRESUMIDO
        : inferido
          ? TITULO_LOCAL_INFERIDO
          : '';
  const titulo = [doSentido, explicacao].filter((t) => t !== '').join(' — ');
  return (
      <span
        key={d}
        // ⚠️⚠️ **AS MESMAS CLASSES DA FROTA, e isso não é F-20 — é o contrário.** F-20 proíbe
        //    duas coisas DIFERENTES com o mesmo nome; aqui é a **mesma** coisa: a cidade de um
        //    dia, com o mesmo `?` tracejado de D-081 e o mesmo tom de inferida. Nome novo é
        //    que ensinava que são sinais diferentes. `--solta` só diz que está posicionada em
        //    absoluto, fora de uma célula `.dia`.
        className={classes(
          'dia-local',
          'dia-local--solta',
          r.tipo === 'falta' && 'dia-local--falta',
          inferido && 'dia-local--inferido',
        )}
        title={titulo === '' ? undefined : titulo}
        style={{ left: offsetsDias[d] ?? 0, width: tamanho(offsetsDias, d) }}
      >
        {/* Um só texto: dois `{}` seguidos eram dois nós de texto. */}
        {seta + textoDoLocal(r)}
      </span>
  );
}

const LinhaSubcontratado = memo(function LinhaSubcontratado({
  dados,
  indice,
  linhaSub,
  janela,
  tema,
  corLigada,
  filtro,
  offsetsDias,
  altura,
  diaSeleccionado = null,
}: {
  readonly dados: DadosMapa;
  /** O índice da LINHA na secção — não o da empresa. É o que o `data-sl` leva. */
  readonly indice: number;
  readonly linhaSub: LinhaDeSubcontratado;
  readonly janela: Janela;
  readonly tema: Tema;
  readonly corLigada: boolean;
  readonly filtro: Filtro;
  readonly offsetsDias: readonly number[];
  readonly altura: number;
  /**
   * ---- ⚠️⚠️ PEÇA 3 · O DIA SELECCIONADO NESTA LINHA — leitor de ecrã (§12.6, 2026-09-28) -----
   *
   * `null` em quase todas as linhas — mesma disciplina de `diaSeleccionado` na `Linha` da
   * frota (§12.6): só a linha apontada por `seleccaoSub` recebe um número, as outras `null`.
   *
   * ⚠️ **NÃO É A CHAVE DE UMA CÉLULA — é um DIA dentro do intervalo de UMA `.sub-barra`.** Não
   * há uma célula por dia nesta secção (é uma barra por SERVIÇO), logo o `role="option"` vai na
   * barra cujo `[d0, d1]` contém este dia, e não numa célula que aqui não existe.
   */
  readonly diaSeleccionado?: number | null;
}) {
  const s = dados.subcontratados[linhaSub.sub];
  const linha = linhaSub.dias;
  const { dia0, dia1 } = janela;

  /**
   * ⚠️⚠️ **A REGRA VIVE NUM MÓDULO PURO E TESTÁVEL, e não aqui.** A versão anterior construía
   * as barras dentro do componente, e o mesmo raciocínio estava copiado no `CartaoSub` e no
   * `detalheSub` — três formulações da mesma regra, com a garantia de que a que divergisse
   * seria a que ninguém corre.
   */
  const barras = useMemo(() => barrasDaLinhaSub(linha, dia0, dia1), [linha, dia0, dia1]);

  const celulas = barras.map((b) => {
    // ⚠️⚠️ **A RESERVA TEM DE SE VER, e nao se via.**
    //
    //   📗 Cliente, 2026-08-10: *«fiz um subcontrato, apareceu como reservado e nao apareceu
    //   no mapa»*. A causa principal era geometrica (ver `esquerda`), mas ao confirmar na base
    //   que a escrita estava certa apareceu isto: as duas reservas subcontratadas do `dev`
    //   estao em `estado_registo = 'reserva'` e a barra **desenhava-as como servico firme**.
    //
    // 📌 **Uma reserva indistinguivel de um servico confirmado e pior do que nao aparecer:**
    //   quem nao a ve procura-a; quem a ve como confirmada deixa de a procurar. A frota
    //   distingue-as por TEXTURA desde o inicio (V6) e a seccao dos subcontratados nasceu sem
    //   isso — a mesma omissao que fez os subcontratados ficarem com o desenho antigo.
    //
    // ⚠️⚠️ **QUALQUER dia da barra**, e nao o primeiro: uma reserva que cobrisse so parte do
    //   intervalo ficava invisivel se o primeiro dia fosse firme. E a mesma regra que a frota
    //   aplica ao acumular as bandeiras da corrida — e vive agora dentro de
    //   `barrasDaLinhaSub`, que a acumula ao longo do servico.
    const dentro = activo(filtro.clientes, b.cliente);
    // ⚠️ A cor manual de UMA viagem (migração 055) vence sempre — é uma escolha do
    // planeador para esta corrida em concreto, nunca a de omissão do cliente.
    const refCorridaSub = b.corrida >= 0 ? dados.corridas[b.corrida] : undefined;
    const indiceCorSub = refCorridaSub !== undefined ? dados.coresCorrida[refCorridaSub] : undefined;
    // ⚠️ A cor ESCOLHIDA do cliente (migração 051, ligada ao mapa em 2026-08-29) —
    //    os subcontratados não têm modo «por itinerário» (são sempre por cliente
    //    aqui), logo aplica-se sempre que exista. Perde para a cor manual acima.
    const cor =
      !corLigada || !dentro
        ? corCliente(-1, tema)
        : indiceCorSub !== undefined
          ? corCliente(indiceCorSub, tema)
          : corDoCliente(dados, b.cliente, tema);
    // ⚠️⚠️ **OFFSET ABSOLUTO, exactamente como as barras da frota** — e nao relativo a janela.
    //
    //   📗 Cliente, 2026-08-10: *«fiz um subcontrato, apareceu como reservado e nao apareceu
    //   no mapa. O que se passa???»*
    //
    // ⚠️⚠️ **A escrita estava certa; a barra e que era desenhada fora do ecra.** Eu tinha posto
    //   `offsetsDias[dia] - offsetsDias[janela.dia0]` dentro de um contentor com
    //   `paddingLeft: offsetsDias[janela.dia0]` — e **`left` de um elemento absoluto resolve-se
    //   contra a CAIXA DE PADDING do pai**, logo o `padding` nao o desloca. Resultado: a barra
    //   ia `offsetsDias[janela.dia0]` pixeis **para a esquerda** do sitio certo. Numa janela
    //   pelo dia 220 sao ~8 800 px: muito fora do ecra.
    //
    // 📌 **E so era visivel com a janela no dia 0** — que e onde todos os meus testes a
    //   punham. A cobertura era real e o ponto cego tambem: `janela.dia0 = 0` faz o termo
    //   errado valer zero, e o teste passa a afirmar que a geometria esta certa.
    //
    // ⚠️⚠️ **A causa de fundo foi ter inventado uma segunda convencao para a mesma coisa.** As
    //   barras da frota vivem em `.camada { position: absolute; inset: 0 }` e usam
    //   `esq(d) = offsetsDias[d]`, sem subtraccao nenhuma. Duas convencoes para a mesma
    //   geometria divergem — e divergiram no primeiro dia de uso.
    const esquerda = offsetsDias[b.d0] ?? 0;
    const largura = (offsetsDias[b.d1 + 1] ?? 0) - (offsetsDias[b.d0] ?? 0);
    const sigla = dados.clientes[b.cliente]?.[0] ?? '—';
    /**
     * ---- ⚠️⚠️ PEÇA 3 · A SEMÂNTICA DE UM ITEM DE LISTA — não uma célula (2026-09-28) --------
     *
     * 📌 **Justificação:** não há uma célula por dia nesta secção — é uma barra por SERVIÇO, e
     * inventar uma célula só para o leitor de ecrã era «flexibilidade não pedida» (regra 2). O
     * padrão certo é `listbox`/`option` (ver o wrapper em `Mapa`): é exactamente o que já existe
     * sem ARIA — uma colecção de itens que se navega com as setas (C₂, `navegar-com-as-setas
     * .test.tsx`) e se selecciona, mostrando o detalhe no painel. `aria-selected` e a `tabIndex`
     * a seguir são a MESMA disciplina do §12.6 na frota, e não uma segunda formulação.
     *
     * ⚠️ **O nome acessível diz cliente, referência e datas** — os três que a barra já mostra
     * visualmente (sigla, itinerário, cor) e o `title` explica por extenso: sem eles um leitor
     * de ecrã anuncia só "opção", que não diz qual das N opções desta linha é esta.
     */
    const selCliente = dados.clientes[b.cliente]?.[1] ?? sigla;
    const rotuloAcessivel =
      `${selCliente}` +
      (refCorridaSub === undefined ? '' : ` · ${refCorridaSub}`) +
      ` · ${dataCurtaDoDia(dados, b.d0)} a ${dataCurtaDoDia(dados, b.d1)}` +
      (b.reserva ? ' · reserva, ainda não confirmada' : '');
    const seleccionada =
      diaSeleccionado !== null &&
      diaSeleccionado !== undefined &&
      diaSeleccionado >= b.d0 &&
      diaSeleccionado <= b.d1;
    return (
      <div
        key={`${b.d0}-${b.corrida}-${b.cliente}`}
        // ⚠️ **`data-sl` e NÃO `data-v`.** O ouvinte delegado do mapa procura `[data-v]` e
        // usa-o como índice em `dados.viaturas`: reutilizá-lo aqui fazia o cartão do cursor
        // descrever a viatura número 3 da frota em cima do serviço do subcontratado 3 — um
        // valor plausível no lugar do certo, que é a classe de defeito que este projecto
        // persegue. Espaços de índice diferentes, atributos diferentes.
        //
        // ⚠️⚠️ **E `data-sl` e NÃO `data-sub`, que é o nome que aqui esteve até ao C₂.** O
        // `data-sub` levava o índice da **EMPRESA**; este leva o da **LINHA**. Manter o nome
        // com o significado trocado era «vestir a roupa de outra coisa»: cada leitor antigo
        // continuaria a compilar e a ler um índice de empresa onde está um de linha — um valor
        // plausível no lugar do certo, em silêncio. Com o nome novo, quem ficou para trás
        // **parte**, que é o que se quer.
        data-sl={indice}
        data-d={b.d0}
        // ⚠️⚠️ **`data-d1` é o fim DESTA barra, e existe para o cartão não o recalcular.** O
        // `CartaoSub` reconstruía o intervalo com uma segunda cópia da regra de continuidade —
        // e essa cópia trazia a condição «e o mesmo número de serviços simultâneos», que morre
        // com a contagem. Ler o fim do próprio elemento que se está a apontar é a única forma
        // que **não pode** divergir do que está desenhado.
        data-d1={b.d1}
        role="option"
        aria-selected={seleccionada}
        aria-label={rotuloAcessivel}
        // ⚠️ Peça 3 · roving tabindex, MESMA disciplina do §12.6 na frota — só a opção
        //   seleccionada fica no percurso do `Tab`; o foco do DOM segue-a no `useEffect` de
        //   `Mapa` (a mesma extensão que já move o foco para `seleccao`).
        tabIndex={seleccionada ? 0 : -1}
        className={classes(
          'sub-barra',
          !dentro && 'sub-barra--fora',
          b.reserva && 'sub-barra--reserva',
        )}
        style={{
          left: esquerda,
          width: Math.max(0, largura - 2),
          // ⚠️⚠️ **A GEOMETRIA VERTICAL VOLTOU A SER SÓ DO CSS, e é um ganho do C₂.** Com bandas,
          //   o TSX escrevia `top`/`height` em 44 % das linhas e o CSS mandava nas outras — duas
          //   fontes para a mesma medida. Com uma linha por serviço **todas as linhas medem o
          //   mesmo**, logo `.sub-barra { top; height }` é outra vez a única fonte.
          // ⚠️ **Eram 30 px com uma banda; são 36 desde 2026-09-16, com a banda das cidades.**
          //   A conta inteira está em `ALTURA_LINHA_SUB` (`dimensoes.ts`) e não aqui — repeti-la
          //   aqui era a segunda fonte que este parágrafo existe para impedir.
          // ⚠️ **A cor do cliente é a TAMPA, não o corpo — ao contrário da frota.**
          //   📗 Cliente: *«tem é de ter uma cor característica diferente»*. Invertendo os
          //   dois papéis, um serviço subcontratado nunca se confunde com um nosso à
          //   distância a que se varre o mapa, e a cor do cliente continua presente para
          //   quem procura um cliente. ⚠️ **A contrapartida, declarada:** nesta secção
          //   perde-se a varredura por mancha de cor; ficam 4 px de tampa e a sigla.
          borderLeftColor: cor.fundo,
        }}
      >
        <span className="sub-barra-txt">{sigla}</span>
        {/* ⚠️⚠️ **O NOME DO ITINERARIO na barra, como na frota** — 📗 *«tem de aparecer
            igualmente todo o itinerario»*. A sigla diz o CLIENTE; o itinerario diz **que
            viagem** e, e numa empresa com tres servicos do mesmo cliente era a unica coisa que
            os distinguia. 📌 Esbatido e a encolher primeiro: a sigla e a identidade (P-30) e
            nao pode ser ela a desaparecer quando a barra e curta. */}
        {b.itinerario >= 0 ? (
          <span className="sub-barra-itin">
            {nomeOuCodigoDoItinerario(dados, b.itinerario)}
          </span>
        ) : null}
        {/* ⚠️⚠️ **AQUI ESTAVA O `BCN→MAD` DA BARRA, e saiu porque a LIMITAÇÃO que o justificava
            fechou (2026-09-16).** Ele existia por escrito como paliativo: *«a frota mostra a
            localidade numa banda própria por dia (`.dia-local`); esta secção não tem essa
            banda… a barra por serviço traz o PRIMEIRO e o ÚLTIMO local»*. 📗 O cliente pediu
            **duas vezes** a banda a sério, e ela existe agora (`BandaDeLocais`, abaixo).
            📌 **Não é informação apagada — é informação promovida:** o primeiro e o último
            local continuam à vista, com todos os do meio, e cada um na sua coluna de dia.
            Mantê-lo era dizer a mesma coisa duas vezes e custar um nó por barra. */}
      </div>
    );
  });

  /**
   * ⚠️⚠️ **O RÓTULO: o nome vai na PRIMEIRA linha da empresa, e nas outras vai a ordem.**
   *
   * 📌 Repetir «Blablacar» em oito linhas seguidas é literalmente a queixa que fez a frota
   * deixar de repetir a sigla por dia — *«repetir `IGT` em oito células seguidas era a
   * principal razão de o mapa ser ilegível»*. O agrupamento lê-se pela continuação (`↳`) e o
   * nome inteiro fica no `title` de todas.
   *
   * ⚠️ **`.sub-nome` nas duas e não uma classe nova**, e não é F-20: `.sub-nome` é *o rótulo da
   * linha na coluna da esquerda*, que é o que estes dois são. 🅿️ O tratamento **esbatido** da
   * continuação exigiria CSS novo (`mapa.css` está fechado a este trabalho) — fica declarado
   * no relatório, com o selector escrito.
   */
  const rotulo =
    linhaSub.ordem === 0
      ? (s?.nome ?? '')
      : `↳ ${String(linhaSub.ordem + 1)}/${String(linhaSub.total)}`;

  return (
    <div className="linha linha--sub" style={{ height: altura }}>
      <div className="viatura viatura--sub">
        <span
          className="sub-nome"
          title={
            linhaSub.total > 1
              ? `${s?.nome ?? ''} — linha ${String(linhaSub.ordem + 1)} de ${String(linhaSub.total)}`
              : (s?.nome ?? '')
          }
        >
          {rotulo}
        </span>
        {/* A base do subcontratado é o que o motor usa para calcular a deslocação
            até ao serviço (D-002): não é decoração, é o custo.
            ⚠️ Só na PRIMEIRA linha da empresa: a base é da empresa e não do serviço, e
            repeti-la em nove linhas fazia-a parecer um atributo de cada uma. */}
        {linhaSub.ordem === 0 && s !== undefined && s.base >= 0 ? (
          <span className="sub-base">{dados.localidades[s.base]?.[0] ?? ''}</span>
        ) : null}
      </div>
      <div className="faixa">
        {/* ⚠️⚠️ **A MESMA CAMADA DAS BARRAS DA FROTA**: `position: absolute; inset: 0` sobre a
            faixa inteira, **sem `padding`**, e as barras em offsets ABSOLUTOS. Ver o comentario
            em `esquerda`: com `padding` e offsets relativos, a barra saia do ecra em qualquer
            janela que nao comecasse no dia 0.
            ⚠️ **Uma diferenca deliberada em relacao a `.camada`:** esta recebe o rato. Na
            frota as barras tem `pointer-events: none` para nao roubarem o cursor as celulas de
            dia, que e quem alimenta o cartao; aqui **nao ha celulas de dia** e e a propria
            barra que traz o `data-sl`. */}
        <div className="sub-barras">{celulas}</div>
        {/* 🔴 **A BANDA 2 — a cidade de cada dia.** 📗 *«com as cidades por dias seguindo a
            mesma estrutura»*. Irmã de `.sub-barras` e não filha dela: são duas bandas da
            linha, e enfiá-la lá dentro punha as cidades debaixo de um contentor chamado
            «barras». ⚠️ Custo medido: **+643 nós** na pior janela de 40 dias — ver o cabeçalho
            de `BandaDeLocais`, com as duas alternativas rejeitadas e o que custavam. */}
        <BandaDeLocais dados={dados} linha={linha} janela={janela} offsetsDias={offsetsDias} />
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// 🔴 Uma linha da secção EM ESPERA — a viagem que saiu do autocarro (peça F §6.3)
// ---------------------------------------------------------------------------

/**
 * Uma viagem à espera de autocarro, desenhada como qualquer outra linha do mapa.
 *
 * 📗 Cliente: *«ou numa linha dummy sem autocarro no final do mapa após os subcontratados, tipo
 * em espera … para o planeador corrigir o mapa manualmente sem destruir as ligações perfeitas
 * entre viagens»*.
 *
 * ⚠️⚠️ **REUTILIZA A MÁQUINA DO C₂ E NÃO A SECÇÃO DOS SUBCONTRATADOS.** `repartirPorLinhas`,
 * `formaDaSeccaoSub` e `barrasDaLinhaSub` são os mesmos — é o que faz estas linhas terem a
 * geometria, os offsets e as barras da frota sem um único caso especial. O que **não** se
 * reutiliza são as classes: uma viagem em espera não é um parceiro, e `.sub-*` sobre ela era
 * **F-20** — o defeito que já custou 54 px do cabeçalho dos meses.
 *
 * ⚠️ **Sem banda de motorista, e não é porque não haja condutor** — há, e é a condição que o
 * cliente pôs (*«tem de sobreviver TUDO, incluindo condutores»*). É porque a barra do condutor
 * da frota descreve **quem conduz o autocarro naquele dia**, e aqui não há autocarro nenhum:
 * desenhá-la afirmava que alguém está a conduzir uma viagem que não saiu.
 */
const LinhaDeEspera = memo(function LinhaDeEspera({
  dados,
  indice,
  linhaEspera,
  janela,
  tema,
  corLigada,
  filtro,
  offsetsDias,
  altura,
}: {
  readonly dados: DadosMapa;
  /** O índice da LINHA na secção — é o que o `data-el` leva. */
  readonly indice: number;
  readonly linhaEspera: LinhaEmEspera;
  readonly janela: Janela;
  readonly tema: Tema;
  readonly corLigada: boolean;
  readonly filtro: Filtro;
  readonly offsetsDias: readonly number[];
  readonly altura: number;
}) {
  const linha = linhaEspera.dias;
  const { dia0, dia1 } = janela;
  const barras = useMemo(() => barrasDaLinhaSub(linha, dia0, dia1), [linha, dia0, dia1]);

  const celulas = barras.map((b) => {
    const dentro = activo(filtro.clientes, b.cliente);
    // ⚠️ A cor manual de UMA viagem (migração 055) vence sempre — mesma precedência da frota e
    //    dos subcontratados. Uma viagem não muda de cor por ficar à espera de autocarro.
    const refCorrida = b.corrida >= 0 ? dados.corridas[b.corrida] : undefined;
    const indiceCor = refCorrida !== undefined ? dados.coresCorrida[refCorrida] : undefined;
    const cor =
      !corLigada || !dentro
        ? corCliente(-1, tema)
        : indiceCor !== undefined
          ? corCliente(indiceCor, tema)
          : corDoCliente(dados, b.cliente, tema);
    // ⚠️⚠️ **OFFSET ABSOLUTO**, como a frota e os subcontratados — ver o comentário de
    //    `esquerda` em `LinhaSubcontratado`: uma segunda convenção para esta geometria já mandou
    //    barras `offsetsDias[janela.dia0]` píxeis para fora do ecrã, e só se via fora do dia 0.
    const esquerda = offsetsDias[b.d0] ?? 0;
    const largura = (offsetsDias[b.d1 + 1] ?? 0) - (offsetsDias[b.d0] ?? 0);
    const sigla = dados.clientes[b.cliente]?.[0] ?? '—';
    const ref = b.corrida >= 0 ? (dados.corridas[b.corrida] ?? '') : '';
    /**
     * ---- ⚠️⚠️ PEÇA 3 · SEMÂNTICA DE BOTÃO — a barra EXECUTA, não selecciona (2026-09-28) -----
     *
     * 📌 **Justificação, e é diferente da dos subcontratados:** aqui um clique já EXECUTA a
     * única acção da secção («Trazer de volta…») — não há estado de selecção nenhum a mostrar
     * (`a-espera-ve-se-no-mapa.test.tsx`, «as setas», medido: *«um clique numa barra em espera
     * já executa a única acção que a secção tem»*). `role="button"` é o que comunica isso a um
     * leitor de ecrã — activar isto FAZ, não escolhe.
     *
     * ⚠️⚠️ **SEM `tabIndex`, de propósito — «semântica sim, foco não».** Essa mesma prova exige
     * ZERO `[data-el][tabindex]`: pôr um `tabIndex={-1}` aqui teria feito o critério acender à
     * primeira corrida, porque `-1` já É um valor do atributo. Não pertence à ordem do `Tab`
     * porque não há selecção nenhuma que o `Tab` complete — a mesma razão, ao contrário.
     */
    const clienteNome = dados.clientes[b.cliente]?.[1] ?? sigla;
    const rotuloAcessivel =
      `Trazer de volta ao mapa: ${clienteNome}` +
      (ref === '' ? '' : ` · ${ref}`) +
      ` · ${dataCurtaDoDia(dados, b.d0)} a ${dataCurtaDoDia(dados, b.d1)}` +
      (b.reserva ? ' · reserva, ainda não confirmada' : '');
    return (
      <div
        key={`${b.d0}-${b.corrida}-${b.cliente}`}
        // ⚠️⚠️ **`data-el` — espaço de índices PRÓPRIO.** Não é `data-v` (que indexa
        //    `dados.viaturas`) nem `data-sl` (que indexa as linhas dos subcontratados):
        //    reutilizar qualquer um deles fazia um leitor antigo ler um índice de outra coisa e
        //    descrever a linha errada com confiança — *«um valor plausível no lugar do certo»*.
        //    Espaços de índice diferentes, atributos diferentes.
        data-el={indice}
        data-d={b.d0}
        // 🔴 2026-10-08 · ARRASTA-SE PARA UMA VIATURA — 📗 *«tem de dar para arrastando mover»*.
        //    Largar numa linha abre o MESMO «Trazer de volta» do clique, com a viatura escolhida.
        //    ⚠️ O intervalo é o do ANO (`extentoDoServico`), pela razão do clique: a barra está
        //    cortada pela janela e trazer só os dias visíveis era uma escrita parcial.
        draggable
        onDragStart={(e) => {
          const ext = extentoDoServico(linha, b.d0);
          iniciarArrastoDeEspera(e.dataTransfer, { referencia: ref, dia0: ext.d0, dia1: ext.d1 });
        }}
        role="button"
        aria-label={rotuloAcessivel}
        // ---- ⚠️⚠️ UMA RESERVA NÃO É UM SERVIÇO CONFIRMADO, e aqui era (2026-09-16) ---------
        //
        //   📌 `barrasDaLinhaSub` **já calculava** `b.reserva` — acumulado ao longo do serviço,
        //   *«QUALQUER dia da barra, e não o primeiro»* —, e esta linha era a única das três
        //   secções do mapa que o **deitava fora**. Uma viagem reservada e uma confirmada
        //   desenhavam-se exactamente iguais: *«quem não a vê procura-a; quem a vê como
        //   confirmada deixa de a procurar»*.
        //
        // ⚠️ **A MESMA formulação dos parceiros, não uma segunda:** o sinal é o `✓` num
        //   `::after` de quem **não** é reserva (`.espera-barra:not(.espera-barra--reserva)`),
        //   espelho exacto de `.sub-barra:not(.sub-barra--reserva)`. Textura/pintura e não
        //   transparência (V6) — a transparência apagaria também a sigla —, e um
        //   pseudo-elemento **não cria nó nenhum**, logo o orçamento do risco R1 não mexe.
        className={classes(
          'espera-barra',
          !dentro && 'espera-barra--fora',
          b.reserva && 'espera-barra--reserva',
        )}
        style={{
          left: esquerda,
          width: Math.max(0, largura - 2),
          borderLeftColor: cor.fundo,
        }}
        // ⚠️ **E o estado vai também por palavras no `title`**, porque o `✓` é um sinal e um
        //   sinal aprende-se; a frase é o que o explica sem ninguém ter de perguntar.
        title={`${ref === '' ? 'viagem sem referência' : ref} — ${
          b.reserva ? 'RESERVA, ainda não confirmada' : 'confirmada'
        }, à espera de autocarro. Clique para a trazer de volta, ou arraste-a para uma viatura.`}
      >
        <span className="espera-barra-txt">{sigla}</span>
        {b.itinerario >= 0 ? (
          <span className="espera-barra-itin">
            {nomeOuCodigoDoItinerario(dados, b.itinerario)}
          </span>
        ) : null}
        {/* ⚠️ **O `BCN→MAD` saiu daqui pela mesma razão que saiu da `.sub-barra`** — é agora a
            banda das cidades, dia a dia. 📗 *«quando movo um pedido para o final ficando
            pendente quero que também guarde a UI pelas cidades por dia e não aparecer apenas a
            linha única entre várias outras»*: é literalmente esta linha que ele descreve. */}
      </div>
    );
  });

  return (
    <div className="linha linha--espera" style={{ height: altura }}>
      <div className="viatura viatura--espera">
        {/* ⚠️ **«sem autocarro» e não um número inventado.** A coluna da esquerda é onde vive a
            identidade da linha, e aqui a identidade É a ausência: pôr lá um traço ou um número
            de fantasia fazia a linha parecer uma viatura da frota que ninguém reconhece. */}
        <span
          className="espera-nome"
          title={
            linhaEspera.total > 1
              ? `sem autocarro — linha ${String(linhaEspera.ordem + 1)} de ${String(linhaEspera.total)}`
              : 'sem autocarro'
          }
        >
          {linhaEspera.ordem === 0
            ? 'sem autocarro'
            : `↳ ${String(linhaEspera.ordem + 1)}/${String(linhaEspera.total)}`}
        </span>
      </div>
      <div className="faixa">
        <div className="espera-barras">{celulas}</div>
        {/* 🔴 **A MESMA banda das cidades dos parceiros — o MESMO componente.** 📗 *«que
            também guarde a UI pelas cidades por dia»*. ⚠️ Uma segunda cópia aqui seria a
            regra 8 à letra: a cidade de um dia é a mesma coisa nas duas secções, e a cópia que
            divergisse era a que ninguém corria. O que continua separado são as **barras**,
            porque aí as coisas são mesmo diferentes. */}
        <BandaDeLocais dados={dados} linha={linha} janela={janela} offsetsDias={offsetsDias} />
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// 🔴 K · Uma linha da secção POR PLANEAR — pedidos que nunca tiveram alocação
// ---------------------------------------------------------------------------

/**
 * Uma «pista» da fila por planear: pedidos que nunca se sobrepõem no tempo, desenhados
 * como barras nas SUAS datas — ver `por-planear-no-mapa.ts` para o algoritmo e o porquê
 * de não reutilizar `LinhaDeEspera`.
 *
 * 🔴 **As cidades de cada dia — desde 2026-10-08.** 📗 *«tem de aparecer para além da linha
 * simples as cidades também e todos os outros detalhes como acontece nas alocações normais»*.
 * ⚠️ Isto dizia que um pedido «só conhece o ponto de recolha e o de largada» — era verdade da
 * LISTA, não da base: o percurso inteiro (pernoitas incluídas) está em `pedido_ponto`, e chega
 * agora por `/api/pedidos/percursos`. A cidade de cada dia é a que a RESERVA vai escrever
 * (`diasDoPedido`, sobre a regra de `reservar.ts`), e os dias sem registo vêem-se INFERIDOS, como
 * na frota — nada se inventa.
 *
 * 🔴 **E arrasta-se para uma viatura**, como o cartão da lista (`arrastoDoPedido`) — o mesmo
 * gesto, a mesma confirmação. Só o que se poderia planear (`razaoParaNaoPlanear`).
 */
const LinhaPorPlanear = memo(function LinhaPorPlanear({
  dados,
  itens,
  ordem,
  total,
  janela,
  tema,
  corLigada,
  filtro,
  offsetsDias,
  indicePorSigla,
  percursos,
  altura,
  aoClicar,
}: {
  readonly dados: DadosMapa;
  readonly itens: readonly ItemPorPlanear[];
  readonly ordem: number;
  readonly total: number;
  readonly janela: Janela;
  readonly tema: Tema;
  readonly corLigada: boolean;
  readonly filtro: Filtro;
  readonly offsetsDias: readonly number[];
  readonly indicePorSigla: ReadonlyMap<string, number>;
  /** O percurso de cada pedido, por id — pode ainda não ter chegado (ver `percursosDaFila`). */
  readonly percursos: Readonly<Record<string, readonly PontoDoPedidoNoMapa[]>>;
  readonly altura: number;
  readonly aoClicar: (id: string) => void;
}) {
  const { dia0, dia1 } = janela;
  // ⚠️ Os itens já vêm RECORTADOS ao ano inteiro (`filaPorPlanearNoMapa`); aqui recorta-se
  //    de novo à JANELA visível — a mesma razão de `barrasDaLinhaSub` só devolver o que cai
  //    entre `dia0` e `dia1`: desenhar fora da janela é trabalho que ninguém vê.
  const visiveis = itens.filter((it) => it.dia1 >= dia0 && it.dia0 < dia1);
  const cidades = visiveis.flatMap((it) =>
    diasDoPedido({
      janela: janelaDe(dados),
      dia0: it.dia0,
      dia1: it.dia1,
      pontos: percursos[it.pedido.id] ?? pontosDaLista(it.pedido),
    })
      .filter((x) => x.d >= dia0 && x.d < dia1)
      .map((x) => noDoLocalSolto(x.d, x.local, offsetsDias, x.hora)),
  );

  const barras = visiveis.map((it) => {
    const p = it.pedido;
    const clienteIdx = indicePorSigla.get(p.clienteSigla) ?? -1;
    const dentro = activo(filtro.clientes, clienteIdx);
    const cor =
      !corLigada || !dentro
        ? corCliente(-1, tema)
        : corDoCliente(dados, clienteIdx, tema);
    const c0 = Math.max(it.dia0, dia0);
    const c1 = Math.min(it.dia1, dia1 - 1);
    const esquerda = offsetsDias[c0] ?? 0;
    const largura = (offsetsDias[c1 + 1] ?? 0) - esquerda;
    const sigla = p.clienteSigla || '—';
    const impedimento = razaoParaNaoPlanear(p);
    return (
      <div
        key={p.id}
        draggable={impedimento === null}
        onDragStart={(e) => {
          if (impedimento !== null) return;
          iniciarArrasto(e.dataTransfer, arrastoDoPedido(p));
        }}
        className={classes(
          'planear-barra',
          !dentro && 'planear-barra--fora',
          // ⚠️ P-30 outra vez, e não uma segunda regra: `separarListas` inclui de propósito
          //    os pedidos INACTIVOS na fila «por planear» (ficam esbatidos e etiquetados na
          //    lista, nunca escondidos) — o mapa segue a mesma convenção.
          !p.activo && 'planear-barra--inactivo',
          p.urgente && 'planear-barra--urgente',
        )}
        style={{ left: esquerda, width: Math.max(0, largura - 2), borderColor: cor.fundo }}
        title={`${p.clienteNome || sigla} · ${p.referencia || '(sem referência)'} · ${p.dataInicio} a ${p.dataFim}${
          p.urgente ? ' · URGENTE' : ''
        }${p.activo ? '' : ' · inactivo'}${horasNoTitulo(p)} — ${
          impedimento === null
            ? 'arraste para uma viatura para planear, ou clique para abrir na lista de pedidos'
            : `não se planeia: ${impedimento}. Clique para abrir na lista de pedidos`
        }`}
        onClick={() => aoClicar(p.id)}
      >
        <span className="planear-barra-txt">{sigla}</span>
        {p.itinerario !== '' ? (
          <span className="planear-barra-itin">{p.itinerario}</span>
        ) : null}
      </div>
    );
  });

  return (
    <div className="linha linha--planear" style={{ height: altura }}>
      <div className="viatura viatura--planear">
        {/* ⚠️ «por planear» e não um número: como em `LinhaDeEspera`, a coluna da esquerda
            aqui não tem identidade física nenhuma para mostrar. */}
        <span
          className="planear-nome"
          title={total > 1 ? `por planear — linha ${String(ordem + 1)} de ${String(total)}` : 'por planear'}
        >
          {ordem === 0 ? 'por planear' : `↳ ${String(ordem + 1)}/${String(total)}`}
        </span>
      </div>
      <div className="faixa">
        <div className="planear-barras">{barras}</div>
        {/* 🔴 A banda das cidades — o MESMO nó das outras secções (`noDoLocalSolto`). */}
        {cidades}
      </div>
    </div>
  );
});

/** Sem o percurso (ainda não chegou), os dois pontos que a lista já traz. */
function pontosDaLista(p: Pedido): readonly PontoPedido[] {
  return [p.recolha, p.largada].filter((x): x is PontoPedido => x !== null);
}

/** « · recolha 10:00 MAD · largada 07:00 BCN» — as horas que o pedido traz, para o `title`. */
function horasNoTitulo(p: Pedido): string {
  const um = (rotulo: string, x: PontoPedido | null): string =>
    x === null ? '' : ` · ${rotulo} ${x.hora === '' ? 'hora por definir' : x.hora.slice(0, 5)}${
      x.localidade === '' ? '' : ` ${x.localidade}`
    }${x.voo === '' ? '' : ` (voo ${x.voo})`}`;
  return um('recolha', p.recolha) + um('largada', p.largada);
}

// ---------------------------------------------------------------------------
// Uma linha de viatura
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ **O QUE UM «Ver no mapa» PODE PEDIR PARA PRÉ-VISUALIZAR.**
 *
 * 📗 Cliente, 2026-08-21: *«o meu objetivo é ver a alocação corretamente já com os dias tal
 * como já fosse definitiva mas com um ligeiro desfoque, para ter uma visão exata. Para além
 * disso, em outros tipos de marcações como partir, marcar perfeitamente como fica cada autocarro
 * — não com a viagem completa mas sim já com as partições»*.
 *
 * ⚠️⚠️ **A primeira versão desenhava uma CAIXA com um rótulo, e não era isto.** Uma caixa
 * tracejada com a palavra «previsão» diz *onde*; não diz *como fica*. O que se pede é a **barra**
 * — a cor do cliente, a sigla, os dias exactos — igual à real, esbatida.
 *
 * 📌 **Quem chama sabe a sigla; só o mapa sabe o ÍNDICE.** Por isso isto leva `sigla` e
 * `itinerario` (o id) e a resolução dos índices vive num sítio só — o mapa. Um chamador a
 * adivinhar índices de paleta era a definição de acoplamento errado.
 */
export interface PrevisaoPedida {
  /** A sigla do cliente — é a identidade que o mapa mostra (D-054). */
  readonly sigla: string;
  /** O id do itinerário, para a cor quando a configuração é «cor por itinerário». */
  readonly itinerario?: string;
  /**
   * ⚠️⚠️ **Os PEDAÇOS que esta viatura fica a fazer — e é isto que faltava.**
   *
   * 📗 *«não com a viagem completa mas sim já com as partições»*.
   *
   * 📌 Numa partição cada autocarro faz **metade**; num «subcontratar dias» os nossos dias
   * podem estar **partidos ao meio** pelos dias do parceiro. Um intervalo só não representa
   * nenhum dos dois. ⚠️ Ausente = um pedaço, `[d0, d1]`.
   */
  readonly pedacos?: readonly (readonly [number, number])[];
}
/**
 * ⚠️⚠️ **A PRÉ-VISUALIZAÇÃO JÁ RESOLVIDA — índices, não siglas.**
 *
 * 📌 `PrevisaoPedida` é o que **quem chama** sabe (a sigla do cliente, o id do itinerário);
 * isto é o que o **mapa** sabe depois de a resolver (os índices da paleta). A separação existe
 * desde 2026-08-21 e mantém-se: um chamador a adivinhar índices pintava a barra com a cor de
 * outro cliente.
 */
export interface PrevisaoResolvida {
  readonly cliente: number;
  readonly itinerario: number;
  readonly pedacos: readonly (readonly [number, number])[];
}

/**
 * ⚠️⚠️ **UMA ALTERAÇÃO DO LOTE, tal como o ecrã das consultas a pede** (B31, 2026-08-31).
 *
 * 📗 Cliente: *«tem de existir uma forma de pré-visualizar as alterações no mapa, aparecendo de
 * forma diferente»*.
 *
 * 📌 **É a mesma forma do `PrevisaoPedida` mais a viatura**, e não um sinal novo: o que se
 * desenha é a barra `barra--previsao` que já existe desde 2026-08-21. ⚠️ Estrear um segundo
 * sinal para «isto ainda não está escrito» ensinava duas linguagens para a mesma coisa — e a
 * segunda seria a que ninguém reconhece no dia em que aparece sozinha.
 */
export interface PrevisaoDeLote {
  /** O id da viatura de **DESTINO** — é ela que recebe a peça. */
  readonly viaturaId: string;
  readonly sigla: string;
  readonly itinerario?: string;
  readonly pedacos: readonly (readonly [number, number])[];
}

export interface LinhaProps {
  readonly dados: DadosMapa;
  readonly viatura: number;
  readonly janela: Janela;
  readonly tema: Tema;
  readonly corLigada: boolean;
  /** 📗 §7: cor por cliente **ou** por itinerário. Ver `indicePorModo`, acima. */
  readonly corPor: 'cliente' | 'itinerario';
  readonly filtro: Filtro;
  readonly inicioSemana: readonly boolean[];
  /** Itinerário seleccionado por clique. Realça a corrida inteira. */
  readonly itinerarioForte: number;
  /**
   * Posições acumuladas dos dias — onde cada coluna começa, em píxeis.
   *
   * ⚠️ Entra nas dependências do `memo` **pela identidade**: o vector é reconstruído
   * quando alguma largura muda e mantém-se igual quando não muda. Sem isso, mudar
   * uma coluna não voltava a desenhar as linhas e as barras ficavam no sítio antigo
   * enquanto as colunas mudavam de largura por baixo delas. E com uma identidade
   * nova a cada renderização, passar o rato voltaria a desenhar a grelha inteira.
   */
  readonly offsetsDias: readonly number[];
  /**
   * ⚠️⚠️ **A PRÉ-VISUALIZAÇÃO DESTA LINHA — os pedaços que ela ficaria a fazer.**
   *
   * 📗 Cliente, 2026-08-21: *«ver a alocação corretamente já com os dias tal como já fosse
   * definitiva mas com um ligeiro desfoque»*.
   *
   * 📌 **Vive AQUI e não numa camada por cima do mapa**, e é essa a diferença que faz a barra
   * parecer real: aqui existe o `bandas(altura)`, o `esq()`/`larg()` desta linha e as classes que
   * as barras verdadeiras usam. Uma camada por cima tinha de repetir as quatro coisas — e a que
   * divergisse era a que ninguém corre.
   *
   * ⚠️ **`null` em 249 das 250 linhas**, e é o que mantém o `memo` a funcionar: só a linha
   * apontada recebe um objecto.
   */
  readonly previsao?: readonly PrevisaoResolvida[] | null;
  /** Altura desta linha. Cada viatura pode ter a sua, como no Excel. */
  readonly altura: number;
  /**
   * Esta linha está sob o cursor durante o arrasto de um pedido (RF-7).
   *
   * ⚠️ Realçar a LINHA e não a célula é deliberado: o arrasto escolhe a **viatura**, e
   * as datas são do pedido. Realçar uma célula prometia que a reserva ia para aquele
   * dia — ver `arrasto-pedido.ts`.
   */
  readonly alvoArrasto?: boolean;
  /**
   * ---- ⚠️⚠️ PEÇA 2 · A LINHA SELECCIONADA — §12.4-bis (2026-09-28) --------------------------
   *
   * `false` em 249 das 250, mesma disciplina de `alvoArrasto`. A banda que cobre TODOS os dias
   * é um único elemento sobreposto em `Mapa` (como `.mapa-alvo`) — isto marca só o `.viatura`
   * (o nome, os 176 px), que é o alvo do clique.
   */
  readonly linhaSeleccionada?: boolean;
  /**
   * ⚠️ **Não dispara se o clique caiu na TIRA de redimensionar** (os últimos `LARGURA_TIRA`
   * píxeis da borda de baixo) — a mesma exclusão de `aoClicarDia`, em `Cabecalho`.
   */
  readonly aoClicarViatura?: ((v: number) => void) | undefined;
  /**
   * ⚠️⚠️ **§12.6 · o dia SELECCIONADO nesta linha — `null` em 249 das 250, como a `previsao`.**
   *
   * 📌 Medido em §12.9: nenhuma célula da grelha tinha `role`/`aria-selected`/`tabIndex` —
   * «confirmado: zero `tabIndex` real, nunca existiu». Esta é a informação que faltava para os
   * pôr: SÓ a linha da viatura seleccionada recebe um número; as outras 249 recebem `null` — a
   * mesma disciplina da `previsao`, e pela mesma razão: um objecto (ou número) novo em TODAS as
   * linhas invalidava o `memo` de todas a cada tecla.
   *
   * ⚠️ **A cópia `linha-fixada` (a «comparar facilmente») recebe sempre `null`** — de propósito,
   * não por esquecimento: ela é uma segunda cópia visual da MESMA viatura, com as MESMAS
   * `data-v`/`data-d`, e dar `tabIndex="0"` às duas fazia haver DOIS nós tabuláveis para a
   * mesma célula lógica, que é o oposto do que "roving tabindex" promete.
   */
  readonly diaSeleccionado?: number | null;
  /**
   * ---- ⚠️⚠️ PEÇA 1 · O INTERVALO ESTENDIDO POR `Shift`+setas NESTA LINHA — §12.4/H -----------
   *
   * `null` em 249 das 250, pela MESMA razão de `diaSeleccionado`: um objecto novo em todas as
   * linhas a cada tecla invalidava o `memo` de todas. `[d0, d1]`, inclusive dos dois lados —
   * já vem calculado (`intervaloSeleccionado`, em `Mapa`) porque a ordem `ancora`/`ponta` não
   * é sempre crescente (estender para a ESQUERDA dá `ancora > ponta`).
   *
   * ⚠️ **A cópia `linha-fixada` recebe sempre `null`**, como `diaSeleccionado` — é a mesma
   * viatura desenhada duas vezes, e pintar a banda lá também não acrescenta informação nenhuma
   * (o intervalo já se vê na linha real); só duplicava o trabalho de desenhar.
   */
  readonly intervaloSeleccionado?: { readonly d0: number; readonly d1: number } | null;
}

/**
 * `memo` aqui não é optimização prematura.
 *
 * É o que permite ao cartão de resumo seguir o cursor **sem** voltar a desenhar as
 * linhas. Sem ele, passar o rato pela grelha redesenhava tudo a cada célula.
 */
// ⚠️⚠️ **EXPORTADA, e é o que faz a folha impressa NÃO ser uma segunda formulação do mapa.**
//    📌 A folha (`FolhaDeImpressao.tsx`) desenha exactamente esta `Linha`, com a **mesma**
//    geometria e as **mesmas** classes — só lhe dá outra `janela` (a da folha, sem
//    virtualização) e outros `offsetsDias` (uniformes, porque a régua do papel é constante).
//    Uma folha desenhada por código próprio divergia do ecrã, e a que divergisse era a que
//    ninguém corrige — a família que já custou a esta casa oito instâncias medidas.
export const Linha = memo(function Linha({
  dados,
  viatura,
  janela,
  tema,
  corLigada,
  corPor,
  filtro,
  inicioSemana,
  itinerarioForte,
  offsetsDias,
  previsao = null,
  altura,
  alvoArrasto = false,
  diaSeleccionado = null,
  intervaloSeleccionado = null,
  linhaSeleccionada = false,
  aoClicarViatura,
}: LinhaProps) {
  const v = dados.viaturas[viatura];
  const linha = dados.grelha[viatura] ?? [];
  const movimentos = dados.movimentos[viatura] ?? [];
  const { dia0, dia1 } = janela;

  const segs = segmentos(linha, dia0, dia1);
  const segsMot = segmentosMotorista(linha, dia0, dia1);

  // ⚠️ Já não é uma multiplicação: cada coluna pode ter a sua largura, logo a
  // posição de um dia é a soma das larguras dos anteriores — que é o que o vector
  // acumulado traz pronto.
  const esq = (d: number): number => offsetsDias[d] ?? 0;
  const larg = (a: number, b: number): number => (offsetsDias[b] ?? 0) - (offsetsDias[a] ?? 0);
  const banda = bandas(altura);

  // ---- banda 1 e blocos de altura inteira --------------------------------
  const barras = [];
  for (const s of segs) {
    const estilo = { left: esq(s.dia0), width: larg(s.dia0, s.dia1) };

    if (s.tipo === 'rotacao') {
      const r = resumoRotacao(movimentos[s.dia0] ?? null);
      const cel = linha[s.dia0];
      // ⚠️ O bloco ocupa a altura inteira e TAPAVA a barra do motorista. E é
      // precisamente neste dia que o motorista muda — foi o cliente a dizê-lo:
      // «nos dias a preto pode haver troca de motorista a mudar o itinerário».
      // Por isso o bloco encolhe e deixa a banda 3 à vista.
      //
      // ⚠️⚠️ **E O MOTORISTA SAIU DAQUI — 📗 pedido do cliente, 2026-08-10.**
      //
      //   📗 *«não interessa ter no quadrado preto o motorista, dado que ele já está em baixo.
      //   O que interessa é ter o local do in da nova viagem e do out da viagem que vai
      //   embora… penso que seja esta a lógica, confirme que é esse o seu pensamento também»*.
      //
      // 📌 **É essa a lógica, e é a do modelo.** F-25 mediu-a: o `in` é do PRIMEIRO dia de uma
      //   viagem (o autocarro vai **buscar** o grupo — 1 241 `in` contra 438 `out` nos
      //   primeiros dias) e o `out` do ÚLTIMO (613 `out` contra 2 `in`). Logo num dia de
      //   rotação o `out` é **do grupo que parte** e o `in` é **do grupo que chega**, que é
      //   exactamente o que ele escreveu.
      //
      // ⚠️⚠️ **E o motorista era mesmo ruído:** o bloco encolheu de propósito para deixar a
      //   banda 3 à vista, e a banda 3 é a barra do motorista — o nome estava duas vezes na
      //   mesma célula, e a de baixo é a que diz **quem conduz naquele dia**. O do bloco vinha
      //   de `celula[2]`, que é o condutor da alocação: em 22,3 % das corridas há mais de um
      //   (F-26), logo era também o menos fiável dos dois.
      //
      // ⚠️⚠️ **O LOCAL vem da CÉLULA, e a razão está MEDIDA — não é atalho.** O modelo suporta uma
      //   localidade por movimento (`movimento.localidade_id`, migração 001), e podia ser
      //   diferente entre o `in` e o `out`. 📌 Medido na base: em **3 064** dias com os dois
      //   movimentos, as duas localidades são **sempre iguais** — e iguais à da célula (0
      //   diferenças). E `local_embarque`, que é o segundo local de F-21, está a **NULL** em
      //   todas as 1 029 rotações. ⚠️ Construir a canalização para levar duas localidades ao
      //   mapa seria máquina sem efeito nenhum hoje; o que fica é esta nota, no sítio onde
      //   alguém a vai procurar no dia em que os dados reais divergirem.
      const local = cel != null && cel[1] >= 0 ? (dados.localidades[cel[1]]?.[0] ?? '') : '';
      barras.push(
        <div key={`r${s.dia0}`} className="bloco bloco--rotacao" style={estilo}>
          {/* A CHEGADA do grupo novo: o `in`. */}
          <span className="bloco-hora">
            <IconeChegada em={0.9} />
            {local === '' ? '' : `${local} `}
            {r.chegada}
          </span>
          {/* A PARTIDA do grupo que vai embora: o `out`. */}
          <span className="bloco-hora">
            <IconePartida em={0.9} />
            {local === '' ? '' : `${local} `}
            {r.partida}
          </span>
        </div>,
      );
      continue;
    }

    // ---- ⚠️⚠️ EM TRÂNSITO — 📗 «esses dias têm de aparecer … em trânsito» ----
    //
    //   Cliente, 2026-08-13: *«termina uma viagem em Barcelona e no dia seguinte está programado
    //   voltar à base… esses dias têm de aparecer o motorista alocado e em trânsito»*.
    //
    // ⚠️ **Antes isto era desenhado como «parada fora»** — o oposto do que acontece. A viatura
    //   está a ANDAR, com condutor e com horas de condução. Ver `natureza` em `segmentos.ts`.
    //
    // 📌 **A banda 3 não muda:** o condutor já vem de `alocacao_condutor` como num dia de serviço,
    //   logo o pedido «indicar o motorista alocado especificamente no mapa» resolve-se por ele
    //   passar a EXISTIR nos dados — que era o que faltava (medido: 2 839 dias, zero com condutor).
    if (s.tipo === 'transito') {
      // ---- ⚠️⚠️ E A CHAVE ESTAVA A SER CONSTRUÍDA COM O OBJECTO (2026-09-03) ----
      //
      //   Estava `dados.destinosDoTransito[`${v}:${s.dia0}`]`, e **`v` é
      //   `dados.viaturas[viatura]` — a viatura, não o índice** (ver o topo desta função). O
      //   literal dava `"[object Object]:4"`, e `mapa.ts:1036` escreve a chave com o ÍNDICE
      //   (`${vi}:${dia}`), como os `notas` e os `movimentosSaida`. 📌 Logo o mapa **nunca leu
      //   um destino**: `iDestino` era sempre `-1` e o `title` de **todos** os dias de estrada
      //   dizia «o destino não está registado» sobre linhas que o têm.
      //
      // ⚠️ **É a SEGUNDA razão, independente, para o `A»B` nunca aparecer** — e não estava no
      //   enunciado do achado, que só apontava a convenção das colunas. As duas tinham de ser
      //   verdade ao mesmo tempo para o rótulo morrer; corrigir só uma não o traria de volta.
      //
      // 📌 Todos os outros leitores deste ficheiro já usavam `viatura` (ex.: `notas`). Este era
      //   o único fora do padrão, e um índice de objecto não dá erro de tipos: `Record<string,…>`
      //   aceita qualquer *template literal*.
      const iDestino = dados.destinosDoTransito[`${viatura}:${s.dia0}`] ?? -1;
      const destino = codigoLocal(dados, iDestino);
      // ---- ⚠️⚠️ O RÓTULO `A»B` SAIU DAQUI, E NÃO ERA «FALTA PINTAR» (2026-09-03) ----
      //
      //   Estava escrito `destino !== '' && destino !== origem ? `${origem}»${destino}` : …`,
      //   com `origem = codigoLocal(dados, s.chave)` e `s.chave = c[1]` (`segmentos.ts`, o
      //   ramo do trânsito) — ou seja, **`localidade_id`**. A forma `Malaga-GRX` da folha real
      //   é o objectivo citado, e **nenhum caminho a conseguia produzir**.
      //
      // ⚠️⚠️ **PROVADO NO ESCRITOR, e não por amostragem:** `transito-da-reserva.ts` escreve
      //   `localidade_id` e `destino_vazio` a partir do **MESMO** valor (`t.destinoId`, os
      //   parâmetros `$4` e `$6` do mesmo `INSERT`), logo `destino !== origem` é falso **por
      //   construção** em todas as linhas que o produto escreve. Nos CSV do gerador: **2 785 de
      //   2 785 (100 %)** com `destino_vazio == localidade`. O `transito-orfao.ts` diz o mesmo
      //   da base: 8 175 de 8 178.
      //
      // ⚠️⚠️ **E `localidade_id` NÃO É a origem — é onde a viatura DORME**, o que a migração 013
      //   escreveu e o que decide o alojamento (D-060). Há um caminho que consegue afastar as
      //   duas colunas (`paragem.ts`, mudar a localidade de um dia que fica em trânsito) e
      //   nesse caso o rótulo **aparecia a mentir**: dizia «de A para B» com A a ser a pernoita
      //   do próprio dia, não o sítio de onde a viatura partiu.
      //
      // 📌 **A origem não existe em coluna nenhuma.** A API sabe-a no momento da escrita
      //   (`origemId`, em `transito-da-reserva.ts`) e usa-a só para os km; nunca a grava.
      //   `origem_localizacao` é `registada`/`inferida` — a **qualidade** do dado, não um sítio.
      //   Logo isto não é «fazer aparecer», é remover o que não tem com que se escrever. Para
      //   onde a viatura vai continua a dizer-se — no `title`, que é o único dos dois que a
      //   informação sustenta.
      //   ⚠️ **O rótulo vive numa constante partilhada com a legenda** (`ROTULO_TRANSITO`): foi a
      //   divergência entre os dois que deixou a legenda a ensinar `BCN»SERZ` por seis dias.
      const rotulo = ROTULO_TRANSITO;
      barras.push(
        <div
          key={`t${s.dia0}`}
          className={classes('barra', 'barra--transito', s.abre && 'abre', s.fecha && 'fecha')}
          style={estilo}
          title={
            destino === ''
              ? 'Em trânsito — deslocação sem passageiros. O destino não está registado.'
              : `Em trânsito para ${destino} — deslocação sem passageiros, com condutor e ` +
                'horas de condução. O alojamento destes dias é da empresa.'
          }
        >
          <span className="barra-sigla">{rotulo}</span>
        </div>,
      );
      continue;
    }

    if (s.tipo === 'parado') {
      // Dia sem viagem, mas com local. Sem cor de cliente: não há cliente.
      //
      // ⚠️ DUAS coisas diferentes, e a diferença é dinheiro. Foi o cliente a
      // apontá-la: «muitas vezes, nos dias vazios, o local deve ser quando eles
      // vão a casa e não têm de pernoitar, por exemplo quando estão em Portugal».
      //
      // MEDIDO na folha real: das 240 células com local em dias sem viagem,
      // **nenhuma** é em Portugal — escrevem o local só quando a viatura NÃO está
      // em casa. Logo o branco da folha não era esquecimento, era «está na base».
      //   na base    → o motorista vai a casa, alojamento zero (D-060)
      //   fora       → alojamento pago, e é dos maiores custos controláveis
      const naBase = s.chave >= 0 && s.chave === dados.localidadeBase;
      barras.push(
        <div
          key={`p${s.dia0}`}
          className={classes(
            'barra',
            'barra--parado',
            naBase && 'barra--base',
            s.abre && 'abre',
            s.fecha && 'fecha',
          )}
          style={estilo}
          title={
            naBase
              ? 'Na base — sem serviço. O motorista vai a casa: não há alojamento a pagar.'
              : 'Parada fora da base — sem serviço, mas com pernoita a pagar.'
          }
        >
          <span className="barra-sigla">{naBase ? 'na base' : 'parada fora'}</span>
        </div>,
      );
      continue;
    }

    // ---- ⚠️⚠️ UM DIA DE OUTRO PLANO: OCUPADO, E NAO AVARIADO (D-1) ---------------
    //
    //   📗 Cliente, 2026-08-21: *«seleccionei o plano Acores, e em muitas linhas aparece a
    //   ferramenta como se fosse oficina e diz "noutro plano". Qual e a logica disto?»*
    //
    // ⚠️⚠️ **Estas celulas caiam no ramo do `'estado'` abaixo** — chave inglesa, cor `grave`,
    //   e o rotulo «Noutro plano» a sair da tabela dos estados de viatura, onde alguem o tinha
    //   metido. 📌 O planeador lia «avariada» sobre um autocarro a trabalhar, e clicar abria o
    //   «Quem substitui?». Agora tem bandeira, tipo de segmento, cor e icone proprios.
    //
    // ⚠️ **E o rotulo diz o que sabe e mais nada.** Quem e o cliente, qual o itinerario e que
    //   corrida e sao informacao do outro plano, e o bloco opaco existe para nao a mostrar
    //   (ADR-002 — o plano particiona a VISIBILIDADE). O que se ve e o que decide: o dia esta
    //   ocupado. E se o outro plano tiver a viatura em oficina, isso e um facto sobre a VIATURA
    //   e diz-se, porque muda a pergunta seguinte de «negocio com eles» para «espera pela peca».
    if (s.tipo === 'outroPlano') {
      const estado = s.chave >= 0 ? (dados.estadosViatura[s.chave] ?? '') : '';
      const cor = OUTRO_PLANO[tema];
      barras.push(
        <div
          key={`op${s.dia0}`}
          className={classes('bloco', 'bloco--outro-plano', s.abre && 'abre', s.fecha && 'fecha')}
          style={{ ...estilo, background: cor, color: tintaSobre(cor) }}
          title={
            estado === ''
              ? 'Noutro plano — a viatura tem serviço neste dia, num plano que não é o activo. O dia está ocupado; o cliente e o itinerário não são visíveis daqui.'
              : `Noutro plano, e a viatura está em ${estado} — é um facto sobre a viatura, não sobre o serviço.`
          }
        >
          <IconeOutroPlano em={1} className="ic" />
          <span className="bloco-rot">
            {estado === '' ? 'noutro plano' : `noutro plano · ${estado}`}
          </span>
        </div>,
      );
      continue;
    }

    if (s.tipo === 'estado') {
      const cel = linha[s.dia0];
      const cor = ESTADO[dados.estados[cel?.[3] ?? 2] ?? 'grave'];
      barras.push(
        <div
          key={`e${s.dia0}`}
          className={classes('bloco', 'bloco--estado', s.abre && 'abre', s.fecha && 'fecha')}
          style={{ ...estilo, background: cor, color: tintaSobre(cor) }}
        >
          <IconeOficina em={1} className="ic" />
          <span className="bloco-rot">{dados.estadosViatura[s.chave] ?? ''}</span>
        </div>,
      );
      continue;
    }

    // Itinerário. O filtro ESBATE, nunca esconde: uma barra que desaparecesse
    // faria a viatura parecer livre num dia em que não está.
    const dentro =
      activo(filtro.clientes, s.cliente) &&
      activo(filtro.rotas, linha[s.dia0]?.[5] ?? -1) &&
      // ⚠️⚠️ **O CONDUTOR É DO DIA — ver `condutorNaBarra`.** Este eixo lia
      //   `linha[s.dia0]?.[2]`, o condutor do PRIMEIRO dia, e por isso esbatia a viagem
      //   inteira quando o condutor filtrado só entrava a meio — com a barra dele acesa na
      //   banda 3, na mesma linha. 📌 P-33: o condutor da linha é **metadado, não
      //   atribuição**; quem conduz num dado dia é a banda 3.
      condutorNaBarra(filtro.motoristas, linha, s.dia0, s.dia1) &&
      // ---- ⚠️⚠️ V05 · A CIDADE É DO DIA, e por isso a regra é OUTRA ------------------
      //
      //   📗 Cliente (pág. 14-16): *«filtro por cidade no mapa»*.
      //
      // ⚠️⚠️ **Os outros DOIS eixos leem o PRIMEIRO dia da corrida** — o cliente e o
      //   itinerário são da viagem, e são-no de facto: são propriedades da corrida,
      //   constantes ao longo dela, logo ler o primeiro dia é exacto e não aproximado.
      //   ⚠️ **O condutor NÃO é** — este comentário afirmava que sim, e era esse o defeito
      //   A9: ver `condutorNaBarra`, que aplica a esse eixo a mesma regra que este.
      //   📌 **A cidade também não é:** uma viagem de oito
      //   dias passa por várias, e a pergunta que se faz é *«quem passa por Madrid?»*.
      //
      // ⚠️ **Basta UM dia**, e não todos: exigir todos esbatia a viagem inteira por causa de
      //   um dia de passagem — e aí o filtro respondia «ninguém passa por Madrid» sobre um
      //   mapa cheio de viagens que passam.
      //
      // 📌 **Sobre os dias DESTA barra e não sobre a linha toda:** a linha tem 365
      //   dias e a barra tem os da corrida. Varrer a linha fazia uma viagem em Janeiro
      //   acender por causa de outra em Dezembro.
      //
      // ⚠️⚠️ **A conta saiu daqui para `cidadeNaBarra` (2026-09-04), e não foi arrumação:**
      //   estava escrita à mão com `d <= s.dia1` e o `dia1` do segmento é **EXCLUSIVO** —
      //   lia o primeiro dia da barra SEGUINTE. Medido: **2 248 de 7 099 barras (31,7 %)**
      //   podiam acender por uma cidade por onde nunca passaram.
      cidadeNaBarra(filtro.localidades, linha, s.dia0, s.dia1);
    // ⚠️⚠️ **A COR PODE SER DO CLIENTE OU DO ITINERÁRIO — 📗 §7 oferece as duas.**
    //
    //   *«mostrar planeamento com cor por cliente ou por itinerário»*. Só existia por
    //   cliente, e cada uma falha onde a outra serve: por cliente varre-se o mapa à procura
    //   de um cliente, mas um cliente com oito itinerários é **uma cor só**; por itinerário
    //   distinguem-se as viagens dele, e em troca a cor repete-se muito mais (~2 679
    //   assinaturas de rota para 26 tons, contra ~40 clientes).
    //
    //   📌 O que não muda é a regra: a cor serve para **varrer**, a **sigla** é a identidade
    //   (P-30). Isso vale nas duas.
    const indicePorModo =
      corPor === 'itinerario' ? (linha[s.dia0]?.[5] ?? -1) : s.cliente;
    // ⚠️ A cor manual de UMA viagem (migração 055) vence sempre — a escolha do
    // planeador para esta corrida em concreto ganha aos dois modos de `corPor`.
    const refCorrida = s.corrida >= 0 ? dados.corridas[s.corrida] : undefined;
    const indiceCorManual = refCorrida !== undefined ? dados.coresCorrida[refCorrida] : undefined;
    // ⚠️⚠️ **A COR ESCOLHIDA DO CLIENTE** (migração 051, ligada ao mapa em
    //    2026-08-29) — 📗 *«quando mudo a cor de um cliente, as viagens que ele tem
    //    não está a mudar a cor»*. Só no modo «por cliente»: em «por itinerário» o
    //    que se quer distinguir são as ROTAS deste cliente, e forçar todas à mesma
    //    cor apagava exactamente isso. Perde para a cor manual da viagem (acima),
    //    ganha ao índice automático.
    const cor =
      !corLigada || !dentro
        ? corCliente(-1, tema)
        : indiceCorManual !== undefined
          ? corCliente(indiceCorManual, tema)
          : corPor === 'cliente'
            ? corDoCliente(dados, s.cliente, tema)
            : corCliente(indicePorModo, tema);
    const dias = s.dia1 - s.dia0;
    // ⚠️ **O ITINERÁRIO DO PRIMEIRO DIA DA BARRA** — 📗 «em vez do nome do cliente passe a
    //    aparecer o nome do itinerário» (2026-08-13). É o mesmo índice de onde já saía a cor
    //    quando se colore por itinerário (`indicePorModo`, acima): uma barra é uma corrida, e
    //    uma corrida tem um itinerário só.
    const rot = rotuloItinerario(dados, s.cliente, dias, linha[s.dia0]?.[5] ?? -1);
    const forte = !Number.isNaN(itinerarioForte) && s.chave === itinerarioForte;

    // QUALQUER dia da corrida, e não apenas o primeiro.
    //
    // Nos dados reais o estado do registo é uniforme dentro de um itinerário, mas
    // ler só o primeiro dia esconderia uma reserva que cobrisse apenas parte da
    // corrida — e «parte deste itinerário não está confirmada» é exactamente o que
    // o planeador precisa de ver. O mesmo vale para o «?» de provisório.
    let bandeirasCorrida = 0;
    for (let d = s.dia0; d < s.dia1; d++) bandeirasCorrida |= linha[d]?.[4] ?? 0;
    const reserva = (bandeirasCorrida & BANDEIRA.RESERVA) !== 0;
    const duvida = (bandeirasCorrida & BANDEIRA.DUVIDA) !== 0;

    // ⚠️⚠️ **QUAL o dia suspeito, e nao so «ha um nesta corrida».**
    //
    //   📗 Cliente, 2026-08-10: *«o que significam os pontos de exclamacao nos itinerarios?
    //   Ao passar o rato por cima desse ponto de exclamacao devera aparecer uma nota, por
    //   exemplo, a explicar a razao»*.
    //
    // ⚠️⚠️ **A pergunta e a prova de que o sinal nao se explicava, e havia DUAS razoes.**
    //
    //   1. O `!` tinha um `title` — e o `title` estava num elemento que **nunca recebe o
    //      rato**: `.barras` tem `pointer-events: none` de proposito, para que as barras nao
    //      roubem o cursor aos dias e o cartao de resumo continue a saber que dia esta
    //      debaixo dele. ⚠️⚠️ **Um `title` ali e decoracao:** o navegador so o mostra a quem
    //      passa por cima, e ninguem passa. 📌 E o mesmo padrao da semana — a estrutura
    //      pronta e o elo a menos.
    //   2. O `!` era desenhado no **inicio da barra** e a explicacao vive no cartao do
    //      **dia** que tem a bandeira. Numa barra de oito dias, o sinal ficava a sete dias
    //      da nota que o explica.
    //
    // 📌 A correccao nao inventa mecanismo nenhum: o `!` passa a ficar **em cima do dia
    //   suspeito**, e o cartao do cursor desse dia — que ja dizia «marcado na origem, mas
    //   SEM partida no dia seguinte» — passa a aparecer onde o olho ja esta. Continua a
    //   responder de imediato, sem o atraso de ~1 s do `title`.
    let diaSuspeito = -1;
    for (let d = s.dia0; d < s.dia1; d++) {
      if (((linha[d]?.[4] ?? 0) & BANDEIRA.ROTACAO_SUSPEITA) !== 0) {
        diaSuspeito = d;
        break;
      }
    }
    const suspeita = diaSuspeito >= 0;
    // ⚠️⚠️ **O ALFINETE — 📗 «um pisco que diga: ok, aqui já não quero mexer mais».**
    //
    //   A bandeira chega à célula desde 2026-08-14, e sem marca visível um serviço fixado era
    //   **indistinguível de um normal**: o planeador fixava e o mapa não o confirmava. 📌 É a
    //   família «o sistema sabe e não diz», e desta vez fui eu a criá-la ao trazer a bandeira sem
    //   quem a desenhasse.
    //
    // ⚠️ **Qualquer dia da corrida basta**, como a suspeita: o alfinete é do serviço, e fixar um
    //   dia do meio fixa a viagem para efeitos de não a mexer.
    const fixado = (bandeirasCorrida & BANDEIRA.FIXADO) !== 0;

    barras.push(
      <div
        key={`i${s.dia0}`}
        /* ---- ⚠️⚠️ B30 · O ARRASTO NÃO PARTE DAQUI, e não pode partir -----------------
           📗 Cliente, 2026-08-30: *«mover facilmente»* — e depois *«o 1 não funciona, não
           consigo»*, sobre esta barra.

           ⚠⚠ **A barra vive dentro de `.camada`, que tem `pointer-events: none`** — de
           propósito: *«sem isto as barras roubariam o rato aos dias, e o cartão de resumo
           deixaria de saber que dia está debaixo do cursor»*. 📌 Um `draggable` aqui é
           **inerte**: o `cursor: grab` nunca aparece e o `dragstart` nunca dispara.

           📌 **O punho é a CÉLULA do dia** (`[data-v][data-d]`), que recebe eventos e de
           onde a corrida se deriva com `extentoDaCorrida` — ver a nota lá. */
        className={classes(
          'barra',
          'barra--itin',
          s.abre && 'abre',
          s.fecha && 'fecha',
          reserva && 'reserva',
          forte && 'forte',
          suspeita && 'suspeita',
          fixado && 'barra--fixado',
        )}
        style={{
          ...estilo,
          background: cor.fundo,
          color: tintaSobre(cor.fundo),
          borderLeftColor: cor.cap,
        }}
      >
        <span className="barra-sigla">{rot.sigla}</span>
        {/* Sem ícone de reserva na barra: a TEXTURA já a diz, e a legenda
            explica-a. Um ícone em 19 % das barras custava ~2 nós cada em 600
            dias visíveis, e o p95 nota-o. */}
        {duvida ? <span className="barra-duvida">?</span> : null}
        {/* ⚠️⚠️ **DENTRO DA BARRA, a seguir à sigla — 📗 «o ponto de exclamação estava melhor
            onde estava, agora está em cima do cliente».**
            ⚠️ **Tem razão, e a minha correcção anterior trocou um problema por outro.** Eu
            pu-lo sobre o dia com a bandeira para o sinal e a explicação coincidirem — e
            aterrou **por cima do nome do cliente**, que é a identidade que o mapa existe para
            mostrar (P-30). 📌 O que estava errado nunca foi a posição: era a explicação não
            aparecer. Resolve-se onde o problema estava — `resumo()` passa a dizer a razão em
            **qualquer dia desta viagem**, nomeando o dia em causa. */}
        {/* ⚠️ O `aria-label` não é decoração: um «!» sozinho não diz nada a quem usa leitor
            de ecrã, e essa pessoa não tem cartão do cursor. */}
        {suspeita ? (
          <span
            className="barra-suspeita"
            aria-label="falta o serviço seguinte: estava marcado como «chega e parte no mesmo dia» e no dia seguinte não há serviço"
          >
            !
          </span>
        ) : null}
        {rot.nome !== '' ? <span className="barra-nome">{rot.nome}</span> : null}
      </div>,
    );
  }

  // ---- ⚠️⚠️ A BARRA DA PRÉ-VISUALIZAÇÃO — igual à real, esbatida --------------------
  //
  //   📗 Cliente, 2026-08-21: *«ver a alocação corretamente já com os dias tal como já
  //   fosse definitiva mas com um ligeiro desfoque para ter uma visão exata»* e *«em outros
  //   tipos de marcações como partir, marcar perfeitamente como fica cada autocarro — não com a
  //   viagem completa mas sim já com as partições»*.
  //
  // ⚠️⚠️ **UM PEDAÇO, UMA BARRA.** Numa partição cada autocarro faz metade; num «subcontratar
  //   dias» os nossos dias podem estar partidos ao meio pelos do parceiro. Desenhar um
  //   intervalo só mostrava uma viagem que ninguém vai fazer — que é pior do que não mostrar
  //   nada, porque parece exacto.
  //
  //   📌 **As MESMAS classes e a MESMA geometria das verdadeiras** (`barra barra--itin`,
  //   `banda.itinerario`, `esq`/`larg`): é o que a faz parecer a alocação e não um desenho
  //   sobre o mapa. O que a distingue é `barra--previsao` — tracejado e desfoque no fundo.
  //
  // ⚠️ **A cor é a do cliente, pela regra do mapa** (`corPor`), e cai na neutra quando a
  //   sigla não é conhecida: uma barra com a cor de OUTRO cliente é pior do que uma cinzenta.
  //
  // ⚠️⚠️ **É UMA LISTA E NÃO UMA SÓ, desde o lote (B31, 2026-08-31).** Duas alterações do mesmo
  //   lote podem cair na **mesma** viatura, e cada uma tem o **seu** cliente. 📌 A primeira
  //   versão juntava os pedaços num objecto só e pintava-os todos com a cor do primeiro cliente —
  //   uma barra plausível com a cor errada, que é exactamente a família de defeito que este
  //   projecto persegue. Uma entrada por alteração resolve-o por construção.
  for (const p of previsao ?? []) {
    const indice = corPor === 'itinerario' ? p.itinerario : p.cliente;
    // ⚠️ A cor escolhida do cliente também vale aqui — senão o fantasma mostrava
    //    uma cor diferente da que a viagem vai ter a seguir, na barra real.
    const corP = !corLigada
      ? corCliente(-1, tema)
      : corPor === 'cliente'
        ? corDoCliente(dados, p.cliente, tema)
        : corCliente(indice, tema);
    const sigla = p.cliente >= 0 ? (dados.clientes[p.cliente]?.[0] ?? '') : '?';
    for (const [a0, a1] of p.pedacos) {
      // ⚠️ Fora da janela desenhada não se desenha: a janela é ~600 dias e o resto do ano
      //    não está no DOM. Sem este corte, o `left` saía negativo e a barra colava-se à
      //    coluna dos nomes.
      if (a1 < dia0 || a0 > dia1) continue;
      const c0 = Math.max(a0, dia0);
      const c1 = Math.min(a1, dia1 - 1);
      if (c1 < c0) continue;
      barras.push(
        <div
          // ⚠️ A sigla entra na chave: duas alterações de clientes diferentes podem começar no
          //    mesmo dia nesta linha, e duas chaves iguais fazem o React desenhar **uma**.
          key={`prev${sigla}${c0}`}
          className="barra barra--itin barra--previsao abre fecha"
          style={{
            left: esq(c0),
            width: larg(c0, c1 + 1),
            height: banda.itinerario,
            background: corP.fundo,
            color: tintaSobre(corP.fundo),
            borderLeftColor: corP.cap,
          }}
        >
          <span className="barra-sigla">{sigla}</span>
          {/* ⚠️⚠️ **A PALAVRA fica**, e não é decoração: é a única pista que sobrevive à
              impressão e à visão cromática reduzida — e um autocarro dado por reservado que
              ninguém reservou é o defeito mais caro que este sistema pode produzir. */}
          <span className="barra-previsao-tag">previsão</span>
        </div>,
      );
    }
  }
  // ---- banda 3 · corridas de motorista -----------------------------------
  //
  // ⚠️⚠️ **METADE DA CÉLULA PARA CADA CONDUTOR NO DIA DE ROTAÇÃO (2026-08-14).**
  //
  //   📗 Cliente: *«muitas vezes é no dia de rotação que se muda o motorista, tem de estar
  //   metade da célula com um motorista e outra com outra»*. Medido na folha real: a troca
  //   acontece em **50 de 293 rotações (17,1 %)**, e a folha só consegue nomear dois na mesma
  //   célula em **4 de 330 (1,2 %)** — porque tem uma célula.
  //
  // ⚠️ **NÃO é tripulação dupla e não pode parecê-lo.** A dupla são duas pessoas ao mesmo
  //   tempo no mesmo autocarro (art. 8.º n.º 5) e desenha-se com o ícone `IconeDupla` numa
  //   barra só. Isto são duas pessoas em SEQUÊNCIA, e desenham-se como duas barras.
  const meiaColuna = (d: number): number => larg(d, d + 1) / 2;
  const barrasMot = segsMot.map((s) => {
    const recuo = s.inicioAMeio ? meiaColuna(s.dia0) : 0;
    const corte = s.fimAMeio ? meiaColuna(s.dia1 - 1) : 0;
    // ⚠️ Os dias ÚTEIS e não `dia1 - dia0`: uma meia coluna não tem espaço para dois nomes,
    //   e `rotuloMotorista` decide a abreviatura pelo espaço que tem.
    const diasUteis = s.dia1 - s.dia0 - (s.inicioAMeio ? 0.5 : 0) - (s.fimAMeio ? 0.5 : 0);
    return (
      <div
        // ⚠️ A chave inclui `daPartida`: numa rotação no PRIMEIRO dia da janela as duas
        //   metades têm o mesmo `dia0`, e duas chaves iguais fazem o React descartar uma
        //   delas — o condutor da segunda viagem desaparecia só nessa posição de scroll.
        key={`m${s.dia0}-${s.daPartida ? 'p' : 'c'}`}
        className={classes(
          'barra',
          'barra--mot',
          s.abre && 'abre',
          s.fecha && 'fecha',
          s.motorista2 >= 0 && 'dupla',
          (s.inicioAMeio || s.fimAMeio) && 'meia',
          s.daPartida && 'barra--mot-parte',
        )}
        style={{ left: esq(s.dia0) + recuo, width: larg(s.dia0, s.dia1) - recuo - corte }}
      >
        {s.motorista2 >= 0 ? <IconeDupla em={0.95} className="ic" /> : null}
        <span className="barra-mot-nome">
          {rotuloMotorista(dados, s.motorista, s.motorista2, Math.max(1, Math.floor(diasUteis)))}
        </span>
      </div>
    );
  });

  // ---- dias: alvo de rato, localidade e ritmo da semana -------------------
  const dias = [];
  for (let d = dia0; d < dia1; d++) {
    const cel = linha[d] ?? null;

    // ⚠️ **O CÓDIGO DA LOCALIDADE NÃO SE REPETE ENQUANTO A VIATURA NÃO SE MEXE.**
    //
    // 📗 É a mesma regra que o cliente já tinha dado para a sigla do itinerário — «repetir
    // IGT em oito células seguidas era a principal razão de o mapa ser ilegível» — e que eu
    // apliquei à banda 1 e **deixei de fora na banda 2**. Ele voltou a apontá-la: «muitas vezes
    // as marcações ficam com a mesma cidade em todas».
    //
    // ⚠️ Medido antes de mexer: só **0,2 %** das corridas de 3+ dias têm uma localidade única,
    // logo o problema NÃO estava nos dados — estava nas **paradas**, onde a viatura está
    // sossegada no mesmo sítio e o mapa escrevia `BCN BCN BCN`.
    //
    // ⚠️ E repete-se em quatro casos, de propósito: no **primeiro dia da janela** (senão a
    // coluna da esquerda ficava sem contexto ao deslocar), quando há **seta** de chegada ou
    // partida (o movimento é o que interessa ver, e o código acompanha-o), quando há **dois
    // locais** (embarque e pernoita — F-21), e numa VIAGEM a decorrer. Colapsar esses
    // esconderia informação em vez de ruído.
    //
    // ⚠️⚠️ **2026-08-26 · só se suprime num dia PARADO — nunca numa viagem.** 📗 Cliente:
    //    *«continuam a existir dias vazios… tens de colocar a pernoita do dia, para quem
    //    olhar para o mapa e perceber onde o autocarro vai pernoitar»*. A medição que
    //    justificou esta regra ("só 0,2 % das corridas têm localidade única — o problema
    //    estava nas PARADAS") já dizia isto: nunca mediu viagens, mediu paradas. Uma viagem
    //    de vários dias pela mesma cidade (a pernoita repete-se em cascata quando ninguém
    //    escolhe outra) tem de mostrar a cidade em TODOS os dias — é a única forma de saber
    //    onde o autocarro dorme sem ter de contar dias a partir da última que apareceu.
    const nota = dados.notas[`${viatura}:${d}`];
    // ⚠️⚠️ **A DECISÃO VIVE EM `local-do-dia.ts`, E É A MESMA DAS TRÊS SECÇÕES.** Até
    //    2026-09-16 este bloco tinha aqui as quatro saídas escritas à mão (`normal`, `r`,
    //    `rAnterior`, `estaParado`, `repetido`), e a secção dos parceiros **não tinha banda
    //    nenhuma** — logo não havia segunda cópia para divergir. Assim que os parceiros e a
    //    espera passaram a mostrar a cidade por dia, passariam a existir **três**. 📌 A
    //    consequência de divergirem não é cosmética: a cidade de um dia **é** a pernoita, e um
    //    branco onde devia estar um `?` lê-se como «está tudo bem» (D-081, art. 8.º n.º 8).
    // ⚠️ **`anterior` é `null` no primeiro dia da janela**, e não a célula que está fora dela:
    //    é o que faz o código repetir-se sempre na coluna da esquerda ao deslocar o mapa.
    const r = localDoDia(dados, cel, d > dia0 ? (linha[d - 1] ?? null) : null);
    // ⚠️⚠️ §12.6 · roving tabindex — SÓ esta célula (quando é ela a seleccionada) fica no
    //    percurso do `Tab`; as outras ficam com `tabIndex={-1}` (focáveis por `.focus()`, não
    //    por `Tab`). É o `diaSeleccionado` da linha — `null` em 249 das 250 — e nunca a cópia
    //    `linha-fixada`, que recebe sempre `null` (ver `LinhaProps`).
    const seleccionada = diaSeleccionado === d;
    dias.push(
      <div
        key={d}
        role="gridcell"
        aria-selected={seleccionada}
        tabIndex={seleccionada ? 0 : -1}
        className={classes(
          'dia',
          // ⚠️⚠️ 2026-08-26 · «se tiverem algo marcado, têm de aparecer da cor do evento» —
          //    o fundo de fim de semana só se aplica a um dia SEM NADA. Qualquer alocação
          //    (viagem, oficina, parado, trânsito…) tem sempre a sua própria cor, e essa
          //    cor é que deve ficar visível — nunca o cinzento do calendário por cima.
          dados.fimDeSemana[d] === true && cel === null && 'dia--fds',
          inicioSemana[d] === true && 'dia--semana',
          cel === null && 'dia--vazio',
          // ---- ⚠️⚠️ §8.3 · O DIA COMPROMETIDO EM QUE A VIATURA NAO TRABALHA -------------
          //
          //   📗 §8.3: um servico de 12 dias com 2 em Marrocos **nao sao 12 dias de trabalho**.
          //   📗 Cliente, 2026-09-07: *«se existirem pedidos desse tipo a logica tem de existir,
          //   mas a app tambem tem de ser alterada»*.
          //
          // ⚠️⚠️ **A migracao 072 deu o dado e a bandeira, e o ecra nao desenhava nada** — o
          //   planeador lia «12 dias de trabalho» onde ha 10, e o mapa existe para dizer a
          //   ocupacao. 📌 A marca vai na CELULA e nao numa barra propria: este dia **e** um dia
          //   daquele servico (traz cliente, itinerario, corrida e pedido, e e isso que o faz
          //   ocupar), logo uma barra propria dizia ao olho que sao tres viagens.
          //
          // ⚠️ **Zero nos novos:** a marca e um `::after` neste elemento, que ja existe. O
          //   orcamento de nos/dia e o risco R1 do ADR-001, e este ficheiro ja recusa um icone em
          //   19 % das barras porque «~2 nos cada em 600 dias visiveis, e o p95 nota-o».
          cel !== null && (cel[4] & BANDEIRA.PARADO_NO_SERVICO) !== 0 && 'dia--sem-trabalho',
          // ---- ⚠️⚠️ A FRONTEIRA DE UM TROÇO ADMINISTRATIVO (migração 085) ------------------
          //
          //   📗 Cliente: «viagens longas têm de ser divididas de 7 em 7 dias, só para já no
          //   caso de um cliente grego». `dividir-em-trocos.ts` NUNCA muda a `referencia` — a
          //   viagem continua a ser UMA e a barra continua contínua (F-19). Esta marca só diz
          //   «aqui começa outro troço de reporte», DENTRO da barra — não a parte em duas, que
          //   é o que `dividir.ts` faz e é o gesto oposto.
          //
          //   ⚠️ **Zero nós novos**, mesma razão do `dia--sem-trabalho` acima: reutiliza a
          //   célula do dia, que já existe. O sinal fica para o CSS (borda, tracejado ou
          //   pseudo-elemento) — este ficheiro só decide QUANDO acende.
          cel !== null && (cel[4] & BANDEIRA.INICIO_DE_TROCO) !== 0 && 'dia--fronteira-troco',
          // ---- ⚠️⚠️ PEÇA 1 · A BANDA DO `Shift`+setas — §12.4/H (2026-09-28) ------------------
          //
          //   ⚠️ **Zero nós novos**, mesma razão das duas marcas acima: é uma classe na célula
          //   que já existe. `intervaloSeleccionado` só chega não-nulo na linha da viatura em
          //   extensão (ver `LinhaProps`), logo este teste é `false` em 249 das 250 linhas.
          intervaloSeleccionado !== null &&
            d >= intervaloSeleccionado.d0 &&
            d <= intervaloSeleccionado.d1 &&
            'dia--intervalo',
        )}
        data-v={viatura}
        data-d={d}
        // ---- ⚠️⚠️ B30 · O ARRASTO PARTE DA CÉLULA, e NÃO da barra (corrigido 2026-08-30) ----
        //
        //   ⚠️⚠️ **A primeira versão pôs o `draggable` na BARRA, e a barra não recebe rato
        //   nenhum:** ela vive dentro de `.camada`, que tem `pointer-events: none` de
        //   propósito — *«sem isto as barras roubariam o rato aos dias, e o cartão de resumo
        //   deixaria de saber que dia está debaixo do cursor»*. 📌 Logo o `draggable` era
        //   **inerte**, o `cursor: grab` nunca aparecia, e o gesto era **impossível de fazer**.
        //
        // ⚠️⚠️ **E as provas passaram todas**, porque verificavam o ATRIBUTO no DOM — nunca
        //   que o elemento consegue receber um ponteiro. Até as mutações sobreviveram, porque
        //   só tocavam no mesmo caminho do atributo. Foi o cliente a dizer «não consigo».
        //
        // 📌 **A célula é o punho certo:** recebe eventos, sabe `(v, d)`, e a corrida deriva-se
        //   com `extentoDaCorrida` — exactamente o que o menu de contexto já faz. Assim a
        //   regra do `pointer-events` fica intacta e o cartão do cursor continua a funcionar.
        //
        // ⚠️ Só num dia com SERVIÇO: um dia vazio ou uma paragem não se arrastam (a paragem
        //   diz onde a viatura está, não é trabalho que se mova) — e um punho que não faz
        //   nada é indistinguível de um punho avariado.
        {...(cel !== null && cel[0] >= 0 ? { draggable: true } : {})}
        // Cada coluna tem a sua largura: vem no elemento, não de uma regra CSS.
        style={{ width: tamanho(offsetsDias, d) }}
      >
        {r.tipo === 'falta' ? (
          // ⚠️ Localidade em FALTA. Medido na folha real: 990 de 16 690 dias
          // dentro de uma corrida (5,9 %) não têm localidade. O cliente exige que
          // seja sempre definida — logo mostra-se a lacuna em vez de a esconder,
          // que é a única forma de alguém a preencher.
          // ⚠️ O `?` sozinho não se explicava — foi o cliente a dizê-lo: «não
          // percebi o ? em certos dias». Duas razões, e as duas eram nossas: o
          // mesmo glifo servia para «provisório» na banda 1 e para «local em
          // falta» na banda 2, e nenhum dos dois estava na legenda. Agora este
          // leva caixa tracejada — lê-se como campo por preencher, não como
          // dúvida — e ambos entraram em «Como ler o mapa».
          <span className="dia-local dia-local--falta" title={TITULO_LOCAL_EM_FALTA}>
            ?
          </span>
        ) : null}
        {r.tipo === 'local' ? (
          <span
            className={classes(
              'dia-local',
              // ⚠️ **A LOCALIDADE INFERIDA VÊ-SE COMO INFERIDA.** Uma reserva preenche todos
              //   os dias — o cliente exigiu-o — mas só conhece o primeiro e o último; os do
              //   meio são onde a viatura *estava*. Sem esta distinção, uma inferência nossa
              //   ficava indistinguível de uma decisão de um planeador, e a pernoita decide o
              //   alojamento pago (D-060) e a prova do repouso semanal (art. 8.º n.º 8).
              r.inferido && 'dia-local--inferido',
            )}
            // ⚠️ Migração 093 · o presumido explica-se ANTES do inferido: o `?` na cidade é a
            //    marca mais forte, e sem o `title` ninguém sabe o que ele quer dizer.
            title={
              r.presumido
                ? TITULO_DESTINO_PRESUMIDO
                : r.inferido
                  ? TITULO_LOCAL_INFERIDO
                  : undefined
            }
          >
            {r.sentido === 'chegada' ? <IconeChegada em={0.9} className="ic" /> : null}
            {r.sentido === 'partida' ? <IconePartida em={0.9} className="ic" /> : null}
            {/* DOIS locais no primeiro dia: onde os passageiros entram e onde se
                pernoita. Medido em 4,6 % das células de localidade da folha real.
                O de embarque vem primeiro e mais fraco — o que fica na linha é
                onde a viatura dorme. */}
            {r.embarque !== '' ? (
              <span className="dia-embarque" title="entrada dos passageiros">
                {r.embarque}/
              </span>
            ) : null}
            <span className="dia-local-txt">{r.local}</span>
            {r.meio !== '' ? <IconeDoMeio meio={r.meio} /> : null}
          </span>
        ) : null}
        {/* 📗 fonte 6.8 · o MARCADOR da nota.
            ⚠️ **Só onde HÁ nota**, e é o que torna isto comportável: o lápis em todas as
            células custava um nó de DOM por célula (~600 dias por janela, orçamento medido
            de 5,4 nós/dia e p95 de 29–45 ms — risco R1 do ADR-001). Aqui o custo é
            proporcional às notas que existem, que são poucas por natureza.
            O texto vai no `title` **e** no cartão do cursor: aqui é o sinal de que há algo
            para ler, não o sítio onde se lê. */}
        {nota !== undefined ? (
          <span className="dia-nota" title={nota} aria-label={`nota: ${nota}`}>
            ✎
          </span>
        ) : null}
      </div>,
    );
  }

  return (
    <div
      // ⚠️⚠️ **B1 · A LINHA DA VIATURA INACTIVA VAI ESBATIDA — e a fila é que a torna segura.**
      //
      //   📗 Cliente: *«No caso dos autocarros desfocá-lo e deixar que se esconda essa linha.»*
      //
      //   ⚠️⚠️ **Esconder, sozinho, PIORA — e por isso aqui só se esbate.** Medido no `staging`
      //   a 2026-09-11: **3 viaturas inactivas com 66 dias de serviço FUTURO** (hoje no plano
      //   de ensaio `D_GVA`, e quem o abrir vê-os na mesma). Esses dias têm
      //   de ser replaneados; escondê-los fazia-os **desaparecer do ecrã sem ninguém os ver**, e
      //   colide com P-30 (*«esbatido, nunca escondido, para não parecer que a viatura está
      //   livre»*). 📌 O esconder é legítimo **depois** de a fila «Precisa de mim» os mostrar
      //   noutro sítio — e é isso que a família `executor_inactivo` passa a fazer.
      className={classes(
        'linha',
        alvoArrasto && 'linha--alvo',
        v?.inactiva === true && 'linha--inactiva',
      )}
      // ⚠️ §12.6 · a linha da grelha, em termos de ARIA — atributo, não regra visual (não
      //    precisa de `mapa.css`). Ver `diaSeleccionado` em `LinhaProps` para a colisão da
      //    cópia `linha-fixada`, que não é uma segunda linha lógica.
      role="row"
      // A altura e as três bandas são desta linha, não de todas: cada viatura pode
      // ter a sua. As bandas vêm calculadas em JS e não por `calc` no CSS porque em
      // `calc` davam sub-píxeis e as arestas das barras ficavam esfumadas.
      style={
        {
          height: altura,
          '--altura-util': `${altura - SEPARADOR_LINHA}px`,
          '--h-itin': `${banda.itinerario}px`,
          '--h-local': `${banda.localidade}px`,
          '--h-mot': `${banda.motorista}px`,
        } as React.CSSProperties
      }
    >
      {/* `data-linha` é o que o arrasto usa para saber QUAL linha redimensionar.
          Deliberadamente não é `data-v`: esse é o atributo das células de dia, e o
          ouvinte de clique procura `[data-v]` — pô-lo aqui faria o arrasto da altura
          seleccionar um dia inexistente. */}
      {/* ⚠️⚠️ **A VIATURA BLOQUEADA VÊ-SE (B4, T-62).** 📗 «bloquear autocarro para não entrar
          na lista de possíveis alterações».

          📌 **Esbatida, nunca escondida** (P-30): esconder a linha fazia a frota parecer menor
          e o planeador procurar um autocarro que existe. E sem marca nenhuma, o motor deixava
          de a propor **em silêncio** — que é o pior resultado de um bloqueio: o planeador
          pergunta «porque é que a 174 nunca aparece?» e não há resposta no ecrã. */}
      {/* ⚠️⚠️ **`inactiva` e `bloqueada` são DUAS COISAS e não podem ter o mesmo sinal.**
          `bloqueada` = *«ela trabalha, não lhe mexas»* (cadeado). `inactiva` = *«já não está
          na frota»* — vendida ou abatida (019). 📌 A marca é o `?`, o mesmo sinal que o
          condutor inactivo leva na banda 3: uma pergunta por responder, e não uma proibição.
          Dois sinais diferentes para o mesmo facto obrigavam a aprender dois.
          ⚠️ E as duas coisas **acumulam-se**: uma viatura pode estar bloqueada E inactiva, e
          o `title` diz as duas — escolher uma escondia a outra. */}
      <div
        className={classes(
          'viatura',
          v?.bloqueada === true && 'viatura--bloqueada',
          v?.inactiva === true && 'viatura--inactiva',
          // ⚠️ Peça 2 · só o PRÓPRIO nome — a banda que cobre a grelha é um elemento à parte
          //    (ver a nota da prop `linhaSeleccionada`, em `LinhaProps`).
          linhaSeleccionada && 'viatura--seleccionada',
        )}
        data-linha={viatura}
        role="rowheader"
        aria-selected={linhaSeleccionada}
        {...(() => {
          const t = [
            v?.bloqueada === true ? 'fora das recomendações do motor — ver «Não mexer aqui»' : '',
            v?.inactiva === true
              ? 'este autocarro já NÃO está activo na frota — o serviço que aqui estiver ' +
                'marcado tem de ser replaneado (ver «Precisa de mim»)'
              : '',
          ].filter((x) => x !== '');
          return t.length === 0 ? {} : { title: t.join(' · ') };
        })()}
        // ---- ⚠️⚠️ PEÇA 2 · CLICAR SELECCIONA A LINHA — §12.4-bis (2026-09-28) ------------------
        //
        //   ⚠️ **Exclui a TIRA de redimensionar** (os últimos `LARGURA_TIRA` píxeis da borda de
        //   baixo) — a MESMA fronteira que `bordaSob` (em `Mapa`) já usa para o arrasto da
        //   altura da linha (D-085). Sem esta exclusão, largar o arrasto ali também
        //   seleccionava a linha por baixo.
        onClick={
          aoClicarViatura === undefined
            ? undefined
            : (e) => {
                const r = e.currentTarget.getBoundingClientRect();
                if (e.clientY >= r.bottom - LARGURA_TIRA) return;
                aoClicarViatura(viatura);
              }
        }
      >
        <span className="viatura-num">{v?.numero ?? ''}</span>
        <span className="viatura-mat">{v?.matricula ?? ''}</span>
        <span className="viatura-lot">{v?.lotacao ?? ''}</span>
        {/* CONDUTOR PRINCIPAL da viatura, como na folha do Excel — medido: 93 %
            das linhas de viatura reais trazem-no no cabeçalho (62 de 67).
            ⚠️ É METADADO, não atribuição: cobre a mediana de 44 % dos dias e às
            vezes nem é quem conduz mais. Quem conduz num dado dia é a barra da
            banda 3. Está aqui para ajudar a planear, não para dizer quem vai. */}
        {v !== undefined && v.motoristaPreferencial >= 0 ? (
          // ⚠️ **O NOME vai no `title`**, e não só o rótulo do campo: sem diminutivo o texto
          //   aqui é o nome completo e o `ellipsis` corta-o. Um rótulo que diz o que a coluna é
          //   não substitui o valor que ela deixou de mostrar.
          <span
            className="viatura-principal"
            title={`condutor principal desta viatura: ${nomeDoMotorista(dados, v.motoristaPreferencial)}`}
          >
            {nomeDoMotorista(dados, v.motoristaPreferencial)}
          </span>
        ) : null}
        {v !== undefined && v.clienteDecoracao >= 0 ? (
          // «Autocarro decorado Mercury» — medido na folha real, em linha própria.
          // Restringe que clientes podem usar a viatura, logo é do planeamento.
          <span className="viatura-dec">
            <IconeDecoracao em={0.85} />
            {dados.clientes[v.clienteDecoracao]?.[0] ?? ''}
          </span>
        ) : null}
      </div>
      <div className="faixa">
        <div className="dias" style={{ paddingLeft: esq(dia0) }}>
          {dias}
        </div>
        <div className="camada">{barras}</div>
        <div className="camada camada--mot">{barrasMot}</div>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// O mapa
// ---------------------------------------------------------------------------

export interface MapaProps {
  readonly dados: DadosMapa;
  readonly configuracao?: ConfiguracaoMapa;
  /**
   * Janela imposta de fora, em vez de derivada do deslocamento.
   *
   * ⚠️ **NÃO é uma opção de utilização.** Existe por uma razão medida: o React 19
   * não reage a um evento `scroll` despachado por código — verificado com o
   * contentor dimensionado, a aritmética da janela correcta e o evento a chegar ao
   * elemento. Sem esta costura, a única forma de medir o mapa seria medir **uma
   * cópia** dele, que é exactamente o erro de medição já cometido neste projeto.
   */
  readonly janelaFixa?: Janela;
  /**
   * Volta a pedir o mapa ao servidor.
   *
   * ⚠️ Chamado depois de uma ESCRITA. Sem isto, o planeador reservava e a grelha ao
   * lado continuava a mostrar a viatura livre — a cache do servidor era invalidada,
   * mas o navegador já tinha os dados. Ver `App.tsx`.
   */
  readonly recarregar?: (() => void) | undefined;
  /**
   * O papel de PLATAFORMA de quem esta a ver — `superadmin`, `utilizador` ou `null`.
   *
   * ⚠️ **Serve para NAO oferecer o que a pessoa nao pode fazer.** O guarda do servidor
   * recusa na mesma (a porta esta la, e nao aqui); o que isto evita e a familia «recusar o
   * que se mostra»: um botao visivel que devolve 403 le-se como avaria, e o proximo passo
   * de quem o ve e abrir um incidente em vez de pedir acesso.
   */
  readonly papel?: string | null;
  /**
   * A ETag com que vieram os dados que estão no ecrã — ver `App.tsx` e `PlanoMudou.tsx`.
   *
   * ⚠️ `undefined`/`null` desliga a sonda por completo, e é o que mantém as provas que montam
   * o mapa com dados fixos sem rede nenhuma.
   */
  readonly etagDoMapa?: string | null;
  /**
   * Publica a LIGAÇÃO AO MAPA (`ligacao-ao-mapa.ts`) para quem vive fora desta árvore — o
   * `ConflitoAoGravar`, montado na raiz em `App.tsx`. ⚠️ O valor é o MESMO que o `Provider` de
   * baixo fornece (uma só formulação); `null` ao desmontar.
   */
  readonly publicarLigacao?: ((l: LigacaoAoMapa | null) => void) | undefined;
  /**
   * 🔴 D-101 · «Ir para…» · pede ao `App` outra fita: `inicio` (dia 1 de um mês) ou `null` para a de
   * hoje, e a data a mostrar quando ela chegar. Sem ele (critérios, medição), não há navegação.
   */
  readonly recentrarFita?: ((inicio: string | null, dataAMostrar: string | DestinoAoAbrir | null) => void) | undefined;
  /** A data (ou o sítio: viatura, motorista e dias) a levar à vista quando esta fita chegou. */
  readonly dataAoAbrir?: string | DestinoAoAbrir | null;
  /** Uma fita nova está a caminho. */
  readonly aCarregarFita?: boolean;
  /** Chamado quando a `dataAoAbrir` já foi levada à vista — para não voltar lá a cada recarga. */
  readonly aoMostrarDataAoAbrir?: (() => void) | undefined;
}

/**
 * 🔴 2026-10-26 · O SÍTIO A MOSTRAR QUANDO UMA FITA NOVA CHEGA — por DATAS, nunca por índices.
 *
 * 📗 Cliente: *«se eu clico "ver no mapa" em algo que não está no período aberto deveria dar para
 * ver de igual forma»*. Um «Ver no mapa» fora da fita pede a fita centrada nesse dia e leva estas
 * datas consigo: na fita nova os índices são outros, e só a data sobrevive à troca.
 */
export interface DestinoAoAbrir {
  readonly data: string;
  readonly dataFim?: string;
  readonly viaturaId?: string;
  readonly motoristaId?: string;
}

/**
 * 🔴 2026-10-26 · A decisão de `foraDaFita`, pura: `null` quando o primeiro dia está na fita (basta
 * deslocar o mapa); senão, a fita centrada nesse dia e o sítio por DATAS para levar até lá.
 */
export function destinoForaDaFita(
  janela: JanelaDoMapa,
  dia0: number,
  dia1: number,
  quem: { readonly viaturaId?: string; readonly motoristaId?: string },
): { readonly inicio: string; readonly destino: DestinoAoAbrir } | null {
  if (dentroDaJanela(janela, dia0)) return null;
  const data = dataDoIndice(janela, dia0);
  return {
    inicio: inicioDaFitaCentradaEm(data),
    destino: { data, dataFim: dataDoIndice(janela, Math.max(dia0, dia1)), ...quem },
  };
}

export function Mapa({
  dados,
  configuracao = CONFIGURACAO_OMISSAO,
  janelaFixa,
  recarregar,
  papel = null,
  etagDoMapa = null,
  publicarLigacao,
  recentrarFita,
  dataAoAbrir = null,
  aCarregarFita = false,
  aoMostrarDataAoAbrir,
}: MapaProps) {
  const caixa = useRef<HTMLDivElement>(null);
  const envolvente = useRef<HTMLDivElement>(null);
  const [janelaCalculada, setJanela] = useState<Janela>({
    viatura0: 0,
    viatura1: 0,
    dia0: 0,
    dia1: 0,
  });
  const janela = janelaFixa ?? janelaCalculada;

  /**
   * ⚠️⚠️ **B₂ · esconder a linha do executor inactivo — 📄 spec de 2026-09-11, §3.**
   *
   * 📗 Cliente: *«No caso dos autocarros desfocá-lo e deixar que se esconda essa linha.»*
   * O B₁ (`linha--inactiva`, mais acima) esbate; falta o interruptor que esconde.
   *
   * ⚠️⚠️ **Começa DESLIGADO, e não é hesitação — é P-30: «esbatido, nunca escondido».**
   * Esconder só é seguro **porque a fila «Precisa de mim» já mostra estes dias noutro
   * sítio** (`familiaDoExecutorInactivo`, `apps/api/src/atencao.ts`) — sem essa fila,
   * esconder fazia desaparecer do ecrã exactamente os dias que têm de ser replaneados.
   * 📌 É por isso que o interruptor vive aqui e não nasceu já ligado.
   */
  const [esconderInactivas, setEsconderInactivas] = useState(false);
  /**
   * Índices das viaturas a esconder — só quando o interruptor está ligado.
   *
   * 📌 `null` e não um `Set` vazio quando desligado: é o caso comum, e mantém
   * `offsetsLinhas` (mais abaixo) na forma uniforme de sempre em vez de reconstruir
   * excepções à toa. ⚠️ Declarado aqui e não perto de `alturaLinhas` porque
   * `vistaAgora`, logo a seguir, também precisa dele.
   */
  const indicesEscondidos = useMemo(() => {
    if (!esconderInactivas) return null;
    const s = new Set<number>();
    for (let v = 0; v < dados.viaturas.length; v++) {
      if (dados.viaturas[v]?.inactiva === true) s.add(v);
    }
    return s;
  }, [esconderInactivas, dados.viaturas]);

  /**
   * 🔴 «P» do §11 — as linhas que o ecrã deixa ver, para a FOLHA IMPRESSA levar as mesmas.
   *
   * ⚠️⚠️ **É `indicesEscondidos` e NÃO o `filtro`, e a diferença é P-30.** O filtro **esbate**
   * e nunca esconde: uma viatura esbatida continua a estar no mapa, e tirá-la do papel fazia a
   * folha afirmar que ela está livre. 📌 Quem quiser imprimir menos usa o interruptor de
   * esconder, ou a caixa «só as linhas com alguma coisa neste período» — que é a alavanca que
   * a medição diz valer a pena (18 folhas → 2 com um filtro de cliente).
   */
  const viaturasVisiveis = useMemo(() => {
    const saida: number[] = [];
    for (let v = 0; v < dados.viaturas.length; v++) {
      if (indicesEscondidos?.has(v) === true) continue;
      saida.push(v);
    }
    return saida;
  }, [dados.viaturas.length, indicesEscondidos]);

  /**
   * ⚠️⚠️ **O QUE ESTÁ À VISTA: serviços e dias livres** — 📗 «troque por algo que faça mais sentido».
   *
   * 📌 Substitui uma contagem de células rotulada «dias», que era uma medida interna de renderização.
   * A pergunta que o planeador faz ao olhar para uma zona do mapa é **quanto trabalho há e quanto
   * espaço sobra** — e com a frota nos 42 % da folha real, «dias livres» é a grandeza que decide onde
   * encaixar uma viagem.
   *
   * ⚠️ **Um dia com PARAGEM conta como LIVRE, e é deliberado:** uma paragem diz onde a viatura está,
   * não que esteja ocupada — o `reservar.ts` escreve por cima dela de propósito. É o mesmo predicado
   * do produto (cliente, itinerário ou corrida), e contá-la como ocupada era a família de erro que
   * este projecto já apanhou seis vezes.
   *
   * ⚠️ **Custo:** percorre a janela visível — ~1 350 células pelo tecto do teste do risco R1 — e só
   * quando ela muda. Não toca no caminho de desenho das barras, que é o que o p95 mede.
   */
  const vistaAgora = useMemo(() => {
    let servicos = 0;
    let livres = 0;
    for (let v = janela.viatura0; v < janela.viatura1; v++) {
      // B₂ · uma linha escondida não ocupa espaço no ecrã — não é «vista agora».
      if (indicesEscondidos?.has(v) === true) continue;
      const linha = dados.grelha[v] ?? [];
      for (let d = janela.dia0; d < janela.dia1; d++) {
        const c = linha[d] ?? null;
        if (c !== null && (c[0] >= 0 || c[5] >= 0 || c[10] >= 0)) servicos += 1;
        else livres += 1;
      }
    }
    return { servicos, livres };
  }, [
    dados.grelha,
    janela.viatura0,
    janela.viatura1,
    janela.dia0,
    janela.dia1,
    indicesEscondidos,
  ]);

  const [sobre, setSobre] = useState<(Posicao & { x: number; y: number }) | null>(null);
  /**
   * O cartão do cursor **dos subcontratados**, em estado próprio.
   *
   * 📗 Cliente, 2026-08-10: *«os detalhes também terão de ser diferentes»*.
   *
   * ⚠️ **Estado separado e não um campo em `sobre`**: `sobre.v` é índice de `viaturas` e
   * atravessa `resumo()`, a selecção, o painel de detalhe e o «Ver no mapa». Enfiar lá um
   * índice de outro vector obrigava as cinco a distinguir os dois casos — e a que se
   * esquecesse mostrava a viatura errada sem nada no ecrã a denunciá-lo.
   */
  const [sobreSub, setSobreSub] = useState<{
    /** ⚠️ Índice da **LINHA** da secção (C₂), e não o da empresa. */
    l: number;
    d: number;
    /**
     * ⚠️⚠️ **O último dia DA BARRA apontada, lido do próprio elemento.** O cartão reconstruía o
     * intervalo com uma segunda cópia da regra de continuidade da barra, e o comentário dele já
     * dizia que *«duas cópias da mesma regra divergem»*. Com B28 a barra passou a ser por
     * SERVIÇO e a condição «o mesmo número de serviços simultâneos» morreu com a contagem —
     * logo a cópia divergiria e o cartão anunciaria «1 dia» sobre uma barra de cinco. Ler o fim
     * do elemento apontado é a única leitura que **não pode** divergir do que está desenhado.
     */
    d1: number;
    x: number;
    y: number;
  } | null>(null);

  /**
   * O subcontratado FIXO por clique — o que abre o painel de detalhe.
   *
   * 📗 Cliente, 2026-08-10: *«tem de dar para ver detalhes de igual forma, tem de permitir
   * clicar»*.
   *
   * ⚠️⚠️ **Antes disto a barra do subcontratado nao respondia ao clique**, e eu tinha escrito no
   * codigo, como se fosse uma decisao, que «um servico subcontratado nao se selecciona (nao ha
   * painel de detalhe para ele)». 📌 Nao era uma decisao: era a ausencia de trabalho a
   * descrever-se como desenho — a familia de comentario que mente sobre o codigo, aqui na
   * versao mais enganadora, a que justifica o que falta.
   *
   * ⚠️⚠️ **Estado separado de `seleccao`, pela mesma razao de `sobreSub`:** `seleccao.v` e indice
   * de `viaturas` e atravessa o `detalhe()`, as accoes do painel, o realce e o «Editar
   * servico». Os dois nunca estao activos ao mesmo tempo — quem selecciona um limpa o outro.
   *
   * ⚠️⚠️ **`l` E NÃO `s`: é o índice da LINHA e não o da EMPRESA (C₂).** Antes disto uma
   * selecção apontava para «a empresa X no dia D» — um conjunto com até **9** serviços (M7,
   * medido) —, e tudo o que agia sobre ela escolhia **um por omissão, em silêncio**: o
   * `detalheSub` anunciava o intervalo do primeiro num painel que listava todos. O campo
   * mudou de nome de propósito, para que nenhum leitor antigo continue a compilar.
   */
  const [seleccaoSub, setSeleccaoSub] = useState<{ l: number; d: number } | null>(null);
  /** B1-(2) · a janela que troca este serviço subcontratado para outra empresa. */
  const [aTrocarSub, setATrocarSub] = useState<{ s: number; d: number } | null>(null);
  /**
   * C · a janela que escolhe empresa + pedido. ⚠️ `s` pode ser `null`: ela abre-se da
   * cabeceira da secção, onde ainda não há empresa nenhuma escolhida.
   */
  const [aSubcontratarAMao, setASubcontratarAMao] = useState<{
    s: number | null;
    d: number;
  } | null>(null);
  /**
   * ⚠️⚠️ **O «Planear» aberto A PARTIR DO MAPA, com uma empresa garantida.**
   *
   * 📌 É o mesmo componente que a lista de pedidos e as consultas abrem — e tinha de ser: a
   * reserva subcontratada tem os mesmos avisos, o mesmo motivo obrigatório nas divergências
   * (D-007) e a mesma auditoria. Uma segunda versão aqui divergiria, e a que divergisse era
   * a que ninguém corre.
   */
  const [planearComParceiro, setPlanearComParceiro] = useState<{
    pedido: Pedido;
    subcontratadoId: string;
  } | null>(null);
  const [seleccao, setSeleccao] = useState<Posicao | null>(null);
  /**
   * ---- ⚠️⚠️ PEÇA 1 · `Shift`+setas ESTENDE A SELECÇÃO POR DIAS (§12.4, peça H) ----------------
   *
   * 📗 §12.4 do desenho: *«`Shift`+setas — Estende por DIAS»*, e §12.9 media-o a **por fazer**,
   * bloqueado de propósito em `Mapa.tsx:4041` com o comentário *«o Shift fica reservado para a
   * selecção múltipla (peça H)»*. A CAPACIDADE de mover várias viagens de uma vez já existe (peça
   * R, `MoverBloco` em modo lote) — o que faltava era o GESTO na grelha que a alimenta.
   *
   * ⚠️⚠️ **NÃO É UMA SEGUNDA SELECÇÃO — é a MESMA `seleccao`, com uma segunda ponta.** `seleccao`
   * continua a ser a célula onde o cursor está (a `ponta` que se move); `ancora` é o dia onde a
   * extensão começou. O intervalo visível é sempre `[min(ancora,ponta), max(ancora,ponta)]`.
   * Guardar um segundo `{v,d0,d1}` independente de `seleccao` obrigava a manter os dois em
   * sincronia à mão em cada gesto — e é exactamente essa segunda fonte de verdade que este
   * projecto já pagou noutros ficheiros.
   *
   * ⚠️ **SÓ NA FROTA E SÓ POR DIAS.** `Shift`+`↑`/`↓` fica por fazer, de propósito: o §12.4 só
   * pede a extensão «por dias», e mudar de VIATURA a meio de uma extensão não tem leitura óbvia
   * (que intervalo mostrar em duas linhas?) — nenhuma pergunta do cliente pede isso. A secção dos
   * subcontratados também fica de fora: não tem um gesto de "lote" que precise de um intervalo.
   *
   * ⚠️⚠️ **NUNCA SE LIMPA EXPLICITAMENTE — DERIVA-SE.** Não há um `setIntervaloDias(null)`
   * espalhado pelos cliques, pelo `Escape`, pelo `irPara` ou pelo menu de contexto: seria a
   * família «duas portas, uma fechada» outra vez, a exigir lembrar de limpar em cada sítio novo
   * que mexe em `seleccao`. Em vez disso, o valor É VÁLIDO só enquanto `intervaloDias.ponta`
   * coincidir com `seleccao.d` na mesma viatura — ver `intervaloSeleccionado`, abaixo. Qualquer
   * gesto que mude `seleccao` sem passar pelo ramo do `Shift` invalida-o de imediato, no mesmo
   * render, sem efeito nem atraso de um quadro.
   */
  const [intervaloDias, setIntervaloDias] = useState<
    { readonly v: number; readonly ancora: number; readonly ponta: number } | null
  >(null);
  /**
   * O intervalo a DESENHAR — `null` quando não há extensão válida ou quando ela ainda não saiu
   * de um dia só (uma banda de um dia é redundante com a própria célula seleccionada).
   */
  const intervaloSeleccionado = useMemo(():
    | { readonly v: number; readonly d0: number; readonly d1: number }
    | null => {
    if (intervaloDias === null || seleccao === null) return null;
    if (intervaloDias.v !== seleccao.v || intervaloDias.ponta !== seleccao.d) return null;
    const d0 = Math.min(intervaloDias.ancora, intervaloDias.ponta);
    const d1 = Math.max(intervaloDias.ancora, intervaloDias.ponta);
    return d0 === d1 ? null : { v: intervaloDias.v, d0, d1 };
  }, [intervaloDias, seleccao]);
  /**
   * ---- ⚠️⚠️ PEÇA 2 · CLICAR NO NÚMERO DE UM DIA, OU NO NOME DE UMA VIATURA, SELECCIONA A
   * COLUNA OU A LINHA — §12.4-bis ------------------------------------------------------------
   *
   * 📗 Cliente, 2026-09-11: *«quando clico numa coluna ou linha esta fique seleccionada e
   * quando clico novamente desselecciona»*.
   *
   * ⚠️⚠️ **É A MESMA SELECÇÃO, COM MAIS FORMAS — não uma segunda.** É por isso que vive ao
   * lado de `seleccao`/`intervaloSeleccionado` e não substitui nenhum: uma célula continua a
   * ser uma célula (o painel e o `Ctrl+K` continuam a lê-la de `seleccao`), e isto é a forma
   * nova — «um dia, TODAS as viaturas» ou «uma viatura, TODOS os dias» — que o §12.4-bis pede.
   *
   * ⚠️ **NUNCA DUAS AO MESMO TEMPO** (a regra do próprio §12.4-bis): seleccionar uma linha com
   * uma coluna já seleccionada SUBSTITUI, nunca acumula — «selecções compostas não se explicam
   * a ninguém».
   */
  const [formaSeleccao, setFormaSeleccao] = useState<
    { readonly tipo: 'coluna'; readonly d: number } | { readonly tipo: 'linha'; readonly v: number } | null
  >(null);
  /**
   * Alternar: clicar na MESMA coluna desselecciona; clicar noutra substitui — nunca acumula
   * com uma linha (a condição já testa `a.tipo === 'coluna'`, logo uma linha activa é sempre
   * substituída, nunca somada).
   */
  const aoClicarColuna = useCallback((d: number) => {
    setFormaSeleccao((a) => (a !== null && a.tipo === 'coluna' && a.d === d ? null : { tipo: 'coluna', d }));
  }, []);
  /** O espelho exacto de `aoClicarColuna`, para a linha (o nome da viatura). */
  const aoClicarLinha = useCallback((v: number) => {
    setFormaSeleccao((a) => (a !== null && a.tipo === 'linha' && a.v === v ? null : { tipo: 'linha', v }));
  }, []);
  /**
   * ⚠️ **A VIATURA AVARIOU E ALGUÉM TEM DE FAZER OS SERVIÇOS DELA.**
   *
   * 📗 Cliente, 2026-08-07: *«marquei avariado no dia 16 agosto, tem de arranjar logo um
   * substituto de forma rápida… clicar em cima da avaria e aparecer logo opções?»*.
   */
  const [aSubstituir, setASubstituir] = useState<Posicao | null>(null);
  /**
   * ⚠️ **Marcar avaria/oficina em VÁRIOS dias.**
   *
   * 📗 *«tem é de ser possível marcar mais dias em vez de apenas 1 sem ter de ir célula a
   * célula»* e *«nos dias livres tem de permitir editar e definir por exemplo oficina»*.
   */
  const [aMarcar, setAMarcar] = useState<Posicao | null>(null);
  /**
   * ⚠️ **A ÚNICA ENTRADA DE TRABALHO NO SISTEMA, e não existia** (📗 5.3.3.1).
   *
   * `{}` cria de raiz; com `duplicarDe` copia outro pedido. Vive aqui e não na lista de
   * pedidos porque precisa dos `dados` — clientes e localidades —, como todas as outras.
   */
  const [aCriarPedido, setACriarPedido] = useState<{ duplicarDe?: PedidoParaDuplicar } | null>(
    null,
  );
  /** O id do último pedido criado — a lista filtra-se por ele. Ver `Pedidos.mostrarPedido`. */
  const [pedidoCriado, setPedidoCriado] = useState<string | null>(null);
  /**
   * ⚠️ **Quantas vezes se pediu para mostrar um pedido** — sobe em CADA chamada de
   * `mostrarPedidoNaLista`, mesmo com o mesmo id. Sem isto, mostrar outra vez o pedido que já
   * estava em `pedidoCriado` não mudava nada que a lista visse, e o filtro não se reaplicava
   * (ver `Pedidos.mostrarPedidoVez`).
   */
  const [vezDoPedido, setVezDoPedido] = useState(0);
  /**
   * ⚠️ **O ALVO DE UM «Ver no mapa», desenhado por cima e temporário.**
   *
   * 📗 Cliente, 2026-08-07: *«quando selecciono ver no mapa tem de ficar mais visível para
   * ser fácil identificar»*. Antes, saltar para uma célula deixava-a apenas **seleccionada**
   * — o mesmo aspecto de um clique qualquer, no meio de ~600 dias desenhados. O olho não
   * tem para onde ir.
   *
   * ⚠️ **É um RECTÂNGULO SOBRE O INTERVALO INTEIRO e não sobre uma célula.** Um serviço são
   * dias, e realçar o primeiro obrigava a procurar onde acaba — que é metade do problema.
   *
   * ⚠️ **E APAGA-SE SOZINHO.** Um realce permanente vira decoração: ao terceiro salto
   * haveria três, e nenhum diria «é aqui». `contagem` muda a cada salto para o mesmo sítio,
   * para a animação recomeçar — sem ela, carregar duas vezes no mesmo botão não fazia nada
   * visível e lia-se como avaria.
   */
  const [realce, setRealce] = useState<
    | {
        readonly v: number;
        readonly d0: number;
        readonly d1: number;
        readonly contagem: number;
        /**
         * ⚠️⚠️ **O QUE O SERVICO SERIA, se fosse escrito** (2026-08-20).
         *
         * 📗 Cliente: *«quero fazer uma atualizacao profunda em tudo que for ver mapa, uma
         * vez que atualmente quando o faco aponta apenas para o retangulo onde marca o local, eu
         * quero que apareca durante um intervalo de tempo uma preview de como fica o servico
         * igual a alocacao normal mas por exemplo com um ligeiro desfoque»*.
         *
         * 📌 **E a razao pela qual isto nao e cosmetica:** quando se salta do painel do
         * planear, a alocacao **ainda nao existe** — nao ha celula nenhuma naqueles dias, logo o
         * rectangulo aponta para o vazio. A pergunta e *«como e que isto vai ficar?»*, e o
         * rectangulo respondia *«e aqui que nao esta nada»*.
         *
         * ⚠️⚠️ **E TEM de se ler como PROVISORIO.** Uma barra igual a real fazia o planeador
         * acreditar que ja esta marcado — e «um valor plausivel no lugar do certo» na forma mais
         * caras deste sistema: um autocarro dado por reservado que ninguem reservou.
         *
         * ⚠️ Ausente quando o alvo **ja existe** no mapa (uma consulta, a fila «precisa de
         * mim»): ali a celula esta desenhada, e uma pre-visualizacao por cima so tapava a
         * realidade.
         */
        /**
         * ⚠️⚠️ **A PRÉ-VISUALIZAÇÃO, agora com os PEDAÇOS e a identidade do cliente.**
         *
         * 📗 Cliente, 2026-08-21: *«o meu objetivo é ver a alocação corretamente já com os
         * dias tal como já fosse definitiva mas com um ligeiro desfoque»* e *«em outros tipos de
         * marcações como partir, marcar perfeitamente como fica cada autocarro — não com a viagem
         * completa mas sim já com as partições»*.
         *
         * ⚠️⚠️ **A versão anterior era uma CAIXA com a palavra «previsão» — e não era isto.**
         * Uma caixa diz *onde*; não diz *como fica*. 📌 O que se desenha agora é a **barra**:
         * a cor do cliente, a sigla, os dias exactos de cada pedaço — igual à real, esbatida e
         * tracejada. E é desenhada **dentro da linha**, pelo mesmo componente que desenha as
         * verdadeiras, para não haver duas geometrias da mesma coisa.
         */
        readonly previsao?: {
          /** Índice em `dados.clientes` — `-1` quando a sigla não é conhecida. */
          readonly cliente: number;
          /** Índice em `dados.itinerarios` — `-1`. */
          readonly itinerario: number;
          /** ⚠️ Os pedaços que ESTA viatura fica a fazer. Nunca vazio. */
          readonly pedacos: readonly (readonly [number, number])[];
        };
      }
    | null
  >(null);
  const [corLigada, setCorLigada] = useState(true);
  /**
   * 📗 §7: cor por cliente **ou** por itinerário.
   *
   * ⚠️ **Arranca no que a configuração diz** e não numa preferência do ecrã: a fonte põe
   * isto nas «configurações gerais», logo é uma decisão da empresa e não de quem está a
   * olhar. O botão muda-a **nesta sessão** — gravá-la é o painel de regras.
   */
  const [corPor, setCorPor] = useState<'cliente' | 'itinerario'>(configuracao.corPor);
  const [filtro, setFiltro] = useState<Filtro>(SEM_FILTRO);
  const [painelAberto, setPainelAberto] = useState(false);
  // ⚠️ Aqui e não no `Topo`: a janela de pedidos é irmã do mapa e não filha da barra.
  // Dentro da barra ficava presa ao seu contexto de empilhamento (`z-index: 20`) e o
  // cartão junto ao cursor (`z-index: 40`) pintava por cima dela.
  const [pedidosAbertos, setPedidosAbertos] = useState(false);
  /**
   * ⚠️⚠️ **REGRA 8 · UM PEDIDO NOVO TEM DE APARECER NA LISTA — e havia TRÊS caminhos.**
   *
   * 📗 Cliente: *«quando se corrige uma falha num local tem de ser corrigido igualmente em toda
   * a app»* e *«tudo tem de ficar em todas as abas»*.
   *
   * ⚠️ **A unidade é a CHAMADA, não o ficheiro.** Varrido a 2026-09-08: exactamente **duas**
   * chamadas criam um pedido — `POST /api/pedidos/criar` (`NovoPedido.tsx`) e
   * `POST /api/entrada/:id/gravar` (`ValidarProposta.tsx`, ligado hoje) —, e **três** sítios
   * daqui o mostram: o «Novo pedido», o «planear de novo» do `RetirarDoMapa` e a proposta. Os
   * dois primeiros tinham **duas versões deste corpo**, e eu ia ser a terceira.
   *
   * 📌 **É o `mostrarPedido` que recarrega a lista, e não um `recarregar` qualquer:** ele passa
   * a ser o filtro de texto do `Pedidos`, e o filtro vai ao **servidor** — logo a lista é
   * pedida outra vez mesmo com a janela já aberta. Sem o segundo passo o pedido criava-se e não
   * se via: a fila está cortada nos 300, e um pedido de Novembro fica fora dela.
   *
   * ⚠️ **NÃO chama `recarregar` (o mapa), e é isenção de mérito:** criar um pedido não escreve
   * nada em `alocacao` — quem põe a viatura no mapa é o «Planear». A rota `gravar` diz
   * exactamente isto (*«O mapa não precisa: criar um pedido não põe nada lá»*), e o
   * `RetirarDoMapa` já tem o seu `recarregar` por outra razão — a de ter libertado dias.
   * 📌 Meter o `recarregar` aqui dentro mudava o comportamento dos dois sítios que já
   * existiam, e *«uma extracção que altera o que extrai não é uma extracção»*.
   */
  const mostrarPedidoNaLista = (id: string): void => {
    setPedidoCriado(id);
    setVezDoPedido((n) => n + 1);
    setPedidosAbertos(true);
  };
  /**
   * ---- K · O «POR PLANEAR» VÊ-SE NO MAPA, NO TEMPO — a fila, buscada aqui -------------
   *
   * ⚠️⚠️ **O `Mapa` faz a SUA PRÓPRIA leitura de `/api/pedidos`, independente da janela
   * `<Pedidos>` (que só existe montada quando `pedidosAbertos` é `true`).** A faixa tem de
   * se ver no mapa MESMO com a janela fechada — é o ponto todo (ver o cabeçalho do
   * ficheiro). É a mesma rota, sem parâmetros — «fila + reservas do horizonte», a mesma
   * que `Pedidos.tsx` pede por omissão — e a MESMA definição de «por planear»
   * (`filaPorPlanearNoMapa` chama `separarListas`, regra 8: uma só formulação).
   *
   * ⚠️ Sem sessão ou com a rede em baixo, fica **vazia em silêncio** — a mesma disciplina
   * da configuração, logo acima: a faixa é dispensável, o mapa não deixa de funcionar
   * sem ela.
   */
  const [pedidosFila, setPedidosFila] = useState<readonly Pedido[]>([]);
  useEffect(() => {
    const abortar = new AbortController();
    fetch('/api/pedidos', { signal: abortar.signal })
      .then(async (r) => {
        if (!r.ok) return;
        const c = (await r.json()) as { pedidos?: readonly Pedido[] };
        setPedidosFila(c.pedidos ?? []);
      })
      .catch(() => {
        // Silêncio deliberado — ver o comentário acima.
      });
    return () => abortar.abort();
    // ⚠️ `pedidoCriado` entra porque criar/duplicar um pedido não passa por
    //    `recarregar` (não escreve em `alocacao` — ver o comentário de
    //    `mostrarPedidoNaLista`, acima) e é exactamente esse pedido novo que a fila
    //    tem de mostrar. `dados` entra porque reservar UM torna-o «não é mais por
    //    planear», e só um mapa recarregado de novo prova isso.
  }, [dados, pedidoCriado]);
  const filaPorPlanear = useMemo(
    () => filaPorPlanearNoMapa(pedidosFila, dados.nDias),
    [pedidosFila, dados.nDias],
  );
  /**
   * ---- 🔴 2026-10-08 · O PERCURSO de cada pedido da fila — as cidades de cada dia ----------
   *
   * 📗 *«tem de aparecer para além da linha simples as cidades também»*. A lista de pedidos só
   * traz a recolha e a largada; as pernoitas do meio vêm de `/api/pedidos/percursos`, numa só
   * chamada para a fila inteira (300 pedidos de rede para desenhar uma faixa eram o mapa a
   * arrancar devagar). ⚠️ **Enquanto não chegam**, a banda desenha-se com a recolha e a largada
   * e os dias do meio como INFERIDOS — que é exactamente o que a reserva escreveria sem as
   * pernoitas; e falhando a rede fica assim, em silêncio, pela mesma razão da fila acima.
   */
  const idsDaFila = useMemo(
    () => filaPorPlanear.linhas.flatMap((l) => l.map((it) => it.pedido.id)).join(','),
    [filaPorPlanear],
  );
  const [percursosDaFila, setPercursosDaFila] = useState<
    Readonly<Record<string, readonly PontoDoPedidoNoMapa[]>>
  >({});
  useEffect(() => {
    if (idsDaFila === '') return;
    const abortar = new AbortController();
    fetch(`/api/pedidos/percursos?ids=${encodeURIComponent(idsDaFila)}`, { signal: abortar.signal })
      .then(async (r) => {
        if (!r.ok) return;
        const c = (await r.json()) as { percursos?: Record<string, readonly PontoDoPedidoNoMapa[]> };
        setPercursosDaFila(c.percursos ?? {});
      })
      .catch(() => {
        // Silêncio deliberado — ver o comentário acima.
      });
    return () => abortar.abort();
  }, [idsDaFila]);
  const indicePorSigla = useMemo(
    () => indicePorSiglaDeCliente(dados.clientes),
    [dados.clientes],
  );
  /**
   * O ecrã das regras de negócio e legais.
   *
   * 📗 fonte 5.1: «permitindo à empresa… poder alterar tais valores». ⚠️ É uma **janela** como as
   * outras, e não uma página: mudar um limite legal decide-se a olhar para o plano que ele afecta.
   */
  const [regrasAbertas, setRegrasAbertas] = useState(false);
  const [contasAbertas, setContasAbertas] = useState(false);
  /**
   * 🔴 «P» do §11 — a janela de imprimir e exportar. Abre-se pela paleta (`Ctrl`+`K`).
   *
   * ⚠️ **Fechada por omissão e sem botão na barra**, e as duas coisas são a mesma decisão: a
   * barra está no tecto de 150 px, e a folha só se monta quando alguém a pede.
   */
  const [imprimirAberto, setImprimirAberto] = useState(false);
  /**
   * ⚠️ `v` NEGATIVO e um subcontratado (`-1 - indice`) — a mesma convencao do realce.
   *
   * 📌 Um campo `sub?: number` a mais dava dois estados para a mesma pergunta, e o dia em que
   * alguem preenchesse os dois seria um dia de depuracao. A convencao ja existe e ja tem
   * testes; reutiliza-la e mais barato do que inventar a segunda.
   */
  const [aCancelar, setACancelar] = useState<{ v: number; d: number } | null>(null);
  /**
   * ⚠️⚠️ **RETIRAR NÃO É CANCELAR, e por isso tem estado próprio.**
   *
   * 📗 Cliente, 2026-08-30: *«permitir retirar e voltar a planear pedidos já marcados»*.
   * 📌 Cancelar diz «o cliente já não quer a viagem»; retirar diz «a viagem faz-se, mas
   * não assim». Partilhar o estado obrigava a um parâmetro `modo` e a uma janela com dois
   * comportamentos — e a que se enganasse cancelava um pedido que ninguém quis cancelar.
   */
  const [aRetirar, setARetirar] = useState<{ v: number; d: number } | null>(null);
  /**
   * ⚠️⚠️ **«COMPARAR FACILMENTE» · A LINHA FIXADA NO TOPO (B32).**
   *
   * 📗 Cliente, 2026-08-30: *«mover facilmente, retirar facilmente, partir facilmente,
   * comparar facilmente»*.
   *
   * ⚠️⚠️ **MEDIDO ANTES DE CONSTRUIR, e o número justifica com folga.** Quando a MESMA
   * viagem está em duas viaturas — o caso que se compara por definição, **625** viagens no
   * `dev` — a distância entre as duas linhas do mapa tem **mediana 82**, p90 **181**, máximo
   * **246** em 250 viaturas. 📌 **552 de 625 (88,3 %) estão fora da janela visível** de ~17
   * linhas, e só **8 (1,3 %)** são vizinhas. Em nove de cada dez casos as duas metades da
   * mesma viagem **não cabem no ecrã ao mesmo tempo**.
   *
   * ⚠️ **UMA SÓ, e não um conjunto.** Duas linhas fixas comem um terço da janela visível e
   * o gesto de as gerir passa a valer mais do que a comparação. 📌 Comparar é sempre **esta
   * contra aquela**; fixar a segunda substitui a primeira, que é o que se quer.
   *
   * ⚠️ **O CUSTO EM NÓS ESTÁ DECLARADO:** uma linha a mais é ~5,4 nós por dia visível, ou
   * seja **~6 %** do orçamento do R1 (ADR-001). 📌 E só quando está fixada — `null` custa zero.
   */
  const [linhaFixada, setLinhaFixada] = useState<number | null>(null);
  /** B30 · o bloco que está a ser movido com o rato, e a linha onde foi largado. */
  const [aMoverBloco, setAMoverBloco] = useState<{
    servico: ServicoArrastado;
    destino: number;
  } | null>(null);
  /** 📗 §4.6 «split de itinerários» — partir a viagem a partir deste dia. */
  const [aDividir, setADividir] = useState<{ v: number; d: number } | null>(null);
  /**
   * ⚠️⚠️ **GARANTIA INVERSA de `aDividir`** (ver `OrganizarEmTrocos`, abaixo): organizar em
   * troços de N em N dias **mantendo** a referência — 📗 «viagens longas têm de ser divididas
   * de 7 em 7 dias, só para já no caso de um cliente grego». Nome sem a palavra «partir», de
   * propósito: `aDividir` MUDA a viagem em duas, isto não muda nada operacional.
   */
  const [aOrganizarEmTrocos, setAOrganizarEmTrocos] = useState<{ v: number; d: number } | null>(
    null,
  );
  /**
   * ⚠️⚠️ **O INVERSO de `aOrganizarEmTrocos`** (ver `JuntarOsTrocos`, abaixo): apaga as
   * fronteiras de troço de uma viagem, que volta a ser um troço só. Rota própria
   * (`/api/plano/unir-trocos`) — ver o porquê no cabeçalho de `dividir-em-trocos.ts`.
   */
  const [aJuntarOsTrocos, setAJuntarOsTrocos] = useState<{ v: number; d: number } | null>(null);
  /**
   * §11 · M — o resultado de arrastar a ponta de uma barra: a janela `DatasDoPedido` abre com
   * estas datas já propostas, e é ela (não este ecrã) que valida e escreve.
   */
  const [arrastoDeDatas, setArrastoDeDatas] = useState<{
    pedidoId: string;
    inicio: string;
    fim: string;
    propostoInicio: string;
    propostoFim: string;
  } | null>(null);
  const arrastoDasDatas = useJanelaAoCentro(440);
  /** §11 · M — a mensagem de recusa quando a ponta agarrada é ambígua (dia de rotação). */
  const [avisoDaPegaDaBarra, setAvisoDaPegaDaBarra] = useState<string | null>(null);
  const arrastoDaPega = useJanelaAoCentro(440);
  /**
   * 🔴 **Peça F §6.3 — a viagem sai do autocarro e fica à espera de outro.** Guarda a célula; o
   * intervalo da viagem calcula-se no gesto (`extentoDaCorrida`), como o `travar` faz.
   */
  const [aPorEmEspera, setAPorEmEspera] = useState<{ v: number; d: number } | null>(null);
  /**
   * 🔴 **Peça F §6.5 — a mesma viagem passa a correr TAMBÉM num segundo autocarro.**
   *
   * ⚠️ Guarda o **intervalo e a referência já resolvidos**, e não `{v, d}` como o irmão da
   * espera: as duas rotas exigem a `referencia` (400 `alvo_incompleto` sem ela), e resolvê-la
   * outra vez na altura de desenhar a janela era uma segunda leitura do mesmo facto.
   */
  const [aCopiarPeriodo, setACopiarPeriodo] = useState<{
    v: number;
    dia0: number;
    dia1: number;
    ref: string;
  } | null>(null);
  /**
   * 🔴 **O INVERSO** — o autocarro seleccionado sai da viagem, que continua nos outros. Guarda
   * o mesmo que o irmão de cima: aqui o `v` é o autocarro **que sai**, e quem fica sai de
   * `outrosAutocarrosDaViagem`.
   */
  const [aRetirarCopia, setARetirarCopia] = useState<{
    v: number;
    dia0: number;
    dia1: number;
    ref: string;
  } | null>(null);
  /**
   * 🔴 **O INVERSO**, e guarda coisas diferentes do irmão de cima: uma viagem em espera **não
   * tem viatura**, logo não há `{v, d}` que a nomeie. O que a identifica é a referência mais o
   * intervalo — que é exactamente o que `/api/plano/trazer-de-espera` pede.
   */
  const [aTrazerDeEspera, setATrazerDeEspera] = useState<{
    referencia: string;
    dia0: number;
    dia1: number;
    /**
     * 🔴 2026-10-08 · o ÍNDICE da viatura onde a barra foi largada — ausente quando veio de um
     * clique. ⚠️ Índice e não id: o `aoLargar` é um `useCallback` sem dependências, e um `dados`
     * lido lá dentro seria o do primeiro desenho; o id resolve-se ao desenhar a janela.
     */
    v?: number;
  } | null>(null);
  /**
   * 🔴 2026-10-08 · há um SERVIÇO a ser arrastado — é o que faz a secção «Em espera» aparecer
   * como sítio onde largar mesmo quando está vazia (📗 *«tem de dar para arrastando mover algo
   * também nestas secções»*). Sem isto, pôr a PRIMEIRA viagem em espera não tinha onde cair.
   */
  const [servicoEmArrasto, setServicoEmArrasto] = useState(false);
  // ⚠️⚠️ **O ALFINETE (J-0).** Guarda o INTERVALO e não o dia: 📗 «podem marcar dias ou
  //    PERÍODOS que não queiram mexer», e o gesto natural é travar a viagem inteira.
  const [aFixar, setAFixar] = useState<{ v: number; d0: number; d1: number } | null>(null);
  /** O menu de contexto da grelha (📗 §4.8), com o ponto onde abriu. */
  const [menuCtx, setMenuCtx] = useState<{ v: number; d: number; x: number; y: number } | null>(
    null,
  );
  // 📗 2026-11-03 · `Esc` fecha o menu do botão direito (o guia da sessão 1 diz «Esc ou clique fora»;
  //    o clique fora já fechava, pelo véu). Mesma pilha das janelas, mesma prova da barra.
  useEscape(() => setMenuCtx(null), menuCtx !== null);
  /**
   * A viatura e a janela livre que o «O que encaixa aqui?» vai perguntar.
   *
   * ⚠️ Calculado a partir do dia vazio em que se carregou — a janela é a **corrida de dias
   * livres** à volta dele, e não o dia só. Um encaixe de um dia é quase sempre nada.
   */
  const [encaixePara, setEncaixePara] = useState<{
    id: string;
    numero: string;
    d0: number;
    d1: number;
  } | null>(null);
  /** 📗 D-091 §2-bis — as perguntas que não tinham ecrã. */
  const [consultasAbertas, setConsultasAbertas] = useState(false);
  const [gestaoAberta, setGestaoAberta] = useState(false);
  /**
   * O separador em que a Gestão abre.
   *
   * ⚠️ Só a fila «Precisa de mim» o mexe (D-097, as localidades sem aeroporto determinado); o
   * botão da barra continua a abrir onde sempre abriu, e por isso volta a `undefined` ao fechar
   * — senão a abertura seguinte herdava o separador de uma linha da fila que ninguém carregou.
   */
  const [abaDaGestao, setAbaDaGestao] = useState<string | undefined>(undefined);
  /** Onde a viatura está parada — 📗 pedido do cliente, 2026-08-10. */
  const [aParar, setAParar] = useState<{ v: number; d: number } | null>(null);
  /**
   * O índice de hoje no ano do mapa, ou `-1`.
   *
   * ⚠️ Calculado UMA vez por ano de dados: `new Date()` a cada render dava um valor novo em
   * cada quadro e punha o `useMemo` da geometria a recalcular sem razão.
   */
  const hoje = useMemo(() => diaDeHoje(janelaDe(dados)), [dados.inicio, dados.nDias]);
  /**
   * ⚠️⚠️ **A LISTA DO QUE ESTÁ POR FECHAR** — 📗 item 1 da proposta, aprovado a 2026-08-10.
   *
   * 📌 É a janela que **cria o histórico** que falta: medido, **6** serviços fechados em 121 456
   * dias, e foram esses 6 que deram `custoPorKmMedido: 0,44` contra o `placeholder` de **0,85**.
   */
  const [porFecharAberto, setPorFecharAberto] = useState(false);
  /**
   * ⚠️⚠️ **A lista das reservas por confirmar (2026-08-30).**
   *
   * 📗 Cliente, sobre a textura no mapa do staging. 📌 Medido: **100 %** das corridas do
   * staging estao em reserva e **ZERO** foram alguma vez confirmadas. A textura dizia a
   * verdade; faltava **onde despachar** — era servico a servico pelo painel, 275 cliques.
   */
  const [porConfirmarAberto, setPorConfirmarAberto] = useState(false);
  /**
   * Em que separador abrir as Consultas quando se chega la pela fila.
   *
   * ⚠️⚠️ Sem isto, «viaturas paradas fora» abria a janela e deixava a pessoa a procurar o
   * separador — e o defeito que deu origem a este cuidado foi precisamente um separador a
   * aparecer no sitio errado.
   */
  const [abaDasConsultas, setAbaDasConsultas] = useState<'paradas' | 'regresso' | null>(null);
  /**
   * ---- ⚠️⚠️ O FILTRO com que as Consultas abrem (2026-08-31) ----------------------------
   *
   * 📌 A família do trânsito na fila apontava para o separador «Dias de estrada», que saiu — e o
   * comentário desse destino dizia *«B15: a fila do trânsito deixou de ser um beco»*. A família
   * **fica** e passa a abrir as paradas **filtradas**, porque nesta casa *«famílias sem ecrã não
   * levam botão»*.
   */
  const [filtroDasConsultas, setFiltroDasConsultas] = useState<'precisam-transito' | null>(null);
  // ⚠️⚠️ **A contagem dos serviços por fechar SAIU daqui** com o botão que a mostrava. 📌 Agora o
  //    número vive na fila «Precisa de mim», que é onde a família existe — e assim há **um** pedido
  //    ao servidor em vez de dois a perguntar coisas sobreponíveis. Remover é a parte que se
  //    esquece: um `fetch` cujo resultado ninguém lê é trabalho que o navegador faz por nada, e
  //    não há nada no ecrã que o denuncie.
  /**
   * A fila «o que precisa de mim» — ponto 4 da proposta.
   *
   * 📌 **Nao mede nada de novo:** agrega seis ecras e ordena-os por consequencia. O valor esta
   * em haver **um sitio por onde comecar o dia** em vez de seis por onde procurar.
   */
  const [atencaoAberta, setAtencaoAberta] = useState(false);
  /** A caixa de entrada — os emails que chegaram, por transformar em pedidos. */
  const [caixaAberta, definirCaixaAberta] = useState(false);
  /**
   * A ENTREGA cuja proposta se está a validar (§10.2), ou `null`.
   *
   * ⚠️ **A janela vive na raiz e não dentro da caixa de entrada**, como todas as outras: lá
   * dentro ficava presa ao empilhamento da janela que a abriu — é a lição que este ficheiro já
   * traz escrita sobre as janelas que nasceram dentro do `.topo`.
   *
   * ⚠️ **E a caixa NÃO se fecha ao abri-la.** As duas ficam ao lado: quem valida uma proposta
   * volta à lista a seguir, e fechar a caixa obrigava a reabri-la e a reencontrar o email.
   */
  /**
   * 🔴🔴 **O QUE ESTÁ ABERTO É UMA ENTREGA **E** UM PEDIDO** — 2026-09-15. 📗 Cliente: *«na
   * caixa de entrada quando eu clico em cima de um dado pedido só quero vê-lo a ele, não a
   * todos!! isto é gravíssimo»*.
   *
   * ⚠️ Era um `number | null` — só a entrega —, e a linha da caixa **sabia** que pedido era.
   * O número morria aqui, e o ecrã abria sempre no primeiro por decidir. `bloco: null` é o
   * caso legítimo de quem abre o email inteiro pelo painel da entrega.
   */
  const [propostaAberta, definirPropostaAberta] = useState<{
    readonly entregaId: number;
    readonly bloco: number | null;
  } | null>(null);
  /**
   * Os itens IMINENTES, para a pastilha.
   *
   * ⚠️⚠️ **A pastilha conta FAMILIAS com trabalho iminente — nem itens, nem o total.** Medido: o
   * total iminente e 3 631 e o total absoluto ronda os 10 000. «5» quer dizer «cinco coisas
   * diferentes precisam de mim» e e accionavel; os numeros de cada uma estao dentro da janela.
   * ⚠️ Em silencio se falhar, como a do «Por fechar»: a contagem e informacao e nao pode tapar
   *    o mapa.
   */
  const [atencaoFamilias, setAtencaoFamilias] = useState(0);
  /**
   * 🔴 2026-11-03 · os emails POR TRATAR, para a pastilha da «Caixa de entrada». Vêm na MESMA
   * resposta (nenhuma chamada nova). ⚠️ `null` = não há número (a leitura falhou, ou não pode
   * ler a caixa) — e então não há pastilha nem erro no ecrã.
   */
  const [emailsPorTratar, setEmailsPorTratar] = useState<number | null>(null);
  useEffect(() => {
    const abortar = new AbortController();
    fetch('/api/plano/atencao', { signal: abortar.signal })
      .then(async (r) => {
        // ⚠️⚠️ **FAMILIAS e nao itens**, e o numero vem calculado do servidor: com a conta feita
        //    aqui haveria dois leitores a chamar «a pastilha» a coisas diferentes.
        const c = (await r.json()) as { familiasComTrabalho?: number; emailsPorTratar?: number | null };
        setAtencaoFamilias(Number(c.familiasComTrabalho ?? 0));
        setEmailsPorTratar(typeof c.emailsPorTratar === 'number' ? c.emailsPorTratar : null);
      })
      .catch(() => setEmailsPorTratar(null));
    return () => abortar.abort();
    // ⚠️ Recontar quando a janela FECHA — como no «Por fechar», e pela mesma razao: e o momento
    //    em que alguem acabou de tratar de alguma coisa.
  }, [atencaoAberta, caixaAberta]);
  /**
   * A paleta de comandos — ponto 3 da proposta.
   *
   * ⚠️⚠️ **`Ctrl+K` e nao uma tecla solta.** 📌 Uma tecla sem modificador (`/`, `k`) roubava a
   * escrita a todos os campos de texto do ecra — as notas da celula, o motivo da divergencia, os
   * km reais. `Ctrl+K` e a convencao, e nao colide com nada que se escreva.
   * ⚠️⚠️ **E o ouvinte esta no `window`**, nao numa `div`: o foco pode estar em qualquer sitio, e um
   * atalho que so funciona quando o mapa tem foco e um atalho que ninguem encontra.
   */
  const [paletaAberta, setPaletaAberta] = useState(false);
  useEffect(() => {
    const ouvir = (e: KeyboardEvent): void => {
      // ⚠️ `metaKey` para o Mac, `ctrlKey` para o resto. E `key.toLowerCase()`, senao com
      //    `Shift` premido a tecla e `K` e o atalho deixava de responder.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletaAberta((a) => !a);
      }
    };
    window.addEventListener('keydown', ouvir);
    return () => window.removeEventListener('keydown', ouvir);
  }, []);

  /**
   * Liga **so** este cliente/motorista/itinerario no filtro respectivo.
   *
   * ⚠️⚠️ **Isola em vez de alternar, e a razao vem do que o filtro significa.** 📌 `null` quer dizer
   * «todos ligados», que e o estado inicial. Se `IGT` na paleta apenas DESLIGASSE o IGT, o efeito
   * era o oposto do que quem escreveu «IGT» quer: ficava com 39 clientes a vista e o dele
   * esbatido. Escrever uma sigla e dizer «quero ver este».
   * ⚠️ E o que se desliga fica **esbatido, nunca escondido** (P-30) — e por isso isto e seguro:
   * nao esconde viaturas, so tira enfase ao resto.
   */
  /**
   * Os comandos que a paleta oferece com o campo vazio.
   *
   * ⚠️⚠️ **A lista vive AQUI e nao dentro da paleta.** 📌 A paleta nao sabe que janelas existem —
   * com a lista dentro dela, acrescentar uma janela obrigava a lembrar-se de dois sitios, e o que
   * fica esquecido e sempre o segundo.
   * ⚠️ E sao **so** aberturas de janela e navegacao: nenhum comando escreve. Ver a nota do topo de
   * `procura.ts` — e a decisao que torna esta vista de risco nulo.
   */
  /**
   * 🧹 REMOVÍVEL · formação — o servidor diz se este utilizador vê a entrada na formação
   * (`entradaNaFormacao` de `/api/auth/sessao`, lido abaixo, no pedido que já se fazia).
   * `false` até se saber, e se a leitura falhar. Ver `entrada-na-formacao.tsx`.
   */
  const [formacaoNoMapa, setFormacaoNoMapa] = useState(false);
  const comandosDaPaleta = useMemo<readonly ComandoDaPaleta[]>(
    () => [
      { chave: 'pedidos', rotulo: 'Pedidos', detalhe: 'a carteira: o que há para planear', fazer: () => setPedidosAbertos(true) },
      { chave: 'consultas', rotulo: 'Consultas', detalhe: 'paradas fora, sem regressar, rearranjo, encaixe', fazer: () => setConsultasAbertas(true) },
      { chave: 'entrada', rotulo: 'Caixa de entrada',
        detalhe: 'os emails que chegaram, por transformar em pedidos',
        fazer: () => definirCaixaAberta(true) },
      { chave: 'atencao', rotulo: 'Precisa de mim', detalhe: 'a fila por consequência', fazer: () => setAtencaoAberta(true) },
      { chave: 'porfechar', rotulo: 'Serviços por fechar', detalhe: 'registar o realizado ao lado do estimado', fazer: () => setPorFecharAberto(true) },
      { chave: 'porconfirmar', rotulo: 'Reservas por confirmar', detalhe: 'passar a definitivo e avisar os intervenientes', fazer: () => setPorConfirmarAberto(true) },
      { chave: 'gestao', rotulo: 'Gestão', detalhe: 'viaturas, motoristas, clientes, itinerários, oficinas', fazer: () => setGestaoAberta(true) },
      { chave: 'regras', rotulo: 'Configuração', detalhe: 'custos, pesos e limites legais', fazer: () => setRegrasAbertas(true) },
      { chave: 'sessao', rotulo: 'A minha sessão', detalhe: 'perfil, permissões, sair', fazer: () => setSessaoAberta(true) },
      // 🔴 «P» do §11 — e é AQUI e não na barra: ela está no tecto de 150 px
      //    (`nada-e-cortado-pela-janela.test.tsx`), e a paleta é onde um verbo cabe sem custar
      //    barra. ⚠️ Escreve-se «imprimir» **ou** «exportar»: são os dois nomes que as pessoas
      //    usam para o mesmo gesto, e uma paleta que só conhece um é uma paleta que não
      //    responde a metade de quem a procura.
      { chave: 'imprimir exportar papel excel csv',
        rotulo: 'Imprimir e exportar',
        detalhe: 'pôr um período em papel, ou levá-lo para o Excel',
        fazer: () => setImprimirAberto(true) },
      // 🧹 REMOVÍVEL · formação — só quando o servidor disse que sim.
      ...(formacaoNoMapa ? [COMANDO_DA_FORMACAO] : []),
    ],
    [formacaoNoMapa],
  );

  /**
   * O que esta filtrado, em palavras — para a barra o dizer e o poder limpar.
   *
   * ⚠️⚠️ **A pastilha «1» ao lado de «Filtros» nao diz QUAL.** 📌 📗 Cliente, 2026-08-11:
   * *«selecionei um cliente e nao fez nada»*. O filtro **estava a funcionar** (verificado por
   * teste), mas o efeito e por **esbatimento** — P-30, nunca esconder — e sem legenda um
   * esbatimento le-se como «nada aconteceu». Dizer «Cliente: CAT» transforma o mesmo pixel na
   * confirmacao de que o gesto pegou.
   * ⚠️ E so aparece quando ha um filtro **estreito**: um conjunto com todos os clientes nao e um
   * filtro, e uma etiqueta a dizer «Cliente: todos» era ruido permanente.
   */
  const resumoDoFiltro = useMemo(() => {
    const nomes = (
      eixo: Eixo,
      rotulo: string,
      lista: readonly unknown[],
      nome: (i: number) => string,
    ) => {
      const c = filtro[eixo];
      if (c === null || c.size === lista.length) return null;
      const ids = [...c];
      return {
        eixo,
        rotulo,
        // Um nome quando e um; a contagem quando sao varios — 12 siglas nao cabem numa etiqueta.
        valor: ids.length === 1 ? nome(ids[0] ?? -1) : `${ids.length} escolhidos`,
      };
    };
    return [
      nomes('clientes', 'Cliente', dados.clientes, (i) => dados.clientes[i]?.[0] ?? '?'),
      // ⚠️ B1 · o filtro dos condutores também leva a marca: é a lista onde se **procura** uma
      //    pessoa, e é lá que saber que ela saiu poupa a procura.
      nomes('motoristas', 'Motorista', dados.motoristas, (i) => nomeDoMotorista(dados, i) || '?'),
      nomes('rotas', 'Itinerário', dados.itinerarios, (i) =>
        nomeOuCodigoDoItinerario(dados, i) === '' ? '?' : nomeOuCodigoDoItinerario(dados, i),
      ),
      // ⚠️ V05 · a cidade. O CÓDIGO e não o nome: é o que o planeador escreve e o que a
      //    grelha mostra — «MAD» e não «Madrid».
      nomes('localidades', 'Cidade', dados.localidades, (i) => dados.localidades[i]?.[0] ?? '?'),
    ].filter((x) => x !== null);
  }, [filtro, dados.clientes, dados.motoristas, dados.itinerarios, dados.localidades]);

  const alternarNoFiltro = useCallback(
    (eixo: Eixo, indice: number, _total: number) => {
      if (indice < 0) return;
      setFiltro((f) => ({ ...f, [eixo]: new Set([indice]) }));
    },
    [],
  );

  const [sessaoAberta, setSessaoAberta] = useState(false);
  /**
   * ⚠️ Pedido **uma vez** e guardado: é o nome no botão, e um pedido por render seria uma ida
   * ao servidor por cada movimento do rato. A janela pede o perfil completo quando abre.
   */
  const [nomeCurtoDaSessao, setNomeCurtoDaSessao] = useState<string | null>(null);
  /**
   * ⚠️⚠️ **O PLANO ACTIVO, PARA O CABEÇALHO O DIZER** — 📗 cliente, 2026-09-10: *«um título no
   * cabeçalho a dizer o plano activo»*.
   *
   * 📌 **Sai do pedido que já se fazia** (`/api/auth/sessao`, uma vez): o perfil já traz
   * `utilizador.planoActivo` e a lista `planos` com a descrição de cada um. Um segundo pedido
   * para isto seria uma ida ao servidor por uma cadeia de texto que já veio.
   *
   * ⚠️ `null` é «ainda não escolheu», e o cabeçalho **di-lo** em vez de ficar em branco: o
   * plano decide o que a pessoa vê e pode escrever (ADR-002), logo não saber em que plano se
   * está é a informação mais valiosa do ecrã naquele momento.
   */
  /** O `id` de quem está na sessão — ver `Contas.euId`. */
  const [idDaSessao, setIdDaSessao] = useState<string | null>(null);
  const [planoNoTopo, setPlanoNoTopo] = useState<string | null>(null);
  /**
   * 🔴 O **nome** do tom do plano activo (080) — `'azul'`, nunca um `#rrggbb`.
   *
   * ⚠️ `null` = ninguém escolheu, e o cabeçalho mostra o nome **sem cor**. 📌 A cor é
   * **reforço** e nunca o único sinal (P-30/P-32): a identidade é o nome, que está ao lado.
   */
  const [tomDoPlano, setTomDoPlano] = useState<string | null>(null);
  useEffect(() => {
    const abortar = new AbortController();
    fetch('/api/auth/sessao', { signal: abortar.signal })
      .then(async (r) => {
        const c = (await r.json()) as {
          utilizador?: { id?: string; nome?: string; planoActivo?: string | null } | null;
          planos?: readonly { plano: string; descricao: string; cor?: string | null }[];
          /** 🧹 REMOVÍVEL · formação — ver `entrada-na-formacao.tsx`. */
          entradaNaFormacao?: boolean;
        };
        // 🧹 REMOVÍVEL · formação. ⚠️ Só o `true` liga: um corpo de erro (401) não traz o campo.
        setFormacaoNoMapa(c.entradaNaFormacao === true);
        // 📗 2026-10-22 · o `id`, para as Contas não oferecerem gestos sobre a própria conta.
        setIdDaSessao(c.utilizador?.id ?? null);
        const nome = c.utilizador?.nome ?? '';
        // O primeiro nome: o botão tem largura para um, não para «Leonel Pinheiro».
        if (nome !== '') setNomeCurtoDaSessao(nome.split(' ')[0] ?? nome);
        // ⚠️⚠️ **`utilizador.planoActivo` e não `perfil.planoActivo`** — é o defeito D-2, que
        //    custou um selector a abrir vazio com um plano escolhido: o servidor põe-no
        //    **dentro** de `utilizador`, e lido do topo era sempre `undefined`.
        const activo = c.utilizador?.planoActivo ?? null;
        if (activo !== null && activo !== '') {
          // ⚠️ A DESCRIÇÃO e não o `id`: «Continente» é o que as pessoas dizem, `P-CONT` é um
          //    código do sistema. 📌 Cai no `id` quando a descrição não vem — um cabeçalho em
          //    branco sobre um plano escolhido era pior do que um código.
          const linha = c.planos?.find((p) => p.plano === activo);
          setPlanoNoTopo(linha?.descricao ?? activo);
          // ⚠️ **Do MESMO objecto que a descrição**, e não de uma segunda leitura: duas
          //    chamadas podiam divergir e pintar o tom de um plano por cima do nome de outro —
          //    que é a família «um valor plausível no lugar do certo».
          setTomDoPlano(linha?.cor ?? null);
        }
      })
      .catch(() => {
        // ⚠️ Em silêncio, e é deliberado: o nome no botão é conforto, e um erro aqui não pode
        //    tapar o mapa. Quem não tem sessão nunca chega a este componente — a porta é o
        //    `addHook` do servidor, e não este `fetch`.
      });
    return () => abortar.abort();
  }, []);

  // ---- ARRASTAR UM PEDIDO PARA UMA VIATURA (RF-7, operação manual) --------
  //
  // 📗 Pedido do cliente: «permitir arrastar os pedidos de agendamento manualmente para
  // onde quiser». `alvoArrasto` é a linha realçada durante o arrasto; `largado` é o que
  // abre a janela de confirmação.
  //
  // ⚠️ NÃO reserva ao largar. Ver `LargarPedido.tsx`: a operação manual contorna o
  // motor, logo é aqui que os limites legais têm de ser mostrados antes de escrever.
  /**
   * O serviço que está a ser editado — viatura e período.
   *
   * 📗 «mudar condutor e trocar viatura, tudo editável… dado que este será sempre um processo com
   * base manual». ⚠️ E o período é a **CORRIDA inteira** e não o dia clicado: um serviço de 6 dias
   * muda de viatura por inteiro. Mover 3 dos 6 parte a corrida em duas viaturas — o que **acontece
   * de facto** (F-22: 43 % das referências reais), mas tem de ser uma decisão e não a omissão.
   */
  const [aEditar, setAEditar] = useState<{
    readonly viatura: number;
    readonly dia0: number;
    readonly dia1: number;
    /**
     * ⚠️ A viagem que PARTE de um dia de rotação, quando o painel foi aberto para ela.
     *
     * 📌 Sem a referência explícita o servidor escolheria a `principal` — e o painel mexia na
     * outra viagem em silêncio. Ver `referenciaAlvo` em `EditarAlocacao`.
     */
    readonly referenciaAlvo?: string;
    /**
     * ⚠️⚠️ O dia de rotação em que a viagem PARTE, quando o painel foi aberto num dia seguinte
     * dela (2026-09-29). Ver `diaDePartida` em `EditarAlocacao`.
     */
    readonly diaDePartida?: number;
    /**
     * ⚠️⚠️ **Até onde o CONDUTOR vai por omissão** — inclui os dias de trânsito a seguir à
     * viagem. Ver `ateAoFimDoTransito`. Ausente = igual a `dia1` (o comportamento de sempre).
     */
    readonly condAte?: number;
  } | null>(null);

  /**
   * O dia cuja nota está aberta (📗 6.8).
   *
   * ⚠️ **UM DIA**, ao contrário de `aEditar`, que é a corrida inteira. A fonte diz «na
   * célula», e é o que se faz no Excel: «saida htl 07:50» é daquele dia.
   */
  const [aNotar, setANotar] = useState<{ readonly viatura: number; readonly dia: number } | null>(
    null,
  );
  /**
   * A viagem cuja cor está a ser escolhida (migração 055).
   *
   * ⚠️ **A REFERÊNCIA, não a corrida inteira nem a célula** — a chave é a viagem
   * concreta, e o override aplica-se a todas as células dela.
   */
  const [aCorDaViagem, setACorDaViagem] = useState<{ readonly referencia: string } | null>(null);
  /**
   * ⚠️⚠️ **A VIAGEM CUJO DIÁRIO ESTÁ ABERTO — §5.6 exigência 3.**
   *
   * 📌 **Em ESTADO e não construído no render**, e isso não é arrumação: o `Diario` tem o alvo
   * na lista de dependências do efeito que carrega, logo um objecto novo a cada render pedia ao
   * servidor em ciclo. No navegador isso não se lê como defeito — lê-se como «o ecrã está
   * lento», que foi exactamente o que aconteceu no `Substituir` (2026-08-07).
   */
  const [aVerDiario, setAVerDiario] = useState<AlvoDoDiarioNoEcra | null>(null);
  /**
   * O dia que está a ser editado — 📗 «tudo tem de poder ser editado».
   *
   * ⚠️ **UM DIA**, como a nota e ao contrário de `aEditar`: trocar a viatura é da corrida
   * inteira, corrigir onde se dorme na quarta-feira é daquele dia.
   */
  const [aCorrigir, setACorrigir] = useState<{
    readonly viatura: number;
    readonly dia: number;
  } | null>(null);

  /**
   * O serviço que está a ser fechado — o realizado ao lado do estimado (D-091 passo 1).
   *
   * ⚠️ É a CORRIDA inteira, como `aEditar` e ao contrário da nota: quem fecha lê um
   * conta-quilómetros ou uma factura, e nenhum dos dois vem repartido por dia.
   */
  const [aFechar, setAFechar] = useState<{
    readonly viatura: number;
    readonly dia0: number;
    readonly dia1: number;
  } | null>(null);

  const [alvoArrasto, setAlvoArrasto] = useState<number>(-1);
  const [largado, setLargado] = useState<{
    readonly pedido: PedidoArrastado;
    readonly viatura: number;
    readonly dia: number;
  } | null>(null);
  const [larguraViatura, setLarguraViatura] = useState(LARGURA_VIATURA);
  const [alturaPainel, setAlturaPainel] = useState(ALTURA_PAINEL);
  // ---- medidas da grelha, uma a uma, como no Excel ------------------------
  //
  // ⚠️ A primeira versão redimensionava TODAS as colunas ao mesmo tempo. O cliente
  // experimentou e recusou: «não gostei da forma como se arrastam… tem de ser a
  // mesma dinâmica do Excel: ao arrastar uma coluna ou linha só interfere no
  // tamanho dessa linha, nunca em todas as outras».
  //
  // Guardam-se só as EXCEPÇÕES — quem foi arrastado. Um mapa vazio é a grelha
  // uniforme de sempre, e é o caso normal: com 365 dias, guardar 365 medidas iguais
  // para poder mudar duas seria desperdício e uma fonte de divergência.
  const [larguraDias, setLarguraDias] = useState<ReadonlyMap<number, number>>(new Map());
  const [alturaLinhas, setAlturaLinhas] = useState<ReadonlyMap<number, number>>(new Map());

  // Posições acumuladas. A identidade destes vectores é o que decide se as linhas
  // voltam a ser desenhadas — por isso `useMemo` com as excepções como dependência,
  // e por isso o arrasto é limitado a um quadro de animação.
  const offsetsDias = useMemo(
    () => acumular(dados.nDias, LARGURA_DIA, larguraDias),
    [dados.nDias, larguraDias],
  );
  /**
   * ⚠️⚠️ **B₂ · UMA LINHA ESCONDIDA TEM ALTURA ZERO — a mesma mecânica de «cada linha à
   * sua medida» (D-085), usada agora para desaparecer sem tirar índices do meio.** Os
   * índices continuam a apontar para a viatura certa (nenhum `.findIndex` no resto do
   * ficheiro se desalinha); só o espaço que ocupam no ecrã muda. 📌 É o que faz o
   * espaçador do topo e o do fim (mais abaixo) ficarem correctos de graça.
   */
  const offsetsLinhas = useMemo(() => {
    if (indicesEscondidos === null || indicesEscondidos.size === 0) {
      return acumular(dados.viaturas.length, ALTURA_LINHA, alturaLinhas);
    }
    const excepcoes = new Map(alturaLinhas);
    for (const v of indicesEscondidos) excepcoes.set(v, 0);
    return acumular(dados.viaturas.length, ALTURA_LINHA, excepcoes);
  }, [dados.viaturas.length, alturaLinhas, indicesEscondidos]);

  /**
   * ⚠️⚠️ **C₂ · AS LINHAS DA SECÇÃO — uma por SERVIÇO, calculadas do plano e sobre o ANO.**
   *
   * 📌 **Sobre o ano e não sobre a janela**, e agora é obrigatório: a linha é o que a selecção,
   * o realce, o painel e os botões apontam, logo um serviço que mudasse de linha ao deslocar o
   * mapa mudava o alvo de uma escrita debaixo do olho de quem a ia fazer.
   *
   * ⚠️ **Custo declarado, medido na folha real:** 15 empresas → **39 linhas**, 637 → **1 170 px**
   * de altura, e **522** nós na pior janela de 40 dias contra os 3 246 que o ADR-001 mediu no
   * mapa inteiro. O tecto de 1 400 células por janela **não é tocado** — ele é do produto
   * `viaturas × dias` da grelha virtualizada, e esta secção não entra nele (ver `recalcular`,
   * que passa a `calcularJanela` apenas `dados.viaturas.length`).
   */
  const linhasSub = useMemo(
    () => linhasDosSubcontratados(dados.grelhaSubcontratados),
    [dados.grelhaSubcontratados],
  );

  /**
   * ⚠️⚠️ **A POSIÇÃO DE UMA LINHA NUNCA É `índice × 30`.**
   *
   * 📌 Três sítios calculavam-na por multiplicação: a **altura total** do contentor, o destino
   * do **«Ver no mapa»** e o **rectângulo de realce**. Hoje todas as linhas medem o mesmo, logo
   * a multiplicação daria o número certo — e é exactamente por isso que ela **não** volta:
   * quando o índice deixou de ser o da empresa e passou a ser o da linha (C₂), a multiplicação
   * continuou a dar um número **plausível** e a apontar para a linha errada. *«Um realce no
   * sítio errado é pior do que nenhum: manda olhar para o lado errado com confiança»* — e o
   * ficheiro já trazia essa frase escrita. Offsets acumulados, como a frota (D-085).
   */
  const formaSub = useMemo(() => formaDaSeccaoSub(linhasSub), [linhasSub]);

  /**
   * 🔴 **As viagens à espera de autocarro** (peça F §6.3) — a mesma repartição por linhas do C₂.
   *
   * ⚠️ **Sobre o ANO e não sobre a janela**, pela mesma razão do C₂: a linha é o que a barra e o
   * gesto apontam, e deslocar o mapa não pode mudar o alvo de uma escrita.
   *
   * ⚠️⚠️ **`formaDaSeccaoSub` partilhada de propósito, apesar do nome.** Ela é
   * `acumular(n, ALTURA_LINHA_SUB)` e mais nada — a máquina dos offsets, não a secção. Escrever
   * uma segunda função idêntica para a espera criava a terceira aritmética paralela que o
   * cabeçalho de `linhas-do-subcontratado.ts` existe para impedir. 🅿️ O nome fica a dever-se —
   * declarado no relatório.
   */
  const linhasEspera = useMemo(() => linhasDaEspera(dados.grelhaEspera), [dados.grelhaEspera]);
  const formaEspera = useMemo(() => formaDaSeccaoSub(linhasEspera), [linhasEspera]);
  // K · `formaDaSeccaoSub` é genérica (só olha para `linhas.length`) — a MESMA função dá
  //    os offsets à secção «por planear», sem reescrever a aritmética das alturas.
  const formaPorPlanear = useMemo(
    () => formaDaSeccaoSub(filaPorPlanear.linhas),
    [filaPorPlanear.linhas],
  );

  /** Verdadeiro enquanto uma medida está a ser arrastada. Ver `aoPassar`. */
  const arrastando = useRef(false);
  const tema = useTema();

  // Nada filtrado por omissão — foi o pedido explícito do cliente: cor em tudo,
  // e o filtro só quando alguém o quiser.
  useEffect(() => {
    setFiltro(SEM_FILTRO);
  }, [dados]);

  const inicioSemana = useMemo(() => iniciosDeSemana(dados.fimDeSemana), [dados.fimDeSemana]);

  /**
   * A chave da corrida seleccionada, no mesmo espaco que `segmentos` usa.
   *
   * Corrida positiva, rota negativa a partir de -2 — ver `segmentos.ts`. Com os
   * indices em cru, a corrida 0 e a rota 0 eram a mesma chave e o realce acendia
   * barras sem relacao.
   */
  const itinerarioForte = useMemo(() => {
    if (seleccao === null) return Number.NaN;
    const c = dados.grelha[seleccao.v]?.[seleccao.d];
    if (c == null || c[7] >= 0) return Number.NaN;
    return c[10] >= 0 ? c[10] : -(c[5] + 2);
  }, [seleccao, dados.grelha]);

  // ---- medição em condições reais (risco R1 do ADR-001) -------------------
  const marca = useRef<number | null>(null);
  const quadros = useRef<number[]>([]);
  const primeiroMedido = useRef(false);
  const [p95, setP95] = useState<number | null>(null);

  /**
   * A janela também num `ref`, e não só no estado.
   *
   * ⚠️ Não é duplicação por descuido. A comparação tem de acontecer **antes** de
   * `setJanela`, para o cronómetro arrancar no momento do evento de scroll. Feita
   * dentro do *updater*, o React só a executa na renderização, e a medição excluía
   * o tempo de agendamento — que é parte do que o utilizador sente. Em
   * `StrictMode` o *updater* corre duas vezes, o que agravava o erro.
   */
  const janelaRef = useRef<Janela>(janela);

  const recalcular = useCallback(() => {
    if (janelaFixa !== undefined) return;
    const el = caixa.current;
    if (el === null) return;
    const nova = calcularJanela(
      { topo: el.scrollTop, esquerda: el.scrollLeft },
      { altura: el.clientHeight, largura: el.clientWidth },
      { viaturas: dados.viaturas.length, dias: dados.nDias },
      { larguraViatura, dias: offsetsDias, linhas: offsetsLinhas },
    );
    if (mesmaJanela(janelaRef.current, nova)) return;
    janelaRef.current = nova;
    marca.current = performance.now();
    setJanela(nova);
  }, [dados.viaturas.length, dados.nDias, janelaFixa, larguraViatura, offsetsDias, offsetsLinhas]);

  /**
   * Leva o mapa até uma viatura e um dia, e realça-os.
   *
   * 📗 Queixa do cliente (2026-08-06): *«eu queria que ao escolher fosse directamente
   * para onde foi seleccionado»*. Reservar sem mostrar onde obrigava o planeador a
   * procurar a sua própria reserva numa grelha de 250 × 365 — 91 250 células.
   *
   * ⚠️ Recebe o **ID** da viatura e não o índice: os índices dependem da ordem das
   * listas, e quem chama isto (a janela das opções, a confirmação do arrasto) só
   * conhece IDs. Resolver o índice aqui é resolvê-lo num sítio só.
   *
   * ⚠️ E **não** desliga a recarga do mapa: as duas coisas acontecem depois de uma
   * escrita, e mexer no deslocamento sem os dados novos levava o olho a uma célula
   * que ainda estava vazia.
   */
  /**
   * O que a célula seleccionada **é** — e é isto que decide os botões do painel.
   *
   * ⚠️ **Três estados e não dois.** Um dia indisponível **tem** serviço por baixo (a avaria
   * não apaga a viagem: é exactamente isso que faz haver alguém por transportar), logo a
   * ordem importa — pergunta-se primeiro pela indisponibilidade.
   *
   * ⚠️ **E uma paragem INFERIDA por nós conta como livre.** Ela não existe na base: oferecer
   * «Itinerário e pernoita…» sobre ela mandava editar uma linha que não há. É a mesma
   * `paragemInferida` que o motor, o encaixe e o rearranjo usam — um predicado só, porque
   * uma dedução lida de seis maneiras diverge nas seis.
   */
  const natureza: 'servico' | 'livre' | 'indisponivel' | undefined = useMemo(() => {
    if (seleccao === null) return undefined;
    const c = dados.grelha[seleccao.v]?.[seleccao.d] ?? null;
    if (c === null) return 'livre';
    if ((c[7] ?? -1) >= 0) return 'indisponivel';
    if (paragemInferida(c)) return 'livre';
    return c[0] >= 0 || c[5] >= 0 || c[10] >= 0 ? 'servico' : 'livre';
  }, [dados.grelha, seleccao]);

  /**
   * ⚠️⚠️ **Esta viagem já tem alguma fronteira de troço marcada?** — decide se «Juntar os
   * troços…» se oferece. Calculado uma só vez e partilhado entre o painel e o menu de
   * contexto: os dois abrem sobre a MESMA selecção (o botão direito fixa-a antes de abrir o
   * menu — ver `aoMenuContexto`), e duas formulações da mesma pergunta divergiriam.
   *
   * 📌 Varre a corrida inteira e não só o dia clicado: a fronteira pode estar em qualquer dia
   * do meio da viagem, e «não tem fronteira NESTE dia» não é a pergunta — a pergunta é «esta
   * viagem tem alguma organização em troços para desfazer».
   */
  const temFronteiraDeTroco = useMemo(() => {
    if (seleccao === null) return false;
    const linha = dados.grelha[seleccao.v] ?? [];
    const ext = extentoDaCorrida(linha, seleccao.d);
    for (let dd = ext.dia0; dd <= ext.dia1; dd++) {
      if (((linha[dd]?.[4] ?? 0) & BANDEIRA.INICIO_DE_TROCO) !== 0) return true;
    }
    return false;
  }, [dados.grelha, seleccao]);

  /**
   * ⚠️⚠️ **«TUDO O QUE ACONTECEU A ESTA VIAGEM» — o alvo do diário (§5.6 exigência 3).**
   *
   * 📌 **UMA formulação, partilhada pelo painel de detalhe e pelo menu de contexto** — os dois
   * sítios de onde a pergunta nasce, e os dois abrem sobre a MESMA selecção (o botão direito
   * fixa-a antes de abrir o menu). Duas construções do alvo dariam dois diários diferentes
   * para a mesma célula, e a que divergisse era a que ninguém corre (regra 8).
   *
   * ⚠️ **As TRÊS chaves vão juntas, e não só a viatura.** A auditoria grava umas acções pela
   * viatura e o dia (`mudar_executor`, `fixar`, `dividir_viagem`), outras pelo **pedido**
   * (`reservar`, `efectivar`, `alterar_pedido`, `libertar_dias`) e uma pela **referência**
   * (`alterar_cor_corrida`). Mandar só a viatura calava a reserva que criou a viagem — que é a
   * primeira coisa que alguém vai lá procurar.
   *
   * ⚠️ **O intervalo é o da CORRIDA e não o dia clicado** — como em «Viatura e condutor…» e
   * «Fechar…». Sem ele, o diário de uma viagem de três dias trazia o ano inteiro daquela
   * viatura, e «doze blocos iguais não são uma lista de decisões» (§5.6 exigência 4).
   *
   * ⚠️ `null` fora de um dia de serviço: num dia livre não há viagem sobre a qual perguntar.
   */
  const alvoDoDiario = useMemo((): AlvoDoDiarioNoEcra | null => {
    if (seleccao === null || natureza !== 'servico') return null;
    const viaturaId = dados.viaturas[seleccao.v]?.id ?? '';
    if (viaturaId === '') return null;
    const linha = dados.grelha[seleccao.v] ?? [];
    const c = linha[seleccao.d] ?? null;
    const ext = extentoDaCorrida(linha, seleccao.d);
    // ⚠️ `>= 0` afirmativo: com o campo ausente num *fixture* incompleto, `undefined < 0` é
    //    **falso** e a ausência lia-se como presença — o defeito do campo 13, medido aqui.
    const referencia = c !== null && c[10] >= 0 ? dados.corridas[c[10]] : undefined;
    const pedidoId = c !== null && c[16] >= 0 ? dados.pedidos[c[16]] : undefined;
    const numero = dados.viaturas[seleccao.v]?.numero ?? '';
    const periodo =
      ext.dia0 === ext.dia1
        ? dataCurtaDoDia(dados, ext.dia0)
        : `${dataCurtaDoDia(dados, ext.dia0)}–${dataCurtaDoDia(dados, ext.dia1)}`;
    return {
      viaturaId,
      dia0: ext.dia0,
      dia1: ext.dia1,
      ...(referencia === undefined ? {} : { referencia }),
      ...(pedidoId === undefined ? {} : { pedidoId }),
      rotulo: `${numero} · ${periodo}${referencia === undefined ? '' : ` · ${referencia}`}`,
    };
  }, [dados, natureza, seleccao]);

  /**
   * Os códigos de itinerário, para o campo de edição do pedido os sugerir.
   *
   * ⚠️⚠️ **ISTO ESTAVA AVARIADO E NÃO DAVA ERRO.** `dados.itinerarios` era `string[]`, logo
   * `x[0]` devolvia a **primeira letra** de cada código: a lista de sugestões era
   * `['I','I','I',…]`, uma vez por itinerário. 📌 Nada estourava — `'IT-BCN5PAR'[0]` é uma
   * string válida —, e é a família «valor plausível no lugar do certo».
   *
   * ✅ Com o par `[id, nome]` (2026-08-14), `x[0]` passa a significar o **id** e a linha ficou
   * correcta **por construção**. Fica o comentário, porque a correcção não se vê no diff.
   */
  const itinerariosConhecidos = useMemo(
    () => dados.itinerarios.map((x) => x[0]).filter((x) => x !== ''),
    [dados.itinerarios],
  );

  /**
   * As outras viaturas que levam a MESMA viagem.
   *
   * ✅ 📗 *«valide se isto já existe na escala real; se existir tem de ser implementado
   * aqui»* — **existe**: 41 pares em 36 dias distintos na folha real, a mesma referência em
   * duas viaturas no mesmo dia. E na base do `dev` são **5 721 pares dia×referência**.
   *
   * ⚠️ **Sem isto, o mapa desenha duas barras iguais e nada diz que são a mesma viagem** —
   * quem move uma sem saber da outra parte um grupo ao meio, e a operação só descobre no dia.
   */
  const outrasDaViagem = useMemo(
    () =>
      seleccao === null ? [] : outrasViaturasDaViagem(dados, seleccao.v, seleccao.d),
    [dados, seleccao],
  );

  /**
   * 🔴 **AS MESMAS, com o `id` — peça F §6.5.** É o que o «Tirar este autocarro da viagem…»
   * precisa, e as duas metades vêm daqui:
   *
   * · **se o gesto se oferece** — lista vazia quer dizer que o que se apagaria **não é uma
   *   cópia, é a viagem**, e é o espelho exacto do 409 `unica_viatura_com_a_viagem`;
   * · **o que a janela mostra** — onde é que a viagem FICA. Sem isso, «tirar o autocarro da
   *   viagem» lê-se como apagá-la, e *«uma recusa que não se consegue verificar não se
   *   corrige»* vale igual para uma escrita.
   *
   * ⚠️ **É a MESMA `outrasViaturasDaViagem` que o painel já usa** («a mesma viagem também em
   * 243»), e não uma segunda contagem: duas respostas à pergunta «quem mais leva esta viagem?»
   * divergiriam, e a que divergisse decidia se o botão aparece.
   *
   * 🅿️ **Declarado:** o servidor calcula o seu próprio `continuaEm` — mas ele vive no `depois`
   * da **auditoria** e não no corpo da resposta, e no 409 seria vazio por construção. Logo esta
   * lista é a do MAPA, e a janela di-lo com essas palavras.
   */
  const outrosAutocarrosDaViagem = useMemo<readonly OutroAutocarroDaViagem[]>(
    () =>
      outrasDaViagem.flatMap((o) => {
        const v = dados.viaturas[o.viatura];
        return v === undefined
          ? []
          : [
              {
                id: v.id,
                numero: o.numero,
                dia0: o.dia0,
                dia1: o.dia1,
                simultanea: o.simultanea,
              },
            ];
      }),
    [dados.viaturas, outrasDaViagem],
  );

  /**
   * ⚠️⚠️ **OS GESTOS DA SELECÇÃO — UMA construção, dois consumidores** (regra 8).
   *
   * 📗 §12.3 do desenho, a peça **H**: *«`Ctrl`+`K` passa a conhecer a selecção»*. A barra de
   * acções do painel e a paleta de comandos passam a oferecer **os mesmos verbos**, e por isso
   * recebem **a mesma função** — não duas construções do mesmo gesto. 📌 Duas formulações
   * divergem, e a que divergir é a que ninguém corre; aqui a divergência seria pior do que o
   * costume, porque estes gestos **escrevem no plano**.
   *
   * ⚠️⚠️ **`useMemo` e não um objecto novo a cada render, e a razão é de comportamento e não
   * de desempenho:** a paleta repõe o cursor no topo sempre que a lista muda de identidade
   * (ver a nota do `linhas` em `PaletaDeComandos.tsx`). Com um objecto novo por render, a seta
   * para baixo mudava o estado, o componente redesenhava, e o cursor voltava ao topo — a
   * paleta ficava impossível de navegar pelo teclado, sem erro nenhum a dizê-lo.
   *
   * ⚠️ **As dependências são as SETE de que os corpos dependem** (seis até §6.5) — o resto são
   * funções `setX` do `useState`, que o React garante estáveis. `dados` cobre `dados.grelha`,
   * `dados.viaturas` e `dados.nDias`, e `natureza` é um `useMemo` sobre `dados.grelha` e
   * `seleccao`, que já lá estão.
   *
   * ⚠️ **`undefined` continua a querer dizer «não há gesto»**, exactamente como antes: os
   * botões da barra testam `!== undefined`, e mudar essa convenção aqui mudava-os a todos em
   * silêncio.
   */
  const gestos = useMemo((): Record<ChaveDoVerbo, (() => void) | undefined> => {
    return {
      editar:
        seleccao === null
          ? undefined
          : () => {
              // 2026-10-27 · a MESMA formulação das três portas — ver `alvoDaEdicao`.
              setAEditar(alvoDaEdicao(dados.grelha[seleccao.v] ?? [], dados.corridas, seleccao.v, seleccao.d));
            },
      // ---- 🔴 O PAR DA CÓPIA (peça F §6.5, §9 Q1) ----------------------------------------
      //
      // 📗 *«copiar **e** mover — e "copiar" é o segundo autocarro»*: o grupo não cabe num só.
      //
      // ⚠️⚠️ **É A CORRIDA INTEIRA e a REFERÊNCIA dela**, como o `porEmEspera` e o `travar` —
      //    e a referência vai explícita porque as duas rotas a exigem (400 `alvo_incompleto`
      //    sem ela): num intervalo pode haver mais do que uma viagem, e é ela que diz qual.
      //
      // ⚠️ **Só num dia de SERVIÇO e só com referência.** Num dia livre ou de oficina não há
      //    viagem para copiar, e o servidor recusaria com `servico_nao_encontrado` — *«um
      //    botão presente que só serve para recusar é ruído»*.
      copiarPeriodo: ((): (() => void) | undefined => {
        if (seleccao === null || natureza !== 'servico') return undefined;
        const linha = dados.grelha[seleccao.v] ?? [];
        const ref = dados.corridas[linha[seleccao.d]?.[10] ?? -1];
        if (ref === undefined) return undefined;
        // ⚠️ A viagem inteira, com o dia de rotação em que parte (`extentoDaViagem`) — a
        //    referência já vai explícita, logo o dia com duas viagens copia só a nossa.
        const ext = extentoDaViagem(linha, seleccao.d);
        // ---- ⚠️ PEÇA 1 · O PERÍODO PRÉ-PREENCHIDO PELA EXTENSÃO DO `Shift`+setas -------------
        //
        //   📌 A omissão continua a ser a CORRIDA INTEIRA — é o que este gesto já fazia. Só
        //   quando há uma extensão activa NESTA viatura é que o período se estreita à
        //   INTERSECÇÃO dela com a corrida — nunca para lá dela (`copiar-periodo.ts` copia uma
        //   viagem, não inventa dias fora dela) e nunca menos do que o dia seleccionado, que
        //   está sempre dentro dos dois intervalos por construção.
        const dentroDaExtensao =
          intervaloSeleccionado !== null && intervaloSeleccionado.v === seleccao.v;
        const dia0 = dentroDaExtensao
          ? Math.max(ext.dia0, intervaloSeleccionado.d0)
          : ext.dia0;
        const dia1 = dentroDaExtensao
          ? Math.min(ext.dia1, intervaloSeleccionado.d1)
          : ext.dia1;
        return () => setACopiarPeriodo({ v: seleccao.v, dia0, dia1, ref });
      })(),
      // ⚠️⚠️ **O INVERSO, e ele SÓ EXISTE quando a viagem corre noutro autocarro** — ao
      //    contrário do `copiarPeriodo`, e pela mesma razão do `juntarTrocos`. Sem outro
      //    executor, o que este gesto apagaria **não é uma cópia, é a viagem**, e o servidor
      //    recusa com 409 `unica_viatura_com_a_viagem`. 📌 A condição aqui é o ESPELHO dessa
      //    recusa, lida do mapa — que pode estar velho de segundos, e é por isso que a janela
      //    continua a saber ler o 409.
      retirarCopia: ((): (() => void) | undefined => {
        if (seleccao === null || natureza !== 'servico') return undefined;
        if (outrosAutocarrosDaViagem.length === 0) return undefined;
        const linha = dados.grelha[seleccao.v] ?? [];
        const ref = dados.corridas[linha[seleccao.d]?.[10] ?? -1];
        if (ref === undefined) return undefined;
        // ⚠️ Idem `copiarPeriodo`: a viagem inteira, com a referência explícita.
        const ext = extentoDaViagem(linha, seleccao.d);
        return () => setARetirarCopia({ v: seleccao.v, dia0: ext.dia0, dia1: ext.dia1, ref });
      })(),
      // ⚠️⚠️ A SEGUNDA VIAGEM do dia de rotação — 📗 «nos detalhes tem de ter as duas viagens».
      //   📌 O intervalo é o DELA (`extentoDaViagemQueParte`) e a referência vai **explícita**:
      //   sem isso o servidor escolhia a `principal` e o gesto mexia na outra viagem.
      //   2026-10-27 · `alvoDaEdicaoQueParte`, ao lado de `alvoDaEdicao` — a viagem toda que parte.
      editarQuePartem: ((): (() => void) | undefined => {
        if (seleccao === null) return undefined;
        const alvo = alvoDaEdicaoQueParte(dados.grelha[seleccao.v] ?? [], dados.corridas, seleccao.v, seleccao.d);
        return alvo === null ? undefined : () => setAEditar(alvo);
      })(),
      // ⚠️ A cor manual (migração 055) — a viagem que CHEGA (celula[10]).
      corDaViagem: ((): (() => void) | undefined => {
        if (seleccao === null) return undefined;
        const ref = dados.corridas[dados.grelha[seleccao.v]?.[seleccao.d]?.[10] ?? -1];
        return ref === undefined ? undefined : () => setACorDaViagem({ referencia: ref });
      })(),
      // ⚠️ A cor manual (migração 055) da SEGUNDA viagem do dia de rotação — a que PARTE
      //   (celula[13]). Mesma guarda de `editarQuePartem`: só existe num dia de rotação.
      corDaViagemQuePartem: ((): (() => void) | undefined => {
        if (seleccao === null) return undefined;
        const linha = dados.grelha[seleccao.v] ?? [];
        const ext = extentoDaViagemQueParte(linha, seleccao.d);
        if (ext === null) return undefined;
        const ref = dados.corridas[linha[seleccao.d]?.[13] ?? -1];
        return ref === undefined ? undefined : () => setACorDaViagem({ referencia: ref });
      })(),
      anotar:
        // ⚠️ **UM DIA, e não a corrida inteira** — ao contrário de «editar serviço».
        // A fonte diz «na célula», e é o que os planeadores fazem no Excel: a nota é
        // daquele dia («saida htl 07:50»), não da viagem toda.
        seleccao === null ? undefined : () => setANotar({ viatura: seleccao.v, dia: seleccao.d }),
      corrigir:
        seleccao === null
          ? undefined
          : () => setACorrigir({ viatura: seleccao.v, dia: seleccao.d }),
      partir: seleccao === null ? undefined : () => setADividir({ v: seleccao.v, d: seleccao.d }),
      // ⚠️ Garantia inversa de `partir`, acima — ver `OrganizarEmTrocos`. Sempre disponível
      //    num dia de serviço, tal como `partir`: organizar em troços não exige nada sobre o
      //    estado actual da viagem, ao contrário de `juntarTrocos` (abaixo).
      organizarEmTrocos:
        seleccao === null
          ? undefined
          : () => setAOrganizarEmTrocos({ v: seleccao.v, d: seleccao.d }),
      // ⚠️⚠️ **SÓ existe quando a viagem JÁ TEM alguma fronteira marcada** (`temFronteiraDeTroco`,
      //    acima) — ao contrário de `organizarEmTrocos`. Sem esta guarda o botão apareceria em
      //    toda a parte a oferecer desfazer algo que nunca foi feito, e clicar devolveria
      //    sempre `nada_a_unir`: um botão presente que só serve para recusar é ruído, não um
      //    beco (o servidor recusaria em voz alta na mesma, mas aqui dá para não o oferecer).
      juntarTrocos:
        seleccao === null || !temFronteiraDeTroco
          ? undefined
          : () => setAJuntarOsTrocos({ v: seleccao.v, d: seleccao.d }),
      // ---- 🔴 PÔR EM ESPERA (peça F §6.3) ------------------------------------------------
      //
      // ⚠️ **Só num dia de SERVIÇO**, ao contrário do `marcarIndisponivel`: pôr em espera tira
      //    uma viagem do autocarro, e num dia livre ou de oficina não há viagem nenhuma para
      //    tirar — o servidor recusaria com `servico_nao_encontrado`, e *«um botão presente que
      //    só serve para recusar é ruído»*.
      //
      // ⚠️⚠️ **O INVERSO não mora aqui, e não é esquecimento:** uma viagem em espera não está em
      //    viatura nenhuma, logo não há célula da frota onde o botão pudesse viver. «Trazer de
      //    volta…» abre-se da própria barra, na secção «Em espera» — e é por isso que a linha no
      //    mapa era condição do gesto e não um extra.
      porEmEspera:
        seleccao === null || natureza !== 'servico'
          ? undefined
          : () => setAPorEmEspera({ v: seleccao.v, d: seleccao.d }),
      travar:
        seleccao === null
          ? undefined
          : () => {
              const linha = dados.grelha[seleccao.v] ?? [];
              const ext = extentoDaCorrida(linha, seleccao.d);
              setAFixar({ v: seleccao.v, d0: ext.dia0, d1: ext.dia1 });
            },
      // ⚠️ §5.6 exigência 3 — o MESMO alvo que o menu de contexto usa (`alvoDoDiario`).
      diario: alvoDoDiario === null ? undefined : () => setAVerDiario(alvoDoDiario),
      marcarIndisponivel:
        // ⚠️ **SEMPRE que há selecção fixa, inclusive em DIAS LIVRES** — é precisamente
        //    aí que faltava poder sinalizar a oficina, e o `Editar dia…` não servia
        //    porque exige que a linha já exista na base.
        seleccao === null ? undefined : () => setAMarcar({ v: seleccao.v, d: seleccao.d }),
      onde:
        // ⚠️⚠️ **SO NOS DIAS SEM ITINERARIO** — 📗 *«adicione nos dias em que nao ha
        //   itinerario esse botao»*.
        //   📌 Num dia de servico e o **itinerario** que diz onde a viatura esta; oferecer
        //   aqui um selector de localidade convidava a escrever uma segunda verdade sobre o
        //   mesmo dia — e o motor le uma delas. E num dia de oficina e a **oficina**.
        //   ⚠️ A condicao le a CELULA e nao o `natureza`: um dia sem linha nenhuma (17 877
        //   deles, medidos) e o caso mais comum e mais importante, e e onde `natureza`
        //   nao diz nada.
        seleccao === null || !diaSemItinerario(dados, seleccao.v, seleccao.d)
          ? undefined
          : () => setAParar({ v: seleccao.v, d: seleccao.d }),
      substituir:
        // ⚠️ **SÓ APARECE QUANDO A CÉLULA ESTÁ INDISPONÍVEL** (`estadoViatura >= 0`).
        //    Um botão «Quem substitui?» em todos os dias era ruído em 91 250 células, e
        //    ruído numa barra de acções ensina a não a ler. Aqui o botão SER visível já é
        //    metade da resposta: diz que há um problema neste dia.
        seleccao === null || ((dados.grelha[seleccao.v]?.[seleccao.d] ?? null)?.[7] ?? -1) < 0
          ? undefined
          : () => setASubstituir({ v: seleccao.v, d: seleccao.d }),
      fecharServico:
        seleccao === null
          ? undefined
          : () => {
              // A CORRIDA inteira, como em «editar»: uma factura é do serviço.
              const linha = dados.grelha[seleccao.v] ?? [];
              const ext = extentoDaCorrida(linha, seleccao.d);
              setAFechar({ viatura: seleccao.v, dia0: ext.dia0, dia1: ext.dia1 });
            },
      cancelarServico:
        // ⚠️⚠️ **TAMBEM NUM SUBCONTRATADO** — 📗 *«tem de aparecer alguns botoes tipo para
        //   cancelar, editar algo»*. 📌 E o cancelamento e a operacao que **melhor** se
        //   aplica a um subcontratado: age sobre o PEDIDO, e um pedido nao sabe nem precisa
        //   de saber quem o executa. O que estava fechado era a descoberta de qual e o
        //   pedido daquela celula — uma rota que olhava so para `viatura_id`.
        // ⚠️⚠️ **E NAO e uma via de escrita nova:** e a mesma janela, a mesma rota
        //   `/api/pedidos/alterar`, o mesmo motivo obrigatorio e a mesma auditoria. Estrear
        //   um caminho de escrita para o caso menos testado seria o desenho ao contrario.
        // ⚠️⚠️ **`-1 - <EMPRESA>` e nao `-1 - <linha>`, e a distincao e o C₂ inteiro.** O
        //   `aCancelar.v` negativo e lido como `dados.subcontratados[-1 - v]`: passar-lhe o
        //   indice da LINHA cancelava o servico de outra empresa a partir de um painel que
        //   fala desta — um valor plausivel no lugar do certo, com consequencia de escrita.
        //   📌 Os dois indices vivem em estados diferentes e nunca se misturam: a geometria
        //   (realce, offsets) fala por LINHA, a escrita fala por EMPRESA.
        seleccaoSub !== null
          ? () =>
              setACancelar({
                v: -1 - (linhasSub[seleccaoSub.l]?.sub ?? 0),
                d: seleccaoSub.d,
              })
          : seleccao === null
            ? undefined
            : () => setACancelar({ v: seleccao.v, d: seleccao.d }),
      // ⚠️ **TAMBÉM NUM SUBCONTRATADO, pela mesma razão do cancelamento:** o gesto age
      //   sobre o PEDIDO, e um pedido não sabe nem precisa de saber quem o executa. 📌 E é
      //   aqui que ele vale mais — retirar um serviço subcontratado devolve-o à fila para
      //   ser planeado com frota própria, que é exactamente a decisão que compensa.
      // ⚠️ `-1 - <EMPRESA>`, pela mesma razão do `cancelarServico` acima.
      retirarDoMapa:
        seleccaoSub !== null
          ? () =>
              setARetirar({
                v: -1 - (linhasSub[seleccaoSub.l]?.sub ?? 0),
                d: seleccaoSub.d,
              })
          : seleccao === null
            ? undefined
            : () => setARetirar({ v: seleccao.v, d: seleccao.d }),
      // B1-(2) · trocar este serviço subcontratado para outra empresa.
      // ⚠️ `s` é o índice da EMPRESA: é ela que o `TrocarSubcontratado` troca.
      trocarEmpresa:
        seleccaoSub === null
          ? undefined
          : () =>
              setATrocarSub({
                s: linhasSub[seleccaoSub.l]?.sub ?? 0,
                d: seleccaoSub.d,
              }),
      encaixar:
        seleccao === null
          ? undefined
          : () => {
              // A corrida de dias LIVRES à volta do dia clicado. ⚠️ Uma paragem inferida
              //    conta como livre — ela não existe na base, e é o mesmo predicado que o
              //    `encaixe.ts` do servidor usa para decidir se a janela está ocupada.
              const linha = dados.grelha[seleccao.v] ?? [];
              const livre = (d: number) => {
                const c = linha[d] ?? null;
                return c === null || paragemInferida(c);
              };
              let d0 = seleccao.d;
              while (d0 > 0 && livre(d0 - 1)) d0--;
              let d1 = seleccao.d;
              while (d1 + 1 < dados.nDias && livre(d1 + 1)) d1++;
              const v = dados.viaturas[seleccao.v];
              if (v === undefined) return;
              setEncaixePara({ id: v.id, numero: v.numero, d0, d1 });
              setConsultasAbertas(true);
            },
    };
  }, [
    dados,
    seleccao,
    seleccaoSub,
    linhasSub,
    temFronteiraDeTroco,
    alvoDoDiario,
    // 🔴 §6.5 · a SÉTIMA: decide se «Tirar este autocarro da viagem…» chega a existir.
    outrosAutocarrosDaViagem,
  ]);

  /**
   * O rótulo do grupo da segunda viagem — do PAINEL e não da paleta: lá o âmbito é o grupo,
   * aqui cada verbo nomeia a viagem no próprio rótulo.
   */
  const rotuloDaQuePartem = useMemo(() => {
    if (seleccao === null) return undefined;
    const ref = dados.corridas[dados.grelha[seleccao.v]?.[seleccao.d]?.[13] ?? -1];
    return ref === undefined ? undefined : `a viagem que PARTE (${ref})`;
  }, [dados, seleccao]);

  /**
   * ⚠️⚠️ **O `Ctrl+K` PASSA A CONHECER A SELECÇÃO** — §12.3 do desenho, a peça **H**.
   *
   * 📗 *«a resposta não é inventar dez atalhos. É um só, que já existe e que já se aprende»*.
   * 📌 E a razão pela qual isto vale a pena não é o teclado: é que **hoje um verbo que não se
   * aplica está simplesmente AUSENTE da barra, sem explicação em lado nenhum**. Medido a
   * 2026-09-15: num dia livre são **16 dos 19**, e a pergunta *«porque é que não posso partir
   * esta viagem?»* não tem resposta em sítio nenhum do sistema.
   *
   * ⚠️ **Os factos saem de onde a barra os lê** — `natureza`, `temFronteiraDeTroco`,
   * `alvoDoDiario`, `jaPassou`, `diaSemItinerario` e os campos 7 e 13 da célula. Uma segunda
   * leitura dos mesmos factos divergiria da barra, e as duas listas deixavam de concordar.
   */
  const contextoDaSeleccao = useMemo((): ContextoDaSeleccao => {
    const celula = seleccao === null ? null : (dados.grelha[seleccao.v]?.[seleccao.d] ?? null);
    return {
      onde: seleccaoSub !== null ? 'subcontratado' : seleccao === null ? 'nada' : 'frota',
      natureza,
      jaPassou: seleccao !== null && jaPassou(dados, seleccao.d),
      indisponivel: (celula?.[7] ?? -1) >= 0,
      // ⚠️ `>= 0` afirmativo: com o campo ausente num *fixture* incompleto, `undefined < 0` é
      //    **falso** e a ausência lia-se como presença — o defeito do campo 13, já medido aqui.
      temViagemQueParte: (celula?.[13] ?? -1) >= 0,
      temFronteiraDeTroco,
      temCorrida: (celula?.[10] ?? -1) >= 0,
      // 🔴 §6.5 · a MESMA lista que o gesto usa para decidir se existe — ver
      //    `outrosAutocarrosDaViagem`. Uma segunda contagem aqui fazia a paleta oferecer o
      //    verbo num caso em que a barra o esconde, que é «duas portas, uma fechada».
      temOutraViatura: outrosAutocarrosDaViagem.length > 0,
      diaSemItinerario: seleccao !== null && diaSemItinerario(dados, seleccao.v, seleccao.d),
      temDiario: alvoDoDiario !== null,
    };
  }, [
    dados,
    seleccao,
    seleccaoSub,
    natureza,
    temFronteiraDeTroco,
    alvoDoDiario,
    outrosAutocarrosDaViagem,
  ]);

  /**
   * Os verbos prontos para a paleta — **a tabela decide se se aplica, o `gestos` decide o que
   * faz, e nenhum dos dois sabe do outro**.
   *
   * ⚠️ Um verbo aplicável **sem** gesto seria um botão morto; não pode acontecer (a união
   * `ChaveDoVerbo` obriga o `gestos` a ter as 22 chaves), mas se acontecesse a linha passa a
   * não aplicável com a razão escrita, em vez de fechar a paleta sem fazer nada.
   */
  const contextuaisDaPaleta = useMemo<readonly VerboDaPaleta[]>(
    () =>
      verbosDaSeleccao(contextoDaSeleccao).map((v) => {
        const fazer = gestos[v.chave];
        const vivo = v.aplicavel && fazer !== undefined;
        return {
          chave: v.chave,
          rotulo: v.rotulo,
          marca: vivo ? marcaDoVerbo(v) : MARCA_NAO_DA,
          // ⚠️ O que faz quando dá; **a razão** quando não dá. A coluna é a mesma porque a
          //    pergunta é a mesma — «o que é esta linha?» —, e a resposta muda com o estado.
          detalhe: vivo ? v.detalhe : (v.porque ?? 'não se aplica a esta selecção'),
          aplicavel: vivo,
          fazer: fazer ?? (() => undefined),
        };
      }),
    [contextoDaSeleccao, gestos],
  );

  /**
   * ⚠️⚠️ **O REALCE DE UM PERIODO — sem viatura, porque ainda nao ha nenhuma.**
   *
   * 📗 Cliente, 2026-08-10: *«nos pedidos também tem de ter a opção ver no mapa antes de
   * planear, é bastante importante, tal como já acontece nas consultas»*.
   *
   * 📌 **Nas consultas há sempre uma viatura; num pedido por planear não há.** O que se vai
   * lá ver é outra coisa: **como está a frota naquela janela de dias** — quem está livre,
   * quem volta a tempo, onde estão. Levar o mapa a uma linha qualquer respondia a uma
   * pergunta que ninguém fez.
   *
   * ⚠️⚠️ **E aqui uma BANDA DE COLUNAS é o desenho certo, ao contrário do que a v2 do realce
   * ensinou.** A régua de ponta a ponta foi recusada porque, havendo uma linha para apontar,
   * atravessar 250 viaturas dizia «em que zona» em vez de «onde». Neste caso **não há linha
   * nenhuma para apontar**: o alvo *é* o intervalo de dias, em toda a frota.
   */
  const [realcePeriodo, setRealcePeriodo] = useState<
    { d0: number; d1: number; contagem: number } | null
  >(null);

  /**
   * ============================================================================
   * ⚠️⚠️ B31 · A PRÉ-VISUALIZAÇÃO DO LOTE INTEIRO — e porque NÃO é o `realce`
   * ============================================================================
   *
   * 📗 Cliente, 2026-08-31: *«tem de existir uma forma de pré-visualizar as alterações no mapa,
   * aparecendo de forma diferente, e depois quando o planeador quiser retirar, tipo um botão
   * "mostrar preview" que após clicar fica "retirar preview"»*.
   *
   * ⚠️⚠️ **O `realce.previsao` NÃO SERVIA, e as duas razões são estruturais:**
   *
   *   1. **É de UMA linha.** `realce` tem um `v` — um lote são ~37 alterações em dezenas de
   *      viaturas. Chamar `irPara` 37 vezes deixava a última a ganhar.
   *   2. ⚠️ **APAGA-SE SOZINHO ao fim de 9 s**, de propósito («um realce permanente vira
   *      decoração»). Uma pré-visualização que se evapora enquanto se lê a lista é
   *      indistinguível de uma que nunca funcionou — e o botão diria «Retirar
   *      pré-visualização» sobre um mapa já limpo.
   *
   * 📌 **Logo é estado PRÓPRIO e sem temporizador:** sai por decisão (o botão, ou fechar as
   * Consultas), nunca por tempo. E o sinal desenhado é **o mesmo** — `barra--previsao`, com a
   * palavra «previsão». Um sinal novo ensinava duas linguagens para a mesma coisa.
   */
  const [previsaoDoLote, setPrevisaoDoLote] = useState<readonly PrevisaoDeLote[] | null>(null);

  /**
   * ---- ⚠️⚠️ J · A PREVISÃO DE UM GESTO ABERTO — 📄 spec §11 · J (2026-09-16) --------------
   *
   * O fantasma que o «Mover para outro autocarro» e o «Acrescentar um 2.º autocarro…» desenham
   * na linha de DESTINO enquanto a sua janela está aberta.
   *
   * ⚠️⚠️ **ESTADO PRÓPRIO, e é a TERCEIRA fonte de previsão a somar-se às outras duas** (o
   * lote e o `realce`). 📌 Não se reaproveitou o `previsaoDoLote` porque as **vidas** são
   * diferentes: a do lote sai pelo botão «Retirar pré-visualização» das Consultas, esta sai
   * quando a janela fecha. Com um estado só, abrir o «Mover» com a previsão do lote ligada
   * apagava as ~37 barras dela e deixava o botão de lá a dizer «Retirar» sobre um mapa limpo —
   * que é literalmente a razão pela qual o `previsaoDoLote` não é o `realce`.
   *
   * ⚠️ **O SINAL DESENHADO É O MESMO** (`barra--previsao`, com a palavra «previsão»): três
   * fontes, uma linguagem. Um sinal novo para «isto ainda não está escrito» ensinava duas
   * línguas para a mesma coisa, e a segunda seria a que ninguém reconhece.
   */
  const [previsaoDoGesto, setPrevisaoDoGesto] = useState<readonly PrevisaoDeLote[] | null>(null);

  /**
   * ⚠️⚠️ **RESOLVIDA UMA VEZ, POR LINHA — e o `useMemo` aqui é o orçamento de nós (R1).**
   *
   * 📌 `Linha` é `memo`. Construir o vector da previsão dentro do ciclo de renderização dava a
   * cada uma das linhas visíveis um objecto **novo** a cada quadro, o que invalidava a
   * memoização de **todas** — incluindo as que não têm previsão nenhuma. Com o `Map`
   * memoizado, as linhas sem alteração recebem `null` (identidade estável) e não voltam a
   * desenhar.
   *
   * ⚠️ **Uma viatura desconhecida é SALTADA e não inventada.** Um `subcontratar_dias` aponta a
   * um parceiro, que não tem linha na frota: desenhar a barra na viatura de índice igual
   * mandava olhar para o autocarro errado com confiança. Quem conta quantas ficaram de fora é
   * o ecrã que as pediu — ver a nota do botão em `Consultas`.
   */
  const previsaoPorLinha = useMemo(() => {
    const porLinha = new Map<number, PrevisaoResolvida[]>();
    const acrescentar = (v: number, p: PrevisaoResolvida): void => {
      const ja = porLinha.get(v);
      if (ja === undefined) porLinha.set(v, [p]);
      else ja.push(p);
    };
    // ⚠️ J · as DUAS fontes de lista passam pela MESMA resolução — o lote e o gesto aberto.
    //    Duas resoluções divergiriam na cor, que é precisamente o defeito que a primeira
    //    versão do lote teve (pintar duas alterações com o cliente da primeira).
    for (const p of [...(previsaoDoLote ?? []), ...(previsaoDoGesto ?? [])]) {
      const v = dados.viaturas.findIndex((x) => x.id === p.viaturaId);
      if (v < 0) continue;
      acrescentar(v, {
        cliente: dados.clientes.findIndex((c) => c[0] === p.sigla),
        itinerario:
          p.itinerario === undefined ? -1 : dados.itinerarios.findIndex((x) => x[0] === p.itinerario),
        pedacos: p.pedacos,
      });
    }
    // ⚠️ **O realce de UMA alteração continua a valer, e SOMA-SE ao lote.** Carregar em «Ver
    //    destino» com a pré-visualização ligada é o gesto natural — e substituir o lote pela
    //    peça apontada fazia as outras 36 barras desaparecerem sem nada o dizer.
    if (realce !== null && realce.previsao !== undefined && realce.v >= 0) {
      acrescentar(realce.v, realce.previsao);
    }
    return porLinha;
  }, [previsaoDoLote, previsaoDoGesto, realce, dados.viaturas, dados.clientes, dados.itinerarios]);

  /**
   * 🔴 2026-10-26 · «Ver no mapa» FORA DA FITA: pede a fita centrada no dia e leva o sítio (por
   * datas) para lá — ver `DestinoAoAbrir`. Devolve `true` quando foi isso que fez. ⚠️ Decide-se
   * pelo PRIMEIRO dia: uma paragem que começa na fita e continua para lá dela vê-se nesta.
   * Uma formulação só, para os três gestos de «ir a» (`irPara`, `irParaMotorista`, `irParaPeriodo`).
   */
  const foraDaFita = useCallback(
    (dia0: number, dia1: number, quem: { readonly viaturaId?: string; readonly motoristaId?: string }): boolean => {
      const d = destinoForaDaFita(janelaDe(dados), dia0, dia1, quem);
      if (recentrarFita === undefined || d === null) return false;
      recentrarFita(d.inicio, d.destino);
      return true;
    },
    [dados, recentrarFita],
  );

  /** Leva o mapa a uma JANELA DE DIAS, sem escolher viatura nenhuma. */
  const irParaPeriodo = useCallback(
    (dia0: number, dia1: number) => {
      if (foraDaFita(dia0, dia1, {})) return;
      const el = caixa.current;
      if (el === null) return;
      const d0 = Math.max(0, dia0);
      // Um terço de folga, como no `irPara`: encostar o período ao canto esconde o que vem
      // antes, e «quem está livre nesta janela» lê-se com o contexto dos dois lados.
      el.scrollLeft = Math.max(0, (offsetsDias[d0] ?? 0) - el.clientWidth / 3);
      setRealcePeriodo((a) => ({
        d0,
        d1: Math.max(d0, dia1),
        contagem: (a?.contagem ?? 0) + 1,
      }));
      recalcular();
    },
    [foraDaFita, offsetsDias, recalcular],
  );

  // ---- 🔴 D-101 · «IR PARA…» — dentro da fita desloca-se; fora dela, pede-se a fita centrada ----
  //   ⚠️ Uma formulação: a data passa a índice pela janela DESTE mapa (`janelaDe(dados)`), e só se
  //   pede outra fita quando o índice cai fora dela — nunca se recarrega o que já está à vista.
  const irParaData = useCallback(
    (isoData: string) => {
      const d = decidirIrPara(janelaDe(dados), isoData);
      if (d.tipo === 'deslocar') irParaPeriodo(d.dia, d.dia);
      else recentrarFita?.(d.inicio, isoData);
    },
    [dados, irParaPeriodo, recentrarFita],
  );
  const deslocarFita = useCallback(
    (meses: number) => recentrarFita?.(inicioDeslocado(dados.inicio, meses), null),
    [dados.inicio, recentrarFita],
  );
  // A data pedida num «Ir para…» que obrigou a carregar outra fita: leva-se lá quando ela chega.
  // ⚠️ E apaga-se a seguir (achado da revisão de 2026-10-26): sem isto, cada escrita ou largura
  //    alterada voltava a levar o mapa à data antiga.

  /**
   * ---- ⚠️⚠️ LEVAR AO MOTORISTA, e não à viatura (2026-08-31) ------------------------------
   *
   * 📗 Cliente: *«a secção sem regressar à base tem de ter para cada linha de motorista o "ver no
   *    mapa", que filtra pelo motorista e leva para onde ele está hoje»*.
   *
   * ⚠️⚠️ **NÃO ESTREIA MECANISMO NENHUM, e é deliberado.** São as duas peças que já existem: o
   * eixo de filtro dos motoristas (`alternarNoFiltro`) e o realce de PERÍODO (`irParaPeriodo`).
   * Duas formas de realçar divergiriam, e a que divergisse seria a que ninguém corre.
   *
   * 📌 **Não há linha de motorista no mapa** — a grelha é por viatura (a vista por motorista está
   * parada por decisão do cliente desde 2026-08-11). Logo o que se aponta é o **período**, e o
   * filtro é o que responde a «onde está esta pessoa»: as barras dela ficam à vista e as outras
   * esbatem (P-30, nunca esconder).
   *
   * ⚠️ **O `id` resolve-se por `motoristasIds`, que é PARALELO a `motoristas`** — a posição `i` é a
   * mesma pessoa nos dois, e é esse índice que o filtro usa. Procurar pelo NOME casaria dois
   * homónimos, e o filtro ficaria na pessoa errada sem nada o denunciar.
   *
   * ⚠️ `-1` = não encontrado: rola-se e **não se filtra**. Um filtro no índice errado esbate a
   * frota inteira e afirma que esta pessoa não conduz nada — pior do que não filtrar.
   */
  const irParaMotorista = useCallback(
    (motoristaId: string, dia0: number, dia1: number) => {
      if (foraDaFita(dia0, dia1, { motoristaId })) return;
      const i = dados.motoristasIds.indexOf(motoristaId);
      if (i >= 0) alternarNoFiltro('motoristas', i, dados.motoristas.length);
      irParaPeriodo(dia0, dia1);
    },
    [foraDaFita, dados.motoristasIds, dados.motoristas.length, alternarNoFiltro, irParaPeriodo],
  );

  /**
   * ---- 🔴 R · AS VIAGENS DE UMA LINHA, para o lote do «mover período» (2026-09-16) --------
   *
   * 📗 Cliente: *«tipo 1 de Setembro a 20 de Outubro e passar tudo para outra linha»*.
   *
   * ⚠️ **Sai da grelha que o mapa já tem em memória** — não custa consulta nenhuma, e é a mesma
   * fonte que desenha as barras. 📌 A regra de continuidade vive em `viagens-da-linha.ts`, que é
   * puro e tem guarda a confrontá-lo com `extentoDaCorrida`: se divergissem, a janela dizia «5
   * viagens» e o gesto movia outras.
   *
   * ⚠️ **`useCallback` porque desce a uma janela que a põe em `useMemo`** — uma função nova a
   * cada render fazia a lista do lote ser recalculada e o efeito da pré-visualização correr em
   * ciclo. É o defeito do `indisponibilidade()` do «Quem substitui?» (2026-08-07), que no
   * navegador não se lê como defeito: lê-se como «o ecrã está lento».
   */
  const viagensDaViatura = useCallback(
    (viaturaId: string) => {
      const v = dados.viaturas.findIndex((x) => x.id === viaturaId);
      if (v < 0) return [];
      return viagensDaLinha(dados.grelha[v] ?? [], dados);
    },
    [dados],
  );

  const irPara = useCallback(
    (viaturaId: string, dia: number, diaFim?: number, previsao?: PrevisaoPedida) => {
      if (foraDaFita(dia, diaFim ?? dia, { viaturaId })) return;
      // ---- ⚠️⚠️ RESOLVER A PREVISÃO AQUI, e não em quem chama --------------------
      //
      //   📌 Quem carrega no botão sabe a **sigla** do cliente; só o mapa sabe o **índice**
      //   dela na paleta. Resolver isto num sítio só é o que evita que cada chamador adivinhe
      //   índices — e um índice errado pinta a barra com a cor de outro cliente, que é pior do
      //   que não a pintar.
      //
      // ⚠️ `-1` quando não se encontra: a barra fica com a cor neutra em vez de com a cor
      //   errada. `corCliente(-1)` é exactamente o que o mapa usa quando o filtro esbate.
      const prev =
        previsao === undefined
          ? undefined
          : {
              cliente: dados.clientes.findIndex((c) => c[0] === previsao.sigla),
              itinerario:
                previsao.itinerario === undefined
                  ? -1
                  : dados.itinerarios.findIndex((x) => x[0] === previsao.itinerario),
              // ⚠️ **Nunca vazio**: sem pedaços, o pedaço é o intervalo pedido. Uma lista vazia
              //   desenhava zero barras e lia-se como «a pré-visualização não funciona».
              pedacos:
                previsao.pedacos !== undefined && previsao.pedacos.length > 0
                  ? previsao.pedacos
                  : ([[Math.max(0, dia), diaFim === undefined ? Math.max(0, dia) : diaFim]] as const),
            };
      const el = caixa.current;
      if (el === null) return;
      const v = dados.viaturas.findIndex((x) => x.id === viaturaId);
      if (v < 0) {
        // ⚠️ **PODE SER UM SUBCONTRATADO**, e antes disto o mapa não ia a lado nenhum: a
        // reserva subcontratada gravava, aparecia na secção própria lá em baixo, e o
        // planeador ficava a olhar para o mesmo sítio do ecrã sem nada ter mudado à vista.
        // Apanhado pelo cliente ao escolher a primeira opção, que era subcontratada.
        const sc = dados.subcontratados.findIndex((x) => x.id === viaturaId);
        if (sc < 0) return;
        // ⚠️⚠️ **C₂ · quem chama conhece a EMPRESA e a secção é indexada por LINHA.** A rota de
        //   reserva não sabe de linhas nenhumas, logo a resolução vive num sítio só
        //   (`linhaDoServicoSub`): a linha desta empresa que tem serviço neste dia, e a
        //   primeira dela quando não tem. Escolher a linha 0 sempre — que era o que o índice
        //   da empresa fazia por acidente — punha o realce sobre uma linha vazia numa empresa
        //   com nove, que é um realce no sítio errado.
        const dSub0 = Math.max(0, dia);
        const iLinhaSub = linhaDoServicoSub(linhasSub, sc, dSub0);
        if (iLinhaSub < 0) return;
        // ⚠️⚠️ **NAO CHEGA DESLOCAR: era isto que fazia o botao «nao dar».**
        //
        //   📗 Cliente, 2026-08-10: *«tem de permitir que o botao ver no mapa funcione
        //   igualmente (nao esta a dar)»*.
        //
        // ⚠️⚠️ O ramo do subcontratado deslocava o ecra e **acabava ali**: sem seleccao, sem
        //   realce e sem painel. 📌 Numa seccao de dez linhas iguais, deslocar sem apontar
        //   le-se como «nao aconteceu nada» — e o defeito e indistinguivel de um botao morto,
        //   que e a razao pela qual ele foi reportado como «nao esta a dar».
        //
        // ⚠️⚠️ **E `scrollHeight` era o fim do CONTEUDO, nao a linha.** Com dez subcontratados,
        //   ir ao fundo mostra o ultimo — e o alvo podia ser o segundo. Agora calcula-se a
        //   posicao da linha: as viaturas todas, mais o titulo da seccao, mais as linhas
        //   anteriores desta.
        const topoSub =
          ALTURA_CABECALHO +
          (offsetsLinhas[dados.viaturas.length] ?? 0) +
          ALTURA_TITULO_SUB +
          (formaSub.offsets[iLinhaSub] ?? 0);
        el.scrollTop = Math.max(0, topoSub - el.clientHeight / 3);
        el.scrollLeft = Math.max(0, (offsetsDias[dSub0] ?? 0) - el.clientWidth / 3);
        // A seleccao e o realce, que e o que se ve. O painel abre com o detalhe do serviço.
        const dSub = dSub0;
        setSeleccao(null);
        setSeleccaoSub({ l: iLinhaSub, d: dSub });
        setRealce((a) => ({
          // ⚠️⚠️ **`v` negativo marca «e uma LINHA da seccao dos subcontratados», e o desenho do
          //   realce le-o com `offsetsSub`.** Sem isto o rectangulo ia para
          //   `offsetsLinhas[iLinhaSub]` — a linha da VIATURA com esse numero, que e outra linha
          //   qualquer no meio de 250. Um realce no sitio errado e pior do que nenhum: manda
          //   olhar para o lado errado com confianca.
          // ⚠️ **Com o C₂ este numero e o da LINHA e nao o da EMPRESA**, e tem de ser: e
          //   `offsetsSub` que o le, e `offsetsSub` passou a ter uma entrada por linha.
          v: -1 - iLinhaSub,
          d0: dSub,
          d1: diaFim === undefined ? dSub : Math.max(dSub, diaFim),
          contagem: (a?.contagem ?? 0) + 1,
          // ⚠️ **`...(x === undefined ? {} : {x})` e nao `previsao: x`**: com
          //    `exactOptionalPropertyTypes`, «a chave existe e vale `undefined`» nao e o mesmo
          //    que «a chave nao existe» — e e a segunda que descreve «nao ha previsao».
          ...(prev === undefined ? {} : { previsao: prev }),
        }));
        recalcular();
        return;
      }
      // Um terço da área visível de folga: encostar a reserva ao canto esconde o
      // contexto — o que está antes e depois — que é o que sustenta a decisão.
      const folgaY = el.clientHeight / 3;
      const folgaX = el.clientWidth / 3;
      el.scrollTop = Math.max(0, (offsetsLinhas[v] ?? 0) - folgaY);
      // ⚠️⚠️ **`dia < 0` QUER DIZER «nao ha dia»**, e entao nao se mexe na horizontal nem se
      //    marca coluna nenhuma. 📌 📗 Cliente, 2026-08-11: *«marcou o primeiro dia da
      //    tabela»* — a paleta chamava isto com o primeiro dia da JANELA VISIVEL quando a
      //    consulta era so uma viatura (`242 CAT` nao tem dia). O mapa punha o realce numa
      //    celula que ninguem pediu, e um realce no sitio errado e pior do que nenhum: leva
      //    o olho a uma resposta falsa.
      if (dia >= 0) el.scrollLeft = Math.max(0, (offsetsDias[dia] ?? 0) - folgaX);
      // A selecção é o que faz o realce do itinerário inteiro aparecer, e é o que o
      // planeador quer ver: não a célula, a corrida.
      // ⚠️⚠️ **SEM DIA, A LINHA REALÇA-SE DE PONTA A PONTA DA JANELA VISÍVEL — e a partir de
      //    2026-09-28 passa a SELECCIONAR também, o que faltava aqui.**
      //
      //    📗 Medido em §12.9 (`docs/superpowers/specs/2026-09-11-agilidade-do-planeador-design.md`):
      //    o `Ctrl+K` «ir para a viatura N» — o caminho mais óbvio para entrar na grelha sem
      //    rato, e o que este §12 promove — levava até à viatura e SAÍA sem `setSeleccao`,
      //    logo as setas não tinham de onde partir. Pelo outro caminho («Ver no mapa» com um
      //    dia real, abaixo) a selecção acontecia: o sistema respondia de duas maneiras à
      //    mesma intenção — a família «duas portas, uma fechada» desta casa.
      //
      //    ⚠️ **O comentário que aqui esteve dizia que seleccionar «fixava o painel num dia
      //    ARBITRÁRIO» — e tinha razão contra a alternativa errada, não contra todas.**
      //    Inventar «dia 1 do ano» seria arbitrário e quase sempre fora da janela (a selecção
      //    nasceria invisível até se rolar). `janelaRef.current.dia0` não inventa nada: é o
      //    primeiro dia JÁ VISÍVEL desta viatura — a mesma âncora que o realce, duas linhas
      //    abaixo, já usa — logo a célula seleccionada já está à vista, sem scroll extra.
      if (dia < 0) {
        const d0 = janelaRef.current.dia0;
        setSeleccao({ v, d: d0 });
        // ⚠️ **Larga o subcontratado, como o clique já faz** (linha ~4227): sem isto, «ir
        //    para a viatura N» vindo de uma linha de subcontratado seleccionada deixava as
        //    setas a mexer na secção errada — o `keydown` olha primeiro para `seleccaoSub`.
        setSeleccaoSub(null);
        setRealce((x) => ({
          v,
          d0: janelaRef.current.dia0,
          d1: janelaRef.current.dia1,
          contagem: (x?.contagem ?? 0) + 1,
          ...(prev === undefined ? {} : { previsao: prev }),
        }));
        recalcular();
        return;
      }
      const d0 = Math.max(0, dia);
      setSeleccao({ v, d: d0 });
      // ⚠️ E o rectângulo por cima, que é o que se vê de longe. Sem `diaFim`, realça a
      //    corrida inteira que passa por ali — quem chama sabe o dia, o mapa é que sabe
      //    onde o serviço acaba.
      const linha = dados.grelha[v] ?? [];
      const ext = diaFim === undefined ? extentoDaCorrida(linha, d0) : { dia0: d0, dia1: diaFim };
      setRealce((a) => ({
        v,
        d0: ext.dia0,
        d1: Math.max(ext.dia0, ext.dia1),
        contagem: (a?.contagem ?? 0) + 1,
        ...(prev === undefined ? {} : { previsao: prev }),
      }));
      recalcular();
    },
    // ⚠️ `dados.subcontratados`, `linhasSub` e `formaSub.offsets` entram aqui porque o ramo do
    //    subcontratado os lê. Os dois primeiros **já eram lidos e não estavam na lista** — é a
    //    família do `gravar` que guardava os valores iniciais numa `closure` e gravava «a viagem
    //    toda» quando o planeador tinha escolhido dois dias.
    [foraDaFita, 
      dados.viaturas,
      dados.grelha,
      dados.subcontratados,
      linhasSub,
      formaSub.offsets,
      offsetsDias,
      offsetsLinhas,
      recalcular,
    ],
  );

  // 🔴 D-101 · a fita nova chegou de um «Ir para…» ou de um «Ver no mapa» fora da fita: leva-se o
  //    sítio à vista, pelas DATAS (ver `DestinoAoAbrir`). Depois do `irPara`, que ele chama.
  useEffect(() => {
    // 📗 2026-11-03 · Com a fita a CARREGAR este `Mapa` ainda é o da fita velha, e gastar a data aqui
    //    deixava o novo (a chave é `dados.inicio`) sem nada a mostrar: «Hoje» fora da fita recarregava
    //    e ficava no 1.º dia. A data espera (`data-ao-abrir-espera-pela-fita.test.tsx`).
    if (dataAoAbrir === null || aCarregarFita) return;
    const j = janelaDe(dados);
    const destino: DestinoAoAbrir = typeof dataAoAbrir === 'string' ? { data: dataAoAbrir } : dataAoAbrir;
    const i = indiceDaData(j, destino.data);
    const f = destino.dataFim === undefined ? i : indiceDaData(j, destino.dataFim);
    if (dentroDaJanela(j, i)) {
      if (destino.viaturaId !== undefined) irPara(destino.viaturaId, i, f);
      else if (destino.motoristaId !== undefined) irParaMotorista(destino.motoristaId, i, f);
      else irParaPeriodo(i, f);
    }
    aoMostrarDataAoAbrir?.();
  }, [dataAoAbrir, aCarregarFita, dados, irPara, irParaMotorista, irParaPeriodo, aoMostrarDataAoAbrir]);

  // ⚠️ A LIGAÇÃO AO MAPA (`ligacao-ao-mapa.ts`): fornecida UMA vez, à volta de toda a árvore, para
  //    as cinco aberturas do Diário mostrarem os dias e o «Ir para» sem as passar à mão.
  const ligacaoAoMapa = useMemo<LigacaoAoMapa>(
    () => ({
      verNoMapa: (viaturaId, dia0, dia1) => irPara(viaturaId, dia0, dia1),
      nomeDoDia: (dia) => dataCurtaDoDia(dados, dia),
      // 🔴 2026-10-30 · Pedido 1 · o número da frota, a mesma formulação do servidor.
      nomeDaViatura: (id) => nomeDoExecutor(id, nomesDosExecutoresDoMapa(dados)),
    }),
    [irPara, dados],
  );
  useEffect(() => {
    if (publicarLigacao === undefined) return undefined;
    publicarLigacao(ligacaoAoMapa);
    return () => publicarLigacao(null);
  }, [publicarLigacao, ligacaoAoMapa]);

  /**
   * ⚠️⚠️ **TRAZER A CÉLULA À VISTA — o mínimo, e não um salto.**
   *
   * 📌 **Não é o `irPara`**, e a diferença é a funcionalidade: aquele responde a «leva-me a um
   * sítio que não estou a ver» — recentra com um terço de folga e acende o realce de 9 s —, e
   * isto responde a «andei uma célula». Recentrar a cada tecla fazia o mapa saltar debaixo do
   * olho de quem percorre uma linha, e acender o realce a cada tecla transformava-o em
   * decoração, que é exactamente a razão de ele se apagar sozinho.
   *
   * ⚠️ **A geometria é a mesma de `alturaTotal`/`larguraTotal`:** as linhas começam em
   * `ALTURA_CABECALHO` e as colunas em `larguraViatura`, que é o espaço que o cabeçalho e a
   * coluna das viaturas ocupam em `position: sticky` — ou seja, a parte da janela onde uma
   * célula está tapada apesar de «visível».
   */
  const trazerAVista = useCallback(
    (v: number, d: number) => {
      const el = caixa.current;
      if (el === null) return;
      const y0 = ALTURA_CABECALHO + (offsetsLinhas[v] ?? 0);
      const y1 = ALTURA_CABECALHO + (offsetsLinhas[v + 1] ?? y0);
      const x0 = larguraViatura + (offsetsDias[d] ?? 0);
      const x1 = larguraViatura + (offsetsDias[d + 1] ?? x0);
      const dy = deslocarParaCaber(
        y0,
        y1,
        el.scrollTop + ALTURA_CABECALHO,
        el.scrollTop + el.clientHeight,
        y1 - y0,
      );
      const dx = deslocarParaCaber(
        x0,
        x1,
        el.scrollLeft + larguraViatura,
        el.scrollLeft + el.clientWidth,
        x1 - x0,
      );
      if (dy !== 0) el.scrollTop = Math.max(0, el.scrollTop + dy);
      if (dx !== 0) el.scrollLeft = Math.max(0, el.scrollLeft + dx);
      // ⚠️ O React 19 não reage a um `scroll` despachado por código (é a razão de
      //    `Mapa.janelaFixa` existir): sem isto, andar para fora da janela desenhada deixava
      //    a selecção numa zona em branco. O `irPara` chama-o pela mesma razão.
      if (dy !== 0 || dx !== 0) recalcular();
    },
    [offsetsDias, offsetsLinhas, larguraViatura, recalcular],
  );

  /**
   * O mesmo, para uma linha da secção dos **subcontratados**.
   *
   * ⚠️⚠️ **É a MESMA aritmética com outro vector de offsets, e não uma segunda.** A única
   * diferença é de onde a secção começa — a frota toda, mais o título —, e é isso que o C₂
   * tornou possível: antes as linhas tinham alturas diferentes por empresa **e** uma linha
   * podia ter vários serviços no mesmo dia, logo «a célula» não existia como objecto. 📌
   * `deslocarParaCaber` é o mesmo dos dois lados: duas fórmulas para a mesma pergunta
   * divergiriam, e a que divergisse seria a que ninguém corre.
   */
  const trazerAVistaSub = useCallback(
    (l: number, d: number) => {
      const el = caixa.current;
      if (el === null) return;
      const base =
        ALTURA_CABECALHO + (offsetsLinhas[dados.viaturas.length] ?? 0) + ALTURA_TITULO_SUB;
      const y0 = base + (formaSub.offsets[l] ?? 0);
      const y1 = base + (formaSub.offsets[l + 1] ?? y0);
      const x0 = larguraViatura + (offsetsDias[d] ?? 0);
      const x1 = larguraViatura + (offsetsDias[d + 1] ?? x0);
      const dy = deslocarParaCaber(
        y0,
        y1,
        el.scrollTop + ALTURA_CABECALHO,
        el.scrollTop + el.clientHeight,
        y1 - y0,
      );
      const dx = deslocarParaCaber(
        x0,
        x1,
        el.scrollLeft + larguraViatura,
        el.scrollLeft + el.clientWidth,
        x1 - x0,
      );
      if (dy !== 0) el.scrollTop = Math.max(0, el.scrollTop + dy);
      if (dx !== 0) el.scrollLeft = Math.max(0, el.scrollLeft + dx);
      if (dy !== 0 || dx !== 0) recalcular();
    },
    [
      dados.viaturas.length,
      formaSub.offsets,
      offsetsDias,
      offsetsLinhas,
      larguraViatura,
      recalcular,
    ],
  );

  /**
   * ⚠️⚠️ **AS SETAS MOVEM A SELECÇÃO — peça L do §12, medida a zero até hoje.**
   *
   * 📌 **UM ouvinte no `window`, como o `Ctrl+K`** — e não um por célula. São ~600 dias
   * desenhados por janela; um ouvinte por célula é o custo que o ADR-001 (risco R1) recusa, e é
   * a mesma razão pela qual o cartão do cursor usa um ouvinte delegado em vez de 700 cadeias
   * por quadro.
   *
   * ⚠️ **Só com selecção fixa.** Uma seta não INVENTA uma selecção: sem clique não há alvo, e
   * escolher um por omissão (a primeira viatura? o dia de hoje?) punha o painel de detalhe — e
   * o alvo de todos os botões — num serviço que ninguém apontou.
   *
   * ⚠️⚠️ **E TRATA O SUBCONTRATADO — o buraco que estava aqui declarado FECHOU com o C₂.** O que
   * estava escrito era: *«a secção tem geometria própria e uma linha pode ter vários serviços no
   * mesmo dia (D-002, M7: até 7)»*. 📌 **As duas razões morreram:** a geometria é agora um
   * vector de offsets acumulados como o da frota, e uma linha é **um serviço**, logo «a célula»
   * existe como objecto. O que resta é escolher o vector certo — e é literalmente isso que o
   * ramo abaixo faz, com a **mesma** `proximaLinha`, o **mesmo** `proximoDia` e o **mesmo**
   * `deslocarParaCaber`.
   *
   * 🅿️ **O que continua por fazer, e declara-se:** a seta **não atravessa** a fronteira entre a
   * frota e a secção. Atravessá-la é trocar de objecto de selecção (e de painel) sem ninguém
   * apontar para nada, e isso é uma decisão de desenho que não foi tomada.
   *
   * ✅⚠️⚠️ **E A SECÇÃO «EM ESPERA» FICA DE FORA POR DECISÃO MEDIDA (2026-09-16), não por
   * dívida.** Estava declarada no §2.1 da FILA v2 como *«as setas não navegam a secção «em
   * espera» — a barra responde ao clique, e um estado de selecção que só desbloqueia uma acção
   * é cerimónia»*. 📌 **A medição fecha-a, e o argumento decisivo não é o da cerimónia — é
   * aritmético:** um clique numa barra em espera **executa já a única acção que lá existe**
   * (abre «Trazer de volta…», ver `aoClicar`), logo **nunca existe um estado em que haja
   * selecção nessa secção e a seta tenha o que navegar**. E as setas não INVENTAM selecção nem
   * atravessam a fronteira (acima) — logo não há sequer por onde lá chegar com o teclado.
   *
   * ⚠️ **Fazê-las navegar exigia primeiro TIRAR a acção do clique** — pôr um passo a mais no
   * único gesto da secção — e depois um terceiro espaço de selecção, o seu `trazerAVista`
   * (a secção não é virtualizada: a geometria é `formaEspera.alturas` e não `offsetsLinhas`) e
   * um terceiro ramo de painel, para **um** verbo. Sai caro e deixa a operação pior.
   *
   * ⚠️ **E não é um problema de teclado só desta secção:** medido, o `Mapa.tsx` tem **zero**
   * `tabIndex` — nenhuma célula da frota nem dos subcontratados é alcançável pelo teclado. A
   * primeira selecção é sempre um clique. Pôr a barra em espera na ordem de tabulação fazia
   * dela a **única** excepção de um mapa uniforme, e no fim de um contentor de 250 linhas.
   * 📌 Se isto se resolver, resolve-se para o mapa inteiro — regra 8 — e não aqui.
   *
   * ✅ As provas estão em `a-espera-ve-se-no-mapa.test.tsx`, secção «as setas».
   *
   * ---- ⚠️⚠️ 2026-09-28 · OS SALTOS — Home/End, PageUp/PageDown, Enter/Esc (§12.4) ----------
   *
   * 📗 Medido em §12.9 do desenho: só as quatro setas e o `Ctrl+K` estavam feitos; os seis
   * saltos estavam **por fazer**, e três deles («não foram pedidas») estavam declarados assim
   * por engano — tinham passado a sê-lo e o comentário não tinha sido actualizado.
   *
   * ⚠️ **`Home`/`End` vão ao início/fim do SERVIÇO, não ao dia 1/365 do ano.** O §12.4 pergunta
   * as duas leituras possíveis sem escolher; a tabela «o que não precisa de CSS» do §12.9
   * já aponta a resposta ao nomear `extentoDaCorrida`/`extentoDaViagemQueParte` como as
   * primitivas a reutilizar — e é a leitura mais próxima do que uma folha de cálculo faz:
   * `Home`/`End` andam dentro do que está seleccionado, não saltam para a origem da folha
   * inteira. Numa célula livre (sem serviço), `extentoDaCorrida` devolve o próprio dia — e por
   * isso `Home`/`End` aí não fazem nada, deixando a tecla livre para o `Home` nativo da página.
   *
   * ⚠️ **`PageUp`/`PageDown` são "no ecrã" — o eixo VERTICAL (viaturas), não os dias.** É o que
   * uma folha de cálculo faz por omissão (`Page Down` desce um ecrã de LINHAS, mantendo a
   * coluna), e é o eixo com 250 posições — o que faz falta paginar. Reutiliza a MESMA
   * `proximaLinha` das setas com um passo maior (`linhasPorPagina`): se o alvo cair numa linha
   * escondida, salta-se outra página, a mesma regra que já existe para o passo de uma só linha
   * — duas formulações do "salta as escondidas" divergiriam.
   *
   * ⚠️ **`Enter` abre o Detalhe (`setPainelAberto(true)`) e `Esc` limpa a selecção** — as
   * MESMAS duas linhas que o botão «limpar» do painel já executa (ver `limpar`, abaixo, no
   * JSX): uma só formulação de "desseleccionar", partilhada pelo rato e pelo teclado.
   */
  useEffect(() => {
    const ouvir = (e: KeyboardEvent): void => {
      // ⚠️⚠️ **`Shift` SAIU DAQUI** — passou a ter o ramo próprio, abaixo, porque `Shift`+seta
      //    deixou de ser «não é nossa» (peça 1, §12.4/H). `Ctrl`/`Meta`/`Alt` continuam
      //    bloqueados tal como estavam: nenhuma das três teclas ganhou significado novo.
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // ⚠️ Reordenado: `oFocoEstaLivre` passa a correr ANTES do `passoDaTecla`, porque os
      //    saltos (abaixo) também precisam desta guarda e é a MESMA — duas leituras do foco
      //    divergiriam. Não muda o comportamento das setas: as duas condições são
      //    independentes, e tinham de passar as duas de qualquer forma.
      if (!oFocoEstaLivre(e.target)) return;

      // ---- ⚠️⚠️ PEÇA 1 · `Shift`+`←`/`→` ESTENDE A SELECÇÃO POR DIAS (§12.4, peça H) ---------
      //
      // ⚠️ Só as duas setas horizontais: `Shift`+`↑`/`↓`, `Shift`+`Home`/`End`/etc. caem aqui e
      //    não fazem nada — ver o comentário de `intervaloDias`, acima, sobre porquê.
      if (e.shiftKey) {
        const passoShift = passoDaTecla(e.key);
        if (passoShift === null || passoShift.dd === 0) return;
        if (seleccaoSub !== null || seleccao === null) return;
        // ⚠️⚠️ **Reaproveita a extensão em curso SÓ se a `ponta` dela ainda for `seleccao.d`.**
        //    É a mesma guarda que `intervaloSeleccionado` usa para se invalidar sozinho — aqui
        //    decide se há uma âncora para continuar ou se esta tecla começa uma extensão nova.
        const base =
          intervaloDias !== null &&
          intervaloDias.v === seleccao.v &&
          intervaloDias.ponta === seleccao.d
            ? intervaloDias
            : { v: seleccao.v, ancora: seleccao.d, ponta: seleccao.d };
        const novaPonta = proximoDia(base.ponta, passoShift.dd, dados.nDias);
        // No limite do ano a ponta não anda — a mesma guarda das setas simples, abaixo.
        if (novaPonta === base.ponta) return;
        e.preventDefault();
        setIntervaloDias({ v: base.v, ancora: base.ancora, ponta: novaPonta });
        setSeleccao({ v: base.v, d: novaPonta });
        trazerAVista(base.v, novaPonta);
        return;
      }

      // ---- ⚠️⚠️ Home/End, PageUp/PageDown, Enter/Esc — os SALTOS do §12.4 ------------------
      const salto = teclaDeSalto(e.key);
      if (salto !== null) {
        const el = caixa.current;
        // ---- ⚠️ PEÇA 2 · `Esc` LIMPA A COLUNA/LINHA, mesmo sem CÉLULA nenhuma seleccionada ---
        //
        //   §12.4-bis: *«`Esc` limpa sempre, seja qual for a forma»*. Sem este ramo, seleccionar
        //   só uma coluna ou uma linha (sem nunca ter clicado numa célula) fazia `Esc` cair nos
        //   `return` de baixo — `seleccao`/`seleccaoSub` continuavam `null` e nada limpava a
        //   banda. Fora daqui porque as duas secções abaixo já limpam `formaSeleccao` quando HÁ
        //   uma célula fixa — este é só o caso em que não há nenhuma.
        if (salto === 'Escape' && seleccaoSub === null && seleccao === null) {
          if (formaSeleccao === null) return;
          e.preventDefault();
          setFormaSeleccao(null);
          return;
        }
        // ---- A secção dos subcontratados, quando é ela a seleccionada ------------------
        if (seleccaoSub !== null) {
          if (salto === 'Home' || salto === 'End') {
            const linha = linhasSub[seleccaoSub.l]?.dias ?? [];
            const ext = extentoDoServico(linha, seleccaoSub.d);
            const d = salto === 'Home' ? ext.d0 : ext.d1;
            if (d === seleccaoSub.d) return;
            e.preventDefault();
            setSeleccaoSub({ l: seleccaoSub.l, d });
            trazerAVistaSub(seleccaoSub.l, d);
            return;
          }
          if (salto === 'PageUp' || salto === 'PageDown') {
            if (el === null) return;
            const passo =
              (salto === 'PageUp' ? -1 : 1) * linhasPorPagina(el.clientHeight, ALTURA_LINHA_SUB);
            const l = linhaDaPagina(seleccaoSub.l, passo, linhasSub.length, null);
            if (l === seleccaoSub.l) return;
            e.preventDefault();
            setSeleccaoSub({ l, d: seleccaoSub.d });
            trazerAVistaSub(l, seleccaoSub.d);
            return;
          }
          if (salto === 'Enter') {
            e.preventDefault();
            setPainelAberto(true);
            return;
          }
          // 'Escape' — limpa os dois, como o botão «limpar» do painel.
          e.preventDefault();
          setSeleccao(null);
          setSeleccaoSub(null);
          setFormaSeleccao(null); // ⚠️ Peça 2 · «limpa sempre, seja qual for a forma».
          return;
        }
        // ---- A frota --------------------------------------------------------------------
        if (seleccao === null) return;
        if (salto === 'Home' || salto === 'End') {
          const linha = dados.grelha[seleccao.v] ?? [];
          const ext = extentoDaCorrida(linha, seleccao.d);
          const d = salto === 'Home' ? ext.dia0 : ext.dia1;
          if (d === seleccao.d) return;
          e.preventDefault();
          setSeleccao({ v: seleccao.v, d });
          trazerAVista(seleccao.v, d);
          return;
        }
        if (salto === 'PageUp' || salto === 'PageDown') {
          if (el === null) return;
          const passo =
            (salto === 'PageUp' ? -1 : 1) * linhasPorPagina(el.clientHeight, ALTURA_LINHA);
          const v = linhaDaPagina(seleccao.v, passo, dados.viaturas.length, indicesEscondidos);
          if (v === seleccao.v) return;
          e.preventDefault();
          setSeleccao({ v, d: seleccao.d });
          setSeleccaoSub(null);
          trazerAVista(v, seleccao.d);
          return;
        }
        if (salto === 'Enter') {
          e.preventDefault();
          setPainelAberto(true);
          return;
        }
        // 'Escape'
        e.preventDefault();
        setSeleccao(null);
        setSeleccaoSub(null);
        setFormaSeleccao(null); // ⚠️ Peça 2 · «limpa sempre, seja qual for a forma».
        return;
      }

      // ---- as quatro setas (inalterado) ----------------------------------------------------
      const passo = passoDaTecla(e.key);
      if (passo === null) return;
      // ---- A secção dos subcontratados, quando é ela a seleccionada --------
      if (seleccaoSub !== null) {
        // ⚠️ **Sem `escondidas`:** nenhuma linha desta secção se esconde. Uma linha só existe
        //    porque um serviço a ocupa em algum dia do ano, e a empresa sem serviço nenhum fica
        //    com a sua — logo não há linha de altura zero por onde a selecção pudesse parar.
        const l = proximaLinha(seleccaoSub.l, passo.dv, linhasSub.length, null);
        const dS = proximoDia(seleccaoSub.d, passo.dd, dados.nDias);
        if (l === seleccaoSub.l && dS === seleccaoSub.d) return;
        e.preventDefault();
        setSeleccaoSub({ l, d: dS });
        trazerAVistaSub(l, dS);
        return;
      }
      if (seleccao === null) return;
      const v = proximaLinha(seleccao.v, passo.dv, dados.viaturas.length, indicesEscondidos);
      const d = proximoDia(seleccao.d, passo.dd, dados.nDias);
      // ⚠️ **Só depois de haver para onde ir.** No limite do ano ou da frota, `preventDefault`
      //    sem movimento nenhum comia a tecla e deixava a página sem a rolagem que ela faria —
      //    um gesto que não faz nada e impede o que fazia.
      if (v === seleccao.v && d === seleccao.d) return;
      e.preventDefault();
      setSeleccao({ v, d });
      // Uma selecção nova na frota larga a do subcontratado, como o clique já faz: com as duas
      // fixas, o painel mostrava o detalhe de uma e as acções da outra.
      setSeleccaoSub(null);
      trazerAVista(v, d);
    };
    window.addEventListener('keydown', ouvir);
    return () => window.removeEventListener('keydown', ouvir);
  }, [
    seleccao,
    seleccaoSub,
    // ⚠️ Peça 1 · lido no ramo do `Shift` (a `base` da extensão) — sem isto, a closure guardava
    //    o `intervaloDias` da primeira renderização, e um `Shift`+seta a meio de uma extensão
    //    já em curso reiniciava-a sempre em vez de a continuar. É a mesma família do `gravar`
    //    memoizado sem `condDia0`/`condDia1` (2026-08-12): nada estoura, o gesto fica errado.
    intervaloDias,
    // ⚠️ Peça 2 · lido só no ramo do `Escape` sem célula — sem isto, um `Esc` a meio de uma
    //    selecção de coluna/linha só limpava na PRÓXIMA renderização, e o teste que o prova
    //    lê o DOM logo a seguir ao `fireEvent`.
    formaSeleccao,
    linhasSub,
    dados.viaturas.length,
    dados.nDias,
    dados.grelha,
    indicesEscondidos,
    trazerAVista,
    trazerAVistaSub,
  ]);

  /**
   * ⚠️⚠️ **§12.6 · O FOCO DO DOM SEGUE A SELECÇÃO — a outra metade da roving tabindex.**
   *
   * 📌 As setas já funcionam sem foco nenhum (o ouvinte está no `window`, não em cada célula),
   * mas SEM ISTO o `tabIndex="0"` da célula seleccionada era decoração: um leitor de ecrã só
   * anuncia `role`/`aria-selected` da célula que TEM o foco, e sem mover o foco a cada tecla o
   * anel do navegador ficava preso na primeira célula enquanto a selecção lógica avançava —
   * quem usa `Tab` para entrar na grelha deixava de conseguir segui-la com as setas.
   *
   * ⚠️ **`preventScroll: true`.** `trazerAVista`/`irPara` já decidiram quanto deslocar (com a
   * folga de contexto que lhes é própria); sem isto o `focus()` do navegador tentava centrar a
   * célula outra vez, por cima do scroll já feito — dois algoritmos a decidir a mesma posição.
   *
   * ⚠️⚠️ **Exclui a cópia `linha-fixada`** (`closest`): ela tem os MESMOS `data-v`/`data-d` da
   * linha real (é a mesma viatura, desenhada duas vezes para «comparar facilmente»), mas nunca
   * recebe `diaSeleccionado` — sem esta exclusão, `querySelectorAll` podia devolver a cópia
   * decorativa primeiro (vem antes no DOM) e focar um nó que a roving tabindex não governa.
   *
   * ⚠️ **Peça 3 (2026-09-28) fecha o 🅿️ que aqui estava** — «só a frota» deixou de ser
   * verdade: ver o segundo `useEffect`, abaixo, para os subcontratados.
   */
  useEffect(() => {
    if (seleccao === null) return;
    const candidatos = caixa.current?.querySelectorAll<HTMLElement>(
      `[data-v="${String(seleccao.v)}"][data-d="${String(seleccao.d)}"]`,
    );
    const alvo = candidatos === undefined ? null : [...candidatos].find(
      (x) => x.closest('.linha-fixada') === null,
    );
    if (alvo !== undefined && alvo !== null) alvo.focus({ preventScroll: true });
  }, [seleccao]);

  /**
   * ---- ⚠️⚠️ PEÇA 3 · O FOCO SEGUE `seleccaoSub` — o espelho do efeito acima (2026-09-28) -----
   *
   * ⚠️⚠️ **NÃO é `[data-d="d"]`, e é a diferença que justifica um efeito à parte.** Na frota
   * uma célula é um dia; aqui uma `.sub-barra` cobre um INTERVALO (`data-d`/`data-d1` são o
   * `d0`/`d1` do serviço), e `seleccaoSub.d` pode cair em qualquer dia desse intervalo — não só
   * no primeiro. Sem procurar pelo intervalo, o foco ficava preso na PRIMEIRA barra da linha
   * sempre que a selecção avançasse dentro da mesma barra, que é exactamente o defeito que o
   * efeito acima existe para não ter na frota.
   */
  useEffect(() => {
    if (seleccaoSub === null) return;
    const candidatos = caixa.current?.querySelectorAll<HTMLElement>(
      `[data-sl="${String(seleccaoSub.l)}"]`,
    );
    const alvo =
      candidatos === undefined
        ? null
        : [...candidatos].find((x) => {
            const d0 = Number(x.dataset['d']);
            const d1 = Number(x.dataset['d1']);
            return seleccaoSub.d >= d0 && seleccaoSub.d <= d1;
          });
    if (alvo !== undefined && alvo !== null) alvo.focus({ preventScroll: true });
  }, [seleccaoSub]);

  // ⚠️ **Apaga-se sozinho.** 9 s: o suficiente para o olho o encontrar depois do salto, e
  //    pouco para não ficar a competir com a selecção seguinte.
  useEffect(() => {
    if (realce === null) return undefined;
    const t = window.setTimeout(() => setRealce(null), 9000);
    return () => window.clearTimeout(t);
  }, [realce]);

  // O mesmo temporizador para a banda do período: um realce permanente vira decoração.
  useEffect(() => {
    if (realcePeriodo === null) return undefined;
    const t = window.setTimeout(() => setRealcePeriodo(null), 9000);
    return () => window.clearTimeout(t);
  }, [realcePeriodo]);

  // Redimensionar muda quantas linhas e colunas cabem, logo a janela tem de ser
  // recalculada. Sem isto, esticar a linha deixava metade do ecrã em branco até ao
  // próximo deslocamento — o conteúdo cresce, a janela desenhada não.
  useEffect(() => {
    recalcular();
  }, [recalcular]);

  // `useLayoutEffect` e não `useEffect`: corre antes de o navegador pintar, o que
  // permite forçar o layout e medir o custo real.
  useLayoutEffect(() => {
    if (marca.current === null) return;
    const el = caixa.current;
    if (el !== null) void el.offsetHeight;
    const decorrido = performance.now() - marca.current;
    marca.current = null;

    // Descartar a PRIMEIRA janela: vem do efeito de montagem, corre já dentro do
    // ciclo do React e mede quase zero. Incluí-la fazia a barra mostrar
    // «p95 0,0 ms» antes de haver qualquer deslocamento — um número tranquilizador
    // e sem significado, que é o pior tipo de número.
    if (!primeiroMedido.current) {
      primeiroMedido.current = true;
      return;
    }

    const q = quadros.current;
    q.push(decorrido);
    if (q.length > 200) q.shift();
    const ordenado = [...q].sort((a, b) => a - b);
    setP95(ordenado[Math.min(ordenado.length - 1, Math.floor(ordenado.length * 0.95))] ?? null);
  }, [janela]);

  useEffect(() => {
    recalcular();
    const el = caixa.current;
    if (el === null || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(() => recalcular());
    observador.observe(el);
    return () => observador.disconnect();
  }, [recalcular]);

  // Delegação: um só ouvinte para todos os dias visíveis, em vez de um por dia.
  const aoPassar = useCallback((evento: EventoRato<HTMLDivElement>) => {
    // Durante um arrasto de medida o cartão de resumo não deve seguir o cursor: o
    // arrasto é justamente o caminho onde não se pode voltar a desenhar por pixel.
    if (arrastando.current) return;
    // Os subcontratados primeiro: as suas barras não têm `data-v`, logo sem este ramo o
    // cursor por cima delas limpava o cartão e o serviço não tinha detalhe nenhum.
    const barraSub = (evento.target as HTMLElement).closest<HTMLElement>('[data-sl]');
    if (barraSub !== null) {
      const iLinha = Number(barraSub.dataset['sl']);
      const dSub = Number(barraSub.dataset['d']);
      const d1Sub = Number(barraSub.dataset['d1'] ?? barraSub.dataset['d']);
      const rSub = barraSub.getBoundingClientRect();
      setSobre(null);
      setSobreSub((a) =>
        a?.l === iLinha && a.d === dSub && a.d1 === d1Sub
          ? a
          : { l: iLinha, d: dSub, d1: d1Sub, x: rSub.left, y: rSub.bottom },
      );
      return;
    }
    setSobreSub(null);
    const alvo = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
    if (alvo === null) {
      setSobre(null);
      return;
    }
    const v = Number(alvo.dataset['v']);
    const d = Number(alvo.dataset['d']);
    const r = alvo.getBoundingClientRect();
    setSobre((a) => (a?.v === v && a.d === d ? a : { v, d, x: r.left, y: r.bottom }));
  }, []);

  const aoSair = useCallback(() => {
    setSobre(null);
    setSobreSub(null);
  }, []);

  const aoClicar = useCallback((evento: EventoRato<HTMLDivElement>) => {
    // ---- 🔴 EM ESPERA: clicar abre o INVERSO, e é a única acção que existe -------------
    //
    // ⚠️⚠️ **Sem selecção intermédia, e é uma decisão.** Na frota o clique fixa a célula e o
    //   painel oferece dezoito gestos; sobre uma viagem em espera há **um**: trazê-la de volta.
    //   Um estado de selecção que só serve para desbloquear uma acção é cerimónia — e uma barra
    //   que fica seleccionada sem nada acontecer lê-se como barra avariada.
    //
    // ⚠️⚠️ **O INTERVALO SAI DE `extentoDoServico` E NÃO DA BARRA.** A barra vem de
    //   `barrasDaLinhaSub(linha, janela.dia0, janela.dia1)` — está **cortada pela janela**. Usar
    //   o `data-d1` dela trazia de volta só os dias visíveis e deixava o resto da viagem em
    //   espera, com o ecrã a dizer que a tinha devolvido: *«uma escrita parcial que se anuncia
    //   inteira»*. `extentoDoServico` corre sobre o ANO, que é onde a viagem vive.
    const barraEspera = (evento.target as HTMLElement).closest<HTMLElement>('[data-el]');
    if (barraEspera !== null) {
      const iLinha = Number(barraEspera.dataset['el']);
      const dia = Number(barraEspera.dataset['d']);
      const dias = linhasEspera[iLinha]?.dias;
      if (dias === undefined) return;
      const ext = extentoDoServico(dias, dia);
      const c = (dias[dia] ?? [])[0];
      setATrazerDeEspera({
        // ⚠️ A referência vai como o plano a tem — `''` incluído. `trazerDeEspera` exige-a
        //   (sem executor não há por onde desambiguar duas viagens nos mesmos dias), e o
        //   servidor recusa com 400 `alvo_incompleto` em voz alta se ela faltar. Inventar aqui
        //   um valor plausível era pior do que a recusa.
        referencia: c === undefined ? '' : (dados.corridas[c[10]] ?? ''),
        dia0: ext.d0,
        dia1: ext.d1,
      });
      return;
    }
    // ⚠️⚠️ **Os subcontratados primeiro.** As barras deles nao tem `data-v`, logo sem este ramo
    //   o clique caia no `return` e a barra nao respondia a nada.
    const barraSub = (evento.target as HTMLElement).closest<HTMLElement>('[data-sl]');
    if (barraSub !== null) {
      const iLinha = Number(barraSub.dataset['sl']);
      const dSub = Number(barraSub.dataset['d']);
      setSeleccao(null);
      // Clicar outra vez no mesmo desmarca — a mesma regra da frota.
      setSeleccaoSub((a) => (a?.l === iLinha && a.d === dSub ? null : { l: iLinha, d: dSub }));
      setPainelAberto(true);
      return;
    }
    const alvo = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
    if (alvo === null) return;
    // ⚠️⚠️ Selecccionar uma celula da frota limpa o subcontratado: com os dois fixos, o painel
    //   tinha de escolher qual mostrar, e qualquer escolha estaria errada metade das vezes.
    setSeleccaoSub(null);
    const v = Number(alvo.dataset['v']);
    const d = Number(alvo.dataset['d']);
    // Clicar outra vez no mesmo dia desmarca — senão não haveria como desmarcar.
    setSeleccao((a) => (a?.v === v && a.d === d ? null : { v, d }));
    // ⚠️⚠️ **A lista de dependências deixou de ser `[]` com o ramo da espera, e isso NÃO é
    //   burocracia:** `linhasEspera` e `dados.corridas` são lidos lá dentro, e uma `closure`
    //   que guarda os valores iniciais é exactamente o defeito de 2026-08-12 — *«marquei só
    //   dois dias e ele mudou os dias todos»*, em que `gravar` era um `useCallback` sem as
    //   duas dependências que mexia. Nada estoura, nada avisa, e o ecrã diz que gravou.
  }, [linhasEspera, dados.corridas]);

  /**
   * ⚠️⚠️ **O MENU DE CONTEXTO NA GRELHA — 📗 fonte §4.8, e não existia.**
   *
   *   📗 *«O mapa deve permitir tarefas com botão do lado direito do rato (menu de contexto)
   *   por ser uma forma rápida de iteração com o mapa.»*
   *
   *   Era um requisito escrito da fonte que estava **por implementar**: o botão direito
   *   existia na lista de pedidos e no mapa não fazia nada — abria o menu do navegador.
   *
   * ⚠️ **O clique direito FIXA a selecção**, e não é detalhe: as acções do painel só
   *   aparecem com selecção fixa, precisamente porque um botão que muda de alvo a cada
   *   célula é um convite a clicar no serviço errado. O menu abre sobre o que ficou fixo.
   *
   * 📌 **Não há acções novas.** As entradas do menu são as mesmas do painel de detalhe, no
   *   mesmo âmbito — um segundo caminho com metade das garantias era o erro a evitar.
   */
  const aoMenuContexto = useCallback((evento: React.MouseEvent<HTMLDivElement>) => {
    const alvo = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
    if (alvo === null) return;
    const v = Number(alvo.dataset['v']);
    const d = Number(alvo.dataset['d']);
    if (!Number.isFinite(v) || !Number.isFinite(d)) return;
    evento.preventDefault();
    setSeleccao({ v, d });
    setMenuCtx({ v, d, x: evento.clientX, y: evento.clientY });
  }, []);

  /**
   * Aceita o arrasto e realça a linha por baixo do cursor.
   *
   * ⚠️ `preventDefault()` é o que **autoriza** o largar: sem ele o navegador recusa o
   * `drop` e o gesto morre sem explicação nenhuma. E só se autoriza quando o que passa
   * por cima é um pedido nosso — senão a grelha aceitava ficheiros e texto de fora.
   */
  /**
   * ⚠️⚠️ **B30 / T-74 · O INÍCIO DO ARRASTO DE UM BLOCO — um só ouvinte, delegado.**
   *
   * 📗 Cliente, 2026-08-30: *«mover facilmente»*.
   *
   * 📌 **Delegado e não por barra**, pela mesma razão dos punhos de redimensionar: são ~280
   * barras por janela, e um `onDragStart` em cada uma era uma função nova por barra em cada
   * redesenho. Aqui é uma, e as barras só trazem `data-mover-*`.
   */
  const aoComecarArrasto = useCallback((evento: React.DragEvent<HTMLDivElement>) => {
    // ⚠️ **Um redimensionamento em curso ganha ao arrasto.** Sem isto, agarrar a borda de uma
    //   coluna sobre um dia com serviço podia iniciar um arrasto de serviço em vez de
    //   redimensionar — dois gestos no mesmo pixel, e o que perde é o que se estava a fazer.
    //   ⚠️ §11 · M · a pega da barra é a MESMA corrida: agarrar a ponta é sobre a própria célula
    //   `draggable`, logo sem esta guarda o `dragstart` competia com o esticar/encurtar.
    if (arrasto.current !== null || arrastoBarra.current !== null) {
      evento.preventDefault();
      return;
    }
    const celula = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
    if (celula === null) return;
    const v = Number(celula.dataset['v']);
    const d = Number(celula.dataset['d']);
    if (!Number.isFinite(v) || !Number.isFinite(d)) return;
    const linha = dados.grelha[v] ?? [];
    const c = linha[d];
    // ⚠️ Sem serviço não há nada para mover — e o `draggable` já não está lá, logo isto é a
    //   rede por baixo e não a guarda principal.
    if (c == null || !(c[0] >= 0)) {
      evento.preventDefault();
      return;
    }
    // 📌 **A MESMA função do menu de contexto** (`extentoDaViagem`): mover é sobre a viagem
    //   inteira, e uma segunda forma de a calcular divergiria no primeiro caso de rotação — que
    //   foi exactamente o que aconteceu (2026-09-29): o dia de rotação em que ela parte ficava
    //   para trás. A referência já vai explícita, logo o dia com duas viagens move só a nossa.
    const ext = extentoDaViagem(linha, d);
    setServicoEmArrasto(true);
    iniciarArrastoDeServico(evento.dataTransfer, {
      viatura: v,
      diaAgarrado: d,
      dia0: ext.dia0,
      dia1: ext.dia1,
      referencia: dados.corridas[c[10]] ?? '',
      sigla: dados.clientes[c[0]]?.[0] ?? '',
    });
  }, []);

  const aoArrastarPorCima = useCallback((evento: React.DragEvent<HTMLDivElement>) => {
    // ⚠️ **AS DUAS FAMÍLIAS, e o cursor diz qual é:** `copy` cria uma reserva, `move` tira o
    //   serviço da linha de origem. 📌 Prometer `copy` num gesto que MOVE era o navegador a
    //   descrever mal o que vai acontecer, antes de haver janela nenhuma a explicá-lo.
    // 🔴 2026-10-08 · uma viagem EM ESPERA arrastada para uma linha de viatura.
    if (eArrastoDeEspera(evento.dataTransfer)) {
      const alvoE = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
      if (alvoE === null) return;
      evento.preventDefault();
      evento.dataTransfer.dropEffect = 'move';
      const vE = Number(alvoE.dataset['v']);
      setAlvoArrasto((a) => (a === vE ? a : vE));
      return;
    }
    if (eArrastoDeServico(evento.dataTransfer)) {
      evento.preventDefault();
      evento.dataTransfer.dropEffect = 'move';
      const alvoS = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
      const vS = alvoS === null ? -1 : Number(alvoS.dataset['v']);
      setAlvoArrasto((a) => (a === vS ? a : vS));
      return;
    }
    if (!eArrastoDePedido(evento.dataTransfer)) return;
    evento.preventDefault();
    evento.dataTransfer.dropEffect = 'copy';
    const alvo = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
    const v = alvo === null ? -1 : Number(alvo.dataset['v']);
    setAlvoArrasto((a) => (a === v ? a : v));
  }, []);

  const aoSairDoArrasto = useCallback(() => setAlvoArrasto(-1), []);

  const aoLargar = useCallback((evento: React.DragEvent<HTMLDivElement>) => {
    setAlvoArrasto(-1);
    setServicoEmArrasto(false);
    // 🔴 2026-10-08 · a viagem em espera largada numa viatura → o «Trazer de volta», já escolhida.
    const espera = esperaArrastadaDe(evento.dataTransfer);
    if (espera !== null) {
      evento.preventDefault();
      const alvoE = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
      const v = alvoE === null ? Number.NaN : Number(alvoE.dataset['v']);
      if (!Number.isFinite(v)) return;
      setATrazerDeEspera({ ...espera, v });
      return;
    }
    const servico = servicoArrastadoDe(evento.dataTransfer);
    if (servico !== null) {
      evento.preventDefault();
      // 🔴 2026-10-08 · largado na secção «Em espera» → o MESMO «Pôr em espera» do painel, sobre
      //    o dia agarrado (a janela resolve a viagem inteira a partir dele, como no painel).
      if ((evento.target as HTMLElement).closest('[data-zona-espera]') !== null) {
        setAPorEmEspera({ v: servico.viatura, d: servico.diaAgarrado });
        return;
      }
      const alvoS = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
      if (alvoS === null) return;
      const destino = Number(alvoS.dataset['v']);
      // ⚠️⚠️ **LARGAR NA PRÓPRIA LINHA NÃO É UM MOVIMENTO** — e não se recusa em voz
      //   alta: não houve engano nenhum, o planeador começou a arrastar e desistiu. 📌 Abrir
      //   uma janela a dizer «vai mover da 12 para a 12» era o sistema a inventar um gesto.
      if (!Number.isFinite(destino) || destino === servico.viatura) return;
      setAMoverBloco({ servico, destino });
      return;
    }
    const pedido = pedidoArrastadoDe(evento.dataTransfer);
    if (pedido === null) return;
    evento.preventDefault();
    const alvo = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
    if (alvo === null) return;
    setLargado({
      pedido,
      viatura: Number(alvo.dataset['v']),
      dia: Number(alvo.dataset['d']),
    });
  }, []);

  // ---- redimensionar colunas e linhas, arrastando a borda -----------------
  //
  // Pedido do cliente: *«quero que seja possível, tal como acontece no Excel,
  // alargar ou apertar uma coluna ou linha, porque algumas estão muito apertadas e
  // não dá para ver tudo»*.
  //
  // ⚠️ **Uma coluna de cada vez, uma linha de cada vez** — como no Excel. A
  // primeira versão mexia em todas ao mesmo tempo e o cliente recusou-a depois de
  // experimentar. O que se guarda é a EXCEPÇÃO do índice arrastado.
  //
  // ⚠️ Um só par de ouvintes em `.mapa-conteudo`, por delegação, e o alvo
  // reconhece-se pela proximidade à borda — como numa folha de cálculo. Punhos
  // dedicados seriam ~57 nós por janela (40 dias + 17 linhas), ou seja ~2 % do
  // orçamento de nós gasto em punhos. As tiras são pseudo-elementos: dão o cursor
  // e o alvo de rato sem entrar na contagem.
  //
  // ⚠️ E aqui está a contrapartida que a versão global não tinha, declarada: com
  // medidas por índice a geometria deixa de ser uma multiplicação, logo **não há
  // variável CSS única para escrever durante o arrasto**. Mudar uma coluna desloca
  // todas as barras à sua direita, e essas posições são calculadas em JavaScript.
  // Por isso o arrasto passa pelo estado do React — mas **no máximo uma vez por
  // quadro de animação**, senão seria um redesenho por píxel de ponteiro.
  const arrasto = useRef<{
    eixo: 'x' | 'y';
    indice: number;
    pos: number;
    base: number;
  } | null>(null);
  const quadroPedido = useRef<number | null>(null);
  const porAplicar = useRef<{ eixo: 'x' | 'y'; indice: number; valor: number } | null>(null);

  // ---- §11 · M · esticar/encurtar uma viagem arrastando a ponta da barra --------------------
  //
  // ⚠️⚠️ **A BARRA NÃO RECEBE RATO** — vive em `.camada`, `pointer-events: none` (ver B30, acima
  //   no ficheiro). O punho é a CÉLULA do dia, como o arrasto de mover a viagem inteira já usa —
  //   e é por isso que este gesto entra no MESMO par de ouvintes delegados do redimensionar de
  //   colunas/linhas (`aoDescerMedida`/`aoSubirMedida`, em `.mapa-conteudo`): não há um segundo
  //   `onPointerDown` por célula (~600 por janela) nem por barra (~280).
  //
  // ⚠️ **Ref PRÓPRIA e não uma variante de `arrasto`** — são geometrias diferentes (`arrasto` é
  //   índice + delta em píxeis; isto é viatura + corrida + ponta), e uma união só complicava os
  //   dois ramos que já existiam. `aoMoverMedida` não precisa de saber desta ref: com `arrasto`
  //   a `null`, já devolve sem fazer nada — não há visualização ao vivo (a confirmação faz-se na
  //   janela de datas que se abre ao largar, não numa barra fantasma).
  const arrastoBarra = useRef<{
    readonly ponta: PontaDaBarra;
    readonly dia0Original: number;
    readonly dia1Original: number;
    readonly pedidoIndice: number;
    /** `clientX` que corresponde à posição 0 do eixo dos dias (`offsetsDias`). */
    readonly origemX: number;
  } | null>(null);

  /**
   * A borda debaixo do ponteiro, se houver: que eixo e **que índice**.
   *
   * O índice vem do `data-*` do elemento e não de contas com coordenadas: com
   * medidas por índice, deduzi-lo de píxeis seria repetir a busca binária num sítio
   * onde ela pode divergir da que desenha.
   */
  const bordaSob = (
    alvo: EventTarget | null,
    x: number,
    y: number,
  ): { eixo: 'x' | 'y'; indice: number } | null => {
    const el = alvo as HTMLElement | null;
    if (el === null) return null;
    const cab = el.closest<HTMLElement>('.dia-cab');
    if (cab !== null) {
      if (x < cab.getBoundingClientRect().right - LARGURA_TIRA) return null;
      return { eixo: 'x', indice: Number(cab.dataset['dia']) };
    }
    const via = el.closest<HTMLElement>('.viatura');
    if (via !== null) {
      if (y < via.getBoundingClientRect().bottom - LARGURA_TIRA) return null;
      return { eixo: 'y', indice: Number(via.dataset['linha']) };
    }
    return null;
  };

  /** Substitui uma medida, devolvendo o mesmo mapa se nada mudou. */
  const comMedida = (
    m: ReadonlyMap<number, number>,
    indice: number,
    valor: number,
  ): ReadonlyMap<number, number> => {
    if (m.get(indice) === valor) return m; // sem alteração, sem redesenho
    const novo = new Map(m);
    novo.set(indice, valor);
    return novo;
  };

  const aoDescerMedida = (evento: EventoPonteiro<HTMLDivElement>): void => {
    const borda = bordaSob(evento.target, evento.clientX, evento.clientY);
    if (borda !== null && !Number.isNaN(borda.indice)) {
      evento.preventDefault();
      evento.currentTarget.setPointerCapture(evento.pointerId);
      arrasto.current = {
        eixo: borda.eixo,
        indice: borda.indice,
        pos: borda.eixo === 'x' ? evento.clientX : evento.clientY,
        base:
          borda.eixo === 'x'
            ? tamanho(offsetsDias, borda.indice)
            : tamanho(offsetsLinhas, borda.indice),
      };
      arrastando.current = true;
      setSobre(null);
      return;
    }

    // ---- §11 · M · a pega da barra --------------------------------------------------------
    //   ⚠️ `bordaSob` acima só reconhece `.dia-cab` (cabeçalho) e `.viatura` (coluna fixa) — a
    //   célula do dia (`[data-v]`) não é filha de nenhum dos dois, logo chegar aqui com uma
    //   célula por baixo do ponteiro é seguro: os dois gestos nunca competem pelo mesmo pixel.
    const celula = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
    if (celula === null) return;
    const v = Number(celula.dataset['v']);
    const d = Number(celula.dataset['d']);
    if (!Number.isFinite(v) || !Number.isFinite(d)) return;
    const linha = dados.grelha[v] ?? [];
    const candidatas = candidatasDaBarra(linha, d);
    if (candidatas.length === 0) return; // dia vazio, oficina, ou sem pedido — nada para esticar
    const rect = celula.getBoundingClientRect();
    const resultado = pegaDaBarra({
      dia: d,
      xNaCelula: evento.clientX - rect.left,
      yNaCelula: evento.clientY - rect.top,
      larguraCelula: rect.width,
      alturaBandaItinerario: bandas(tamanho(offsetsLinhas, v)).itinerario,
      larguraTira: LARGURA_TIRA,
      candidatas,
    });
    if (resultado.tipo === 'ambigua') {
      // ⚠️ Um dia de rotação em que a viagem que parte tem UM dia só: o fim de uma e o fim (ou
      //   início) da outra caem na mesma borda, e não há como saber qual se quer pela posição
      //   do rato. Recusa-se — não se escolhe ao acaso — e diz-se (instrução da tarefa).
      evento.preventDefault();
      setAvisoDaPegaDaBarra(
        'Este dia tem duas viagens no mesmo sítio (dia de rotação) e não dá para saber qual ' +
          'se quer esticar por aqui. Mude as datas pelo Detalhe da viagem.',
      );
      return;
    }
    if (resultado.tipo === 'nenhuma') return;
    evento.preventDefault();
    evento.currentTarget.setPointerCapture(evento.pointerId);
    arrastoBarra.current = {
      ponta: resultado.ponta,
      dia0Original: resultado.corrida.dia0,
      dia1Original: resultado.corrida.dia1,
      pedidoIndice: resultado.corrida.pedidoIndice,
      origemX: rect.left - (offsetsDias[d] ?? 0),
    };
    arrastando.current = true;
    setSobre(null);
    // ⚠️ A MESMA pista visual que a coluna/linha já dão — cursor de redimensionar durante o
    //   gesto. Não há pega estática por CSS (ver o cabeçalho do ficheiro `pega-da-barra.ts` e o
    //   relatório desta tarefa): `::before`/`::after` de `.dia` já servem `dia--fronteira-troco`
    //   e `dia--sem-trabalho`, e um dia pode ser as duas coisas ao mesmo tempo que uma ponta de
    //   viagem — reutilizá-los aqui arriscava apagar um dos dois sinais em silêncio.
    document.body.style.cursor = 'ew-resize';
  };

  const aoMoverMedida = (evento: EventoPonteiro<HTMLDivElement>): void => {
    const a = arrasto.current;
    if (a === null) return;
    const delta = (a.eixo === 'x' ? evento.clientX : evento.clientY) - a.pos;
    const [minimo, maximo] =
      a.eixo === 'x'
        ? [LARGURA_DIA_MIN, LARGURA_DIA_MAX]
        : [ALTURA_LINHA_MIN, ALTURA_LINHA_MAX];
    porAplicar.current = {
      eixo: a.eixo,
      indice: a.indice,
      valor: Math.round(Math.max(minimo, Math.min(maximo, a.base + delta))),
    };
    if (quadroPedido.current !== null) return; // já há um quadro à espera
    quadroPedido.current = requestAnimationFrame(() => {
      quadroPedido.current = null;
      const p = porAplicar.current;
      if (p === null) return;
      if (p.eixo === 'x') setLarguraDias((m) => comMedida(m, p.indice, p.valor));
      else setAlturaLinhas((m) => comMedida(m, p.indice, p.valor));
    });
  };

  const aoSubirMedida = (evento: EventoPonteiro<HTMLDivElement>): void => {
    // ---- §11 · M · largar a pega da barra ---------------------------------------------------
    //   ⚠️ Verifica-se PRIMEIRO e sai: `arrasto.current` está a `null` neste gesto (é uma ref
    //   diferente), logo o ramo de baixo devolveria de imediato sem fazer nada — mas o `return`
    //   explícito é o que documenta que os dois não se misturam.
    if (arrastoBarra.current !== null) {
      const ab = arrastoBarra.current;
      evento.currentTarget.releasePointerCapture(evento.pointerId);
      arrastoBarra.current = null;
      arrastando.current = false;
      document.body.style.cursor = '';
      const contentX = evento.clientX - ab.origemX;
      const diaAlvo = indiceEm(offsetsDias, contentX);
      const novo = novoIntervaloAposArrasto(
        { dia0: ab.dia0Original, dia1: ab.dia1Original },
        ab.ponta,
        diaAlvo,
      );
      // Um clique sem arrasto real (ou um arrasto que volta ao ponto de partida) não abre nada
      // — é a rede que impede o «cliquei sem querer e apareceu uma janela».
      if (novo.dia0 === ab.dia0Original && novo.dia1 === ab.dia1Original) return;
      const pedidoId = dados.pedidos[ab.pedidoIndice] ?? '';
      if (pedidoId === '') return; // rede por baixo — `candidatasDaBarra` já filtra isto
      setArrastoDeDatas({
        pedidoId,
        inicio: dataIsoDoDia(janelaDe(dados), ab.dia0Original),
        fim: dataIsoDoDia(janelaDe(dados), ab.dia1Original),
        propostoInicio: dataIsoDoDia(janelaDe(dados), novo.dia0),
        propostoFim: dataIsoDoDia(janelaDe(dados), novo.dia1),
      });
      return;
    }

    if (arrasto.current === null) return;
    evento.currentTarget.releasePointerCapture(evento.pointerId);
    arrasto.current = null;
    arrastando.current = false;
    // O último movimento pode ter ficado num quadro que ainda não correu; aplicá-lo
    // aqui garante que o valor final é o que o ponteiro deixou, e não o penúltimo.
    if (quadroPedido.current !== null) {
      cancelAnimationFrame(quadroPedido.current);
      quadroPedido.current = null;
    }
    const p = porAplicar.current;
    porAplicar.current = null;
    if (p === null) return;
    if (p.eixo === 'x') setLarguraDias((m) => comMedida(m, p.indice, p.valor));
    else setAlturaLinhas((m) => comMedida(m, p.indice, p.valor));
  };

  useEffect(
    () => () => {
      if (quadroPedido.current !== null) cancelAnimationFrame(quadroPedido.current);
    },
    [],
  );

  /**
   * Duplo clique na borda devolve **essa** coluna ou linha à medida de omissão.
   *
   * Não é adorno: sem caminho de volta, quem arrasta até um extremo e não gosta
   * fica sem forma de reverter a não ser recarregar a página e perder o resto do
   * estado — filtros, selecção, painel.
   */
  const aoDuploCliqueMedida = (evento: EventoRato<HTMLDivElement>): void => {
    const borda = bordaSob(evento.target, evento.clientX, evento.clientY);
    if (borda === null || Number.isNaN(borda.indice)) return;
    const remover = (m: ReadonlyMap<number, number>): ReadonlyMap<number, number> => {
      if (!m.has(borda.indice)) return m;
      const novo = new Map(m);
      novo.delete(borda.indice);
      return novo;
    };
    if (borda.eixo === 'x') setLarguraDias(remover);
    else setAlturaLinhas(remover);
  };

  const larguraTotal = larguraViatura + (offsetsDias[dados.nDias] ?? 0);
  // ⚠️ A secção dos subcontratados entra na altura total. Sem isto, o contentor
  // acabava na última viatura e a secção ficava cortada pelo `overflow`.
  // ⚠️ **A contagem é de LINHAS e não de empresas (C₂):** `formaSub.altura` já soma as linhas
  //    todas, mas quem decide se a secção existe é o mesmo número que a desenha.
  const nSub = linhasSub.length;
  // 🔴 A secção de espera entra na altura total pela MESMA razão da dos subcontratados: sem
  //    isto o contentor acabava antes dela e o `overflow` cortava-a.
  // ⚠️⚠️ **Com ZERO viagens em espera não há título nem linhas — e é o caso normal.** Hoje nada
  //    na base está em espera. Uma secção permanentemente vazia afirmava que existe qualquer
  //    coisa ali; e 0 linhas custa 0 nós e 0 píxeis, o que é o que o orçamento do R1 quer.
  const nEspera = linhasEspera.length;
  // K · a MESMA razão das duas secções acima: sem isto o `overflow` cortava a faixa.
  // ⚠️ **A condição é o TOTAL da fila, não `linhas.length`.** Um pedido sem data legível ou
  //    fora do ano não desenha linha nenhuma — mas «diz-se, não se esconde» (D-081): se
  //    a fila inteira caísse nesses dois casos, `linhas.length` seria 0 e o título (com a
  //    ressalva de quantos e porquê) desapareceria com ele. `total` é a soma dos três.
  const nPorPlanear = filaPorPlanear.total;
  // 🔴 2026-10-08 · a secção aparece também VAZIA enquanto se arrasta um serviço — é onde se larga.
  const mostrarEspera = nEspera > 0 || servicoEmArrasto;
  const alturaTotal =
    ALTURA_CABECALHO +
    (offsetsLinhas[dados.viaturas.length] ?? 0) +
    (nSub > 0 ? ALTURA_TITULO_SUB + formaSub.altura : 0) +
    (mostrarEspera ? ALTURA_TITULO_SUB + formaEspera.altura : 0) +
    (nPorPlanear > 0 ? ALTURA_TITULO_SUB + formaPorPlanear.altura : 0);

  const linhas = [];
  for (let v = janela.viatura0; v < janela.viatura1; v++) {
    // ⚠️⚠️ **B₂ · uma linha escondida não desenha nó nenhum.** `offsetsLinhas` já lhe deu
    //    altura zero (acima); não a desenhar de todo é o que cumpre o orçamento do R1
    //    (ADR-001) — 0 px «visível» continuaria a gastar o mesmo trabalho de desenho que
    //    uma linha normal, e é exactamente o que esconder deve poupar.
    if (indicesEscondidos?.has(v) === true) continue;
    linhas.push(
      <Linha
        key={v}
        dados={dados}
        viatura={v}
        // ⚠️⚠️ **Só as linhas APONTADAS recebem previsão; as outras recebem `null`.**
        //    📌 É o que mantém o `memo` a funcionar: um objecto novo em cada linha invalidava
        //    a memoização de todas, e o R1 do ADR-001 é o número de nós desenhados por janela.
        //    ⚠️ O `Map` é memoizado (ver `previsaoPorLinha`), logo `undefined` → `null` é uma
        //    constante e as linhas sem alteração não voltam a desenhar.
        previsao={previsaoPorLinha.get(v) ?? null}
        janela={janela}
        tema={tema}
        corLigada={corLigada}
        corPor={corPor}
        filtro={filtro}
        inicioSemana={inicioSemana}
        itinerarioForte={itinerarioForte}
        offsetsDias={offsetsDias}
        altura={tamanho(offsetsLinhas, v)}
        alvoArrasto={v === alvoArrasto}
        // ⚠️ §12.6 · mesma disciplina da `previsao`, duas linhas acima: só a viatura
        //    seleccionada recebe um número, as outras 249 recebem `null` (memo intacto).
        diaSeleccionado={seleccao !== null && seleccao.v === v ? seleccao.d : null}
        // ⚠️ Peça 1 · mesma disciplina — só a viatura em extensão recebe o intervalo.
        intervaloSeleccionado={intervaloSeleccionado !== null && intervaloSeleccionado.v === v ? intervaloSeleccionado : null}
        // ⚠️ Peça 2 · mesma disciplina — só a viatura seleccionada como LINHA leva a marca.
        linhaSeleccionada={formaSeleccao?.tipo === 'linha' && formaSeleccao.v === v}
        aoClicarViatura={aoClicarLinha}
      />,
    );
  }

  const fixado = seleccao ?? (sobre === null ? null : { v: sobre.v, d: sobre.d });
  /**
   * B32 · qual linha fixada desenhar por cima, ou `null`. Ver `linhaFixadaADesenhar`.
   *
   * ⚠️ B₂ · uma linha ESCONDIDA nunca se desenha fixada — senão «esconder» deixava uma
   * cópia dela sempre visível no topo, que é o oposto do que o interruptor pede.
   */
  const fixadaBruta = linhaFixadaADesenhar(linhaFixada, janela);
  const fixadaADesenhar =
    fixadaBruta !== null && indicesEscondidos?.has(fixadaBruta) === true ? null : fixadaBruta;

  return (
    <LigacaoAoMapa.Provider value={ligacaoAoMapa}>
    <div
      className="mapa-envolvente"
      ref={envolvente}
      style={
        {
          '--largura-viatura': `${larguraViatura}px`,
          '--altura-painel': `${painelAberto ? alturaPainel : 0}px`,
          // ⚠️ **A altura do cabeçalho vem daqui e não escrita à letra no CSS.** 📌 É onde a
          // linha fixada (`.linha-fixada`) encosta; com o número repetido no CSS, mudá-lo em
          // `dimensoes.ts` punha-a 38 px fora do sítio e nada o denunciava a não ser o olho.
          '--altura-cabecalho': `${ALTURA_CABECALHO}px`,
          // ⚠️ As medidas da grelha NÃO vivem aqui. Cada coluna e cada linha tem a
          // sua, logo vão no elemento respectivo — largura no `.dia`/`.dia-cab`,
          // altura e bandas no `.linha`. Uma variável só no topo era o que a versão
          // anterior tinha, e é exactamente o que fazia arrastar uma coluna mexer em
          // todas as outras.
        } as React.CSSProperties
      }
    >
      <Topo
        dados={dados}
        filtro={filtro}
        definirFiltro={setFiltro}
        corLigada={corLigada}
        definirCor={setCorLigada}
        corPor={corPor}
        definirCorPor={setCorPor}
        esconderInactivas={esconderInactivas}
        definirEsconderInactivas={setEsconderInactivas}
        janela={janela}
        p95={p95}
        vistaAgora={vistaAgora}
        pedidosAbertos={pedidosAbertos}
        consultasAbertas={consultasAbertas}
        alternarConsultas={() => setConsultasAbertas((a) => !a)}
        caixaAberta={caixaAberta}
        alternarCaixa={() => definirCaixaAberta((a) => !a)}
        gestaoAberta={gestaoAberta}
        alternarGestao={() => setGestaoAberta((a) => !a)}
        sessaoAberta={sessaoAberta}
        setSessaoAberta={setSessaoAberta}
        atencaoAberta={atencaoAberta}
        alternarAtencao={() => setAtencaoAberta((a) => !a)}
        paletaAberta={paletaAberta}
        abrirPaleta={() => setPaletaAberta((a) => !a)}
        resumoDoFiltro={resumoDoFiltro}
        limparEixo={(eixo) => setFiltro((a) => ({ ...a, [eixo]: null }))}
        limparTudo={() => setFiltro(SEM_FILTRO)}
        atencaoFamilias={atencaoFamilias}
        emailsPorTratar={emailsPorTratar}
        {...(recentrarFita !== undefined
          ? {
              navegacaoDaFita: (
                <NavegarNaFita
                  janela={janelaDe(dados)}
                  irParaData={irParaData}
                  deslocarFita={deslocarFita}
                  aCarregar={aCarregarFita}
                />
              ),
            }
          : {})}
        {...(hoje < 0
          ? recentrarFita === undefined
            ? {}
            : // 🔴 D-101 · hoje fora desta fita (depois de um «Ir para…»): «Hoje» volta à fita de hoje.
              // ⚠️ A fita de hoje pede-se EXPLÍCITA: com `null` o `fetch` levaria no cabeçalho a fita
              //    do ecrã, e o servidor serviria a mesma outra vez.
              { irParaHoje: () => recentrarFita(inicioDaFitaCentradaEm(hojeEmPortugal()), hojeEmPortugal()) }
          : {
              irParaHoje: () => {
                const el = caixa.current;
                if (el === null) return;
                // ⚠️ Um terço de folga, como o `irPara`: encostar hoje ao canto esquerdo
                //    esconde a semana que passou, e é ela que explica o que está a acontecer.
                el.scrollLeft = Math.max(0, (offsetsDias[hoje] ?? 0) - el.clientWidth / 3);
                recalcular();
              },
            })}
        nomeCurtoDaSessao={nomeCurtoDaSessao}
        formacaoNoMapa={formacaoNoMapa}
        planoNoTopo={planoNoTopo}
        soPartilhado={planoNoTopo === null && papel !== null && papel !== 'superadmin'}
        tomDoPlano={tomDoPlano}
        alternarPedidos={() => setPedidosAbertos((a) => !a)}
        {...(recarregar === undefined ? {} : { recarregar })}
        abrirRegras={() => setRegrasAbertas(true)}
        abrirContas={papel === 'superadmin' ? () => setContasAbertas(true) : undefined}
        etagDoMapa={etagDoMapa}
      />

      <div
        className="mapa"
        ref={caixa}
        // ⚠️ §12.6 · `role="grid"` cobre a frota (linhas/células com `role`/`aria-selected`/
        //    `tabIndex` — ver `Linha`, `Cabecalho`). ✅ Peça 3 (2026-09-28) fecha o resto — e
        //    NÃO com `"row"`/`"gridcell"`: nem os subcontratados nem a espera têm uma célula
        //    por dia (é uma barra por SERVIÇO), e inventar geometria por dia só para lhe dar um
        //    `role` seria a "flexibilidade não pedida" que a regra 2 recusa. Cada secção leva a
        //    semântica que TEM: `listbox`/`option` nos subcontratados (`LinhaSubcontratado`,
        //    já navegável pelas setas — C₂), `button` na espera (`LinhaDeEspera`, um clique já
        //    executa a única acção que a secção tem — ver o cabeçalho de `LinhaDeEspera`).
        role="grid"
        aria-label="Mapa de planeamento"
        onScroll={recalcular}
        onMouseOver={aoPassar}
        onMouseLeave={aoSair}
        onClick={aoClicar}
        onContextMenu={aoMenuContexto}
        onDragStart={aoComecarArrasto}
        onDragOver={aoArrastarPorCima}
        onDragLeave={aoSairDoArrasto}
        onDrop={aoLargar}
        // 🔴 2026-10-08 · o arrasto acabou sem largar em sítio nenhum: a zona «Em espera» sai.
        onDragEnd={() => setServicoEmArrasto(false)}
      >
        <div
          className="mapa-conteudo"
          style={{ width: larguraTotal, height: alturaTotal }}
          onPointerDown={aoDescerMedida}
          onPointerMove={aoMoverMedida}
          onPointerUp={aoSubirMedida}
          onPointerCancel={aoSubirMedida}
          onDoubleClick={aoDuploCliqueMedida}
        >
          <Cabecalho
            dados={dados}
            janela={janela}
            inicioSemana={inicioSemana}
            offsetsDias={offsetsDias}
            hoje={hoje}
            {...(realcePeriodo === null ? {} : { periodo: realcePeriodo })}
            // ⚠️ Peça 2 · a coluna seleccionada e o clique que a liga/desliga.
            {...(formaSeleccao?.tipo === 'coluna'
              ? { colunaSeleccionada: formaSeleccao.d }
              : {})}
            aoClicarDia={aoClicarColuna}
          />
          {/* ⚠️ **O ALVO DO «Ver no mapa».** Posicionado em pixéis a partir das mesmas
              posições acumuladas que desenham a grelha (D-085) — nunca `dia × largura`,
              que deixaria de bater assim que uma coluna fosse alargada. O `top` soma
              `ALTURA_CABECALHO` porque as linhas começam depois dele, no fluxo normal. */}
          {realce !== null ? (
            /* ⚠️⚠️ **TERCEIRA VERSÃO, e as duas anteriores erraram em sentidos opostos.**
                v1: uma caixa com cantos redondos, brilho e fundo, sobre um intervalo errado
                    — «está horrível», e tapava o que se tinha ido lá ver.
                v2: duas réguas de ponta a ponta, linha inteira e coluna inteira — *«ele
                    selecciona uma linha e uma coluna completa, eu só quero seleccionar o
                    itinerário»*. Ao tentar ser encontrável de longe, deixei de apontar: uma
                    régua que atravessa 250 viaturas não diz **onde**, diz **em que zona**.
                v3: **o itinerário e mais nada** — os dias daquele serviço, naquela linha.
                    Contorno de 2 px, sem brilho e sem cantos redondos, com um fundo tão
                    ténue que o texto por baixo continua a ler-se. O alvo é o serviço. */
            <div
              key={realce.contagem}
              className="mapa-alvo"
              aria-hidden="true"
              style={geometriaDoRealce(realce, {
                larguraViatura,
                offsetsDias,
                offsetsLinhas,
                nViaturas: dados.viaturas.length,
                offsetsSub: formaSub.offsets,
              })}
            />
          ) : null}
          {/* ⚠️⚠️ **A PRÉ-VISUALIZAÇÃO MUDOU DE SÍTIO, e é a correcção de 2026-08-21.**

              📗 Cliente: *«eu não queria como fizeste; o meu objetivo é ver a alocação
              corretamente já com os dias tal como já fosse definitiva mas com um ligeiro
              desfoque, para ter uma visão exata»*.

              ⚠️⚠️ **Aqui estava uma CAIXA tracejada com a palavra «previsão» — e não era
              isto.** Uma caixa por cima do mapa diz *onde*; não diz *como fica*. E não podia
              dizer: a este nível não há `bandas(altura)`, nem `esq()`/`larg()` desta linha, nem
              as classes das barras. 📌 A pré-visualização passou para DENTRO da `Linha`, onde
              as barras verdadeiras são desenhadas — pelo mesmo código, com a mesma geometria e
              a cor do cliente. É a diferença entre desenhar sobre o mapa e desenhar o mapa.

              ⚠️ O rectângulo (`mapa-alvo`) FICA: é o que se encontra de longe depois do
              salto. A barra é o que se lê de perto. */}
          {/* ⚠️⚠️ **A RÉGUA DE HOJE — atravessa a frota, e aqui é o desenho certo.**
              📌 A v2 do realce foi recusada por atravessar 250 viaturas quando havia **uma
              linha para apontar**. Hoje não é uma linha: é uma **coluna**, e uma coluna
              desenha-se como coluna. É a mesma distinção que decidiu a banda do período.
              ⚠️ 1 px, ciano, e **sem preenchimento**: cobre 250 linhas, e qualquer fundo aqui
              pintava o mapa inteiro. E `pointer-events: none`, senão roubava o cursor à célula
              que está debaixo dela — que é justamente a que se quer inspeccionar. */}
          {hoje >= janela.dia0 && hoje < janela.dia1 ? (
            <div
              className="regua-hoje"
              aria-hidden="true"
              style={{
                left: larguraViatura + (offsetsDias[hoje] ?? 0),
                width: Math.max(1, (offsetsDias[hoje + 1] ?? 0) - (offsetsDias[hoje] ?? 0)),
                top: ALTURA_CABECALHO,
                height: alturaTotal - ALTURA_CABECALHO,
              }}
            />
          ) : null}
          {/* ---- ⚠️⚠️ PEÇA 2 · A BANDA DA COLUNA/LINHA SELECCIONADA — §12.4-bis (2026-09-28) ---
              📌 **UM ÚNICO elemento sobreposto, como `.mapa-alvo` e `.regua-hoje` acima — nunca
              uma classe por célula.** Uma coluna são 250 viaturas e uma linha são 365 dias: o
              orçamento de nós do R1 (ADR-001) não pode crescer com o tamanho da selecção. A
              geometria é a MESMA de `.regua-hoje` (coluna) e de `.mapa-alvo` (linha), lida dos
              mesmos vectores acumulados — nunca `dia × largura`. */}
          {formaSeleccao?.tipo === 'coluna' ? (
            <div
              className="mapa-eixo-seleccionado"
              aria-hidden="true"
              style={{
                left: larguraViatura + (offsetsDias[formaSeleccao.d] ?? 0),
                width: Math.max(
                  1,
                  (offsetsDias[formaSeleccao.d + 1] ?? 0) - (offsetsDias[formaSeleccao.d] ?? 0),
                ),
                top: ALTURA_CABECALHO,
                height: alturaTotal - ALTURA_CABECALHO,
              }}
            />
          ) : null}
          {formaSeleccao?.tipo === 'linha' ? (
            <div
              className="mapa-eixo-seleccionado"
              aria-hidden="true"
              style={{
                left: larguraViatura,
                width: larguraTotal - larguraViatura,
                top: ALTURA_CABECALHO + (offsetsLinhas[formaSeleccao.v] ?? 0),
                height:
                  (offsetsLinhas[formaSeleccao.v + 1] ?? 0) - (offsetsLinhas[formaSeleccao.v] ?? 0),
              }}
            />
          ) : null}
          {/* Espaçadores: a altura vem das posições acumuladas e não de uma
              multiplicação, porque as linhas já não têm todas a mesma altura. */}
          {/* ---- ⚠️⚠️ «COMPARAR FACILMENTE» · A LINHA FIXADA -----------------------
              📗 Cliente, 2026-08-30. Medido: quando a mesma viagem está em duas viaturas,
              as duas linhas estão a uma mediana de **82** e **88,3 %** ficam fora da janela.

              ⚠️⚠️ **NÃO SE DESENHA QUANDO A LINHA REAL JÁ ESTÁ VISÍVEL.** 📌 Duas cópias da
              mesma viatura no ecrã fazem o planeador comparar uma linha consigo mesma — e
              pior: as duas responderiam ao clique, e a que ele não está a olhar também.

              ⚠️ **`sticky` e não `fixed`:** ela pertence à grelha e tem de deslizar na
              horizontal com os dias. Com `fixed` ficaria parada enquanto os dias corriam
              por baixo, e as colunas deixavam de bater — que é precisamente o que se está
              a comparar. */}
          {fixadaADesenhar !== null ? (
            <div className="linha-fixada">
              <Linha
                dados={dados}
                viatura={fixadaADesenhar}
                previsao={null}
                janela={janela}
                tema={tema}
                corLigada={corLigada}
                corPor={corPor}
                filtro={filtro}
                inicioSemana={inicioSemana}
                itinerarioForte={itinerarioForte}
                offsetsDias={offsetsDias}
                altura={tamanho(offsetsLinhas, fixadaADesenhar)}
                alvoArrasto={false}
                // ⚠️ §12.6 · SEMPRE `null` — é uma segunda cópia visual da mesma viatura;
                //    marcá-la também dava DOIS nós com `tabIndex="0"` para a mesma célula
                //    lógica. Ver a nota em `LinhaProps.diaSeleccionado`.
                diaSeleccionado={null}
              />
            </div>
          ) : null}
          <div style={{ height: offsetsLinhas[janela.viatura0] ?? 0 }} />
          {linhas}
          <div
            style={{
              height:
                (offsetsLinhas[dados.viaturas.length] ?? 0) - (offsetsLinhas[janela.viatura1] ?? 0),
            }}
          />
          {/* SUBCONTRATADOS — secção própria, e não linhas da frota (ver
              `LinhaSubcontratado`). Não é virtualizada: são ~10 empresas, não 250
              viaturas, e virtualizar dez linhas seria complexidade sem ganho. */}
          {nSub > 0 ? (
            <>
              <div className="sub-titulo" style={{ height: ALTURA_TITULO_SUB }}>
                {/* 🔴 2026-10-30 · o conteúdo é que se prende à esquerda — ver `.titulo-seccao-fixo`. */}
                <div className="titulo-seccao-fixo">
                  <span className="sub-titulo-txt">Subcontratados</span>
                  {/* ---- ⚠️⚠️ C · CRIAR UM SUBCONTRATO À MÃO ------------------------------
                      📗 *«Tem de dar para criar subcontratos manualmente no mapa»*.
                      ⚠️⚠️ **AQUI e não numa célula vazia, e a razão é o orçamento de nós.** Os
                      dias sem serviço desta secção **não têm elemento nenhum** no DOM (as
                      barras são posicionadas em absoluto); dar-lhes um alvo de clique custava
                      365 × N nós por janela e o risco R1 do ADR-001 recusa acima de 1 400
                      células. 📌 Um botão na cabeceira custa **um** nó, e é onde se procura o
                      que é da secção inteira.
                      ⚠️ E leva o dia que estiver seleccionado, quando houver: é o que põe à
                      frente os pedidos que o cobrem, sem esconder os outros (P-30). */}
                  <button
                    type="button"
                    className="btn btn--peq"
                    onClick={() =>
                      setASubcontratarAMao({
                        s: seleccaoSub === null ? null : (linhasSub[seleccaoSub.l]?.sub ?? null),
                        d: seleccaoSub?.d ?? seleccao?.d ?? janela.dia0,
                      })
                    }
                    title="escolher uma empresa e um pedido — abre o «Planear» com ela garantida na lista"
                  >
                    Subcontratar à mão…
                  </button>
                </div>
              </div>
              {/* ---- ⚠️⚠️ PEÇA 3 · A SECÇÃO É UM `listbox`, não uma célula por dia -----------
                  📌 **Justificação:** não há grelha aqui (é uma barra por SERVIÇO, não um dia
                  por coluna), logo `role="grid"` da frota não se aplica. `listbox`/`option` é o
                  padrão certo porque é literalmente o que já existe sem ARIA: uma colecção de
                  itens que se navega com as setas (C₂) e se selecciona um de cada vez — o
                  próprio `aria-selected` das opções, acima, é a metade que faltava.
                  ⚠️ Um único nó novo — não por dia nem por linha — na secção inteira. */}
              <div role="listbox" aria-label="Serviços subcontratados">
                {linhasSub.map((l, i) => (
                  <LinhaSubcontratado
                    key={`${String(l.sub)}-${String(l.ordem)}`}
                    dados={dados}
                    indice={i}
                    linhaSub={l}
                    janela={janela}
                    tema={tema}
                    corLigada={corLigada}
                    filtro={filtro}
                    offsetsDias={offsetsDias}
                    altura={formaSub.alturas[i] ?? ALTURA_LINHA_SUB}
                    diaSeleccionado={seleccaoSub !== null && seleccaoSub.l === i ? seleccaoSub.d : null}
                  />
                ))}
              </div>
            </>
          ) : null}
          {/* ---- 🔴 EM ESPERA — 📗 «no final do mapa APÓS os subcontratados» ----------------
              A ordem é literal e não é preferência: o cliente nomeou o sítio. E é a leitura
              certa — primeiro os nossos autocarros, depois quem os substitui, e por fim o que
              ainda não tem autocarro nenhum.
              ⚠️ Não é virtualizada, como a dos subcontratados: são as viagens em espera, não
              250 viaturas — e por isso **não entra no produto `viaturas × dias`** que o teste do
              risco R1 limita a 1 400 células. O que custa mede-se em nós e em altura. */}
          {mostrarEspera ? (
            <>
              <div className="espera-titulo" style={{ height: ALTURA_TITULO_SUB }} data-zona-espera="">
                {/* 🔴 2026-10-30 · o conteúdo é que se prende à esquerda — ver `.titulo-seccao-fixo`. */}
                <div className="titulo-seccao-fixo">
                  <span className="espera-titulo-txt">Em espera</span>
                  {/* ⚠️⚠️ **A RESSALVA AO LADO DO TÍTULO, e não numa ajuda.** Nenhum planeador
                      alguma vez viu esta secção, e uma linha sem autocarro no fim do mapa não se
                      explica sozinha — sem isto lê-se como «viaturas que faltam no mapa». Diz o
                      que é **e** o que fazer, que é o gesto seguinte. */}
                  <span className="espera-titulo-nota">
                    {servicoEmArrasto
                      ? 'largue aqui para pôr a viagem em espera — tudo fica guardado (dias, condutores, notas).'
                      : 'viagens sem autocarro — tudo guardado (dias, condutores, notas). Clique numa ' +
                        'barra para a trazer de volta, ou arraste-a para uma viatura.'}
                  </span>
                </div>
              </div>
              {/* ⚠️ Peça 3 · `role="group"` e não `listbox` — aqui não há selecção nenhuma
                  para agrupar (cada barra EXECUTA ao clicar, ver `LinhaDeEspera`), só um
                  conjunto de botões. Um único nó novo na secção inteira. */}
              <div role="group" aria-label="Viagens em espera de autocarro" data-zona-espera="">
                {linhasEspera.map((l, i) => (
                  <LinhaDeEspera
                    key={l.ordem}
                    dados={dados}
                    indice={i}
                    linhaEspera={l}
                    janela={janela}
                    tema={tema}
                    corLigada={corLigada}
                    filtro={filtro}
                    offsetsDias={offsetsDias}
                    altura={formaEspera.alturas[i] ?? ALTURA_LINHA_SUB}
                  />
                ))}
              </div>
            </>
          ) : null}
          {/* ---- K · POR PLANEAR — 📗 «vê o buraco e a viagem que lá cabe na mesma coluna»
              Por ÚLTIMO, depois da espera: a espera já foi planeada uma vez (só perdeu o
              autocarro); um pedido por planear nunca teve nada. A ordem lê-se como o grau
              de trabalho que falta, crescente até ao fim do mapa.
              ⚠️ NÃO reutiliza `linhasDaEspera`/`CelulaCompacta` — ver o cabeçalho de
              `por-planear-no-mapa.ts`: um pedido não tem alocação nenhuma, só um intervalo
              de dias já conhecido. Reutiliza-se a MESMA altura de linha e a MESMA exclusão
              do orçamento de nós do risco R1 (não é `viaturas × dias`). */}
          {nPorPlanear > 0 ? (
            <>
              <div className="planear-titulo" style={{ height: ALTURA_TITULO_SUB }}>
                {/* 🔴 2026-10-30 · o conteúdo é que se prende à esquerda — ver `.titulo-seccao-fixo`. */}
                <div className="titulo-seccao-fixo">
                  <span className="planear-titulo-txt">Por planear</span>
                  <span className="planear-titulo-nota">{notaDaFilaPorPlanear(filaPorPlanear)}</span>
                </div>
              </div>
              {filaPorPlanear.linhas.map((itens, i) => (
                <LinhaPorPlanear
                  // A ORDEM na secção, tal como `l.ordem` nas linhas de espera/subcontratado
                  // acima — não há identidade própria da LINHA, só dos pedidos que ela leva.
                  key={i}
                  dados={dados}
                  itens={itens}
                  ordem={i}
                  total={filaPorPlanear.linhas.length}
                  janela={janela}
                  tema={tema}
                  corLigada={corLigada}
                  filtro={filtro}
                  offsetsDias={offsetsDias}
                  indicePorSigla={indicePorSigla}
                  percursos={percursosDaFila}
                  altura={formaPorPlanear.alturas[i] ?? ALTURA_LINHA_SUB}
                  aoClicar={mostrarPedidoNaLista}
                />
              ))}
            </>
          ) : null}
        </div>
        <Puxador
          eixo="x"
          className="puxador puxador--coluna"
          valor={larguraViatura}
          minimo={LARGURA_VIATURA_MIN}
          maximo={LARGURA_VIATURA_MAX}
          variavel="--largura-viatura"
          alvo={envolvente}
          aoFixar={setLarguraViatura}
          rotulo="largura da coluna das viaturas"
        />
      </div>

      {sobre !== null && seleccao === null ? <Cartao dados={dados} posicao={sobre} /> : null}
      {/* ⚠️⚠️ **O comentario que estava aqui MENTIA, e era a versao mais enganadora:** dizia
          que «um servico subcontratado nao se selecciona (nao ha painel de detalhe para ele)»
          como se fosse uma decisao de desenho. 📌 Era a ausencia de trabalho a
          descrever-se como intencao. Agora ha, e o cartao esconde-se com a seleccao fixa —
          como o da frota, porque dois blocos a dizer o mesmo sobre o mesmo servico competem
          pela mesma atencao. */}
      {sobreSub !== null && seleccaoSub === null ? (
        <CartaoSub dados={dados} linhaSub={linhasSub[sobreSub.l]} posicao={sobreSub} />
      ) : null}

      <Painel
        dados={dados}
        // ⚠️⚠️ **`alvo` fica NULL quando o alvo e um subcontratado, e isso e a correccao inteira
        //   das accoes.** Todos os botoes do painel verificam `alvo !== null`, logo nenhum
        //   aparece — e e o que tem de acontecer: nao se edita o servico de outra empresa, nao
        //   se marca oficina na viatura dela, nao se lhe atribui um condutor nosso (D-002).
        //   📌 Sem uma unica condicao nova, e sem a possibilidade de esquecer uma das oito.
        alvo={seleccaoSub === null ? fixado : null}
        {...(seleccaoSub === null
          ? {}
          : {
              alvoSub: {
                sub: linhasSub[seleccaoSub.l]?.sub ?? -1,
                linha: linhasSub[seleccaoSub.l]?.dias ?? [],
                d: seleccaoSub.d,
              },
            })}
        // ⚠️⚠️ **`fixo` conta os DOIS.** Os botoes do painel so aparecem com a seleccao fixa (um
        //   botao que muda de alvo a cada celula e um convite a clicar no servico errado) — e
        //   com `seleccao !== null` sozinho, clicar num subcontratado abria o painel **sem
        //   botao nenhum**, que e o defeito que isto vem corrigir.
        fixo={seleccao !== null || seleccaoSub !== null}
        aberto={painelAberto}
        alternar={() => setPainelAberto((a) => !a)}
        limpar={() => {
          setSeleccao(null);
          setSeleccaoSub(null);
        }}
        configuracao={configuracao}
        altura={alturaPainel}
        definirAltura={setAlturaPainel}
        envolvente={envolvente}
        // ⚠️⚠️ **OS GESTOS VÊM TODOS DE `gestos`, e não são construídos aqui** — ver a nota
        //   dele, acima. 📌 A paleta de comandos (`Ctrl+K` contextual, §12.3) oferece estes
        //   mesmos verbos e recebe **estas mesmas funções**: com duas construções, mover uma
        //   viagem pelo painel e pela paleta podiam mover intervalos diferentes.
        editar={gestos.editar}
        copiarPeriodo={gestos.copiarPeriodo}
        retirarCopia={gestos.retirarCopia}
        editarQuePartem={gestos.editarQuePartem}
        rotuloDaQuePartem={rotuloDaQuePartem}
        corDaViagem={gestos.corDaViagem}
        corDaViagemQuePartem={gestos.corDaViagemQuePartem}
        anotar={gestos.anotar}
        corrigir={gestos.corrigir}
        partir={gestos.partir}
        organizarEmTrocos={gestos.organizarEmTrocos}
        juntarTrocos={gestos.juntarTrocos}
        porEmEspera={gestos.porEmEspera}
        travar={gestos.travar}
        diario={gestos.diario}
        marcarIndisponivel={gestos.marcarIndisponivel}
        onde={gestos.onde}
        substituir={gestos.substituir}
        fecharServico={gestos.fecharServico}
        // ⚠️ 📗 «os botões que aparecem nos dias com viagens não podem ser exactamente
        //    iguais aos dias vazios». Ver `natureza` na assinatura do painel.
        natureza={natureza}
        cancelarServico={gestos.cancelarServico}
        retirarDoMapa={gestos.retirarDoMapa}
        trocarEmpresa={gestos.trocarEmpresa}
        outrasDaViagem={outrasDaViagem}
        irPara={irPara}
        encaixar={gestos.encaixar}
      />

      {/* 📗 D-091 passo 1 — o realizado, que é o que CRIA o histórico que não existe. */}
      {aFechar !== null ? (
        <FecharServico
          key={chaveDaJanela(aFechar)}
          dados={dados}
          viatura={aFechar.viatura}
          dia0={aFechar.dia0}
          dia1={aFechar.dia1}
          fechar={() => setAFechar(null)}
          recarregar={recarregar}
        />
      ) : null}

      {/* 📗 «tudo tem de poder ser editado» — a célula, como no Excel. */}
      {aCorrigir !== null ? (
        <EditarCelula
          key={chaveDaJanela(aCorrigir)}
          dados={dados}
          viatura={aCorrigir.viatura}
          dia={aCorrigir.dia}
          fechar={() => setACorrigir(null)}
          recarregar={recarregar}
        />
      ) : null}

      {/* 📗 fonte 6.8, D-079 — texto livre, e o sistema NÃO o interpreta. */}
      {aNotar !== null ? (
        <NotaDaCelula
          key={chaveDaJanela(aNotar)}
          dados={dados}
          viatura={aNotar.viatura}
          dia={aNotar.dia}
          fechar={() => setANotar(null)}
          recarregar={recarregar}
        />
      ) : null}

      {/* A cor manual de uma viagem (migração 055) — sobrepõe-se à do cliente só nela. */}
      {aCorDaViagem !== null ? (
        <CorDaCorrida
          key={chaveDaJanela(aCorDaViagem)}
          dados={dados}
          referencia={aCorDaViagem.referencia}
          tema={tema}
          fechar={() => setACorDaViagem(null)}
          recarregar={recarregar}
        />
      ) : null}

      {/* 📗 fonte 5.3.3.1 · a fila de trabalho, agora como JANELA sobre o mapa.
          Última na ordem do DOM de propósito: sem `z-index` a competir, a última
          irmã pintada é a que fica em cima — e é a que o utilizador está a usar. */}
      {pedidosAbertos ? (
        <Pedidos
          fechar={() => setPedidosAbertos(false)}
          // O painel mostra o NOME COMPLETO da localidade, como foi pedido: «para além
          // das 3 letras, o nome completo, para não existirem dúvidas».
          nomeLocalidade={(i) => nomeLocal(dados, i)}
          nomeMotorista={(i) => nomeDoMotorista(dados, i) || '—'}
          recarregar={recarregar}
          numeroViatura={(i) => dados.viaturas[i]?.numero ?? String(i)}
          irPara={irPara}
          irParaPeriodo={irParaPeriodo}
          {...(dados.viaturas.length === 0
            ? {}
            : {
                /**
                 * ⚠️⚠️ **A RESOLUÇÃO VIVE AQUI porque é aqui que está a grelha.** O detalhe do
                 * pedido conhece o **número** da viatura e a **data**; a janela de edição precisa
                 * dos **índices**. 📌 Fazer esta conversão no `Pedidos` dava duas formas de
                 * encontrar a mesma célula, e a que divergisse editava outro dia.
                 *
                 * ⚠️ E abre a **mesma janela** que o painel usa (`EditarCelula` →
                 * `POST /api/plano/celula`), com as suas guardas, o seu motivo obrigatório num
                 * dia efectivado e a sua auditoria. Um segundo caminho de escrita para editar a
                 * mesma linha era o erro a evitar.
                 */
                aoEditarDia: (numero: string, data: string) => {
                  const v = dados.viaturas.findIndex((x) => x.numero === numero);
                  // `data` vem como `YYYY-MM-DD`; o índice do dia é a diferença em UTC.
                  const d = Math.round(
                    (Date.parse(`${data}T00:00:00Z`) - inicioEmMs(janelaDe(dados))) / 86_400_000,
                  );
                  // ⚠️ Recusa em silêncio SÓ o que não existe: uma viatura de outro ano ou um
                  //    número que não é da frota. Abrir a janela sobre `-1` editava a viatura 0.
                  if (v < 0 || d < 0 || d >= dados.nDias) return;
                  setACorrigir({ viatura: v, dia: d });
                },
              })}
          aoCriarPedido={(duplicarDe) => setACriarPedido(duplicarDe === undefined ? {} : { duplicarDe })}
          // Só os códigos, e não os : ver a nota em .
          itinerariosConhecidos={itinerariosConhecidos}
          // 053 · idem, para o selector "por cliente" do Histórico.
          clientesConhecidos={dados.clientes}
          // 2026-08-25 · idem, para os `<select>` de recolha/despejo do «Editar pedido…».
          localidadesConhecidas={dados.localidades}
          {...(pedidoCriado === null
            ? {}
            : { mostrarPedido: pedidoCriado, mostrarPedidoVez: vezDoPedido })}
        />
      ) : null}

      {/* A confirmação de um pedido largado numa viatura. Depois dos pedidos na ordem
          do DOM: quem arrastou está a olhar para esta janela, não para a lista. */}
      {regrasAbertas ? <RegrasNegocio fechar={() => setRegrasAbertas(false)} /> : null}
      {contasAbertas ? <Contas fechar={() => setContasAbertas(false)} euId={idDaSessao} /> : null}
      {/* ---- 🔴 «P» do §11 · IMPRIMIR E EXPORTAR ----------------------------------------
          📌 **O verbo vive na paleta de comandos e NÃO na barra de topo**, e a razão está
          medida em `nada-e-cortado-pela-janela.test.tsx`: a barra está no tecto de 150 px e
          uma sexta linha não se compra com outro `+30`. A paleta é onde os verbos cabem sem
          custar barra — é para isso que o `Ctrl`+`K` existe.
          ⚠️ **A folha só existe enquanto se imprime** (ver `FolhaDeImpressao.tsx`): fora
          disso esta janela não acrescenta um único nó à grelha. */}
      {imprimirAberto ? (
        <FolhaDeImpressao
          dados={dados}
          viaturasVisiveis={viaturasVisiveis}
          diaVisivel={janela.dia0}
          corLigada={corLigada}
          corPor={corPor}
          filtro={filtro}
          inicioSemana={inicioSemana}
          plano={planoNoTopo}
          quem={nomeCurtoDaSessao}
          fechar={() => setImprimirAberto(false)}
        />
      ) : null}
      {/* ⚠️⚠️ **O MENU DE CONTEXTO DA GRELHA — 📗 fonte §4.8.**
          *«O mapa deve permitir tarefas com botão do lado direito do rato (menu de contexto)
          por ser uma forma rápida de iteração com o mapa.»* Estava por implementar.

          📌 **As mesmas acções do painel, e nenhuma nova.** Um segundo caminho de escrita
          com metade das garantias era o erro a evitar — aqui só muda o **gesto**.

          ⚠️ E o menu **depende do que a célula é**, como o painel: num dia livre não se
          oferece «Itinerário e pernoita…», que escreve numa linha que não existe. */}
      {menuCtx !== null ? (
        <>
          {/* O véu fecha ao clicar fora, e é o que impede o menu de ficar preso. */}
          <div className="menu-ctx-veu" onClick={() => setMenuCtx(null)} aria-hidden="true" />
          <div
            className="menu-ctx menu-ctx--grelha"
            role="menu"
            aria-label="Acções da célula"
            style={{
              // ⚠️ Recortado à janela: aberto no canto direito, um menu de 220 px saía do
              //    ecrã e as acções ficavam inalcançáveis.
              left: Math.min(menuCtx.x, window.innerWidth - 240),
              top: Math.min(menuCtx.y, window.innerHeight - 260),
            }}
          >
            <div className="menu-ctx-cab">
              {dados.viaturas[menuCtx.v]?.numero ?? ''} · {dataCurtaDoDia(dados, menuCtx.d)}
            </div>
            {natureza === 'indisponivel' ? (
              <button
                type="button"
                role="menuitem"
                className="menu-ctx-item"
                onClick={() => {
                  setASubstituir({ v: menuCtx.v, d: menuCtx.d });
                  setMenuCtx(null);
                }}
              >
                Quem substitui?
              </button>
            ) : null}
            {natureza === 'servico' ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  className="menu-ctx-item"
                  onClick={() => {
                    // 2026-10-27 · a MESMA formulação do «editar» do painel (`alvoDaEdicao`) — esta
                    //    cópia não levava o `condAte` e o dia de trânsito ficava de fora (B03).
                    setAEditar(alvoDaEdicao(dados.grelha[menuCtx.v] ?? [], dados.corridas, menuCtx.v, menuCtx.d));
                    setMenuCtx(null);
                  }}
                >
                  Viatura e condutor…
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="menu-ctx-item"
                  onClick={() => {
                    setACorrigir({ viatura: menuCtx.v, dia: menuCtx.d });
                    setMenuCtx(null);
                  }}
                >
                  Itinerário e pernoita…
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="menu-ctx-item"
                  onClick={() => {
                    setANotar({ viatura: menuCtx.v, dia: menuCtx.d });
                    setMenuCtx(null);
                  }}
                >
                  Nota…
                </button>
                {/* ⚠️ **PARTIR A VIAGEM — 📗 §4.6 «split de itinerários».** É o gesto que
                    PRECEDE mover metade: a avaria a meio, o grupo que se divide, a viatura
                    que tem de voltar antes do fim. Depois disto a segunda metade é um
                    serviço próprio e move-se pelos caminhos que já existem.
                    📌 No menu de contexto e não no painel: parte-se **num dia**, e o botão
                    direito é o único gesto que nomeia um dia sem ambiguidade. */}
                <button
                  type="button"
                  role="menuitem"
                  className="menu-ctx-item"
                  onClick={() => {
                    setADividir({ v: menuCtx.v, d: menuCtx.d });
                    setMenuCtx(null);
                  }}
                >
                  Partir a viagem aqui…
                </button>
                {/* ⚠️⚠️ **ORGANIZAR EM TROÇOS — GARANTIA INVERSA da de cima.** 📗 «viagens
                    longas têm de ser divididas de 7 em 7 dias, só para já no caso de um
                    cliente grego». Nome sem a palavra «partir», de propósito: aqui a
                    referência NUNCA muda — a viagem continua a ser uma só. Ver
                    `OrganizarEmTrocos`. */}
                <button
                  type="button"
                  role="menuitem"
                  className="menu-ctx-item"
                  onClick={() => {
                    setAOrganizarEmTrocos({ v: menuCtx.v, d: menuCtx.d });
                    setMenuCtx(null);
                  }}
                >
                  Organizar em troços…
                </button>
                {/* ⚠️ **JUNTAR OS TROÇOS — o inverso do botão de cima**, só oferecido quando a
                    viagem já tem alguma fronteira marcada (`temFronteiraDeTroco`) — sem isso o
                    gesto só serviria para recusar. Ver `JuntarOsTrocos`. */}
                {temFronteiraDeTroco ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="menu-ctx-item"
                    onClick={() => {
                      setAJuntarOsTrocos({ v: menuCtx.v, d: menuCtx.d });
                      setMenuCtx(null);
                    }}
                  >
                    Juntar os troços…
                  </button>
                ) : null}
                {/* ⚠️⚠️ **RETIRAR DO MAPA — o pedido larga o lugar e NÃO é cancelado.**
                    📗 Cliente, 2026-08-30: *«permitir retirar e voltar a planear pedidos já
                    marcados»*. 📌 Fica **acima** do separador e do «Cancelar…» de propósito: é o
                    gesto menos destrutivo dos dois, e pô-los lado a lado sem separação fazia
                    escolher por posição em vez de por significado. */}
                <button
                  type="button"
                  role="menuitem"
                  className="menu-ctx-item"
                  onClick={() => {
                    setARetirar({ v: menuCtx.v, d: menuCtx.d });
                    setMenuCtx(null);
                  }}
                >
                  Retirar do mapa…
                </button>
                {/* ⚠️⚠️ **O DIÁRIO DESTA VIAGEM — §5.6 exigência 3, o MESMO alvo do painel.**
                    📌 `alvoDoDiario` é calculado uma vez e partilhado pelos dois sítios: o
                    botão direito **fixa a selecção** antes de abrir o menu (ver
                    `aoMenuContexto`), logo os dois falam da mesma célula. Duas construções do
                    alvo dariam dois diários para a mesma viagem.
                    ⚠️ Fica ACIMA do separador: não escreve nada, e a zona abaixo do separador
                    é a dos gestos destrutivos. */}
                {alvoDoDiario !== null ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="menu-ctx-item"
                    onClick={() => {
                      setAVerDiario(alvoDoDiario);
                      setMenuCtx(null);
                    }}
                  >
                    O que aconteceu…
                  </button>
                ) : null}
                <div className="menu-ctx-sep" />
                <button
                  type="button"
                  role="menuitem"
                  className="menu-ctx-item"
                  onClick={() => {
                    setACancelar({ v: menuCtx.v, d: menuCtx.d });
                    setMenuCtx(null);
                  }}
                >
                  Cancelar…
                </button>
              </>
            ) : null}
            <button
              type="button"
              role="menuitem"
              className="menu-ctx-item"
              onClick={() => {
                setAMarcar({ v: menuCtx.v, d: menuCtx.d });
                setMenuCtx(null);
              }}
            >
              {natureza === 'indisponivel' ? 'Alterar ou levantar…' : 'Marcar oficina/avaria…'}
            </button>
            {/* ⚠️⚠️ **FORA dos ramos por natureza, de propósito.** Comparar é sobre a
                **linha**, e uma viatura parada é exactamente a que se quer pôr ao lado de
                outra — restringir isto a células com serviço tirava o gesto do caso que mais
                o pede. */}
            <div className="menu-ctx-sep" />
            <button
              type="button"
              role="menuitem"
              className="menu-ctx-item"
              onClick={() => {
                const v = menuCtx.v;
                setLinhaFixada((a) => (a === v ? null : v));
                setMenuCtx(null);
              }}
            >
              {linhaFixada === menuCtx.v
                ? 'Soltar esta linha'
                : `Fixar a ${dados.viaturas[menuCtx.v]?.numero ?? ''} no topo, para comparar`}
            </button>
          </div>
        </>
      ) : null}

      {aDividir !== null ? (
        <PartirViagem
          key={chaveDaJanela(aDividir)}
          viaturaId={dados.viaturas[aDividir.v]?.id ?? ''}
          numero={dados.viaturas[aDividir.v]?.numero ?? ''}
          dia={aDividir.d}
          data={dataCurtaDoDia(dados, aDividir.d)}
          fechar={() => setADividir(null)}
          recarregar={recarregar}
        />
      ) : null}

      {aOrganizarEmTrocos !== null ? (
        <OrganizarEmTrocos
          key={chaveDaJanela(aOrganizarEmTrocos)}
          viaturaId={dados.viaturas[aOrganizarEmTrocos.v]?.id ?? ''}
          numero={dados.viaturas[aOrganizarEmTrocos.v]?.numero ?? ''}
          dia={aOrganizarEmTrocos.d}
          data={dataCurtaDoDia(dados, aOrganizarEmTrocos.d)}
          fechar={() => setAOrganizarEmTrocos(null)}
          recarregar={recarregar}
        />
      ) : null}

      {aJuntarOsTrocos !== null ? (
        <JuntarOsTrocos
          key={chaveDaJanela(aJuntarOsTrocos)}
          viaturaId={dados.viaturas[aJuntarOsTrocos.v]?.id ?? ''}
          numero={dados.viaturas[aJuntarOsTrocos.v]?.numero ?? ''}
          dia={aJuntarOsTrocos.d}
          data={dataCurtaDoDia(dados, aJuntarOsTrocos.d)}
          fechar={() => setAJuntarOsTrocos(null)}
          recarregar={recarregar}
        />
      ) : null}

      {/* ---- §11 · M · esticar/encurtar arrastando a ponta da barra --------------------------
          ⚠️ **A MESMA janela `DatasDoPedido` de `Detalhe.tsx`, e não uma cópia** (regra 8): o
          arrasto só decide QUAIS datas propor; quem valida colisões, avisa de dias apagados e
          escreve continua a ser `/api/pedidos/alterar`. Chrome mínimo (`.janela--motivo`), como
          `OrganizarEmTrocos`/`PartirViagem` acima — este trabalho só pode tocar em `Mapa.tsx`
          do lado do ecrã, e um componente por gesto num ficheiro próprio não se aplicava a um
          embrulho tão fino. */}
      {arrastoDeDatas !== null ? (
        <div key={chaveDaJanela(arrastoDeDatas)} className="janela janela--motivo" style={arrastoDasDatas.estilo} role="dialog" aria-label="Mudar as datas (arrasto)">
          <div {...arrastoDasDatas.cab} className="janela-cab faixa-quebra">
            <strong className="menu-titulo">Mudar as datas</strong>
            <button
              type="button"
              className="btn btn--icone"
              onClick={() => setArrastoDeDatas(null)}
              aria-label="Fechar"
            >
              <IconeFechar em={1} />
            </button>
          </div>
          <div className="janela-corpo">
            <DatasDoPedido
              pedidoId={arrastoDeDatas.pedidoId}
              /* 🔴 2026-10-06 · os dias da BARRA — os do autocarro (R-2). */
              referencial="viatura"
              inicio={arrastoDeDatas.inicio}
              fim={arrastoDeDatas.fim}
              propostos={{ inicio: arrastoDeDatas.propostoInicio, fim: arrastoDeDatas.propostoFim }}
              {...(recarregar === undefined ? {} : { recarregar })}
              irParaPeriodo={irParaPeriodo}
              janela={janelaDe(dados)}
            />
          </div>
        </div>
      ) : null}

      {/* ---- §11 · M · a ponta agarrada era ambígua (dia de rotação) -------------------------
          ⚠️ Não se escolhe uma das duas viagens ao acaso — ver `pega-da-barra.ts`. */}
      {avisoDaPegaDaBarra !== null ? (
        <div className="janela janela--motivo" style={arrastoDaPega.estilo} role="dialog" aria-label="Não é possível esticar por aqui">
          <div {...arrastoDaPega.cab} className="janela-cab faixa-quebra">
            <strong className="menu-titulo">Não é possível esticar por aqui</strong>
            <button
              type="button"
              className="btn btn--icone"
              onClick={() => setAvisoDaPegaDaBarra(null)}
              aria-label="Fechar"
            >
              <IconeFechar em={1} />
            </button>
          </div>
          <div className="janela-corpo">
            <p className="menu-nota">{avisoDaPegaDaBarra}</p>
            <div className="motivo-accoes faixa-quebra">
              <button type="button" className="btn" onClick={() => setAvisoDaPegaDaBarra(null)}>
                Ok
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ---- 🔴 OS DOIS SENTIDOS DA ESPERA (peça F §6.3) ----------------------------------
          ⚠️ O intervalo é o da CORRIDA inteira (`extentoDaCorrida`), como o `travar` — e não o
          dia seleccionado: `pousarEmEspera` recebe `dia0`/`dia1` e o rótulo do grupo do painel
          já diz «a viagem toda». Mandar um dia só tirava um dia do meio de uma viagem e deixava
          as duas metades no autocarro, que é uma escrita parcial anunciada como inteira. */}
      {aPorEmEspera !== null ? (
        <PorEmEspera
          key={chaveDaJanela(aPorEmEspera)}
          viaturaId={dados.viaturas[aPorEmEspera.v]?.id ?? ''}
          numero={dados.viaturas[aPorEmEspera.v]?.numero ?? ''}
          referencia={
            dados.corridas[dados.grelha[aPorEmEspera.v]?.[aPorEmEspera.d]?.[10] ?? -1] ?? ''
          }
          // ⚠️ A viagem inteira, com o dia de rotação em que parte (`extentoDaViagem`): cada
          //    viagem leva o SEU lado desse dia — 📗 «separando esse dia num dia para cada viagem».
          dia0={extentoDaViagem(dados.grelha[aPorEmEspera.v] ?? [], aPorEmEspera.d).dia0}
          dia1={extentoDaViagem(dados.grelha[aPorEmEspera.v] ?? [], aPorEmEspera.d).dia1}
          nomeDoDia={(d) => dataCurtaDoDia(dados, d)}
          fechar={() => setAPorEmEspera(null)}
          recarregar={recarregar}
        />
      ) : null}

      {aTrazerDeEspera !== null ? (
        <TrazerDeEspera
          key={chaveDaJanela(aTrazerDeEspera)}
          referencia={aTrazerDeEspera.referencia}
          dia0={aTrazerDeEspera.dia0}
          dia1={aTrazerDeEspera.dia1}
          viaturaInicial={
            aTrazerDeEspera.v === undefined ? undefined : dados.viaturas[aTrazerDeEspera.v]?.id
          }
          // ⚠️ A frota INTEIRA — a mesma lista do «mover» desde a peça G. Uma viagem em espera
          //   não tem autocarro de origem, logo não há nenhum a excluir.
          viaturas={dados.viaturas}
          nomeDoDia={(d) => dataCurtaDoDia(dados, d)}
          fechar={() => setATrazerDeEspera(null)}
          recarregar={recarregar}
          verNoMapa={irPara}
        />
      ) : null}

      {/* ---- 🔴 OS DOIS SENTIDOS DA CÓPIA (peça F §6.5) -----------------------------------
          ⚠️ O intervalo e a referência vêm **já resolvidos** do gesto (`extentoDaCorrida` +
          `dados.corridas`), como no `porEmEspera`: a janela não volta a lê-los da grelha, senão
          eram duas leituras do mesmo facto — e a que divergisse copiava outros dias. */}
      {aCopiarPeriodo !== null ? (
        <CopiarPeriodo
          key={chaveDaJanela(aCopiarPeriodo)}
          viaturaOrigemId={dados.viaturas[aCopiarPeriodo.v]?.id ?? ''}
          numeroOrigem={dados.viaturas[aCopiarPeriodo.v]?.numero ?? ''}
          referencia={aCopiarPeriodo.ref}
          dia0={aCopiarPeriodo.dia0}
          dia1={aCopiarPeriodo.dia1}
          viaturas={dados.viaturas}
          nomeDoDia={(d) => dataCurtaDoDia(dados, d)}
          fechar={() => setACopiarPeriodo(null)}
          recarregar={recarregar}
          verNoMapa={irPara}
          // ⚠️ J · a sigla é SÓ para o fantasma sair com a cor certa, e sai do primeiro dia do
          //   intervalo já resolvido. Sem ela o mapa pinta-o cinzento, que é a regra do
          //   `irPara`: uma barra com a cor de OUTRO cliente é pior do que uma neutra.
          sigla={
            dados.clientes[dados.grelha[aCopiarPeriodo.v]?.[aCopiarPeriodo.dia0]?.[0] ?? -1]?.[0] ??
            ''
          }
          previsualizar={setPrevisaoDoGesto}
        />
      ) : null}

      {aRetirarCopia !== null ? (
        <RetirarCopia
          key={chaveDaJanela(aRetirarCopia)}
          viaturaId={dados.viaturas[aRetirarCopia.v]?.id ?? ''}
          numero={dados.viaturas[aRetirarCopia.v]?.numero ?? ''}
          referencia={aRetirarCopia.ref}
          dia0={aRetirarCopia.dia0}
          dia1={aRetirarCopia.dia1}
          outros={outrosAutocarrosDaViagem}
          nomeDoDia={(d) => dataCurtaDoDia(dados, d)}
          fechar={() => setARetirarCopia(null)}
          recarregar={recarregar}
          verNoMapa={irPara}
        />
      ) : null}

      {aFixar !== null ? (
        <Alfinete
          key={chaveDaJanela(aFixar)}
          dados={dados}
          viatura={aFixar.v}
          dia0={aFixar.d0}
          dia1={aFixar.d1}
          etiquetaDoPeriodo={
            aFixar.d0 === aFixar.d1
              ? dataCurtaDoDia(dados, aFixar.d0)
              : `${dataCurtaDoDia(dados, aFixar.d0)} a ${dataCurtaDoDia(dados, aFixar.d1)}`
          }
          fechar={() => setAFixar(null)}
          recarregar={recarregar}
        />
      ) : null}

      {aCancelar !== null ? (
        <CancelarServico
          key={chaveDaJanela(aCancelar)}
          viaturaId={aCancelar.v < 0 ? '' : (dados.viaturas[aCancelar.v]?.id ?? '')}
          subcontratadoId={
            aCancelar.v < 0 ? (dados.subcontratados[-1 - aCancelar.v]?.id ?? '') : ''
          }
          dia={aCancelar.d}
          fechar={() => setACancelar(null)}
          recarregar={recarregar}
        />
      ) : null}

      {/* ⚠️ **«E VOLTAR A PLANEAR»:** o `planearDeNovo` reaproveita o mesmo mecanismo do
          pedido recém-criado — abre a lista **filtrada por este pedido**, que é onde o
          «Planear» verificado vive. 📌 Uma segunda entrada para o motor a partir daqui era
          duas formulações do mesmo gesto, e a que divergisse era a que ninguém corre. */}
      {/* ⚠️⚠️ **B30 / T-74 · O ARRASTO NÃO ESCREVE SOZINHO.** Largar um bloco na linha
          errada é um gesto de meio segundo, e o que ele faz é mudar de autocarro um serviço
          já comunicado. 📌 O gesto irmão (arrastar um pedido da lista) também confirma — e
          por menos: aquele CRIA, este mexe no que já está. */}
      {aMoverBloco !== null ? (
        <MoverBloco
          key={chaveDaJanela(aMoverBloco)}
          servico={aMoverBloco.servico}
          viaturaOrigemId={dados.viaturas[aMoverBloco.servico.viatura]?.id ?? ''}
          viaturaDestinoId={dados.viaturas[aMoverBloco.destino]?.id ?? ''}
          numeroOrigem={dados.viaturas[aMoverBloco.servico.viatura]?.numero ?? ''}
          numeroDestino={dados.viaturas[aMoverBloco.destino]?.numero ?? ''}
          // ⚠️⚠️ G · a lista INTEIRA, e não só o par origem/destino do arrasto — é o que
          //   deixa escolher um autocarro que não estava à vista. `opcoesDeDestino` (dentro
          //   do `MoverBloco`) é quem exclui a origem e nunca deixa cair a escolha do filtro.
          viaturas={dados.viaturas}
          dataInicio={dataCurtaDoDia(dados, aMoverBloco.servico.dia0)}
          dataFim={dataCurtaDoDia(dados, aMoverBloco.servico.dia1)}
          // ⚠️ O dia onde ele AGARROU — é o atalho do «a partir daqui», que é o caso
          //   maioritário (11 de 12 partições reais movem a cauda).
          diaAgarrado={aMoverBloco.servico.diaAgarrado}
          nomeDoDia={(d) => dataCurtaDoDia(dados, d)}
          fechar={() => setAMoverBloco(null)}
          recarregar={recarregar}
          // ⚠️ Por `id` directo (G, 2026-09-14): o `MoverBloco` já sabe o id de quem
          //   escolheu na lista, e `irPara` já aceita um id de viatura ou subcontratado.
          //   Ir buscar o índice aqui era uma tradução a mais, e uma classe de erro a mais
          //   se a ordem da grelha alguma vez mudar entre o arrasto e o clique em «Mover».
          verNoMapa={irPara}
          // ---- 🔴 R · MOVER VÁRIAS VIAGENS DE UMA VEZ (2026-09-16) -----------------------
          //   📗 *«tipo 1 de Setembro a 20 de Outubro e passar tudo para outra linha»*.
          //   ⚠️ **Uma FUNÇÃO e não uma lista**, porque a janela precisa das viagens das DUAS
          //   linhas — a de origem e a do destino que o selector pode mudar a meio. Sai da
          //   grelha que o mapa já tem em memória: não custa uma consulta.
          viagensDe={viagensDaViatura}
          dataDoDia={(d) => dataIsoDoDia(janelaDe(dados), d)}
          diaDaData={(iso) => diaDoAnoDaData(janelaDe(dados), iso)}
          // ⚠️⚠️ J · o fantasma na linha de DESTINO enquanto a janela está aberta — 📄 §11 · J.
          //   📌 A janela é `position: fixed` e arrastável exactamente para isto: 📗 *«quero
          //   que seja meio que um pop-up que permita ver o mapa enquanto selecciona a opção»*.
          previsualizar={setPrevisaoDoGesto}
        />
      ) : null}

      {/* ---- ⚠️⚠️ B1-(2) · TROCAR ESTE SUBCONTRATO POR OUTRO ---------------------------
          📗 *«tem de dar para alterar os subcontratos para uns outros»*. Mesma rota, mesmos
          travões, mesma auditoria da troca de viatura — o que mudou foi a ORIGEM poder ser
          uma empresa. */}
      {aTrocarSub !== null ? (
        <TrocarSubcontratado
          key={chaveDaJanela(aTrocarSub)}
          dados={dados}
          sub={aTrocarSub.s}
          dia={aTrocarSub.d}
          fechar={() => setATrocarSub(null)}
          recarregar={recarregar}
          irPara={irPara}
        />
      ) : null}

      {/* ---- ⚠️⚠️ C · CRIAR UM SUBCONTRATO À MÃO -----------------------------------------
          📌 **Isto não escreve nada**: escolhe a empresa e o pedido, e abre o «Planear» com a
          empresa garantida na lista. A reserva continua a ser a de sempre. */}
      {aSubcontratarAMao !== null ? (
        <SubcontratarAMao
          key={chaveDaJanela(aSubcontratarAMao)}
          dados={dados}
          sub={aSubcontratarAMao.s}
          dia={aSubcontratarAMao.d}
          fechar={() => setASubcontratarAMao(null)}
          aoPlanear={(pedido, subcontratadoId) => {
            // ⚠️ Fecha a escolha ao abrir o «Planear»: duas janelas empilhadas sobre a mesma
            //    decisão fazem o planeador escolher em qual carregar.
            setASubcontratarAMao(null);
            setPlanearComParceiro({ pedido, subcontratadoId });
          }}
        />
      ) : null}

      {/* ---- ⚠️⚠️ O «PLANEAR», aberto do mapa, com a empresa GARANTIDA -------------------
          📌 O MESMO componente que a lista de pedidos e as consultas abrem. Uma segunda versão
          aqui teria de repetir o motivo obrigatório (D-007), a escolha de condutor e a
          auditoria — e a que divergisse era a que ninguém corre. */}
      {planearComParceiro !== null ? (
        <PainelPlanear
          key={chaveDaJanela(planearComParceiro)}
          pedido={planearComParceiro.pedido}
          garantirSubcontratado={planearComParceiro.subcontratadoId}
          fechar={() => setPlanearComParceiro(null)}
          nomeLocalidade={(i) => nomeLocal(dados, i)}
          nomeMotorista={(i) => nomeDoMotorista(dados, i) || '—'}
          numeroViatura={(i) => dados.viaturas[i]?.numero ?? String(i)}
          recarregar={recarregar}
          irPara={irPara}
        />
      ) : null}

      {aRetirar !== null ? (
        <RetirarDoMapa
          key={chaveDaJanela(aRetirar)}
          viaturaId={aRetirar.v < 0 ? '' : (dados.viaturas[aRetirar.v]?.id ?? '')}
          subcontratadoId={
            aRetirar.v < 0 ? (dados.subcontratados[-1 - aRetirar.v]?.id ?? '') : ''
          }
          dia={aRetirar.d}
          fechar={() => setARetirar(null)}
          recarregar={recarregar}
          planearDeNovo={mostrarPedidoNaLista}
          // 🔴 2026-10-26 · «retirar um período» — as viagens da linha, da mesma grelha.
          {...(aRetirar.v >= 0
            ? {
                periodo: {
                  viagens: viagensDaViatura(dados.viaturas[aRetirar.v]?.id ?? ''),
                  janela: janelaDe(dados),
                  // ⚠️ O índice VERDADEIRO de hoje (fora da fita é negativo ou maior que ela), e não
                  //    o `-1` do `hoje` do ecrã: com a fita no passado, `-1` fazia o ecrã anunciar
                  //    como retiráveis dias que já passaram (achado da revisão de 2026-10-26).
                  hoje: indiceDaData(janelaDe(dados), hojeEmPortugal()),
                },
              }
            : {})}
        />
      ) : null}

      {/* 📗 D-091 §2-bis — sem IA nenhuma, e é o que um agente teria de chamar. */}
        {/* ⚠️⚠️ **`recarregar` FALTAVA AQUI, e era o defeito que o cliente viu:** *«os
            rearranjos não estão a ser alterados no mapa»*. Todas as outras janelas que
            escrevem o recebem; esta não, e aceitar um movimento gravava na base, recalculava
            a lista — e deixava o mapa a mostrar o plano velho até alguém recarregar a página.
            📌 Uma escrita que não se vê é indistinguível de uma escrita que não aconteceu. */}
      {gestaoAberta ? (
        <Gestao
          fechar={() => {
            setGestaoAberta(false);
            setAbaDaGestao(undefined);
          }}
          recarregar={recarregar}
          abaInicial={abaDaGestao}
        />
      ) : null}
      {porFecharAberto ? (
        <PorFechar
          fechar={() => setPorFecharAberto(false)}
          recarregar={recarregar}
          irPara={irPara}
        />
      ) : null}
      {porConfirmarAberto ? (
        <PorConfirmar
          fechar={() => setPorConfirmarAberto(false)}
          recarregar={recarregar}
          irPara={irPara}
        />
      ) : null}
      {/* ⚠️⚠️ **Cada linha da fila abre o ecra que JA responde a pergunta** — nunca um caminho
          novo. 📌 E as familias sem ecra proprio nao levam botao nenhum: um botao que fecha a
          janela e nao leva a sitio nenhum e um beco, e este projecto ja aprendeu que um estado
          sem saida se le como avaria. O que a linha faz nesse caso e dizer onde se ve. */}
      {atencaoAberta ? (
        <Atencao
          fechar={() => setAtencaoAberta(false)}
          // 2026-10-27 · «dias por cobrir»: cada pedido leva ao mapa nos dias em falta.
          irPara={(v, d0, d1) => irPara(v, d0, d1)}
          abrir={(destino) => {
            if (destino === 'divergencias') setRegrasAbertas(true);
            else if (destino === 'por-fechar') setPorFecharAberto(true);
            // ⚠️⚠️ **A fila mandava para «pedidos», e la NAO se confirma em lista.** 📌 A
            //    familia chama-se «reservas por efectivar» e o unico sitio onde se efectivava
            //    era o painel de detalhe de UMA celula — a linha levava a um ecra que conta o
            //    problema e nao o resolve, que e a definicao de beco neste projecto.
            else if (destino === 'por-confirmar') setPorConfirmarAberto(true);
            else if (destino === 'pedidos') setPedidosAbertos(true);
            // ⚠️ D-097 parte B: as localidades sem aeroporto determinado marcam-se na Gestão —
            //    📗 *«tem de ser configurável na gestão, logicamente»*. Abre **no separador
            //    delas**: abrir em «viaturas» era levar ao ecrã e não à pergunta.
            else if (destino === 'gestao-localidades') {
              setAbaDaGestao('localidade');
              setGestaoAberta(true);
            }
            else if (destino === 'consultas-paradas') {
              setAbaDasConsultas('paradas');
              setConsultasAbertas(true);
            } else if (destino === 'consultas-regressar') {
              setAbaDasConsultas('regresso');
              setConsultasAbertas(true);
            } else if (destino === 'consultas-transito') {
              // ⚠️⚠️ B15 CONTINUA a não ser um beco (2026-08-31): a **paragem** é o objecto onde
              //    o trânsito se decide, e o cartão dela já sabe escrever os dias. O separador
              //    saiu; a família abre as paradas filtradas por «precisam de trânsito».
              setAbaDasConsultas('paradas');
              setFiltroDasConsultas('precisam-transito');
              setConsultasAbertas(true);
            }
          }}
        />
      ) : null}
      {/* ⚠️⚠️ **A PALETA NAO SABE QUE JANELAS EXISTEM** — os comandos vem daqui. 📌 Com a lista
          dentro dela, acrescentar uma janela obrigava a lembrar-se de dois sitios, e o que fica
          esquecido e sempre o segundo. */}
      {paletaAberta ? (
        <PaletaDeComandos
          dados={dados}
          fechar={() => setPaletaAberta(false)}
          comandos={comandosDaPaleta}
          // §12.3 · os verbos da selecção, com os que não se aplicam a dizer porquê (peça H).
          contextuais={contextuaisDaPaleta}
          irParaViatura={(id) => irPara(id, -1)}
          irParaDia={(d) => irParaPeriodo(d, d)}
          filtrarCliente={(i) => alternarNoFiltro('clientes', i, dados.clientes.length)}
          filtrarMotorista={(i) => alternarNoFiltro('motoristas', i, dados.motoristas.length)}
          filtrarItinerario={(i) => alternarNoFiltro('rotas', i, dados.itinerarios.length)}
        />
      ) : null}
      {sessaoAberta ? <Sessao fechar={() => setSessaoAberta(() => false)} /> : null}

      {consultasAbertas ? (
        <Consultas
          fechar={() => setConsultasAbertas(false)}
          encaixeInicial={encaixePara ?? undefined}
          abaInicial={abaDasConsultas ?? undefined}
          filtroInicial={filtroDasConsultas ?? undefined}
          irParaMotorista={irParaMotorista}
          irPara={irPara}
          // ⚠️⚠️ **B31 · A PRÉ-VISUALIZAÇÃO DO LOTE, e o mapa é quem a guarda.**
          //
          //    📌 O estado vive **aqui** e não nas Consultas porque é o mapa que desenha; e
          //    porque fechar a janela das Consultas tem de o limpar — uma barra fantasma sem
          //    o botão que a retira é exactamente «uma escrita que não se vê» ao contrário:
          //    um sinal que ninguém consegue apagar. Quem limpa é o efeito de desmontagem do
          //    próprio ecrã, que chama isto com `null`.
          definirPrevisaoDoLote={setPrevisaoDoLote}
          recarregar={recarregar}
          // ⚠️ **OS MESMOS TRÊS que vão para o `Pedidos`** — 📗 *«tens de permitir planear aqui
          //    também exactamente com a mesma lógica»*. O separador «O que encaixa» abre agora o
          //    `PainelPlanear`, e ele traduz índices em nomes. 📌 Resolver isto dentro das
          //    consultas dava uma segunda forma de traduzir o mesmo índice, e a que divergisse
          //    escreveria o nome de outra localidade ao lado da opção certa.
          nomeLocalidade={(i) => nomeLocal(dados, i)}
          nomeMotorista={(i) => nomeDoMotorista(dados, i) || '—'}
          numeroViatura={(i) => dados.viaturas[i]?.numero ?? String(i)}
          // ⚠️ A aba «Onde estava X?» procura em memória, sem chamada ao servidor — ver
          //    o cabeçalho de `localizacao.ts`. Precisa da grelha inteira, não só dos
          //    resolvedores índice→nome acima.
          dados={dados}
        />
      ) : null}

      {caixaAberta ? (
        <CaixaDeEntrada
          aoFechar={() => definirCaixaAberta(false)}
          // ⚠️ Neste bloco só se abre o «Novo pedido» que já existe, VAZIO. A ligação ao
          //    email é trabalho do bloco B — aqui o planeador copia e cola.
          aoCriarPedido={() => { definirCaixaAberta(false); setACriarPedido({}); }}
          aoValidar={(entregaId, bloco) => definirPropostaAberta({ entregaId, bloco })}
        />
      ) : null}

      {/* ⚠⚠ **REGRA 8 · `aoGravar` é a TERCEIRA chamada**, e a razão de o corpo ter saído
          para `mostrarPedidoNaLista`. Gravar a proposta cria um pedido; sem isto ele ficava na
          base e a lista continuava a mostrar a cópia que leu ANTES da escrita — o mesmo defeito
          que o `recargas` do `Pedidos` existe para resolver, por uma porta nova.
          ⚠️ O comentário fica ACIMA do elemento: dentro da lista de atributos já custou quatro
          erros de sintaxe a esta base. */}
      {propostaAberta === null ? null : (
        <ValidarProposta
          entregaId={propostaAberta.entregaId}
          blocoInicial={propostaAberta.bloco}
          aoFechar={() => definirPropostaAberta(null)}
          aoGravar={mostrarPedidoNaLista}
          // 🔴 §4.3 · a primeira saída do confronto: **procurar** na lista de pedidos a reserva
          //    que já está no plano, para a corrigir à mão — aplicar a alteração de um email
          //    não é alcançável (§7.1). ⚠️ É o MESMO corpo do `aoGravar` (o filtro de texto do
          //    `Pedidos`), e é por isso que ele serve um `id` de pedido **e** uma referência de
          //    cliente. 📌 Um segundo corpo aqui era a quarta versão de «mostrar um pedido».
          aoProcurarNaLista={mostrarPedidoNaLista}
          // 🔴 §3 · a saída de «procurei na Gestão e não está»: leva-se lá, no separador dos
          //    clientes. ⚠️ **Não é um formulário dentro da proposta**, e a razão está no
          //    `SelectorDeCliente`: `guardar` exige um `id` e não o gera, logo um formulário ali
          //    punha o planeador a inventar `CLI0042` — o defeito de texto livre que este
          //    trabalho veio corrigir, transplantado para o gesto ao lado.
          // ⚠️ **Abre no separador dos clientes e não em «viaturas»**, que é o precedente do
          //    `gestao-localidades`: abrir no ecrã e não na pergunta é levar a meio caminho.
          aoCriarCliente={() => {
            setAbaDaGestao('cliente');
            setGestaoAberta(true);
          }}
          /*
            🔴🔴 **«VER O ITINERÁRIO NO MAPA» — 📗 *«um outro [botão] para ver o itinerário tanto
               nos pedidos como no mapa»* (2026-09-15).**

            ⚠️⚠️ **A CONVERSÃO DATA → ÍNDICE DE DIA VIVE AQUI, E É POR ISSO QUE A ASSINATURA
               LEVA DATAS.** O índice do mapa é *dias desde 1 de Janeiro do ANO DO PLANO*, e o
               ano — `dados.ano` — só existe deste lado. 📌 Deixar o ecrã de validar derivá-lo da
               própria data (`dataInicio.slice(0, 4)`) era uma SEGUNDA formulação de «que ano é
               que o mapa mostra»: numa reserva que atravessa o Ano Novo, ou com o mapa aberto
               noutro ano, ela divergia e o salto aterrava no dia errado **em silêncio**.

            ⚠️ **E é o `diaDoAnoDoPlano` que já existe, não uma sexta formulação.** Ele ancora no
               ano do PLANO (e não no da entrada) e devolve **`−1`** para uma data de outro ano
               ou para lixo — que é exactamente o que o `irPara` lê como *«não há dia»*: leva o
               olho à LINHA da viatura sem apontar coluna nenhuma. 📌 Degradar para «a linha, sem
               dia» é honesto; apontar `2` — que é o que a forma antiga dava a `2027-01-03` num
               plano de 2026 — era apontar 3 de Janeiro com toda a confiança.
          */
          aoVerNoMapa={(v, de, ate) => {
            // ⚠️⚠️ `import()` e não `import` estático — ver a nota no topo do ficheiro, onde
            //    as janelas diferidas se declaram. `Consultas.js` (83 kB) já vem carregado
            //    nesta altura sempre que se chega a ver esta proposta a partir das Consultas,
            //    e nos outros casos o atraso é o de uma transferência que corre em paralelo
            //    com o resto — nunca visível, porque `irPara` só desloca o ecrã depois.
            void import('./Consultas.js').then(() => {
              // 🔴 2026-10-26 · o índice verdadeiro, sem o `-1` de fora da fita: `irPara` muda de fita.
              irPara(v, indiceDaData(janelaDe(dados), de), indiceDaData(janelaDe(dados), ate));
            });
          }}
        />
      )}

      {/* A urgência: a viatura está parada e há gente à espera dela. */}
      {aCriarPedido !== null ? (
        <NovoPedido
          key={chaveDaJanela(aCriarPedido)}
          dados={dados}
          duplicarDe={aCriarPedido.duplicarDe}
          fechar={() => setACriarPedido(null)}
          // ⚠️ A lista tem de o mostrar logo: criar um pedido e não o ver aparecer é
          //    indistinguível de a criação ter falhado.
          aoCriar={(id) => {
            // ⚠️ Recarregar **e** mostrar — e o «mostrar» vive num só sítio (regra 8): ver
            //    `mostrarPedidoNaLista`. O `recarregar` fica AQUI porque é deste caminho: o
            //    «Novo pedido» pode nascer de um dia do mapa, e aí a grelha mudou.
            recarregar?.();
            mostrarPedidoNaLista(id);
          }}
        />
      ) : null}

      {aParar !== null ? (
        <Paragem
          key={chaveDaJanela(aParar)}
          dados={dados}
          viatura={aParar.v}
          dia={aParar.d}
          fechar={() => setAParar(null)}
          recarregar={recarregar}
        />
      ) : null}

      {aMarcar !== null ? (
        <Indisponibilidade
          key={chaveDaJanela(aMarcar)}
          dados={dados}
          viatura={aMarcar.v}
          dia={aMarcar.d}
          fechar={() => setAMarcar(null)}
          recarregar={recarregar}
          aoDeixarServicos={(d) => setASubstituir({ v: aMarcar.v, d })}
          // ⚠️ Peça 1 · a extensão do `Shift`+setas propõe o período — só quando é desta viatura.
          {...(intervaloSeleccionado !== null && intervaloSeleccionado.v === aMarcar.v
            ? { intervaloSugerido: { d0: intervaloSeleccionado.d0, d1: intervaloSeleccionado.d1 } }
            : {})}
        />
      ) : null}

      {aSubstituir !== null ? (
        <Substituir
          key={chaveDaJanela(aSubstituir)}
          dados={dados}
          viatura={aSubstituir.v}
          dia={aSubstituir.d}
          fechar={() => setASubstituir(null)}
          recarregar={recarregar}
          irPara={irPara}
        />
      ) : null}

      {aEditar !== null ? (
        <EditarAlocacao
          key={chaveDaJanela(aEditar)}
          janela={janelaDe(dados)}
          viaturaId={dados.viaturas[aEditar.viatura]?.id ?? ''}
          viaturaNumero={dados.viaturas[aEditar.viatura]?.numero ?? String(aEditar.viatura)}
          dia0={aEditar.dia0}
          dia1={aEditar.dia1}
          condAte={aEditar.condAte ?? aEditar.dia1}
          // 2026-10-30 · a mesma data curta de toda a app (`periodoCurto`): a forma antiga
          //    escrevia «31–5 de Novembro» numa viagem de 31 Out a 5 Nov.
          periodo={periodoCurto(janelaDe(dados), aEditar.dia0, aEditar.dia1)}
          nomeViatura={(id) => nomeDoExecutor(id, nomesDosExecutoresDoMapa(dados))}
          // As duas viagens do dia de rotação, quando as há. Vem do MAPA porque é ele que tem a
          // célula; ver `viagensDoDia` em `EditarAlocacao`.
          referenciaAlvo={aEditar.referenciaAlvo}
          diaDePartida={aEditar.diaDePartida}
          // ⚠️⚠️ **O papel decide-se pela CÉLULA de `dia0`, não por haver referência.** Aberto no
          //    dia seguinte de uma viagem B que parte num dia de rotação, e sendo esse dia OUTRA
          //    rotação (B acaba, C parte), «haver referência» marcava «a que PARTE» — que é C, e o
          //    condutor de B ia para C. 📌 A viagem nomeada no campo 13 de `dia0` → `partida`.
          papelInicial={(() => {
            const c = dados.grelha[aEditar.viatura]?.[aEditar.dia0];
            return aEditar.referenciaAlvo !== undefined &&
              c != null &&
              c[13] >= 0 &&
              dados.corridas[c[13]] === aEditar.referenciaAlvo
              ? 'partida'
              : 'principal';
          })()}
          viagensDoDia={(() => {
            const c = dados.grelha[aEditar.viatura]?.[aEditar.dia0];
            if (c == null || c[13] < 0) return null;
            return {
              chega: dados.corridas[c[10]] ?? '—',
              parte: dados.corridas[c[13]] ?? '—',
            };
          })()}
          fechar={() => setAEditar(null)}
          recarregar={recarregar}
        />
      ) : null}

      {/* ⚠️ §5.6 exigência 3 — o diário DESTA viagem. O mesmo componente que a barra de topo
          abre sem alvo: uma segunda janela «histórico» teria uma segunda formulação da frase e
          do agrupamento, e a que divergisse era a que ninguém corre. */}
      {aVerDiario !== null ? (
        // ⚠️⚠️ **`recarregarMapa` NÃO ESTAVA AQUI, e era a metade que faltava.** O `Desfazer.tsx`
        //    (a barra de topo) passava-o e este caminho não — e é **este** que abre o diário com
        //    `alvo`, que é a única condição em que `ComoEstavaDaLinha` se desenha
        //    (`alvoActivo !== null`, `Diario.tsx`). Logo o verbo que só existe no diário da
        //    CÉLULA era exactamente o que gravava sem a grelha por trás mudar.
        // 📌 *«Uma escrita que não se vê é indistinguível de uma que não aconteceu»* — a família
        //    por que esta casa reverteu um gesto inteiro (`e7cae6d7`).
        <Diario
          key={chaveDaJanela(aVerDiario)}
          alvo={aVerDiario}
          fechar={() => setAVerDiario(null)}
          recarregarMapa={recarregar}
        />
      ) : null}

      {largado !== null ? (
        <LargarPedido
          key={chaveDaJanela(largado)}
          pedido={largado.pedido}
          viaturaId={dados.viaturas[largado.viatura]?.id ?? ''}
          viaturaNumero={dados.viaturas[largado.viatura]?.numero ?? String(largado.viatura)}
          diaLargado={largado.dia}
          diaLargadoTexto={
            Number.isFinite(largado.dia) && largado.dia >= 0
              ? `${dados.diaDoMes[largado.dia] ?? ''} de ${MESES[dados.mesPorDia[largado.dia] ?? 0] ?? ''}`
              : ''
          }
          fechar={() => setLargado(null)}
          recarregar={recarregar}
          irPara={irPara}
        />
      ) : null}
    </div>
    </LigacaoAoMapa.Provider>
  );
}

// ---------------------------------------------------------------------------
// Cabeçalho fixo: mês + dia
// ---------------------------------------------------------------------------

// ⚠️ **Exportado pela mesma razão que a `Linha`:** a folha impressa usa este cabeçalho, com a
//    sua janela. Um cabeçalho próprio no papel era a segunda formulação a entrar pelo sítio
//    onde ninguém olha — os meses e os dias.
export function Cabecalho({
  dados,
  janela,
  inicioSemana,
  offsetsDias,
  hoje,
  periodo,
  colunaSeleccionada,
  aoClicarDia,
}: {
  dados: DadosMapa;
  janela: Janela;
  inicioSemana: readonly boolean[];
  offsetsDias: readonly number[];
  /** O índice de hoje, ou `-1`. Ver `diaDeHoje`. */
  hoje: number;
  /**
   * ---- ⚠️⚠️ PEÇA 2 · A COLUNA SELECCIONADA — §12.4-bis (2026-09-28) ------------------------
   *
   * `undefined`/fora da janela em quase todos os renders — só um dia leva a classe, pela mesma
   * disciplina de `hoje`/`periodo`. **A banda que cobre a grelha inteira NÃO vive aqui**: é um
   * único elemento sobreposto em `Mapa` (como `.mapa-alvo`), e não uma classe por linha — isto
   * marca só o PRÓPRIO cabeçalho, o alvo do clique que a liga e desliga.
   */
  colunaSeleccionada?: number | undefined;
  /**
   * ⚠️⚠️ **NÃO dispara se o clique caiu na TIRA de redimensionar** (os últimos `LARGURA_TIRA`
   * píxeis da direita) — é a mesma exclusão que `bordaSob` já aplica ao arrasto, para os dois
   * gestos nunca competirem pelo mesmo pixel (ver o `onClick`, abaixo).
   */
  aoClicarDia?: ((d: number) => void) | undefined;
  /**
   * ⚠️⚠️ **O ALVO DO «Ver no mapa» DE UM PEDIDO — e está AQUI, no cabeçalho dos dias.**
   *
   * 📗 Cliente, 2026-08-10: *«o ver no mapa está com o problema que tínhamos antes nas
   * consultas»*.
   *
   * ⚠️⚠️ **Tinha razão, e a minha justificação estava errada.** Eu escrevi que atravessar a
   * frota era o desenho certo «porque não há linha para apontar» — e desenhei duas réguas de
   * ponta a ponta, que é **exactamente a v2 que ele já tinha recusado** nas consultas. O
   * argumento era bom e o resultado no ecrã era o mesmo, o que faz do argumento um mau
   * argumento.
   *
   * 📌 **A lição que a v3 já ensinava, generalizada: marca-se A COISA, não tudo o que a
   * intersecta.** O alvo de um pedido por planear é um **intervalo de dias** — e os dias
   * vivem no cabeçalho. Marcar 4 células de cabeçalho diz «estes dias» sem pintar 250 linhas
   * nem tapar nada do que se foi lá ler.
   *
   * ⚠️⚠️ **E tem de ser no cabeçalho por uma razão técnica, não só de desenho:** o cabeçalho é
   * `position: sticky`, logo o realce **acompanha o deslocamento vertical**. Um rectângulo
   * absoluto em `top: 0` dentro de `.mapa-conteudo` desaparecia ao descer a grelha.
   */
  periodo?: { readonly d0: number; readonly d1: number } | undefined;
}) {
  const meses = [];
  const dias = [];

  // Agrupar os dias visíveis por mês: uma célula por mês, não um rótulo por dia.
  let inicio = janela.dia0;
  for (let d = janela.dia0; d < janela.dia1; d++) {
    const mes = dados.mesPorDia[d];
    const seguinte = d + 1 < janela.dia1 ? dados.mesPorDia[d + 1] : -1;
    if (mes !== seguinte) {
      meses.push(
        <div
          key={inicio}
          className="mes"
          // A largura do mês é a soma das larguras dos seus dias, que podem ser
          // diferentes entre si — daí a diferença de posições e não uma multiplicação.
          style={{ width: (offsetsDias[d + 1] ?? 0) - (offsetsDias[inicio] ?? 0) }}
        >
          <span>{MESES[mes ?? 0]}</span>
        </div>,
      );
      inicio = d + 1;
    }
    const data = dataUtcDoIndice(janelaDe(dados), d);
    dias.push(
      <div
        key={d}
        className={classes(
          'dia-cab',
          dados.fimDeSemana[d] === true && 'dia-cab--fds',
          inicioSemana[d] === true && 'dia-cab--semana',
          // ⚠️ As pontas do intervalo levam classe própria: sem elas, quatro células iguais
          //    não dizem onde o pedido começa nem onde acaba — e essas duas datas são
          //    metade da informação.
          // ⚠️⚠️ **HOJE**, no cabecalho e na grelha. Ver `diaDeHoje`.
          d === hoje && 'dia-cab--hoje',
          periodo !== undefined && d >= periodo.d0 && d <= periodo.d1 && 'dia-cab--alvo',
          periodo !== undefined && d === periodo.d0 && 'dia-cab--alvo-ini',
          periodo !== undefined && d === periodo.d1 && 'dia-cab--alvo-fim',
          // ⚠️ Peça 2 · só o PRÓPRIO cabeçalho — a banda que cobre a grelha é um elemento à
          //    parte (ver a nota da prop `colunaSeleccionada`, acima).
          d === colunaSeleccionada && 'dia-cab--seleccionada',
        )}
        // ⚠️ `data-dia` é o que o arrasto usa para saber QUAL coluna redimensionar.
        // Deduzi-lo de coordenadas seria repetir a busca binária num sítio onde ela
        // pode divergir da que desenha.
        data-dia={d}
        role="columnheader"
        style={{ width: tamanho(offsetsDias, d) }}
        aria-selected={d === colunaSeleccionada}
        // ---- ⚠️⚠️ PEÇA 2 · CLICAR SELECCIONA A COLUNA — §12.4-bis (2026-09-28) ---------------
        //
        //   ⚠️ **Exclui a TIRA de redimensionar** (os últimos `LARGURA_TIRA` píxeis da direita)
        //   — é a MESMA fronteira que `bordaSob` (em `Mapa`) já usa para o arrasto da largura
        //   da coluna (D-085). Sem esta exclusão, largar o arrasto ali também seleccionava a
        //   coluna por baixo, e os dois gestos passavam a competir pelo mesmo pixel.
        onClick={
          aoClicarDia === undefined
            ? undefined
            : (e) => {
                const r = e.currentTarget.getBoundingClientRect();
                if (e.clientX >= r.right - LARGURA_TIRA) return;
                aoClicarDia(d);
              }
        }
      >
        <span className="dia-cab-num">{dados.diaDoMes[d]}</span>
        <span className="dia-cab-sem">{DIAS_SEMANA[data.getUTCDay()]}</span>
      </div>,
    );
  }

  return (
    <div className="mapa-cabecalho">
      <div className="canto">
        <span className="canto-ano">{dados.ano}</span>
        <span className="canto-rot">
          {dados.viaturas.length} viaturas · {dados.nDias} dias
        </span>
      </div>
      <div className="faixas">
        <div className="faixa-cab" style={{ paddingLeft: offsetsDias[janela.dia0] ?? 0 }}>
          {meses}
        </div>
        {/* ⚠️ §12.6 · a única das duas que corresponde 1:1 às colunas da grelha — a dos
            meses agrupa vários dias por célula e não é uma "linha de cabeçalho de coluna". */}
        <div
          className="faixa-cab"
          role="row"
          style={{ paddingLeft: offsetsDias[janela.dia0] ?? 0 }}
        >
          {dias}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cabeçalho da aplicação
// ---------------------------------------------------------------------------

/**
 * Três zonas, e a ordem é a do pedido do cliente: identidade à esquerda, caixas
 * de comando ao centro, medição à direita.
 *
 * O título leva o subtítulo **por baixo** — «Turismo · GVA» era uma etiqueta
 * solta ao lado e lia-se como se fosse outra coisa.
 */
function Topo({
  dados,
  filtro,
  definirFiltro,
  corLigada,
  definirCor,
  corPor,
  definirCorPor,
  esconderInactivas,
  definirEsconderInactivas,
  janela,
  p95,
  vistaAgora,
  pedidosAbertos,
  consultasAbertas,
  alternarConsultas,
  caixaAberta,
  alternarCaixa,
  gestaoAberta,
  alternarGestao,
  sessaoAberta,
  setSessaoAberta,
  atencaoAberta,
  alternarAtencao,
  paletaAberta,
  abrirPaleta,
  resumoDoFiltro,
  limparEixo,
  limparTudo,
  atencaoFamilias,
  emailsPorTratar = null,
  irParaHoje,
  navegacaoDaFita,
  nomeCurtoDaSessao,
  formacaoNoMapa,
  planoNoTopo,
  soPartilhado,
  tomDoPlano,
  alternarPedidos,
  recarregar,
  abrirRegras,
  abrirContas,
  etagDoMapa,
}: {
  dados: DadosMapa;
  filtro: Filtro;
  definirFiltro: (f: Filtro) => void;
  corLigada: boolean;
  definirCor: (b: boolean) => void;
  /** 📗 §7: a cor representa o cliente ou o itinerário. */
  corPor: 'cliente' | 'itinerario';
  definirCorPor: (m: 'cliente' | 'itinerario') => void;
  /** B₂ · esconder a linha das viaturas fora da frota. Ver a nota junto do `useState`. */
  esconderInactivas: boolean;
  definirEsconderInactivas: (b: boolean) => void;
  janela: Janela;
  p95: number | null;
  /**
   * ⚠️ **Serviços e dias livres à VISTA** — 📗 «troque por algo que faça mais sentido».
   *
   * Calculado no `Mapa`, que é quem tem a grelha e a janela; aqui só se mostra. Passá-lo por
   * propriedade (em vez de recalcular) evita duas contagens da mesma coisa — e a que divergisse
   * seria a que ninguém olha.
   */
  vistaAgora: { readonly servicos: number; readonly livres: number };
  /**
   * Abre as regras de negócio e legais.
   *
   * ⚠️ Vem de fora pela mesma razão que os pedidos: a **janela** vive no `Mapa`, logo o estado dela
   * também — e um segundo estado aqui daria dois donos para a mesma janela.
   */
  abrirRegras: () => void;
  /** `undefined` quando quem ve nao e superadmin — e entao a entrada nem aparece. */
  abrirContas?: (() => void) | undefined;
  /** ⚠️ O estado da janela de pedidos vive no `Mapa`, porque a janela também. */
  pedidosAbertos: boolean;
  consultasAbertas: boolean;
  alternarConsultas: () => void;
  /** 2026-11-03 · a caixa de entrada de email — o estado vive no `Mapa`, como a janela. */
  caixaAberta: boolean;
  alternarCaixa: () => void;
  gestaoAberta: boolean;
  alternarGestao: () => void;
  sessaoAberta: boolean;
  setSessaoAberta: (f: (a: boolean) => boolean) => void;
  atencaoAberta: boolean;
  alternarAtencao: () => void;
  /** A paleta de comandos — ver a nota do botão `Ctrl+K` no grupo dos utilitários. */
  paletaAberta: boolean;
  abrirPaleta: () => void;
  /** O que está filtrado, em palavras — ver a nota no `Mapa`. */
  resumoDoFiltro: readonly { readonly eixo: string; readonly rotulo: string; readonly valor: string }[];
  limparEixo: (eixo: string) => void;
  limparTudo: () => void;
  /** FAMILIAS com trabalho iminente — ver a nota no `Mapa`: nem itens, nem total. */
  atencaoFamilias: number;
  /** 🔴 2026-11-03 · emails por tratar; `null` = sem número (sem pastilha). */
  emailsPorTratar?: number | null;
  /** Leva o mapa a hoje. `undefined` quando hoje não cai no ano do mapa. */
  irParaHoje?: (() => void) | undefined;
  /** 🔴 D-101 · «Ir para…» e meses anteriores/seguintes (`NavegarNaFita`). */
  navegacaoDaFita?: React.ReactNode;
  /**
   * O primeiro nome de quem está autenticado, ou `null` enquanto não se sabe.
   *
   * ⚠️ **O nome no BOTÃO e não só dentro da janela.** 📌 Num posto partilhado, «quem está
   * autenticado?» tem de se responder sem abrir nada — senão a resposta só chega a quem já
   * desconfiou, e quem não desconfiou trabalha na sessão de outra pessoa.
   */
  nomeCurtoDaSessao: string | null;
  /** 🧹 REMOVÍVEL · formação — mostra o botão «Formação» (ver `entrada-na-formacao.tsx`). */
  formacaoNoMapa: boolean;
  /**
   * ⚠️⚠️ **O PLANO ACTIVO, já com a DESCRIÇÃO resolvida** — 📗 cliente, 2026-09-10: *«um
   * título no cabeçalho a dizer o plano activo»*. `null` = ainda não escolheu, e o cabeçalho
   * di-lo.
   *
   * 📌 **Prop e não um `fetch` próprio**, pela mesma razão do nome: o perfil já é pedido uma
   * vez no `Mapa`, e um segundo pedido aqui era uma ida ao servidor por uma cadeia de texto
   * que já veio — mais duas respostas possíveis para a mesma pergunta.
   */
  planoNoTopo: string | null;
  /**
   * 🔴 2026-10-01 · decisão do cliente: sem plano escolhido, quem não é superadmin vê só o
   * PARTILHADO (o servidor lê com `planoDasLeituras`). O cabeçalho di-lo, senão um mapa quase
   * vazio lia-se como «o plano perdeu os dados». O superadmin continua a ver tudo.
   */
  soPartilhado: boolean;
  /**
   * 🔴 O NOME do tom do plano (080) — `'azul'`, nunca um `#rrggbb`. `null` = sem tom.
   *
   * ⚠️ Vem do MESMO objecto que a `planoNoTopo`, e é isso que impede pintar o tom de um
   * plano por cima do nome de outro.
   */
  tomDoPlano: string | null;
  alternarPedidos: () => void;
  /**
   * ⚠️ Recarrega o mapa depois de uma escrita. Chega aqui **por causa do «Desfazer»**: uma
   * escrita que não se vê é indistinguível de uma que não aconteceu, e desfazer é uma escrita.
   */
  recarregar?: (() => void) | undefined;
  /**
   * A ETag do mapa que está no ecrã — a base do aviso «o plano mudou noutro sítio» (O₁).
   *
   * ⚠️ Chega aqui como propriedade e não por um pedido próprio, pela mesma razão do
   * `planoNoTopo`: quem carregou o mapa é que sabe com que ETag ele veio, e uma segunda ida ao
   * servidor daria duas respostas possíveis para a mesma pergunta.
   */
  etagDoMapa?: string | null;
}) {
  const [aberto, setAberto] = useState<'config' | 'filtro' | 'legenda' | null>(null);
  // 📗 2026-11-03 · `Esc` fecha o painel da barra que está aberto (Filtros, Legenda, Configuração): o
  //    guia da sessão 1 manda fechá-los «com o × ou com Esc» e só o × fechava. A mesma pilha das
  //    janelas (`useEscape`): fecha só o de cima. Ver `esc-fecha-o-que-esta-aberto.test.tsx`.
  useEscape(() => setAberto(null), aberto !== null);
  const nFiltros = contaFiltros(filtro, dados);
  /** B₂ · quantas viaturas estão fora da frota — para o interruptor contar o que esconde. */
  const nInactivas = dados.viaturas.reduce((n, v) => n + (v.inactiva === true ? 1 : 0), 0);

  return (
    <header className="topo">
      {/* ==================================================================
          ⚠️⚠️ A FILA DE BOTOES E UMA FAIXA SO — e nunca parte a meio
          ==================================================================
          📗 Cliente, 2026-09-08: *«as seccoes do topo esta a vir ate muito ca abaixo, isto e
          o `ctrl k` nao pode descer, tem de ficar na primeira linha»*.

          ⚠️⚠️ **MEDIDO ANTES DE MEXER** (Chrome sem cabeca, `nada-e-cortado-pela-janela`): a
          1280 px os filhos do `.topo` ocupavam **DUAS** faixas e a de baixo tinha **so** a
          pastilha `Ctrl+K`. Nao era um desenho: era uma quebra de linha. A 1600 e a 1920 cabia
          numa, logo o defeito so aparece nos ecras reais das planeadoras — 1920 a 125 % de
          escala sao **1536 px de CSS**, e a 150 % sao 1280.

          ⚠️⚠️ **E A CAUSA E QUE `.topo` QUEBRA DE LINHA.** O flexbox coloca os itens pelo
          tamanho de CONTEUDO e quebra quando nao cabem — **so encolhe dentro de uma linha que
          ja escolheu**. Medido: a fila precisa de **238,1 px** a mais do que cabe a 1280 px, e
          a peca que sobra e sempre a ultima. Como a faixa das metricas tem `flex-basis: 100 %`
          (e uma linha propria de proposito, 2026-09-02), sobravam TRES faixas antes do mapa.

          📌 **A saida e a fila deixar de poder quebrar e passar a poder CEDER.** Com
          `flex-wrap: nowrap` nesta caixa, o flexbox e obrigado a comprimir — e quem cede sao
          as duas zonas de TEXTO (a marca e os rotulos dos comandos), com `text-overflow:
          ellipsis`, primeiro a marca. ⚠️ **Nenhum botao desaparece e nenhum rotulo e
          escondido** (P-30): o texto continua no DOM, o `title` da-o por inteiro, e o
          esbatimento e progressivo em vez de um ponto de quebra escolhido a olho.

          ⚠️ **O grupo da direita (`Precisa de mim` … `Ctrl+K`) NAO encolhe** — e por isso que
          a pastilha fica sempre inteira e sempre na primeira faixa, que foi o pedido.

          ⚠️ Os menus flutuantes ficam FORA desta caixa: sao `position: absolute` contra o
          `.topo` (que e o `position: relative`), e enfia-los aqui mudava o bloco de
          contencao deles. ================================================================== */}
      <div className="topo-fila">
      <div className="topo-marca">
        {/* O logótipo vem do ficheiro do repositório e não de um URL externo: numa
            aplicação que corre no escritório, uma imagem remota é uma dependência
            de rede para mostrar a própria marca. É o mesmo ficheiro. A altura é
            fixada e a largura fica `auto`, para a proporção 1920×889 nunca ser
            distorcida. */}
        <img className="logo" src="/logo-xlvision.png" alt="xlVISION" />
        <span className="separador-v" />
        {/* ⚠️ O `title` existe porque o texto pode ficar ESBATIDO com `ellipsis` quando a fila
            aperta (ver `.topo-fila` em `mapa.css`): sem ele, um texto cortado deixava de ter
            forma de se ler por inteiro — e aí seria escondido, não esbatido (P-30). */}
        <span
          className="topo-titulos"
          title={`Mapa de planeamento — Turismo · Grupo Vale do Ave${
            planoNoTopo === null
              ? ` — sem plano escolhido${soPartilhado ? ': vê-se só o partilhado, escolha um plano para ver o resto' : ''}`
              : ` — plano ${planoNoTopo}`
          }`}
        >
          <h1 className="titulo">Mapa de planeamento</h1>
          {/* ==============================================================
              ⚠️⚠️ O PLANO ACTIVO NO CABEÇALHO — 📗 cliente, 2026-09-10
              ==============================================================
              📗 *«Por uma distinção entre planos… um título no cabeçalho a dizer o plano
              activo»*.

              ⚠️⚠️ **DENTRO do `.topo-titulos` e não como peça nova da fila, e é uma decisão
              MEDIDA.** O `.topo-fila` tem `flex-wrap: nowrap` desde 2026-09-08 porque a
              1280 px *«a fila precisa de 238,1 px a mais do que cabe»* — e a peça que sobra
              é sempre a última. 📌 Uma pastilha nova no topo voltava a estourar esse
              orçamento nos ecrãs reais das planeadoras (1920 a 150 % são 1280 px de CSS).
              Aqui não há filho novo na fila: entra na zona de texto que **já cede** com
              `ellipsis`, e o `title` dá-a por inteiro (P-30 — esbatido, nunca escondido).

              🔴 **E LEVA O TOM DO PLANO (080) — 📗 *«uma cor de fundo… uma distinção entre
              planos»*.** ⚠️⚠️ O que chega aqui é o **nome** (`'azul'`) e nunca um `#rrggbb`:
              quem pinta é o CSS, com um par por tema. 📌 **É essa a razão de o portão do
              contraste continuar a poder medi-lo** — ele mede CSS estático em Chrome sem
              cabeça, e uma cor escolhida à mão na Gestão nasceria depois do *build*, fora do
              alcance de portão nenhum. O tecto é uma **igualdade** e está em **197 de 197**.

              ⚠️ **A cor é REFORÇO e nunca o único sinal** (P-30/P-32): a identidade é o nome,
              que está aqui ao lado — quem não distinga os tons lê o plano na mesma. E sem tom
              escolhido não há atributo nenhum, logo o cabeçalho fica exactamente como estava.

              ⚠️ **Sem plano DIZ-SE**, em vez de ficar em branco: o plano decide o que se vê e
              o que se pode escrever (ADR-002), e um cabeçalho que cala isso deixa a pessoa a
              olhar para um mapa sem saber de quem é. ============================== */}
          <span className="subtitulo">
            {/* ⚠️ **SEM EMOJI, e o guarda apanhou-o.** Escrevi «⚠️ sem plano escolhido» e o
                critério «NÃO usa emoji nem sinais tipográficos como ícones» (`Mapa.test.tsx`,
                P-32) ficou vermelho — com razão: os sinais deste ecrã são SVG de um só
                `<path>`. 📌 A palavra «sem» faz o trabalho, e não precisa de fonte de emoji
                nenhuma para o fazer. */}
            {/* 📗 2026-10-29 · o nome do plano SAIU daqui para a faixa das métricas (ver
                `topo-plano-faixa`, abaixo): nesta zona é o texto que cede quando a fila aperta, e
                num portátil lia-se «Açor…» — sobre o nome que o guia manda confirmar. */}
            Turismo · Grupo Vale do Ave
          </span>
        </span>
      </div>

      <nav className="topo-caixas" aria-label="Comandos do mapa">
        {/* ⚠️⚠️ **TRES GRUPOS POR FUNCAO, e nao sete pastilhas iguais numa fila.**
            📗 Cliente, 2026-08-10: *«esta um pouco feio e confuso assim, melhore o design; os
            botoes todos em cima uns dos outros… o titulo precisa de ser ajustado»*.
            ⚠️⚠️ **A queixa era de arrumacao e havia um defeito por baixo:** «Pedidos»,
            «Consultas» e «Gestao» levavam **o mesmo icone** (`IconeLista`), e «Legenda» e a
            sessao levavam ambos `IconeInfo`. 📌 Um icone repetido nao ajuda a encontrar nada e
            ocupa espaco a fingir que ajuda — e e o mesmo erro que este projecto ja corrigiu
            duas vezes com os nomes de classe: **duas coisas diferentes nunca com o mesmo
            sinal**.
            📌 **A ordem e a mensagem, e os grupos sao a hierarquia:**
              1. **TRABALHAR** — «Pedidos» e «Consultas». 📗 A fonte 5.3.3.1 diz que o
                 planeamento comeca nos pedidos, logo e o unico botao com peso de accao
                 principal. Os outros seis nao competem com ele.
              2. **VER** — «Filtros» e «Legenda»: mudam o que se ve, nao o plano. Fantasma.
              3. **ADMINISTRAR** — «Gestao» e «Configuracao»: mudam o universo e as regras.
            E a sessao sai da fila: fica a direita, junto as medidas, porque nao e trabalho —
            e sobre **quem** trabalha. */}
        <span className="topo-grupo">
          <BotaoMenu
            icone={<IconeCaixaEntrada em={1} />}
            rotulo="Pedidos"
            aberto={pedidosAbertos}
            alternar={alternarPedidos}
            principal
          />
          {/* 📗 2026-11-03 · a caixa de entrada de EMAIL abre-se também por aqui, e não só pelo
              `Ctrl+K` («o ponto mais crítico»). O rótulo cede abaixo de 1 560 px (fica o ícone,
              com o nome no `title` e no `aria-label`). 🔴 2026-11-03 · com a contagem dos emails
              por tratar, que vem na resposta da fila que o mapa já lê; a pastilha fica fora do
              rótulo que cede, logo continua à vista abaixo de 1 800 px. Sem número, sem pastilha. */}
          <BotaoMenu
            icone={<IconeEnvelope em={1} />}
            rotulo="Caixa de entrada"
            {...(emailsPorTratar !== null && emailsPorTratar > 0 ? { contagem: String(emailsPorTratar) } : {})}
            cede="muito-cedo"
            aberto={caixaAberta}
            alternar={alternarCaixa}
          />
          <BotaoMenu
            icone={<IconePesquisa em={1} />}
            rotulo="Consultas"
            aberto={consultasAbertas}
            alternar={alternarConsultas}
          />
        </span>

        <span className="topo-sep" aria-hidden="true" />

        <span className="topo-grupo">
          <BotaoMenu
            icone={<IconeFunil em={1} />}
            rotulo="Filtros"
            cede="tarde"
            {...(nFiltros === 0 ? {} : { contagem: `${nFiltros}` })}
            aberto={aberto === 'filtro'}
            alternar={() => setAberto((a) => (a === 'filtro' ? null : 'filtro'))}
          />
          <BotaoMenu
            icone={<IconeInfo em={1} />}
            rotulo="Legenda"
            cede="cedo"
            aberto={aberto === 'legenda'}
            alternar={() => setAberto((a) => (a === 'legenda' ? null : 'legenda'))}
          />
        </span>

        <span className="topo-sep" aria-hidden="true" />

        <span className="topo-grupo">
          <BotaoMenu
            icone={<IconeLista em={1} />}
            rotulo="Gestão"
            cede="cedo"
            aberto={gestaoAberta}
            alternar={alternarGestao}
          />
          <BotaoMenu
            icone={<IconeEngrenagem em={1} />}
            rotulo="Configuração"
            cede="cedo"
            aberto={aberto === 'config'}
            alternar={() => setAberto((a) => (a === 'config' ? null : 'config'))}
          />
        </span>
      </nav>

      {/* ⚠️⚠️ **A SESSAO FORA DA FILA DOS COMANDOS.** Nao e trabalho: e sobre **quem**
          trabalha. 📌 E no canto oposto ao logotipo, que e a convencao que todos ja conhecem
          de qualquer aplicacao — procurar «quem sou» no meio de «Filtros» e «Gestao» era o
          que fazia a fila parecer arbitraria. */}
      {/* ⚠️⚠️ **«PRECISA DE MIM» VIVE AQUI, e nao na fila dos comandos.** 📗 Cliente,
          2026-08-11: *«com os novos botoes a barra superior ficou uma confusao… nao seria melhor
          por num outro lugar, organizar melhor?»* — e tinha razao.
          📌 **O diagnostico nao era de agrupamento, era de CATEGORIA e de CONTENCAO.**
          «Pedidos» e «Consultas» sao **sitios onde se vai trabalhar**; esta fila diz **se e
          preciso ir**. Nao e um comando, e o estado do trabalho — logo pertence ao canto de
          «quem sou / o que me espera», ao lado da Sessao, que e a convencao que todos conhecem.
          ⚠️⚠️ **E «Por fechar» SAIU da barra porque ja estava DENTRO desta fila** — e uma das sete
          familias, com botao que abre exactamente aquela janela. Dois botoes de topo em que um
          contem o outro e o que faz uma barra parecer arbitraria.
          ⚠️ **Preco declarado:** fechar servicos passa a ser dois cliques em vez de um. Aceito-o
          porque nao e tarefa de arranque de dia — e de acompanhamento, e a fila leva la com a
          razao para ir («faltam N para os custos deixarem de ser palpite»).
          ⚠️⚠️ **A pastilha conta FAMILIAS e nao itens.** O total iminente medido e **3 631**, e uma
          pastilha com 3 631 nao e um aviso, e papel de parede. */}
      <button
        type="button"
        className={classes('btn', 'btn--sino', atencaoAberta && 'btn--on')}
        onClick={alternarAtencao}
        aria-expanded={atencaoAberta}
        title="o que precisa de mim: divergências, reservas por efectivar, paradas, serviços por fechar"
        // 📗 2026-10-29 · o rótulo pode ceder (ver `.rotulo-cede` em `mapa.css`); o nome fica aqui,
        //    com a contagem, porque um `aria-label` substitui o texto todo do botão, pastilha incluída.
        aria-label={atencaoFamilias === 0 ? 'Precisa de mim' : `Precisa de mim (${String(atencaoFamilias)})`}
      >
        <IconeSino em={1} />
        <span className="rotulo-cede rotulo-cede--tarde">Precisa de mim</span>
        {atencaoFamilias === 0 ? null : <span className="pastilha">{atencaoFamilias}</span>}
      </button>

      <button
        type="button"
        className={classes('btn', 'btn--sessao', sessaoAberta && 'btn--on')}
        onClick={() => setSessaoAberta((a) => !a)}
        aria-expanded={sessaoAberta}
        title={`a minha sessão (${nomeCurtoDaSessao ?? 'Sessão'}): perfil, permissões e sair`}
        aria-label={nomeCurtoDaSessao ?? 'Sessão'}
      >
        <IconePessoa em={1} />
        <span className="rotulo-cede">{nomeCurtoDaSessao ?? 'Sessão'}</span>
      </button>

      {/* 🧹 REMOVÍVEL · formação — não desenha nada quando a formação está desligada. */}
      <BotaoDaFormacao visivel={formacaoNoMapa} />

      {/* ⚠️⚠️ **«HOJE» É UM BOTÃO, e não só uma linha.** 📌 Ver onde hoje está não é o mesmo
          que **chegar lá**: num ano de 365 colunas, voltar a hoje depois de ir ver Dezembro são
          dezenas de deslocamentos. É o gesto que qualquer calendário tem e este não tinha. */}
      {/* ⚠️⚠️ **O QUE ESTA FILTRADO DIZ-SE, E LIMPA-SE NUM CLIQUE.** 📗 Cliente, 2026-08-11:
          *«selecionei um cliente e nao fez nada… nem marcou no mapa… tem de dar para resetar o
          filtro facilmente»*.
          📌 **O filtro estava a funcionar** — verificado por teste: a pastilha dos Filtros
          acende e as barras dos outros clientes passam a neutro. O que faltava era **dizer o
          que ficou filtrado**: uma pastilha «1» ao lado de «Filtros» nao diz *qual*, e o efeito
          e por ESBATIMENTO (P-30: nunca esconder), que sem legenda se le como «nao fez nada».
          ⚠️ E o reset tinha de ser aqui, nao dentro do painel: quem isolou um cliente pela
          paleta nao passou pelo painel nenhuma vez, e ir procura-lo la e a definicao de nao ser
          facil. */}
      {resumoDoFiltro.length === 0 ? null : (
        <span className="filtro-activo" role="status">
          {resumoDoFiltro.map((f) => (
            <button
              key={f.eixo}
              type="button"
              className="filtro-chip"
              onClick={() => limparEixo(f.eixo)}
              title={`limpar o filtro de ${f.rotulo}`}
            >
              {f.rotulo}: <strong>{f.valor}</strong> <span aria-hidden="true">×</span>
            </button>
          ))}
          {resumoDoFiltro.length > 1 ? (
            <button
              type="button"
              className="filtro-chip filtro-chip--tudo"
              onClick={limparTudo}
            >
              limpar tudo
            </button>
          ) : null}
        </span>
      )}

      {irParaHoje !== undefined ? (
        <button type="button" className="btn btn--peq btn--hoje" onClick={irParaHoje}>
          Hoje
        </button>
      ) : null}
      {navegacaoDaFita}

      {/* ⚠️⚠️ **VOLTAR ATRÁS — 📗 «no mapa ter algo tipo voltar atrás como tem no Word».**
          📌 **Aqui e não dentro de uma janela**, e é medição e não gosto: `apps/web/src/mapa`
          tem **28 componentes de janela**, **25 com pelo menos um `POST`**. Pôr o desfazer numa
          delas obrigava a saber em qual se tinha errado — que é exactamente o que quem erra não
          sabe. Fica no grupo dos utilitários, ao lado do «Hoje» e da lupa: são os gestos de
          «como chego lá / como volto atrás», e não sítios onde se trabalha.
          ⚠️ `sinal={dados}` **não é dado** — é o gatilho de releitura da pilha, como no `Kpis`.
          Sem ele o botão continuava a oferecer um gesto que já não é o topo. */}
      <Desfazer sinal={dados} {...(recarregar === undefined ? {} : { recarregar })} />


      {/* ⚠️⚠️ **UM ATALHO QUE NINGUEM DESCOBRE E UMA FUNCIONALIDADE QUE NAO EXISTE.** 📌 A paleta
          nao tem botao nenhum na barra, e nada no ecra diz que `Ctrl+K` faz algo — a pessoa que
          mais ganharia com ela e exactamente a que nunca a vai encontrar.
          ⚠️⚠️ **E fica AQUI, no grupo dos utilitarios, e nao na fila de comandos.** 📗 O cliente
          acabou de me pedir para aliviar essa fila (2026-08-11), e este nao e um sitio onde se vai
          trabalhar: e um atalho para chegar a qualquer um deles — a mesma categoria do «Hoje», que
          e o outro gesto de «como chego la sem arrastar?».
          ⚠️ E **tambem se clica**: uma pista que so informa obriga a decorar a combinacao antes de
          poder usar a coisa. */}
      <button
        type="button"
        className={classes('btn', 'btn--peq', 'btn--procurar', paletaAberta && 'btn--on')}
        onClick={abrirPaleta}
        title="procurar no plano: viatura, dia, cliente, motorista ou itinerário"
      >
        <IconePesquisa em={0.9} />
        <kbd>Ctrl+K</kbd>
      </button>
      </div>

      {/* ⚠️⚠️ **ERA UMA CONTAGEM DE CÉLULAS ROTULADA «dias» — 📗 «troque por algo que faça mais
          sentido».** 📌 O número era `(viaturas visíveis) × (dias visíveis)`: uma medida **interna
          de renderização**, com o rótulo errado (são viatura-dias, não dias) e sem significado para
          quem planeia. Um número no canto do ecrã que ninguém sabe ler é pior do que nenhum: ocupa o
          sítio de um que se leria.
          ⚠️ **O que passa a estar ali é a pergunta que o planeador faz ao olhar para uma zona do
          mapa:** quanto trabalho há e quanto espaço sobra. Com a frota a 42 % (a ocupação da folha
          real), «dias livres» é agora a grandeza que decide onde encaixar uma viagem. */}
      {/* ⚠️⚠️ B29-bis — os numeros do PLANO, contados no servidor, numa PASTILHA e nao numa fila.
          📗 Cliente, 2026-08-30: *«as kpis ficaram incrivelmente mal… mais escondida e subtil ou
          entao esconde-las»*. 📌 A queixa era **duas filas de numeros de ambitos diferentes** a
          competir pelo mesmo olhar — esta («ano») e a `topo-medidas` («esta zona do mapa»).
          Esbate-los deixava as duas filas onde estavam; passa a haver **uma so fila**, a da zona
          visivel, e o resto a um clique. A razao de nao se esconderem por completo esta na nota 2
          de `Kpis.tsx`: o O3 nao tem outro mecanismo a puxar os km vazios.
          ⚠️ Fica ANTES das medidas da zona visivel, e o rotulo `ano` na propria pastilha e o que
          impede «32,6 % km vazios» ao lado de «551 dias livres» de se ler como contradicao.
          ⚠️ `sinal={dados}` NAO e dado: e o gatilho de recarga — ver a nota em `Kpis.tsx`. */}
      {/* ⚠️⚠️ **A FAIXA DAS MÉTRICAS GANHA CORPO PRÓPRIO, e não é resto de linha.**
          📗 Cliente, 2026-09-02, a apontar para o cabeçalho: «o layout também parece pouco
          profissional» — com uma captura em que a barra de botões não coube numa linha e o
          `<Kpis>` + `.topo-medidas` (que até aqui eram só mais dois filhos soltos de `.topo`,
          sem `margin-left: auto` nenhum) ficaram pendurados à esquerda de uma segunda linha
          vazia, sem fundo nem moldura — como se faltasse ali alguma coisa.
          📌 A saída não é caber melhor na primeira linha — isso foi pedido explicitamente para
          NÃO se mexer. É dar a esta linha uma identidade SEMPRE, e não só quando sobra: uma
          faixa de estatísticas, com o seu próprio fundo e separador, à largura toda do
          cabeçalho — em vez de um alinhamento que muda consoante o que couber ao lado. */}
      <div className="topo-metricas">
        <Kpis sinal={dados} />

        {/* 📗 2026-10-29 · O PLANO ATIVO, NUMA FAIXA ONDE CABE INTEIRO. 📗 Cliente: *«o nome do plano
            ativo aparece cortado num portátil («Açor…» a 1 366–1 600 px)»* — e o guia da sessão 1
            manda CONFIRMAR esse nome antes de cada teste. MEDIDO (Chrome sem cabeça,
            `plano-no-cabecalho-legivel.test.tsx`): no bloco do título o nome era o texto que
            cedia, e dar-lhe largura lá só se fazia cortando os rótulos dos comandos (a fila do
            topo não quebra e não tem mais folga). Esta faixa ocupa a largura toda e quebra de
            linha, logo o nome nunca é cortado e a barra não ganha transbordo. Continua no
            cabeçalho, com o tom do plano (080) e, sem plano, a dizê-lo (ADR-002).
            ⚠️ SEM EMOJI (P-32): a palavra «sem» faz o trabalho. ⚠️ `data-tom` só existe quando há
            tom — um atributo vazio casava `[data-tom]` e dava a moldura a um plano sem cor. */}
        <span className="topo-plano-faixa">
          plano ativo:{' '}
          <span className="topo-plano" {...(tomDoPlano === null ? {} : { 'data-tom': tomDoPlano })}>
            {planoNoTopo === null
              ? `sem plano escolhido${soPartilhado ? ' — só o partilhado' : ''}`
              : planoNoTopo}
          </span>
        </span>

        {/* ⚠️⚠️ **O₁ · «o plano mudou noutro sítio».** 📗 spec de 2026-09-11, §11 O ponto 1:
            *«quando o mapa recarrega por causa de outro, dizer — não trocar o conteúdo em
            silêncio»*. 📌 Fica **nesta faixa e não numa caixa por cima**: quem está a decidir
            sobre a viatura 108 tem de continuar a vê-la, e a faixa já existe — logo o aviso
            não empurra a grelha para baixo debaixo do cursor de ninguém, que era a ironia
            possível desta funcionalidade.
            ⚠️ `sinal={dados}` NÃO é dado: é o gatilho que apaga o aviso a cada recarga — a
            mesma razão do `Kpis` e do `Desfazer`. Ver `PlanoMudou.tsx`. */}
        <PlanoMudou
          etagNoEcra={etagDoMapa}
          sinal={dados}
          {...(recarregar === undefined ? {} : { recarregar })}
        />

        {/* ⚠️⚠️ **O₂ · «quem mais está no plano».** 📗 spec de 2026-09-11, §11 O ponto 2.
            📌 **Vizinho do `PlanoMudou` e não fundido com ele, e a razão é medida:** aquele
            compara **ETags**, logo só pode dizer *«mudou noutro sítio»*; este lê `sessao` e
            `auditoria`, logo **sabe quem**. São dois factos diferentes sobre duas perguntas
            diferentes, e colá-los numa frase só obrigava a que o mais fraco mandasse — que é
            a mesma conclusão a que a peça «desde ontem» chegou (ver `presenca.ts`).
            ⚠️ Cala-se quando se está sozinho, e a faixa não ganha altura: `:empty` esconde-o. */}
        <QuemEstaNoPlano sinal={dados} />

        <div className="topo-medidas" role="status">
          <span className="metrica" title="serviços visíveis nesta zona do mapa">
            <span className="metrica-valor">{vistaAgora.servicos.toLocaleString('pt-PT')}</span>
            <span className="metrica-rot">serviços</span>
          </span>
          <span className="separador-v" />
          <span className="metrica" title="viatura-dias sem serviço nesta zona — onde há espaço para planear">
            <span className="metrica-valor">{vistaAgora.livres.toLocaleString('pt-PT')}</span>
            <span className="metrica-rot">dias livres</span>
          </span>
          <span className="separador-v" />
          <span className={classes('metrica', p95 !== null && p95 > 60 && 'metrica--alta')}>
            {/* ⚠️⚠️ Um travessao ao lado de «p95» le-se como **avariado**. Enquanto nao ha
                medicao, diz-se que se esta a medir — que e o que se passa. */}
            <span className="metrica-valor">{p95 === null ? 'a medir' : `${p95.toFixed(1)} ms`}</span>
            <span className="metrica-rot">p95</span>
          </span>
        </div>
      </div>

      {aberto === 'config' ? (
        // 📗 2026-10-21 · «botões sem sentido e feios e com muito texto» (sobre este painel). Passa a
        //    uma lista de definições: o cabeçalho só com o título, «Aparência do mapa» com UMA escolha
        //    para a cor (sem cor · por cliente · por itinerário — eram um interruptor mais dois botões
        //    que só apareciam depois de o ligar) e um interruptor de verdade, e «Administração» com as
        //    duas ligações. Uma nota de uma linha por definição, e mais nada.
        <section className="menu menu--config painel-da-barra" aria-label="Configuração">
          <div className="menu-cab faixa-quebra">
            <strong className="menu-titulo">Configuração</strong>
            <button
              type="button"
              className="btn btn--icone defn-fechar"
              onClick={() => setAberto(null)}
              aria-label="Fechar"
            >
              <IconeFechar em={1} />
            </button>
          </div>

          <p className="defn-seccao">Aparência do mapa</p>
          {/* ⚠️⚠️ **COR POR CLIENTE OU POR ITINERÁRIO — 📗 §7.** *«mostrar planeamento com cor por
              cliente ou por itinerário»*. Cada uma falha onde a outra serve; a nota diz onde. */}
          <div className="defn-linha">
            <span className="defn-rot">
              <IconePaleta em={1} /> Cor das viagens
            </span>
            <div className="segmentos" role="group" aria-label="Cor das viagens">
              {(
                [
                  ['sem', 'sem cor'],
                  ['cliente', 'por cliente'],
                  ['itinerario', 'por itinerário'],
                ] as const
              ).map(([modo, rotulo]) => {
                const activo = modo === 'sem' ? !corLigada : corLigada && corPor === modo;
                return (
                  <button
                    key={modo}
                    type="button"
                    className={classes('segmento', activo && 'segmento--on')}
                    aria-pressed={activo}
                    onClick={() => {
                      if (modo === 'sem') {
                        definirCor(false);
                        return;
                      }
                      definirCor(true);
                      definirCorPor(modo);
                    }}
                  >
                    {rotulo}
                  </button>
                );
              })}
            </div>
          </div>
          <p className="defn-nota">
            {!corLigada
              ? 'Tons neutros — a sigla continua a identificar o cliente.'
              : corPor === 'cliente'
                ? 'Uma cor por cliente — as viagens do mesmo cliente ficam iguais.'
                : 'Uma cor por rota — distingue as viagens do mesmo cliente, mas as cores repetem-se.'}
          </p>

          {/* ---- ⚠️⚠️ B₂ · ESCONDER A LINHA DO EXECUTOR INACTIVO — 📄 spec 2026-09-11, §3 -----
              📗 *«desfocá-lo e deixar que se esconda essa linha»*. ⚠️⚠️ **Começa DESLIGADO (P-30)
              e diz SEMPRE quantas ficam escondidas** — os dias continuam em «Precisa de mim». */}
          <Interruptor
            ligado={esconderInactivas}
            definir={definirEsconderInactivas}
            rotulo="Esconder viaturas fora da frota"
          />
          <p className="defn-nota">
            {nInactivas === 0
              ? 'Nenhuma viatura fora da frota neste plano.'
              : esconderInactivas
                ? `${nInactivas} ${nInactivas === 1 ? 'linha escondida' : 'linhas escondidas'} — os dias continuam em «Precisa de mim».`
                : `${nInactivas} ${nInactivas === 1 ? 'viatura fora' : 'viaturas fora'} da frota — esbatida${nInactivas === 1 ? '' : 's'} no mapa.`}
          </p>

          {/* ⚠️ As REGRAS não são preferências de ecrã: mudam o que o sistema considera conforme em
              todo o plano — ecrã próprio. ⚠️ 📗 fonte §5.2: as Contas só aparecem ao superadmin (o
              servidor recusa na mesma; isto evita um botão que devolve 403). */}
          <p className="defn-seccao">Administração</p>
          <button
            type="button"
            className="defn-ligacao"
            onClick={() => {
              abrirRegras();
              setAberto(null);
            }}
          >
            Regras de negócio e legais <span aria-hidden="true">›</span>
          </button>
          {abrirContas !== undefined ? (
            <button
              type="button"
              className="defn-ligacao"
              onClick={() => {
                abrirContas();
                setAberto(null);
              }}
            >
              Contas e permissões <span aria-hidden="true">›</span>
            </button>
          ) : null}
        </section>
      ) : null}
      {aberto === 'filtro' ? (
        <PainelFiltros
          dados={dados}
          filtro={filtro}
          definirFiltro={definirFiltro}
          fechar={() => setAberto(null)}
        />
      ) : null}
      {aberto === 'legenda' ? <Legenda fechar={() => setAberto(null)} /> : null}
    </header>
  );
}

/**
 * Interruptor de dois estados — 📗 2026-10-21 um interruptor DE VERDADE (`role="switch"`, trilho e
 * bola), em vez de um botão com «ligada»/«desligada» escrito: o estado vê-se sem se ler.
 */
function Interruptor({
  ligado,
  definir,
  rotulo,
}: {
  ligado: boolean;
  definir: (b: boolean) => void;
  rotulo: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      className="defn-linha interruptor"
      onClick={() => definir(!ligado)}
    >
      <span className="defn-rot">{rotulo}</span>
      <span className={classes('interruptor-trilho', ligado && 'interruptor-trilho--on')} aria-hidden="true">
        <span className="interruptor-bola" />
      </span>
    </button>
  );
}

function BotaoMenu({
  icone,
  rotulo,
  contagem,
  aberto,
  alternar,
  principal = false,
  cede,
}: {
  icone: React.ReactNode;
  rotulo: string;
  /**
   * 📗 2026-10-29 · **O rótulo cede quando a fila do topo aperta** (ver `.rotulo-cede` em
   * `mapa.css`): `'muito-cedo'` abaixo de 1 800 px (a «Caixa de entrada», 2026-11-03: a 1 600 px o rótulo dela encolhia o logótipo a 7 px), `'cedo'` abaixo de 1 560 px, `'tarde'` só abaixo de 1 200 px; sem `cede` nunca sai
   * («Pedidos» e «Consultas», por decisão do cliente). O nome fica no `title` e no `aria-label`.
   */
  cede?: 'muito-cedo' | 'cedo' | 'tarde';
  contagem?: string;
  aberto: boolean;
  alternar: () => void;
  /**
   * ⚠️ **Peso de accao principal.** 📗 A fonte 5.3.3.1 diz que o planeamento comeca nos
   * pedidos, e com sete pastilhas identicas isso nao se via. 📌 **Um so** botao o leva: dois
   * «principais» nao sao uma hierarquia, sao duas cores.
   */
  principal?: boolean;
}) {
  return (
    <button
      type="button"
      className={classes('btn', principal && 'btn--destaque', aberto && 'btn--on')}
      onClick={alternar}
      aria-expanded={aberto}
      // ⚠️ O `title` existe porque o rótulo dos comandos com `cede` SAI DA VISTA quando a fila do
      //    topo aperta (ver `.rotulo-cede` em `mapa.css`). Sem ele o botão ficava só com o ícone e
      //    sem forma de saber o nome — aí seria escondido, e não esbatido (P-30).
      title={rotulo}
      // ⚠️ O `aria-label` substitui o texto todo do botão: leva a contagem, que senão se perdia.
      aria-label={contagem === undefined ? rotulo : `${rotulo} (${contagem})`}
    >
      {icone}
      <span className={cede === undefined ? undefined : classes('rotulo-cede', cede === 'tarde' && 'rotulo-cede--tarde', cede === 'muito-cedo' && 'rotulo-cede--muito-cedo')}>
        {rotulo}
      </span>
      {contagem !== undefined ? <span className="pastilha">{contagem}</span> : null}
      <IconeChevron em={0.9} aberto={aberto} className="ic ic--fraco" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Filtros — três eixos, porque foram os três que o cliente pediu
// ---------------------------------------------------------------------------

type Eixo = 'clientes' | 'motoristas' | 'rotas' | 'localidades';

function PainelFiltros({
  dados,
  filtro,
  definirFiltro,
  fechar,
}: {
  dados: DadosMapa;
  filtro: Filtro;
  definirFiltro: (f: Filtro) => void;
  fechar: () => void;
}) {
  const [eixo, setEixo] = useState<Eixo>('clientes');
  const [procura, setProcura] = useState('');
  const idProcura = useId();

  const itens: readonly { readonly i: number; readonly sigla: string; readonly nome: string }[] =
    eixo === 'clientes'
      ? dados.clientes.map((c, i) => ({ i, sigla: c[0], nome: c[1] }))
      : eixo === 'motoristas'
        ? dados.motoristas.map((m, i) => ({ i, sigla: m, nome: '' }))
        : eixo === 'rotas'
          ? // ⚠️ Aqui a «sigla» é o CÓDIGO e o «nome» é o nome — ao contrário dos clientes, onde
            //    a sigla é a abreviatura. O código é a chave que o planeador escreve.
            dados.itinerarios.map(([id, nome], i) => ({ i, sigla: id, nome }))
          : // ⚠️ V05 · a cidade, com o **código** como sigla (`MAD`) e o nome ao lado
            //    (`Madrid`) — é assim que a grelha a mostra, e a procura casa os dois.
            dados.localidades.map(([codigo, nome], i) => ({ i, sigla: codigo, nome }));

  const total = itens.length;
  const seleccionados = filtro[eixo];
  const visiveis =
    procura === ''
      ? itens
      : itens.filter(
          (x) =>
            x.sigla.toLowerCase().includes(procura.toLowerCase()) ||
            x.nome.toLowerCase().includes(procura.toLowerCase()),
        );

  const pôr = (novo: ReadonlySet<number> | null): void =>
    definirFiltro({ ...filtro, [eixo]: novo });

  const alternar = (i: number): void => {
    const base = seleccionados ?? new Set(itens.map((x) => x.i));
    const n = new Set(base);
    if (n.has(i)) n.delete(i);
    else n.add(i);
    pôr(n.size === total ? null : n);
  };

  return (
    <section className="menu painel-da-barra" aria-label="Filtros">
      <div className="menu-cab faixa-quebra">
        <div className="abas faixa-quebra" role="tablist">
          {(
            [
              ['clientes', 'Clientes', dados.clientes.length],
              ['motoristas', 'Motoristas', dados.motoristas.length],
              ['rotas', 'Itinerários', dados.itinerarios.length],
              // ⚠️⚠️ **V05 · a quarta aba, e sem ela o eixo não existe para ninguém.**
              //   📌 Acrescentar o eixo ao `Filtro` e esquecer a aba dava um filtro que o
              //   código sabe aplicar e o planeador não consegue ligar — «duas portas, uma
              //   fechada», e desta vez pela minha mão. Foi o critério que o apanhou.
              ['localidades', 'Cidades', dados.localidades.length],
            ] as const
          ).map(([k, rot, n]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={eixo === k}
              className={classes('aba', eixo === k && 'aba--on')}
              onClick={() => {
                setEixo(k);
                setProcura('');
              }}
            >
              {rot}
              <span className="pastilha">
                {filtro[k] === null ? n.toLocaleString('pt-PT') : `${filtro[k]?.size}/${n}`}
              </span>
            </button>
          ))}
        </div>
        <button type="button" className="btn btn--icone" onClick={fechar} aria-label="Fechar">
          <IconeFechar em={1} />
        </button>
      </div>

      <div className="menu-acoes menu-acoes--esq faixa-quebra">
        <label className="procura" htmlFor={idProcura}>
          <IconePesquisa em={1} className="ic ic--fraco" />
          <input
            id={idProcura}
            type="search"
            value={procura}
            onChange={(e) => setProcura(e.target.value)}
            placeholder="Procurar"
          />
        </label>
        <button type="button" className="btn btn--peq" onClick={() => pôr(null)}>
          <IconeVisto em={1} />
          <span>Marcar todos</span>
        </button>
        <button type="button" className="btn btn--peq" onClick={() => pôr(new Set())}>
          <IconeVazio em={1} />
          <span>Desmarcar todos</span>
        </button>
      </div>

      <p className="menu-nota">
        O que se desmarca fica <strong>esbatido</strong>, não escondido — para nunca parecer que a
        viatura está livre quando não está.
      </p>

      <div className="menu-lista">
        {visiveis.map(({ i, sigla, nome }) => {
          const on = activo(seleccionados, i);
          // ⚠️ A amostra mostra a cor ESCOLHIDA do cliente quando existe — senão o
          //    painel de filtros mentia sobre a cor que a barra vai ter no mapa.
          const cor = eixo === 'clientes' ? corDoCliente(dados, i, 'claro') : null;
          return (
            <button
              key={i}
              type="button"
              className={classes('cliente', on && 'cliente--on')}
              onClick={() => alternar(i)}
              aria-pressed={on}
              title={nome !== '' ? nome : sigla}
            >
              <span className="cliente-caixa">{on ? <IconeVisto em={0.9} /> : null}</span>
              {cor !== null ? (
                <span
                  className="cliente-amostra"
                  style={{ background: cor.fundo, borderLeftColor: cor.cap }}
                />
              ) : (
                <span className="cliente-amostra cliente-amostra--vazia" />
              )}
              <span className="cliente-sigla">{sigla}</span>
              <span className="cliente-nome">{nome}</span>
            </button>
          );
        })}
        {visiveis.length === 0 ? <p className="menu-nota">Nada corresponde.</p> : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Legenda — «o mapa fácil de entender» começa por explicar os seus sinais
// ---------------------------------------------------------------------------

// ⚠️⚠️ **Exportada porque a FOLHA IMPRESSA leva a legenda — e leva ESTA.** 📌 Em papel não há
//    cartão do cursor nem *tooltip*: quem recebe a folha só tem os sinais e o que a legenda
//    diz deles. Uma legenda própria para o papel era a terceira instância de F-20 pela porta
//    do significado — a folha ensinaria sinais que o ecrã já não usa, e ninguém a corrigiria
//    porque ninguém a lê no ecrã.
export function Legenda({ fechar }: { fechar: () => void }) {
  return (
    <section className="menu menu--legenda painel-da-barra" aria-label="Legenda">
      <div className="menu-cab faixa-quebra">
        <strong className="menu-titulo">Como ler o mapa</strong>
        <button type="button" className="btn btn--icone" onClick={fechar} aria-label="Fechar">
          <IconeFechar em={1} />
        </button>
      </div>

      <div className="legenda-grelha">
        <div className="legenda-bloco">
          <h3>As três bandas de cada viatura</h3>
          <ol className="legenda-bandas">
            <li>
              <span className="amostra amostra--itin">IGT</span>
              <span>
                <strong>Itinerário.</strong> Uma barra por corrida, com a sigla do cliente. A cor é
                para varrer o mapa; a sigla é a identidade.
              </span>
            </li>
            <li>
              <span className="amostra amostra--local">MAD</span>
              <span>
                <strong>Local do dia.</strong> Muda todos os dias. Nos dias sem chegada nem partida é{' '}
                <em>onde o motorista dorme</em>.
              </span>
            </li>
            <li>
              <span className="amostra amostra--mot">Silva</span>
              <span>
                <strong>Motorista.</strong> Barra própria, porque não coincide com a do itinerário:
                a troca a meio parte uma e não a outra.
              </span>
            </li>
          </ol>
        </div>

        <div className="legenda-bloco">
          <h3>Sinais</h3>
          <ul className="legenda-itens">
            <li>
              <span className="amostra amostra--rotacao">
                <IconeRotacao em={1} />
              </span>
              <span>
                <strong>Dia de rotação.</strong> Chega de um itinerário e parte noutro no mesmo dia.
                É onde a margem se ganha.
              </span>
            </li>
            <li>
              <span className="amostra amostra--estado">
                <IconeOficina em={1} />
              </span>
              <span>
                <strong>Viatura indisponível.</strong> Oficina, avaria ou sinistro. Nestes dias o
                motorista está em casa ou noutra viatura.
              </span>
            </li>
            <li>
              <span className="amostra amostra--outro-plano">
                <IconeOutroPlano em={1} />
              </span>
              <span>
                <strong>Noutro plano.</strong> A viatura tem serviço neste dia, num plano que não é
                o activo. <strong>O dia está ocupado</strong> — o cliente e o itinerário não se vêem
                daqui, porque o plano particiona o que se vê, e não o autocarro.
              </span>
            </li>
            {/* ⚠️⚠️ **O ESTADO PASSA A TER OS DOIS SINAIS** — 📗 cliente, 2026-08-12: *«preciso que
                haja diferenciação no mapa do que está por confirmar, reservado etc, por estado…
                por um certinho no que está confirmado, algo simples»*.
                📌 Até aqui só o **desvio** tinha marca: sem textura significava «confirmado» por
                ausência, e uma ausência não se lê como afirmação — lê-se como «ainda não olhei».
                ⚠️ O `✓` é um pseudo-elemento no CSS e **não custa nós ao DOM**: marcar 81 % das
                barras com um `<span>` era quatro vezes o custo que o projecto já tinha recusado
                para 19 %. */}
            <li>
              <span className="amostra amostra--confirmado">IGT ✓</span>
              <span>
                <strong>Confirmado, ou por confirmar.</strong> O <code>✓</code> no fim da barra diz
                que o serviço está definitivo — cliente e motoristas avisados. <strong>Sem
                <code>✓</code> é uma reserva</strong>, ainda por confirmar. Confirma-se no{' '}
                <em>Detalhe</em> do itinerário, ou em lista pelo <em>Reservas por confirmar</em>.
                {/* ⚠️⚠️ **UMA ENTRADA E NÃO DUAS — 2026-08-31, decisão do cliente.** A textura
                    de reserva deixou de se pintar porque dizia exactamente o mesmo que a
                    ausência do `✓` (a regra do CSS é `:not(.reserva)`), e no `staging` cobria
                    **100 %** das barras. 📌 Duas entradas de legenda para um só sinal ensinam
                    que há dois estados a procurar no mapa, e só há um a ler. */}
              </span>
            </li>
            <li>
              <span className="amostra amostra--dupla">
                <IconeDupla em={1} />
              </span>
              <span>
                <strong>Tripulação dupla.</strong> Dois condutores em simultâneo — regime legal de
                repouso diferente (art. 8.º n.º 5).
              </span>
            </li>
            {/* ⚠️⚠️ **O SINAL NOVO TINHA DE ENTRAR AQUI, e faltava.** A regra do projecto é que «o
                mapa fácil de entender começa por explicar os seus sinais» — e eu acrescentei a
                costura tracejada da troca de condutor sem a explicar.
                📌 E o risco é exactamente o que a implementação evita: a barra partida ao meio
                lida como tripulação dupla é um **regime legal diferente**. Por isso esta entrada
                fica ao lado da de cima e diz o que a distingue. */}
            <li>
              <span className="amostra amostra--troca-condutor" />
              <span>
                <strong>Troca de condutor no dia de rotação.</strong> A barra do motorista parte-se
                ao meio: a primeira metade fecha a viagem que chega, a segunda abre a que parte.
                ⚠️ <strong>Não é tripulação dupla</strong> — são dois condutores em{' '}
                <em>sequência</em>, não em simultâneo. Medido na escala real: acontece em{' '}
                <strong>17,1 %</strong> das rotações.
              </span>
            </li>
            {/* ⚠️ Os DOIS `?` do mapa, e estão aqui porque o cliente não os
                percebeu — «não percebi o ? em certos dias». Eram o mesmo glifo
                com dois significados e nenhum deles explicado. Continuam a ser
                `?` porque é o que a folha real escreve (`Rodolfo?`, `PAR in ?`),
                mas agora distinguem-se: um na barra do itinerário, o outro em
                caixa tracejada na banda do local. */}
            {/* ⚠️⚠️ **O `!` NAO ESTAVA NA LEGENDA**, e os dois `?` estavam. Foi o cliente a
                perguntar o que era — 📗 *«o que significam os pontos de exclamacao nos
                itinerarios?»*. 📌 Um sinal vermelho que ninguem sabe ler nao e um aviso:
                e uma mancha que se aprende a ignorar, e isso e pior do que nao a ter. */}
            <li>
              <span className="amostra amostra--suspeita">!</span>
              <span>
                <strong>Falta o serviço seguinte.</strong> Um dia desta viagem estava marcado
                como <em>«chega e parte no mesmo dia»</em> (o dia de rotação, acima), mas no dia
                seguinte a viatura <strong>não tem serviço nenhum</strong> — logo não parte de
                lado nenhum: é só uma chegada. <strong>Provavelmente falta lançar o serviço
                seguinte</strong>, e é isso que há a confirmar.
                {' '}
                Medido na folha actual: <strong>14,3 %</strong> dos dias marcados estavam nesta
                situação, e foi o cliente a apanhá-lo. ⚠️ Assinala-se em vez de se corrigir em
                silêncio — quem sabe se o serviço existe é quem planeou, não o sistema. Passe o
                rato por qualquer dia da viagem para ver a nota, que diz <em>qual</em> é o dia.
              </span>
            </li>
            <li>
              <span className="amostra amostra--duvida">?</span>
              <span>
                <strong>Provisório.</strong> Na barra do itinerário. O planeador marcou-o como não
                firme — é a interrogação que a folha actual já usa.
              </span>
            </li>
            <li>
              <span className="amostra amostra--falta">?</span>
              <span>
                <strong>Local por definir.</strong> Na banda do local, em caixa tracejada. Falta
                dizer onde a viatura está — e nos dias sem chegada nem partida esse local{' '}
                <em>é onde o motorista dorme</em>: decide se o repouso é legal (art. 8.º n.º 8) e se
                há alojamento a pagar. Medido na folha actual: <strong>5,9 %</strong> dos dias de
                viagem estão em branco.
              </span>
            </li>
            {/* ⚠️⚠️ **A cor caracteristica dos subcontratados tem de estar AQUI.** A legenda
                existe porque «o mapa facil de entender comeca por explicar os seus sinais»:
                uma cor nova que nao se explica e um sinal que cada pessoa interpreta a sua
                maneira, e a primeira interpretacao errada e a mais provavel — «aquele
                autocarro esta avariado». */}
            <li>
              <span className="amostra amostra--sub">SIG</span>
              <span>
                <strong>Serviço subcontratado.</strong> Na secção própria, abaixo da frota. O corpo
                da barra é <em>ardósia</em> e a cor do cliente fica na tampa da esquerda — o
                inverso das barras da frota, para não se confundirem. Um subcontratado é uma{' '}
                <strong>empresa</strong> e pode ter vários serviços no mesmo dia: nesse caso a
                barra leva a contagem. O condutor e a viatura são dela.
              </span>
            </li>
            <li>
              <span className="amostra amostra--base">na base</span>
              <span>
                <strong>Sem serviço.</strong> Lisa quando está <em>na base</em> — o motorista vai a
                casa e não há alojamento a pagar. Tracejada quando fica{' '}
                <em>parada fora</em>, que é pernoita paga. Na folha actual esta distinção não existe:
                das 240 células com local em dias sem viagem, nenhuma é em Portugal.
              </span>
            </li>
            <li>
              <span className="amostra amostra--fixado">IGT</span>
              <span>
                <strong>Fixado — «aqui já não quero mexer».</strong> O contorno tracejado e o ponto
                no início marcam um serviço que o planeador <em>travou</em>:{' '}
                <strong>não entra no planeamento nem nos rearranjos automáticos</strong>. Continua a
                poder ser alterado à mão, com motivo.
              </span>
            </li>
            {/* ⚠️⚠️ **§8.3 · O DIA QUE NAO E DE TRABALHO** — a marca entrou no mapa a 2026-09-08
                (migracao 072) e sem esta entrada era um sinal que manda perguntar. 📌 Fica ao
                lado do **transito**, que e o vizinho com que se confunde: os dois sao dias sem
                passageiros, e a diferenca e que num deles a viatura ANDA. */}
            <li>
              <span className="amostra amostra--sem-trabalho">IGT</span>
              <span>
                <strong>Dia sem trabalho, dentro do serviço.</strong> A viatura está{' '}
                <em>comprometida</em> com esta viagem e <strong>não trabalha</strong> neste dia — o
                grupo seguiu por outro meio (um <em>ferry</em>, por exemplo) ou o dia é de outro
                operador. <strong>Não conta como dia trabalhado</strong> nos contadores do art. 8.º,
                e o dia continua ocupado: a viatura não pode ser mobilizada para outra coisa.{' '}
                <strong>Não é o mesmo que oficina</strong> — não há avaria nenhuma — nem que{' '}
                <em>em trânsito</em>, onde a viatura anda e há horas de condução.
              </span>
            </li>
            {/* ⚠️⚠️ **A FRONTEIRA DE UM TROÇO ADMINISTRATIVO** (migração 085, 2026-09-14) — 📗
                «viagens longas têm de ser divididas de 7 em 7 dias, só para já no caso de um
                cliente grego». Fica aqui porque é a mesma família do sinal de cima: uma marca
                DENTRO de um serviço que já existe, sem mudar nenhum facto operacional. */}
            <li>
              <span className="amostra amostra--fronteira-troco" />
              <span>
                <strong>Início de um troço.</strong> A viagem continua a ser <strong>uma só</strong>{' '}
                — a mesma referência, a mesma barra —, organizada em troços administrativos para
                reporte (por exemplo, de 7 em 7 dias). <strong>Não é uma partição</strong>: não
                muda cliente, itinerário, condutor nem custo. Ver <em>«Partir a viagem…»</em> para
                a operação que de facto separa duas viagens.
              </span>
            </li>
            <li>
              {/* ⚠️⚠️ **A AMOSTRA VEM DA CONSTANTE DA BARRA** (`ROTULO_TRANSITO`) e não de um
                  literal. Estava aqui `BCN»SERZ` — um sinal que o mapa deixou de desenhar a
                  2026-09-03, porque a ORIGEM não existe em coluna nenhuma. 📌 Uma legenda que
                  ensina um sinal que o mapa não usa é pior do que não haver legenda: manda
                  procurar na grelha uma forma que nenhum caminho de escrita consegue produzir. */}
              <span className="amostra amostra--transito">{ROTULO_TRANSITO}</span>
              <span>
                <strong>Em trânsito.</strong> A viatura está a <em>andar sem passageiros</em>.{' '}
                <strong>Para onde vai diz-se ao passar o rato</strong> na barra — de onde vem não
                está registado em sítio nenhum, e por isso não se mostra.{' '}
                <strong>Não é o mesmo que parada:</strong> tem condutor na banda 3,
                tem horas de condução e conta para os limites do art. 8.º. O alojamento e as
                refeições destes dias são <strong>da empresa</strong>; os dias com itinerário são do
                cliente.
              </span>
            </li>
          </ul>
        </div>

        <div className="legenda-bloco">
          <h3>Calendário e cor</h3>
          <ul className="legenda-itens">
            <li>
              <span className="amostra amostra--fds" />
              <span>
                <strong>Fim de semana.</strong> Como na folha actual, marcado na linha do dia.
              </span>
            </li>
            <li>
              <span className="amostra amostra--semana" />
              <span>
                <strong>Segunda-feira.</strong> Fronteira da semana legal (art. 4.º i)) — é onde os
                contadores de condução reiniciam.
              </span>
            </li>
            <li>
              <span className="amostra amostra--quente" />
              <span>
                <strong>Quente é problema.</strong> Verde é conforme. Frio e púrpura são clientes.
                Nenhuma cor de cliente é quente, para o alarme não ter concorrência.
              </span>
            </li>
          </ul>
          <p className="menu-nota">
            Passe o rato por um dia para o resumo; <strong>clique</strong> para fixar e realçar o
            itinerário inteiro. As divisórias arrastam-se.
            {/* ⚠️⚠️ **Sem isto ninguém descobre as setas**, e é a mesma lição que a frase
                seguinte já regista sobre a tira de 6 px: «uma funcionalidade que não se
                encontra não existe». 📌 Diz-se onde o gesto nasce — depois do clique —, porque
                uma seta sem selecção não faz nada de propósito: inventar um alvo punha o painel
                e todos os botões num serviço que ninguém apontou. */}
            <br />
            Com um dia fixo, as <strong>setas ← ↑ ↓ →</strong> movem a selecção de célula em
            célula e trazem-na à vista — sem saltar o mapa nem mexer no plano.
            {/* Sem isto ninguém descobre o gesto: a tira tem 6 px e só se revela ao
                passar o rato. Uma funcionalidade que não se encontra não existe. */}
            <br />
            <strong>Como no Excel:</strong> arraste a <em>borda direita</em> de um número de dia para
            alargar <em>essa</em> coluna, ou a <em>borda de baixo</em> de uma viatura para esticar{' '}
            <em>essa</em> linha. As outras não se mexem. <strong>Duplo clique</strong> na borda
            devolve-a à medida original.
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * Onde desenhar o rectangulo do «Ver no mapa».
 *
 * ⚠️⚠️ **SAIU DO `style` PARA AQUI porque e a parte deste ficheiro que ja errou duas vezes**, e
 * uma expressao condicional dentro de um atributo `style` nao se consegue testar:
 *
 *   1. **Os 176 px da coluna do nome** — reportado tres vezes. `offsetsDias[d]` e uma posicao
 *      no espaco dos DIAS; o rectangulo e absoluto dentro de `.mapa-conteudo`, cujo x = 0 e o
 *      inicio da coluna do nome. Sem a somar, o alvo saia cinco colunas a esquerda. 📌 E
 *      parecia intermitente porque a coluna se pode arrastar (96–340 px), logo o erro mudava
 *      de tamanho.
 *   2. **O `v` negativo do subcontratado** — a seccao deles vem depois de toda a frota e nao
 *      esta em `offsetsLinhas`. `offsetsLinhas[-1]` da `undefined`, cai para `0`, e o realce
 *      aparecia sobre a PRIMEIRA VIATURA da frota: a apontar com confianca para o sitio
 *      errado, que e pior do que nao apontar.
 *
 * A convencao: `v >= 0` e um indice de `viaturas`; **`v < 0` e o subcontratado `-1 - v`**.
 */
/**
 * O indice do dia de HOJE no ano do mapa, ou `-1` se hoje nao cai neste ano.
 *
 * 📗 Cliente, 2026-08-10: *«quero algo no design mais chamativo no calendario, nao sei bem o
 * que… pense em melhorias que melhorem a experiencia e facilitem a experiencia do planeador»*.
 *
 * ⚠️⚠️ **NAO HAVIA MARCADOR DE HOJE — numa grelha de 250 x 365.** 📌 E a ancora mais util que
 * falta: tudo o que um planeador faz e relativo a hoje («esta viatura esta parada desde
 * quando?», «isto e daqui a quanto tempo?»), e sem ela o unico ponto de referencia sao os
 * numeros do cabecalho — que obrigam a ler uma data e a fazer a conta de cabeca.
 *
 * ⚠️ **No dia de PORTUGAL e normalizado ao dia**, pela mesma razao que `jaPassou`: com a hora do
 * navegador, quem olha de outro fuso via o dia anterior ou o seguinte — e um marcador de «hoje» na
 * coluna errada e pior do que nenhum, porque ninguem duvida dele. 🔴 Ate 2026-09-30 era UTC, o que
 * punha o marcador em ontem entre as 00:00 e as 01:00 de Lisboa no Verao. A conta e a do servidor
 * (`diaDaJanelaEmPortugal`, a mesma de `agoraNoPlano`).
 */
export function diaDeHoje(janela: JanelaDoMapa): number {
  const d = diaDaJanelaEmPortugal(janela);
  // ⚠️ **Fora do ano devolve `-1` e nao um extremo.** Fixa-lo a 0 ou a 364 punha o marcador em
  //    1 de Janeiro ou 31 de Dezembro, e isso le-se como uma afirmacao sobre hoje.
  const nDias = janela.nDias;
  return d >= 0 && d < nDias ? d : -1;
}

export function geometriaDoRealce(
  realce: { readonly v: number; readonly d0: number; readonly d1: number },
  medidas: {
    readonly larguraViatura: number;
    readonly offsetsDias: readonly number[];
    readonly offsetsLinhas: readonly number[];
    readonly nViaturas: number;
    /**
     * ⚠️⚠️ **Os offsets ACUMULADOS da secção dos subcontratados** — B28 deu a cada linha a
     * altura que o seu pior dia exige, logo `(-1 - v) * ALTURA_LINHA_SUB` passou a apontar
     * para a linha errada a partir da primeira empresa com duas bandas. É o mesmo defeito que
     * o `v` negativo já tinha tido, por outra aritmética.
     */
    readonly offsetsSub: readonly number[];
  },
): { left: number; width: number; top: number; height: number } {
  const { larguraViatura, offsetsDias, offsetsLinhas, nViaturas, offsetsSub } = medidas;
  const sub = realce.v < 0;
  const iSub = -1 - realce.v;
  return {
    left: larguraViatura + (offsetsDias[realce.d0] ?? 0),
    // O minimo de 6 px: um intervalo de largura zero desenharia nada, e «nada» le-se como
    // «o salto nao funcionou».
    width: Math.max(6, (offsetsDias[realce.d1 + 1] ?? 0) - (offsetsDias[realce.d0] ?? 0)),
    top: sub
      ? ALTURA_CABECALHO +
        (offsetsLinhas[nViaturas] ?? 0) +
        ALTURA_TITULO_SUB +
        (offsetsSub[iSub] ?? 0)
      : ALTURA_CABECALHO + (offsetsLinhas[realce.v] ?? 0),
    height: sub
      ? // ⚠️ A altura da linha sai dos MESMOS offsets, por diferença: com `ALTURA_LINHA_SUB`
        //    fixo, o rectângulo cobria 30 px de uma linha de 52 e o serviço da última banda
        //    ficava fora do realce.
        ((offsetsSub[iSub + 1] ?? 0) - (offsetsSub[iSub] ?? 0)) || ALTURA_LINHA_SUB
      : (offsetsLinhas[realce.v + 1] ?? 0) - (offsetsLinhas[realce.v] ?? 0),
  };
}

/**
 * Este dia NAO tem itinerario? — decide se o «Onde está parada…» aparece.
 *
 * 📗 Cliente, 2026-08-10: *«adicione nos dias em que nao ha itinerario esse botao»*.
 *
 * ⚠️⚠️ **Le a CELULA e nao a «natureza» do painel.** Um dia **sem linha nenhuma** e o caso mais
 * comum e mais importante — medidos **17 877 dias-viatura** assim — e e precisamente onde a
 * `natureza` nao tem nada para dizer. 📌 Foi o mesmo erro que impediu o `editarCelula` de
 * marcar oficina em dias livres: exigir que a linha exista para poder falar dela.
 *
 * ⚠️ E um dia de **oficina/avaria** tambem NAO conta: ali quem diz onde a viatura esta e a
 * oficina, e dois selectores para o mesmo facto divergem.
 */
export function diaSemItinerario(dados: DadosMapa, viatura: number, dia: number): boolean {
  const c = dados.grelha[viatura]?.[dia] ?? null;
  if (c === null) return true;
  // Sem cliente, sem itinerario, sem corrida — e sem estado de viatura.
  return c[0] < 0 && c[5] < 0 && c[10] < 0 && c[7] < 0;
}

// ---------------------------------------------------------------------------
// Cartão de resumo, junto ao cursor
// ---------------------------------------------------------------------------

function Cartao({
  dados,
  posicao,
}: {
  dados: DadosMapa;
  posicao: Posicao & { x: number; y: number };
}) {
  const linhas = resumo(dados, posicao.v, posicao.d);
  // Manter o cartão dentro da janela: junto à borda direita, abre para a esquerda.
  const largura = 260;
  const x = Math.min(posicao.x, window.innerWidth - largura - 12);
  const abaixo = posicao.y + 160 < window.innerHeight;
  return (
    <div
      className="resumo"
      style={
        abaixo
          ? { left: x, top: posicao.y + 6 }
          : { left: x, bottom: window.innerHeight - posicao.y + ALTURA_LINHA + 6 }
      }
      role="tooltip"
    >
      {linhas.map((l, i) => (
        <div key={i} className={i === 0 ? 'resumo-cab' : 'resumo-linha'}>
          {l}
        </div>
      ))}
      <div className="resumo-dica">clique para fixar</div>
    </div>
  );
}

/**
 * O cartão do cursor de um serviço **subcontratado** — e diz coisas diferentes.
 *
 * 📗 Cliente, 2026-08-10: *«os detalhes também terão de ser diferentes»*.
 *
 * 📌 **A diferença não é cosmética, é de domínio (D-002):** metade do que o cartão da
 * frota mostra não existe aqui e a outra metade não é nossa.
 *
 * | O cartão da frota mostra | Aqui |
 * |---|---|
 * | condutor nosso, por dia | **o condutor é da empresa subcontratada** — não o sabemos |
 * | estado da viatura (oficina, avaria) | não temos estado da frota de outra empresa |
 * | pernoita e alojamento a nosso cargo | **é dela**, e por isso não entra nos nossos custos |
 * | um serviço por dia | **pode ter vários** — é uma empresa, não um autocarro |
 *
 * ⚠️ **O que se mostra é a BASE da empresa**, porque é o que o motor usa para calcular a
 * deslocação até ao serviço: não é decoração, é o custo que fez esta opção ganhar.
 */
function CartaoSub({
  dados,
  linhaSub,
  posicao,
}: {
  dados: DadosMapa;
  /**
   * ⚠️⚠️ **A LINHA apontada (C₂), e não a empresa.** O cartão lia
   * `dados.grelhaSubcontratados[s][d]` — todos os serviços daquele dia — sob o intervalo de
   * **uma** barra. Numa empresa com nove serviços, passar o rato por um deles descrevia os
   * nove com as datas de um.
   */
  linhaSub: LinhaDeSubcontratado | undefined;
  posicao: { l: number; d: number; d1: number; x: number; y: number };
}) {
  const sc = linhaSub === undefined ? undefined : dados.subcontratados[linhaSub.sub];
  const servicos = linhaSub?.dias[posicao.d] ?? [];

  // ⚠️⚠️ **O INTERVALO VEM DA PRÓPRIA BARRA (`data-d1`), e não de uma segunda cópia da regra.**
  //
  //    Aqui estava a reconstrução do fim da barra, com a condição extra «e o mesmo NÚMERO de
  //    serviços simultâneos» — e o comentário dela dizia, com razão, que *«duas cópias da mesma
  //    regra divergem, e a divergência aqui aparecia como um cartão a anunciar oito dias sobre
  //    uma barra de três»*.
  //
  // 📌 **B28 matou a condição:** com uma barra por SERVIÇO a contagem por dia desapareceu, logo
  //    esta cópia passaria a partir barras que o produto desenha inteiras. Ler o fim do
  //    elemento que o cursor está a apontar é a única leitura que não pode divergir dele.
  const fim = Math.max(posicao.d, posicao.d1);

  const linhas: string[] = [`${sc?.nome ?? 'subcontratado'} · SUBCONTRATADO`];
  linhas.push(
    `${dataCurtaDoDia(dados, posicao.d)} a ${dataCurtaDoDia(dados, fim)} · ${fim - posicao.d + 1} dia(s)`,
  );
  for (const c of servicos) {
    const cli = dados.clientes[c[0]]?.[1] ?? 'cliente por identificar';
    const loc = c[1] >= 0 ? (dados.localidades[c[1]]?.[1] ?? dados.localidades[c[1]]?.[0]) : null;
    linhas.push(`${cli}${loc === null || loc === undefined ? '' : ` · ${loc}`}`);
  }
  // ⚠️⚠️ **QUE LINHA DA EMPRESA É ESTA — e é o C₂ a dizer-se no ecrã.** A empresa com nove
  //    serviços simultâneos ocupa nove linhas seguidas, e sem esta frase o cartão descrevia
  //    nove linhas com o mesmo nome e nada a distingui-las. 📌 A contagem não desapareceu:
  //    mudou de sítio, de «2× num dia que não mostra os dois» para «a 2.ª de 9 linhas, e as
  //    outras oito estão desenhadas».
  if (linhaSub !== undefined && linhaSub.total > 1) {
    linhas.push(
      `linha ${String(linhaSub.ordem + 1)} de ${String(linhaSub.total)} desta empresa — ` +
        'é uma empresa, não um autocarro',
    );
  }
  if (sc !== undefined && sc.base >= 0) {
    const base = dados.localidades[sc.base]?.[1] ?? dados.localidades[sc.base]?.[0] ?? '';
    linhas.push(`base: ${base} — é daqui que o motor conta a deslocação`);
  }
  linhas.push('condutor e viatura são da empresa subcontratada');

  const largura = 300;
  const x = Math.min(posicao.x, window.innerWidth - largura - 12);
  const abaixo = posicao.y + 160 < window.innerHeight;
  return (
    <div
      className="resumo resumo--sub"
      style={
        abaixo
          ? { left: x, top: posicao.y + 6 }
          : { left: x, bottom: window.innerHeight - posicao.y + 6 }
      }
      role="tooltip"
    >
      {linhas.map((l, i) => (
        <div key={i} className={i === 0 ? 'resumo-cab' : 'resumo-linha'}>
          {l}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Painel de detalhe — fechado por omissão
// ---------------------------------------------------------------------------

/**
 * O dia já passou?
 *
 * ⚠️ Compara em **UTC** e com a data de hoje **normalizada ao dia**. Comparar
 * `Date.now()` directamente fazia o dia de hoje contar como passado a partir das 00:00, e
 * um serviço que ainda está a decorrer não se fecha.
 */
/**
 * `8 Set` — o dia nomeado, para o rótulo do grupo de acções do painel.
 *
 * ⚠️ Existe porque «este dia da viagem» sobre uma barra de oito dias se lê como «o serviço».
 * Nomear a data é o que separa os dois âmbitos sem ninguém ter de adivinhar.
 */
/**
 * 🔴 R · `YYYY-MM-DD` ↔ índice do dia no ano — as duas pontas do mesmo par.
 *
 * ⚠️ **UTC de propósito**, como em todo o resto do plano: com o fuso local, `2026-01-01` na
 * Europa continental dá **-1** em Janeiro e o intervalo saía deslocado um dia.
 *
 * 📌 São a mesma aritmética que o `Indisponibilidade.tsx` já fazia em privado — 🅿️ **declarado:**
 * ele continua com a sua cópia local, e uni-las é trabalho que ninguém pediu neste turno. Ficam
 * exportadas daqui para o próximo o poder fazer num sítio só.
 */
export function dataIsoDoDia(janela: JanelaDoMapa, dia: number): string {
  return dataDoIndice(janela, dia);
}

/** `YYYY-MM-DD` → índice do dia no ano. `NaN` quando a data não se lê — e quem chama recusa-a. */
export function diaDoAnoDaData(janela: JanelaDoMapa, iso: string): number {
  return Math.round((Date.parse(`${iso}T00:00:00Z`) - inicioEmMs(janela)) / 86_400_000);
}

export function dataCurtaDoDia(dados: { readonly inicio: string; readonly nDias: number }, dia: number): string {
  // 🔴 2026-10-30 · Pedido 1 · a data curta tem UMA formulação, partilhada com o servidor
  //    (`diaCurto`, em `como-o-planeador-le.ts`) — havia quatro.
  return diaCurto(janelaDe(dados), dia);
}

// ============================================================================
// ORGANIZAR EM TROÇOS DE N EM N DIAS — MANTENDO A REFERÊNCIA
// ============================================================================
//
// 📗 Cliente: *«Viagens longas têm de ser divididas de 7 em 7 dias, só para já no caso de um
//    cliente grego.»*
//
// ⚠️⚠️ **GARANTIA INVERSA À DE `PartirViagem`** (📄 `PartirViagem.tsx`, §4.6). Aquela muda a
//    `referencia` a partir do dia escolhido — a viagem passa a ser DUAS. Esta NUNCA muda a
//    `referencia` — a viagem continua a ser UMA, só que organizada em troços administrativos.
//    📌 **É por isso que o rótulo NUNCA usa a palavra «partir»** — dois botões a dizer «partir»
//    e a fazerem coisas opostas seria a família «duas coisas diferentes com o mesmo nome» que
//    este projecto já pagou (F-20, `.barra`/`.opcao-porque`). «Organizar em troços…» descreve o
//    que de facto acontece: nenhuma viatura, dia, cliente ou custo muda.
//
// ⚠️⚠️ **DEFINIDA AQUI E NÃO NUM FICHEIRO PRÓPRIO** (ao contrário de `PartirViagem.tsx`): a
//    fronteira deste trabalho só permite tocar em `Mapa.tsx` do lado do ecrã. Fica assinalado
//    no relatório — o padrão do projecto é um componente por gesto, num ficheiro próprio.
//
// ⚠️ **AGORA A ESCRITA É REAL** (migração 085, `alocacao.inicio_de_troco`) — ao contrário da
//    primeira tentativa desta peça (`c570004a`, revertida em `e7cae6d7`), que só gravava a
//    auditoria e dizia «Organizada em N troços» sobre uma base que não tinha mudado um byte.
//    A confirmação de sucesso continua a ficar no ecrã (não fecha sozinha) porque a fronteira
//    marcada só se VÊ na grelha através da classe `.dia--fronteira-troco` — que, à data deste
//    trabalho, ainda não tem CSS (outro agente está a editar `mapa.css`). Fechar sozinho,
//    confiando no mapa para mostrar a mudança, seria apostar numa classe que pode não existir
//    ainda quando isto for publicado.
// ============================================================================

interface OrganizarEmTrocosProps {
  readonly viaturaId: string;
  readonly numero: string;
  readonly dia: number;
  /** A data já escrita, para a janela nomear a viagem sem repetir a aritmética. */
  readonly data: string;
  readonly fechar: () => void;
  readonly recarregar?: (() => void) | undefined;
}

function OrganizarEmTrocos({
  viaturaId,
  numero,
  dia,
  data,
  fechar,
  recarregar,
}: OrganizarEmTrocosProps) {
  const [motivo, definirMotivo] = useState('');
  // ⚠️ Texto e não número: um campo vazio a meio de se escrever ("") não é um número válido,
  //    e forçá-lo a 0 ou a NaN a cada tecla é pior do que validar só ao gravar.
  const [intervaloDias, definirIntervaloDias] = useState('7');
  const [erro, definirErro] = useState<string | null>(null);
  const [aGravar, definirAGravar] = useState(false);
  // ⚠️ **NÃO fecha sozinho ao gravar** — ver o cabeçalho: a fronteira só se vê no mapa quando a
  //    classe `.dia--fronteira-troco` tiver CSS, e até lá esta mensagem é a única prova.
  const [feito, definirFeito] = useState<{ readonly trocos: number } | null>(null);
  useEscape(fechar);
  const arrasto = useJanelaAoCentro(440);

  const organizar = useCallback(async () => {
    if (motivo.trim() === '') return;
    const n = Number(intervaloDias);
    if (!Number.isInteger(n) || n < 1) {
      definirErro('o intervalo tem de ser um número inteiro de dias, no mínimo 1');
      return;
    }
    definirAGravar(true);
    definirErro(null);
    try {
      const res = await escreverNaRede<{
        readonly trocos?: readonly { readonly dia0: number; readonly dia1: number }[];
      }>('/api/plano/dividir-em-trocos', {
        viaturaId,
        dia,
        intervaloDias: n,
        motivo: motivo.trim(),
      });
      if (res.tipo === 'recusa') {
        // ⚠️ A razão vem do servidor — «esta viagem já cabe num único troço de N dias» e «há
        //    uma rotação» são accionáveis, e uma frase nossa esconderia isso.
        definirErro(res.frase);
        return;
      }
      definirFeito({ trocos: res.corpo?.trocos?.length ?? 0 });
      // ⚠️ Uma escrita que não se vê é indistinguível de uma que não aconteceu — recarrega o
      //    mapa para a fronteira aparecer assim que a classe CSS existir.
      recarregar?.();
    } finally {
      definirAGravar(false);
    }
  }, [viaturaId, dia, motivo, intervaloDias, recarregar]);

  return (
    <div className="janela janela--motivo" style={arrasto.estilo} role="dialog" aria-label="Organizar em troços">
      <div {...arrasto.cab} className="janela-cab faixa-quebra">
        <strong className="menu-titulo">Organizar em troços</strong>
        <button type="button" className="btn btn--icone" onClick={fechar} aria-label="Fechar">
          <IconeFechar em={1} />
        </button>
      </div>
      <div className="janela-corpo">
        <p className="cancelar-alcance">
          Viatura <strong>{numero}</strong> · viagem que inclui <strong>{data}</strong>
        </p>
        <p className="menu-nota">
          Organiza a viagem em troços de N dias, para reporte — a viagem continua a ser{' '}
          <strong>uma só</strong>: a referência não muda, e no mapa continua a ser uma barra
          contínua.
        </p>

        {feito !== null ? (
          <p className="menu-nota" role="status">
            ✅ Organizada em <strong>{feito.trocos}</strong> troços de {intervaloDias} dias.
          </p>
        ) : null}

        {erro !== null ? (
          <p className="pedidos-erro" role="alert">
            {erro}
          </p>
        ) : null}

        {feito === null ? (
          <>
            <label className="filtro-caixa">
              <span>dias por troço</span>
              <input
                type="number"
                min={1}
                value={intervaloDias}
                onChange={(e) => definirIntervaloDias(e.target.value)}
              />
            </label>

            <label className="motivo-rotulo" htmlFor="organizar-trocos-motivo">
              Motivo (obrigatório)
            </label>
            <textarea
              id="organizar-trocos-motivo"
              className="motivo-campo"
              rows={3}
              value={motivo}
              autoFocus
              onChange={(e) => definirMotivo(e.target.value)}
              placeholder="p. ex. «reporte semanal pedido pelo cliente grego»"
            />
          </>
        ) : null}

        <div className="motivo-accoes faixa-quebra">
          <button type="button" className="btn" onClick={fechar}>
            {feito !== null ? 'Fechar' : 'Voltar'}
          </button>
          {feito === null ? (
            <button
              type="button"
              className="btn btn--principal"
              disabled={motivo.trim() === '' || aGravar}
              title={motivo.trim() === '' ? 'escreva o motivo antes de organizar' : undefined}
              onClick={() => void organizar()}
            >
              {aGravar ? 'a organizar…' : 'Organizar em troços'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// JUNTAR OS TROÇOS — apagar as fronteiras, de volta a UMA viagem sem divisões
// ============================================================================
//
// ⚠️⚠️ **O INVERSO de `OrganizarEmTrocos`**, e é o gesto que a forma da coluna 085 prometia
//    tornar trivial: um único `UPDATE … SET inicio_de_troco = false` no servidor (ver
//    `unirTrocos`, `dividir-em-trocos.ts`) — sem renumerar nada.
//
// ⚠️ **Só se monta quando `temFronteiraDeTroco` é verdadeiro** (calculado em `Mapa`, e
//    partilhado entre o painel e o menu de contexto): sem fronteira nenhuma para apagar, o
//    servidor recusaria com `nada_a_unir`, e o botão só existiria para dizer que não.
// ============================================================================

interface JuntarOsTrocosProps {
  readonly viaturaId: string;
  readonly numero: string;
  readonly dia: number;
  readonly data: string;
  readonly fechar: () => void;
  readonly recarregar?: (() => void) | undefined;
}

function JuntarOsTrocos({ viaturaId, numero, dia, data, fechar, recarregar }: JuntarOsTrocosProps) {
  const [motivo, definirMotivo] = useState('');
  const [erro, definirErro] = useState<string | null>(null);
  const [aGravar, definirAGravar] = useState(false);
  // ⚠️ Mesma disciplina de `OrganizarEmTrocos`: não fecha sozinho, porque o efeito visível
  //    (a fronteira deixar de se ver na grelha) depende de uma classe CSS ainda pendente.
  const [feito, definirFeito] = useState<{ readonly fronteirasRemovidas: number } | null>(null);
  useEscape(fechar);
  const arrasto = useJanelaAoCentro(440);

  const juntar = useCallback(async () => {
    if (motivo.trim() === '') return;
    definirAGravar(true);
    definirErro(null);
    try {
      const res = await escreverNaRede<{
        readonly fronteirasRemovidas?: readonly number[];
      }>('/api/plano/unir-trocos', {
        viaturaId,
        dia,
        motivo: motivo.trim(),
      });
      if (res.tipo === 'recusa') {
        definirErro(res.frase);
        return;
      }
      definirFeito({ fronteirasRemovidas: res.corpo?.fronteirasRemovidas?.length ?? 0 });
      recarregar?.();
    } finally {
      definirAGravar(false);
    }
  }, [viaturaId, dia, motivo, recarregar]);

  return (
    <div className="janela janela--motivo" style={arrasto.estilo} role="dialog" aria-label="Juntar os troços">
      <div {...arrasto.cab} className="janela-cab faixa-quebra">
        <strong className="menu-titulo">Juntar os troços</strong>
        <button type="button" className="btn btn--icone" onClick={fechar} aria-label="Fechar">
          <IconeFechar em={1} />
        </button>
      </div>
      <div className="janela-corpo">
        <p className="cancelar-alcance">
          Viatura <strong>{numero}</strong> · viagem que inclui <strong>{data}</strong>
        </p>
        <p className="menu-nota">
          Apaga as fronteiras de troço desta viagem — volta a ser <strong>um troço só</strong>.
          Não muda nem cliente, nem itinerário, nem custo: só a organização de reporte.
        </p>

        {feito !== null ? (
          <p className="menu-nota" role="status">
            ✅ Removida{feito.fronteirasRemovidas === 1 ? '' : 's'}{' '}
            <strong>{feito.fronteirasRemovidas}</strong> fronteira
            {feito.fronteirasRemovidas === 1 ? '' : 's'}.
          </p>
        ) : null}

        {erro !== null ? (
          <p className="pedidos-erro" role="alert">
            {erro}
          </p>
        ) : null}

        {feito === null ? (
          <>
            <label className="motivo-rotulo" htmlFor="juntar-trocos-motivo">
              Motivo (obrigatório)
            </label>
            <textarea
              id="juntar-trocos-motivo"
              className="motivo-campo"
              rows={3}
              value={motivo}
              autoFocus
              onChange={(e) => definirMotivo(e.target.value)}
              placeholder="p. ex. «afinal não é preciso organizar por reporte semanal»"
            />
          </>
        ) : null}

        <div className="motivo-accoes faixa-quebra">
          <button type="button" className="btn" onClick={fechar}>
            {feito !== null ? 'Fechar' : 'Voltar'}
          </button>
          {feito === null ? (
            <button
              type="button"
              className="btn btn--principal"
              disabled={motivo.trim() === '' || aGravar}
              title={motivo.trim() === '' ? 'escreva o motivo antes de juntar' : undefined}
              onClick={() => void juntar()}
            >
              {aGravar ? 'a juntar…' : 'Juntar os troços'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** O dia já passou — no dia de PORTUGAL, o mesmo relógio do portão de escrita (`agoraNoPlano`). */
export function jaPassou(dados: { readonly inicio: string; readonly nDias: number }, dia: number): boolean {
  return dia < diaDaJanelaEmPortugal(janelaDe(dados));
}

function Painel({
  dados,
  alvo,
  alvoSub,
  fixo,
  aberto,
  alternar,
  limpar,
  configuracao,
  altura,
  definirAltura,
  envolvente,
  editar,
  copiarPeriodo,
  retirarCopia,
  anotar,
  fecharServico,
  corrigir,
  partir,
  organizarEmTrocos,
  juntarTrocos,
  porEmEspera,
  travar,
  diario,
  corDaViagem,
  editarQuePartem,
  rotuloDaQuePartem,
  corDaViagemQuePartem,
  substituir,
  marcarIndisponivel,
  onde,
  encaixar,
  cancelarServico,
  retirarDoMapa,
  trocarEmpresa,
  natureza,
  outrasDaViagem,
  irPara,
}: {
  dados: DadosMapa;
  alvo: Posicao | null;
  /**
   * O serviço SUBCONTRATADO fixo — 📗 *«tem de dar para ver detalhes de igual forma»*.
   *
   * ⚠️⚠️ **Quando isto vem preenchido, `alvo` vem `null`** — e é essa a correcção inteira das
   * acções. Os oito botões do painel verificam todos `alvo !== null`, logo nenhum aparece, e é
   * o que tem de acontecer: não se edita o serviço de outra empresa, não se marca oficina na
   * viatura dela, não se lhe atribui um condutor nosso (D-002). 📌 Sem uma única condição nova,
   * e sem a possibilidade de esquecer um dos oito.
   */
  alvoSub?:
    | {
        /** Índice da EMPRESA — o nome, a base e as rotas de escrita são dela. */
        readonly sub: number;
        /**
         * ⚠️⚠️ **A LINHA (C₂): um serviço por dia.** Antes o painel recebia só o índice da
         * empresa e lia dela **todos** os serviços do dia, anunciando o intervalo do primeiro
         * como se fosse o do conjunto. Recebendo a linha, o painel descreve **o** serviço que
         * foi apontado — que é o que «comportar-se como um autocarro» quer dizer.
         */
        readonly linha: readonly (readonly CelulaCompacta[])[];
        readonly d: number;
      }
    | undefined;
  fixo: boolean;
  /**
   * Abre a edição do serviço deste dia.
   *
   * 📗 «mudar condutor e trocar viatura, tudo editável… este será sempre um processo com base
   * manual». ⚠️ Só aparece com a selecção **fixa** (clique): com o rato a passar, um botão que
   * muda de alvo a cada célula é um convite a clicar no serviço errado.
   */
  editar?: (() => void) | undefined;
  /**
   * 🔴 **ACRESCENTAR UM SEGUNDO AUTOCARRO — peça F §6.5, §9 Q1.**
   *
   * 📗 Cliente: *«copiar **e** mover — e "copiar" é o segundo autocarro»*. A MESMA viagem passa
   * a correr em dois, que é o caso do grupo que não cabe num só — medido: **16,9 %** das
   * referências reais estão em mais do que um autocarro.
   *
   * ⚠️⚠️ **É o vizinho do `editar`, e é aí que a diferença se aprende:** aquele MOVE a viagem
   * (a origem fica sem ela), este não tira nada. Por isso o rótulo não usa a palavra «mover»
   * nem a palavra «mapa» — a segunda é do «Retirar do mapa…». É o molde do par «Organizar em
   * troços…» / «Juntar os troços…», que nunca diz «partir».
   *
   * `undefined` fora de um dia de serviço, ou num dia sem referência de viagem: as rotas
   * exigem a `referencia` (400 `alvo_incompleto`) e é ela que diz QUAL viagem se copia.
   */
  copiarPeriodo?: (() => void) | undefined;
  /**
   * 🔴 **O INVERSO — o autocarro seleccionado sai da viagem, que continua nos outros.**
   *
   * 📗 §6.5: *«tirar o segundo autocarro apaga aquelas alocações e a referência fica na
   * primeira»*. *Um gesto sem inverso é uma armadilha.*
   *
   * ⚠️⚠️ **`undefined` quando a viagem não corre em mais nenhum autocarro** — ao contrário do
   * `copiarPeriodo`, e pela mesma razão do `juntarTrocos`: aí o que se apagaria **não é uma
   * cópia, é a viagem**, e o servidor recusa com 409 `unica_viatura_com_a_viagem`. A condição
   * é o espelho dessa recusa, lida do mapa — e a janela sabe lê-la na mesma, porque o mapa
   * pode estar velho de segundos.
   */
  retirarCopia?: (() => void) | undefined;
  aberto: boolean;
  alternar: () => void;
  limpar: () => void;
  /** 📗 fonte 6.8 — abre o lápis da nota deste dia. */
  anotar?: (() => void) | undefined;
  /** 📗 D-091 passo 1 — regista o que aconteceu de facto. */
  fecharServico?: (() => void) | undefined;
  /** 📗 «tudo tem de poder ser editado» — abre a edição deste dia. */
  corrigir?: (() => void) | undefined;
  /**
   * ⚠️⚠️ **PARTIR A VIAGEM NESTE DIA — 📗 «os planeadores muitas vezes têm de partir viagens a
   * meio» (2026-08-13), e o gesto só existia no BOTÃO DIREITO.**
   *
   * 📌 O menu de contexto foi a escolha certa pela razão errada: o comentário dizia que «o botão
   * direito é o único gesto que nomeia um dia sem ambiguidade», e o **grupo do dia deste painel já
   * nomeia a data** — foi para isso que ele passou a mostrá-la. Logo a ambiguidade não existe aqui.
   *
   * ⚠️ **E um gesto que só se descobre por acidente é um gesto que não existe.** Uma operação que
   * a operação real faz «muitas vezes» não pode depender de alguém adivinhar que há menu de
   * contexto. O menu **fica** — quem já o usa não perde nada.
   */
  partir?: (() => void) | undefined;
  /**
   * ⚠️⚠️ **ORGANIZAR EM TROÇOS DE N EM N DIAS — GARANTIA INVERSA de `partir`, acima.**
   *
   * 📗 «viagens longas têm de ser divididas de 7 em 7 dias, só para já no caso de um cliente
   * grego». Aqui a referência NUNCA muda — a viagem continua a ser uma só, organizada em
   * troços administrativos. O rótulo não usa a palavra «partir», de propósito: `partir` MUDA
   * a viagem em duas, isto não muda nada operacional. Ver `OrganizarEmTrocos`.
   */
  organizarEmTrocos?: (() => void) | undefined;
  /**
   * ⚠️⚠️ **JUNTAR OS TROÇOS — o INVERSO de `organizarEmTrocos`.**
   *
   * `undefined` quando a viagem não tem fronteira nenhuma marcada (`temFronteiraDeTroco` em
   * `Mapa`): sem isso o botão só serviria para o servidor recusar com `nada_a_unir`. Ver
   * `JuntarOsTrocos`.
   */
  juntarTrocos?: (() => void) | undefined;
  /**
   * 🔴 **PÔR EM ESPERA — a viagem sai do autocarro (peça F §6.3, migração 089).**
   *
   * 📗 Cliente: *«ou numa linha dummy sem autocarro no final do mapa após os subcontratados,
   * tipo em espera … para o planeador corrigir o mapa manualmente sem destruir as ligações
   * perfeitas entre viagens»*.
   *
   * `undefined` fora de um dia de serviço: num dia livre ou de oficina não há viagem para tirar
   * do autocarro, e o servidor recusaria com `servico_nao_encontrado`.
   *
   * ⚠️⚠️ **O INVERSO NÃO É UM BOTÃO DESTA BARRA, e não é omissão:** uma viagem em espera não
   * está em viatura nenhuma, logo não há célula da frota que a nomeie. «Trazer de volta…» abre
   * da própria barra, na secção «Em espera» no fim do mapa. *Um gesto sem inverso é uma
   * armadilha* — o inverso existe, e é lá que ele vive.
   */
  porEmEspera?: (() => void) | undefined;
  /**
   * ⚠️⚠️ **TRAVAR — «aqui já não quero mexer» (J-0).**
   *
   * 📗 Cliente, 2026-08-13: *«tem de existir um pisco que diga: ok, aqui já não quero mexer
   * mais»*.
   *
   * 📌 **Abre com o intervalo da CORRIDA e não com o dia**: travar um dia de uma viagem de cinco
   * deixa os outros quatro livres, e o motor move o serviço **por inteiro** — logo um alfinete
   * parcial dá uma sensação de protecção que não existe. O planeador pode sempre encurtar.
   *
   * ⚠️ Fica no grupo da VIAGEM e não no do dia, pela mesma razão do «Cancelar…»: o alcance do
   * gesto é a viagem, e pô-lo no grupo do dia sugeria que travava um dia só.
   */
  travar?: (() => void) | undefined;
  /**
   * ⚠️⚠️ **«O QUE ACONTECEU…» — o diário DESTA viagem (§5.6 exigência 3 do desenho).**
   *
   * 📗 *«Abre-se da célula do mapa — "tudo o que aconteceu a esta viagem". É assim que a
   * pergunta nasce.»*
   *
   * 📌 **Fica no grupo da VIAGEM porque é isso que apanha**, e é a única acção deste grupo que
   * **não escreve nada**: o diário lê a auditoria e mais nada (D-007 — a prova documental não
   * se edita de um ecrã). Por isso vai no fim, depois dos gestos que mudam o plano.
   */
  diario?: (() => void) | undefined;
  /**
   * ⚠️ A cor manual desta viagem (migração 055) — abre `CorDaCorrida`. Sobrepõe-se à cor
   * do cliente só nesta corrida, em todas as viaturas e dias dela.
   */
  corDaViagem?: (() => void) | undefined;
  /**
   * ⚠️⚠️ **AGIR NA SEGUNDA VIAGEM DE UM DIA DE ROTAÇÃO — a que PARTE.**
   *
   * 📗 Cliente, 2026-08-14: *«nos detalhes dos dias de rotação tem de ter as duas viagens»*.
   *
   * 📌 `undefined` nos dias normais, que são ~99 % deles. Abre o mesmo painel de edição com o
   * intervalo **da viagem que parte** e a referência dela **explícita**.
   */
  editarQuePartem?: (() => void) | undefined;
  /** O rótulo do grupo dela — a referência, para não haver dúvida sobre qual é. */
  rotuloDaQuePartem?: string | undefined;
  /** A cor manual da viagem que PARTE (migração 055) — mesma razão de `corDaViagem`. */
  corDaViagemQuePartem?: (() => void) | undefined;
  /**
   * ⚠️ **A viatura está indisponível neste dia — quem faz os serviços dela?**
   *
   * 📗 *«marquei avariado no dia 16 agosto, tem de arranjar logo um substituto de forma
   * rápida»*. Só existe quando a célula tem estado de viatura: um botão destes em todos os
   * dias era ruído em 91 250 células, e aqui **o botão ser visível já é meia resposta**.
   */
  substituir?: (() => void) | undefined;
  /**
   * Onde a viatura está parada — 📗 *«tem de poder ser editado manualmente… selecionar o período
   * e alterar o estado»*.
   *
   * ⚠️ **`undefined` em dias COM itinerário**, e é a guarda inteira: num dia de serviço é o
   * itinerário que diz onde ela está, e num dia de oficina é a oficina. 📌 Duas respostas para
   * o mesmo facto divergem, e o motor lê uma delas.
   */
  onde?: (() => void) | undefined;
  /**
   * Marca avaria/oficina num intervalo de dias.
   *
   * ⚠️ Existe em **todos** os dias, incluindo os livres: é aí que se quer sinalizar a
   * oficina, e é aí que o `Editar dia…` não chegava — ele exige que a linha já exista.
   */
  marcarIndisponivel?: (() => void) | undefined;
  /** Abre «O que encaixa aqui?» — só faz sentido num dia livre. */
  encaixar?: (() => void) | undefined;
  /** 📗 «cancelar ou reagendar» — cancela o PEDIDO inteiro, e a janela conta o que apanha. */
  cancelarServico?: (() => void) | undefined;
  /**
   * ⚠️⚠️ **RETIRAR DO MAPA — e não é o mesmo que cancelar.**
   *
   * 📗 Cliente, 2026-08-30: *«permitir retirar e voltar a planear pedidos já marcados»*.
   *
   * 📌 Antes disto, a única saída a partir do mapa para um serviço mal colocado era
   * «Cancelar…» — que apaga o **pedido**. Quem só tem essa à mão acaba por cancelar e criar
   * um pedido novo, e perde o histórico do original.
   */
  retirarDoMapa?: (() => void) | undefined;
  /**
   * ⚠️ **B1-(2) · só num dia SUBCONTRATADO.** `undefined` numa célula da frota: trocar de
   * viatura tem gesto próprio («Viatura e condutor…»), e dois botões para o mesmo gesto
   * seriam dois sítios onde a regra pode divergir.
   */
  trocarEmpresa?: (() => void) | undefined;
  /**
   * ⚠️⚠️ **O QUE A CÉLULA É — e é isto que decide os botões.**
   *
   * 📗 Cliente, 2026-08-08: *«os botões da barra de detalhes são uma confusão autêntica…
   * os botões que aparecem nos dias com viagens não podem ser exactamente iguais aos dias
   * vazios»*. Tinha razão, e o defeito é de raciocínio: a barra oferecia **os mesmos cinco
   * botões em qualquer célula** — «Trocar viatura…» num dia sem viatura nenhuma alocada,
   * «Corrigir dia…» num dia que não tem linha na base para corrigir.
   *
   * 📌 **Um botão que não pode funcionar é pior do que um botão em falta:** quem carrega
   * nele aprende que o sistema falha, e da segunda vez não carrega no que funciona.
   */
  natureza?: 'servico' | 'livre' | 'indisponivel' | undefined;
  /** As outras viaturas que levam a mesma viagem — ver `outrasDaViagem` no `Mapa`. */
  outrasDaViagem?: readonly {
    readonly viatura: number;
    readonly numero: string;
    readonly dia0: number;
    readonly dia1: number;
    readonly simultanea: boolean;
  }[];
  /** Leva o mapa até à outra viatura — uma lista que não leva lá não serve de nada. */
  irPara?: ((viaturaId: string, dia: number, diaFim?: number) => void) | undefined;
  configuracao: ConfiguracaoMapa;
  altura: number;
  definirAltura: (n: number) => void;
  envolvente: React.RefObject<HTMLDivElement | null>;
}) {
  const pares =
    alvoSub !== undefined
      ? detalheSub(dados, alvoSub.sub, alvoSub.linha, alvoSub.d)
      : alvo === null
        ? []
        : detalhe(dados, alvo.v, alvo.d, configuracao.minutosRotacaoMinimo);
  /**
   * ⚠️⚠️ **A VIAGEM A QUE O DIA SUBCONTRATADO PERTENCE** — 📗 *«os dias subcontratados … têm de
   * estar ligados à viagem a que estão relacionados, nos detalhes, para saltar para a
   * viagem»*. Ver `viagem-do-subcontratado.ts`: aqui só se desenha, e o salto é o `irPara`
   * que já existe.
   */
  const viagensSub = useMemo(
    () => (alvoSub === undefined ? [] : viagensDoSubcontratado(dados, alvoSub.linha, alvoSub.d)),
    [dados, alvoSub],
  );
  const resumoCurto =
    alvo === null && alvoSub === undefined
      ? 'Passe o rato por um dia; clique para fixar.'
      : pares
          .slice(0, 3)
          .map(([, valor]) => valor)
          .join('  ·  ');

  return (
    <section className={classes('painel', aberto && 'painel--aberto')}>
      {aberto ? (
        <Puxador
          eixo="y"
          className="puxador puxador--painel"
          valor={altura}
          minimo={ALTURA_PAINEL_MIN}
          maximo={ALTURA_PAINEL_MAX}
          variavel="--altura-painel"
          alvo={envolvente}
          aoFixar={definirAltura}
          invertido
          rotulo="altura do painel de detalhe"
        />
      ) : null}

      <div className="painel-cab faixa-quebra">
        <button type="button" className="btn btn--peq" onClick={alternar} aria-expanded={aberto}>
          <IconeChevron em={1} aberto={aberto} />
          <span>Detalhe</span>
        </button>
        {fixo ? (
          <span className="pastilha pastilha--fixo">
            fixo
            <button type="button" className="pastilha-x" onClick={limpar} aria-label="largar">
              <IconeFechar em={0.9} />
            </button>
          </span>
        ) : null}
        {!aberto ? <span className="painel-resumo">{resumoCurto}</span> : null}
        {/* ⚠️ SÓ COM A SELECÇÃO FIXA. Com o rato a passar, um botão que muda de alvo a cada célula
            é um convite a editar o serviço errado — e editar escreve. */}
        {/* ==================================================================
            ⚠️⚠️ OS BOTÕES AGRUPADOS PELO QUE O GESTO ATINGE (2026-08-07)
            ==================================================================

            📗 Cliente: *«optimizes o design dos botões, estão muito confusos
            nesta organização»*.

            Eram SEIS numa fila corrida, sem hierarquia, e dois deles chamavam-se
            **«Editar serviço…»** e **«Editar dia…»** — nomes quase iguais para
            âmbitos opostos: um muda a corrida inteira de viatura, o outro corrige
            uma célula. Quem lê depressa engana-se, e enganar-se aqui muda o plano.

            ⚠️ **A confusão não era o número de botões: era não se saber O QUE
            CADA UM APANHA.** A grelha tem três âmbitos naturais e o planeador já
            os conhece — a corrida, o dia, e a viatura ao longo de vários dias. Os
            botões passam a estar arrumados por eles, com o âmbito escrito.

            📌 E os nomes deixam de colidir: «Editar serviço» passa a **«Trocar
            viatura…»**, que é o que ele faz de facto (viatura + condutor da
            corrida), e «Editar dia» passa a **«Corrigir dia…»**.

            ⚠️⚠️ **CADA BOTÃO LEVA UM `data-gesto`, e não é decoração** (§12.3, a peça H).
            É por ele que a guarda `paleta-conhece-a-seleccao.test.tsx` compara, no DOM
            renderizado, o conjunto de botões desta barra com o conjunto de verbos que a
            tabela `verbos-da-seleccao.ts` diz aplicarem-se — em cada contexto. 📌 Sem uma
            marca estável, a comparação teria de ser por RÓTULO, e há três botões chamados
            «Viatura e condutor…»: a guarda passaria com os predicados a divergir.
            ⚠️ **É um atributo e não uma classe**, de propósito: uma classe nova obrigaria a
            uma regra no `mapa.css` (há guarda para isso) e não significa nada visualmente.
            ⚠️ **E o botão DESACTIVADO do subcontratado não o leva** — ele não oferece o
            gesto, **recusa-o com a razão escrita**, que é exactamente o que a paleta faz com
            a marca «não dá». Marcá-lo fazia a barra afirmar que oferece «Viatura e
            condutor…» sobre um serviço de outra empresa.
            ================================================================== */}

        {/* ⚠️ **A URGÊNCIA FICA FORA DOS GRUPOS, e à frente de tudo.** Quando há
            gente à espera de um autocarro que não vai, arrumar bem o resto é
            secundário. Só aparece com a viatura indisponível — e o botão SER
            visível já é meia resposta. */}
        {fixo && substituir !== undefined && alvo !== null ? (
          <button
            type="button"
            className="btn btn--peq btn--principal painel-substituir"
            data-gesto="substituir"
            onClick={substituir}
            title="a viatura está indisponível nestes dias — ver que serviços ficam por cobrir"
          >
            Quem substitui?
          </button>
        ) : null}

        {/* ⚠️⚠️ **ESTA VIAGEM VAI EM MAIS DO QUE UM AUTOCARRO.**

            ✅ 📗 *«valide se isto já existe na escala real; se existir tem de ser implementado
            aqui também»* — **existe, e está medido**: 41 pares em 36 dias distintos na folha
            real têm a mesma referência em **duas viaturas no mesmo dia**, uma com o itinerário
            completo e a outra só com a referência.

            ⚠️ **Sem isto o mapa desenha duas barras iguais e nada as liga.** Quem move uma sem
            saber da outra parte um grupo ao meio — e a operação só descobre no dia.

            📌 **Simultânea e sequencial não são a mesma coisa**, e por isso não têm o mesmo
            aviso: uma quer dizer «mexer nesta deixa gente em terra», a outra quer dizer «esta
            já acabou aqui». Dar o mesmo alarme às duas ensina a ignorá-lo. */}
        {fixo && (outrasDaViagem ?? []).length > 0 && alvo !== null ? (
          <span className="painel-grupo painel-grupo--viagem faixa-quebra">
            {/* ⚠️⚠️ **ESTA ETIQUETA GEROU DUAS PERGUNTAS DO CLIENTE, e as duas eram sobre ela.**
                📗 *«marquei uma avaria do autocarro 129 e passou 3 dias para o 294, só que nos
                detalhes aparece-me que tenho 3 autocarros… analise o porquê e se foi um bug,
                agora estou confuso»* e *«detectei outro caso de viagem repetida no mesmo
                período»*.
                📌 **Medido na base: nenhum dos dois era bug.** A corrida `K8547-96C` já ia em
                **dois** autocarros antes (129 e 281, dias 13–14), a 129 foi para oficina a 15,
                e a 294 entrou 15–17 — logo três autocarros ao longo da viagem, e o número
                estava certo. E os 5 721 dias com a mesma corrida em duas viaturas estão
                **espalhados pelos 12 meses** (382 a 568 por mês) contra **179** trocas de
                executor no ano inteiro: vêm da semeadura, e reproduzem o que a folha real faz —
                43 % das referências em duas viaturas (F-22), um grupo grande em dois autocarros.
                ⚠️⚠️ **Logo o defeito era da ETIQUETA, e são três defeitos numa linha:**
                  1. **O `⚠️` num caso NORMAL.** Isto acontece em 43 % das referências reais. Um
                     alarme no caso normal ensina a ignorar alarmes — a regra que este projecto
                     já aplicou ao dia de rotação (avisar em 23 % dos casos ensina a ignorar).
                  2. **Contava 3 e mostrava 2 números**, sem dizer que o terceiro é o que está
                     seleccionado. O planeador procura o que falta.
                  3. **Não dizia QUANDO.** «3 autocarros» num serviço de 5 dias esconde a única
                     coisa que decide se há problema: **ao mesmo tempo** (grupo grande, normal)
                     ou **em dias diferentes** (passagem de testemunho, também normal). O `→`
                     que distinguia os dois era um glifo que ninguém tinha de saber ler. */}
            <span className="painel-grupo-rot">
              esta viagem: {(outrasDaViagem ?? []).length + 1} autocarros
            </span>
            {/* Esta, primeiro e sem botão: é onde o planeador está, e o número que «faltava». */}
            <span className="viagem-esta">
              {dados.viaturas[alvo.v]?.numero ?? ''} (esta)
            </span>
            {(outrasDaViagem ?? []).map((o) => (
              <button
                key={o.viatura}
                type="button"
                className={classes('btn', 'btn--peq', o.simultanea && 'viagem-junto')}
                title={
                  o.simultanea
                    ? `a mesma viagem AO MESMO TEMPO, de ${dataCurtaDoDia(dados, o.dia0)} a ` +
                      `${dataCurtaDoDia(dados, o.dia1)} — é um grupo grande em dois autocarros, ` +
                      'e acontece em 43 % das viagens reais. Mexer só numa parte o grupo.'
                    : `a mesma viagem NOUTROS DIAS, de ${dataCurtaDoDia(dados, o.dia0)} a ` +
                      `${dataCurtaDoDia(dados, o.dia1)} — é uma passagem de testemunho entre ` +
                      'autocarros, não uma repetição.'
                }
                onClick={() => irPara?.(dados.viaturas[o.viatura]?.id ?? '', o.dia0, o.dia1)}
              >
                {o.numero}
                {/* ⚠️⚠️ **OS DIAS NO PROPRIO BOTAO**, e nao so no `title`: um `title` tem ~1 s de
                    atraso e ninguem espera por ele para responder a «porque e que sao tres». */}
                <span className="viagem-dias">
                  {' '}
                  {dataCurtaDoDia(dados, o.dia0)}
                  {o.dia1 > o.dia0 ? `–${dataCurtaDoDia(dados, o.dia1)}` : ''}
                </span>
              </button>
            ))}
            <span className="viagem-nota">
              {(outrasDaViagem ?? []).some((o) => o.simultanea)
                ? 'ao mesmo tempo é um grupo grande em dois autocarros — normal em 43 % das viagens'
                : 'em dias diferentes: é uma passagem de testemunho, não uma repetição'}
            </span>
          </span>
        ) : null}

        {/* ⚠️⚠️ **AS DUAS VIAGENS DE UM DIA DE ROTACAO** (migracao 027, campos 11-13).
            📗 Cliente: *«visualmente esta tudo igual?»* — a celula esta, e de proposito: a caixa
            preta e a inversao das sublinhas ja existiam e os planeadores conhecem-nas. Mas o
            painel falava **so da viagem que chega**, porque nada no `apps/web` lia os campos
            novos: a identidade da segunda viagem viajava no contrato e morria aqui.
            ⚠️ **A CLASSE E A BASE, NUNCA `--viagem`:** essa e a etiqueta da mesma viagem em
            varios autocarros, e reutiliza-la punha DOIS elementos com o mesmo nome no painel —
            as consultas de tres testes passaram a encontrar este em vez do delas. E F-20.
            ⚠️ **E O LUGAR E AQUI, depois do grupo da viagem:** o painel tem uma ordem de ambito
            — viagem → dia → viatura — que e requisito e tem teste. Isto e do DIA, e posto antes
            quebrava-a. Foi o teste a apanhar-me, e tinha razao. */}
        {(() => {
          // ⚠️ `alvo` e uma POSICAO e nao a celula — a celula vem da grelha. Escrevi-o ao
          //    contrario a primeira vez e o compilador apontou os sete indices de uma vez, que e
          //    o que se quer de um tuple declarado num sitio so.
          const cr = alvo === null ? null : (dados.grelha[alvo.v]?.[alvo.d] ?? null);
          if (!fixo || cr === null || cr[13] < 0) return null;
          return (
            /* ⚠️⚠️ **NAO E UM GRUPO DE ACCOES, e a primeira versao fingia que era.** `painel-grupo`
               com `painel-grupo-rot` e a forma de um grupo de AMBITO — «a viagem toda», «este
               dia», «esta viatura» — e ha um teste que le essa sequencia por ordem. O meu rotulo
               entrou nela e apareceu na 1.a posicao, onde tem de estar «a viagem toda».
               📌 Isto e uma NOTA sobre o dia, nao um ambito de accao: usa `viagem-nota`, que ja
               existe, e fica fora da sequencia. O teste apanhou-me duas vezes no mesmo painel, e
               nas duas por eu vestir a roupa de outra coisa. */
            <span className="viagem-nota">
              dia de rotação: duas viagens · chega {dados.clientes[cr[0]]?.[0] ?? '—'}
              {cr[5] >= 0 ? ` (${nomeOuCodigoDoItinerario(dados, cr[5])})` : ''} · parte{' '}
              {dados.clientes[cr[11]]?.[0] ?? '—'}
              {cr[12] >= 0 ? ` (${nomeOuCodigoDoItinerario(dados, cr[12])})` : ''}
            </span>
          );
        })()}

        {/* ⚠️⚠️ **O GRUPO DE ACCOES DE UM SUBCONTRATADO — proprio, e nao o da frota.**
            📗 Cliente, 2026-08-10: *«tem de aparecer alguns botoes tipo para cancelar, editar
            algo»*.
            ⚠️⚠️ **E preciso ter apanhado isto ANTES de publicar:** eu tinha ligado o
            `cancelarServico` ao subcontratado e o botao **nunca apareceria** — ele vive dentro
            do grupo da frota, que exige `alvo !== null`, e num subcontratado `alvo` e `null` de
            proposito (e o que esconde as accoes que nao se aplicam). 📌 A ligacao feita e o
            leitor a faltar, outra vez, e desta vez apanhada a reler as guardas em vez de a
            confiar no `typecheck`.
            📌 **O que aparece e o que aparece DESACTIVADO sao os dois informacao.** Um botao
            que falta nao explica nada; desactivado com a razao, ensina o modelo: a frota e
            dela, o pedido e nosso. */}
        {/* ⚠️⚠️ **O DIA SUBCONTRATADO LIGA À VIAGEM — 📗 *«para saltar para a viagem»*.**
            ⚠️⚠️ **UM GRUPO POR SERVIÇO, e não um botão por dia.** Um subcontratado é uma
            EMPRESA e pode ter três serviços no mesmo dia (D-002) — «saltar para a viagem» não
            tem resposta única, logo listam-se **todos** e o planeador escolhe. Escolher um por
            omissão levava o olho à viagem de outro cliente.
            ⚠️⚠️ **E QUANDO NÃO HÁ VIATURA NOSSA, DIZ-SE.** Medido no `dev`: 10,0 % das
            referências correm em mais de uma viatura, mas uma viagem inteiramente da empresa
            não corre em nenhuma. Um botão que não aterra em lado nenhum é indistinguível de um
            botão avariado — é o `return` silencioso do «Resta subcontratar», outra vez. */}
        {fixo && alvoSub !== undefined && viagensSub.length > 0 ? (
          <span className="painel-grupo painel-grupo--viagem-sub faixa-quebra">
            <span className="painel-grupo-rot">
              {viagensSub.length === 1
                ? 'a viagem deste serviço'
                : `${viagensSub.length} serviços neste dia — a viagem de cada um`}
            </span>
            {viagensSub.map((vg) => (
              <span key={vg.indiceNoDia} className="viagem-sub">
                <span className="viagem-sub-quem">
                  {vg.clienteSigla === '' ? 'cliente por identificar' : vg.clienteSigla}
                  {vg.referencia === '' ? '' : ` · ${vg.referencia}`}
                </span>
                {vg.corrida < 0 ? (
                  // ⚠️ Sem corrida não há viagem a que ligar, e isso **não é um erro**: uma
                  //    reserva pode ainda não ter referência. Diz-se, em vez de um botão morto.
                  <span className="viagem-sub-nota">
                    sem referência de viagem — não há por onde ligar
                  </span>
                ) : vg.destinos.length === 0 ? (
                  <span className="viagem-sub-nota">
                    esta viagem corre só nesta empresa — nenhuma viatura nossa a faz
                  </span>
                ) : (
                  vg.destinos.map((dst) => (
                    <button
                      key={dst.viatura}
                      type="button"
                      className={classes('btn', 'btn--peq', dst.simultanea && 'viagem-junto')}
                      title={
                        dst.simultanea
                          ? `a mesma viagem AO MESMO TEMPO na viatura ${dst.numero}, de ` +
                            `${dataCurtaDoDia(dados, dst.dia0)} a ${dataCurtaDoDia(dados, dst.dia1)}` +
                            ' — o grupo não cabe num autocarro e esta empresa é o reforço.'
                          : `a mesma viagem NOUTROS DIAS na viatura ${dst.numero}, de ` +
                            `${dataCurtaDoDia(dados, dst.dia0)} a ${dataCurtaDoDia(dados, dst.dia1)}` +
                            ' — a empresa cobre um pedaço da viagem, não a repete.'
                      }
                      onClick={() => irPara?.(dst.id, dst.dia0, dst.dia1)}
                    >
                      Ver na {dst.numero}
                      <span className="viagem-dias">
                        {' '}
                        {dataCurtaDoDia(dados, dst.dia0)}
                        {dst.dia1 > dst.dia0 ? `–${dataCurtaDoDia(dados, dst.dia1)}` : ''}
                      </span>
                    </button>
                  ))
                )}
              </span>
            ))}
          </span>
        ) : null}

        {fixo && alvoSub !== undefined ? (
          <span className="painel-grupo faixa-quebra">
            <span className="painel-grupo-rot">este serviço subcontratado</span>
            {cancelarServico !== undefined ? (
              <button
                type="button"
                className="btn btn--peq painel-cancelar"
                data-gesto="cancelarServico"
                onClick={cancelarServico}
                title="cancelar o pedido e, se quiser, libertar os dias — pede o motivo e fica na auditoria"
              >
                Cancelar o serviço…
              </button>
            ) : null}
            {retirarDoMapa !== undefined ? (
              <button
                type="button"
                className="btn btn--peq"
                data-gesto="retirarDoMapa"
                onClick={retirarDoMapa}
                title="liberta os dias no mapa sem cancelar o pedido — ele volta à fila e pode ser planeado com frota própria"
              >
                Retirar do mapa…
              </button>
            ) : null}
            {/* ---- ⚠️⚠️ B1-(2) · TROCAR DE EMPRESA, e isto era um BECO ------------------
                📗 Cliente: *«tem de dar para alterar os subcontratos para uns outros»*.
                ⚠️⚠️ **Este botão estava DESACTIVADO** e o título mandava *«cancelar e voltar
                a planear»* — o que perde o pedido de vista e cria história nova para uma
                viagem que não mudou. 📌 O que ele quer é a MESMA viagem noutra empresa, e
                isso é o `/api/plano/executor`, que só não aceitava um parceiro na ORIGEM. */}
            {trocarEmpresa !== undefined ? (
              <button
                type="button"
                className="btn btn--peq"
                data-gesto="trocarEmpresa"
                onClick={trocarEmpresa}
                title="passa este serviço para outra empresa subcontratada — os mesmos travões e a mesma auditoria da troca de viatura"
              >
                Trocar de empresa…
              </button>
            ) : null}
            {/* ⚠️⚠️ **DESACTIVADO COM A RAZAO, e nao ausente**, e a razao ENCOLHEU: o
                condutor continua a ser da empresa (D-002) e isso nao se edita daqui — mas
                mandar «cancelar e replanear» deixou de ser a unica saida, e um aviso
                desactualizado e pior do que nenhum. */}
            <button
              type="button"
              className="btn btn--peq"
              disabled
              title="o condutor é da empresa subcontratada e não o conhecemos — para passar este serviço para a NOSSA frota, use «Retirar do mapa…» e volte a planear"
            >
              Viatura e condutor…
            </button>
          </span>
        ) : null}

        {fixo &&
        natureza === 'servico' &&
        (editar !== undefined || fecharServico !== undefined || diario !== undefined) &&
        alvo !== null ? (
          <span className="painel-grupo faixa-quebra">
            {/* ⚠️⚠️ **NUM DIA DE ROTAÇÃO «a viagem toda» SÃO DUAS, e o rótulo tinha de o dizer.**
                📗 Cliente, 2026-08-14: *«nos detalhes dos dias de rotação tem de ter as duas
                viagens»*.
                📌 Estes botões agem sobre a viagem que **CHEGA** — é a linha `principal`, de onde
                a célula tira a identidade. Dizê-lo é o que impede o planeador de mover, fechar ou
                cancelar a viagem errada a acreditar que agia na outra.
                ⚠️ **Não é um beco:** o condutor da que parte edita-se em «Viatura e condutor…»,
                que já pergunta qual das duas. O que falta para as restantes acções está declarado
                no `CLAUDE.md`: cada caminho de escrita tem de receber o intervalo DA viagem que
                nomeia, e o do painel é o da que chega. */}
            <span className="painel-grupo-rot">
              {(() => {
                const c = dados.grelha[alvo.v]?.[alvo.d];
                // ⚠️ `>= 0` afirmativo e não `< 0`: com um campo ausente `undefined < 0` é
                //   **falso** e a ausência lia-se como presença. A forma afirmativa é falsa nos
                //   dois casos, que é o que se quer.
                if (c == null || !(c[13] >= 0)) return 'a viagem toda';
                return `a viagem que CHEGA (${dados.corridas[c[10]] ?? '—'})`;
              })()}
            </span>
            {editar !== undefined ? (
              <button
                type="button"
                className="btn btn--peq painel-editar"
                data-gesto="editar"
                onClick={editar}
                title="quem conduz e que autocarro faz esta viagem — a corrida inteira, não só este dia"
              >
                Viatura e condutor…
              </button>
            ) : null}
            {/* ---- 🔴 O PAR DA CÓPIA — peça F §6.5, §9 Q1 --------------------------------
                📗 *«copiar **e** mover — e "copiar" é o segundo autocarro»*: o grupo não cabe
                num só, e a mesma viagem passa a correr em dois (16,9 % das referências reais).
                ⚠️⚠️ **ENCOSTADOS AO «Viatura e condutor…», que é o gesto que MOVE** — é ao lado
                dele que a diferença se aprende, e ler mal um destes deixa um grupo a mais ou a
                menos num autocarro. Nenhum dos dois diz «mover» nem «mapa»: o segundo é do
                «Retirar do mapa…», que é outra coisa.
                ⚠️ **E o `retirarCopia` NÃO pertence ao trio que tira o serviço do mapa** (pôr
                em espera · retirar · cancelar): a viagem **continua** no outro autocarro. Pô-lo
                lá ensinava a lê-lo como o terceiro grau de estrago, que não é. */}
            {copiarPeriodo !== undefined ? (
              <button
                type="button"
                className="btn btn--peq"
                data-gesto="copiarPeriodo"
                onClick={copiarPeriodo}
                title="a MESMA viagem passa a correr também num segundo autocarro — este fica como está, não é mover"
              >
                Acrescentar um 2.º autocarro…
              </button>
            ) : null}
            {retirarCopia !== undefined ? (
              <button
                type="button"
                className="btn btn--peq"
                data-gesto="retirarCopia"
                onClick={retirarCopia}
                title="tira só as alocações deste autocarro — a viagem continua no outro que a leva"
              >
                Tirar este autocarro da viagem…
              </button>
            ) : null}
            {/* ⚠️ Só depois de o dia ter passado: fechar um serviço que ainda não
                aconteceu produzia km «reais» iguais aos estimados, que é pior do
                que não ter dados porque não se distingue (D-091 passo 1). */}
            {fecharServico !== undefined && jaPassou(dados, alvo.d) ? (
              <button
                type="button"
                className="btn btn--peq painel-fechar"
                data-gesto="fecharServico"
                onClick={fecharServico}
                title="registar o que aconteceu de facto — km, custo e imprevistos"
              >
                Fechar…
              </button>
            ) : null}
            {/* ⚠️ **CANCELAR É DO PEDIDO E NÃO DA CÉLULA**, e a janela di-lo com números
                antes de gravar: quantos dias, quantas viaturas, quantos já efectivados.
                📌 Fica no grupo da VIAGEM porque é isso que apanha — pô-lo no grupo do dia
                sugeria que cancelava um dia só, que é precisamente o engano a evitar. */}
            {/* ---- 🔴 PÔR EM ESPERA — peça F §6.3, migração 089 ---------------------------
                📗 *«ou numa linha dummy sem autocarro no final do mapa após os subcontratados,
                tipo em espera … para o planeador corrigir o mapa manualmente sem destruir as
                ligações perfeitas entre viagens»*.
                ⚠️⚠️ **PRIMEIRO DOS TRÊS, e a ordem continua a ser a mensagem.** São agora TRÊS
                os gestos que tiram o serviço do mapa, e distinguem-se pelo que DESTROEM: pôr em
                espera não destrói nada (a viagem fica inteira, com condutores, noutra linha);
                retirar larga as alocações e devolve o pedido à fila; cancelar di-lo morto.
                📌 Por ordem crescente de estrago, que é o que faz o planeador ler o primeiro. */}
            {porEmEspera !== undefined ? (
              <button
                type="button"
                className="btn btn--peq"
                data-gesto="porEmEspera"
                onClick={porEmEspera}
                title="tirar a viagem do autocarro e guardá-la no fim do mapa — nada se perde, e volta quando quiser"
              >
                Pôr em espera…
              </button>
            ) : null}
            {/* ⚠️⚠️ **ANTES do «Cancelar…», e a ordem é a mensagem.** São os dois gestos que
                tiram o serviço do mapa, e só um deles é reversível: retirar devolve o pedido à
                fila, cancelar di-lo morto. 📌 Pôr o destrutivo em segundo lugar é o que faz o
                planeador ler o primeiro. */}
            {retirarDoMapa !== undefined ? (
              <button
                type="button"
                className="btn btn--peq"
                data-gesto="retirarDoMapa"
                onClick={retirarDoMapa}
                title="liberta os dias futuros no mapa SEM cancelar o pedido — ele volta à fila por planear"
              >
                Retirar do mapa…
              </button>
            ) : null}
            {cancelarServico !== undefined ? (
              <button
                type="button"
                className="btn btn--peq painel-cancelar"
                data-gesto="cancelarServico"
                onClick={cancelarServico}
                title="cancelar o pedido e (opcionalmente) libertar os dias no mapa"
              >
                Cancelar…
              </button>
            ) : null}
            {/* ⚠️⚠️ **«NÃO MEXER AQUI» — o gesto do alfinete (J-0).**
                📗 Cliente, 2026-08-13: *«tem de existir um pisco que diga: ok, aqui já não quero
                mexer mais»*.
                📌 **A marca existia no mapa desde 16-08 e o gesto não existia em lado nenhum** —
                a única forma de travar um serviço era SQL à mão. É «duas portas, uma fechada»
                pelo lado contrário: o caminho de escrita guardado, e o gesto que cria a guarda
                inexistente.
                ⚠️ Fica no grupo da VIAGEM porque é o que ele apanha: a corrida inteira. */}
            {travar !== undefined ? (
              <button
                type="button"
                className="btn btn--peq"
                data-gesto="travar"
                onClick={travar}
                title="travar esta viagem: deixa de entrar no planeamento e nos rearranjos automáticos"
              >
                Não mexer…
              </button>
            ) : null}
            {/* ⚠️⚠️ **O DIÁRIO DESTA VIAGEM — §5.6 exigência 3.** 📌 **Último do grupo, e de
                propósito:** é o único botão daqui que **não escreve**. Pô-lo entre os que
                escrevem punha uma leitura no meio de decisões, e é a leitura que se faz
                ANTES ou DEPOIS delas, nunca no lugar de uma. */}
            {diario !== undefined ? (
              <button
                type="button"
                className="btn btn--peq"
                data-gesto="diario"
                onClick={diario}
                title="tudo o que aconteceu a esta viagem — quem mexeu, quando e o quê (só lê; não altera nada)"
              >
                O que aconteceu…
              </button>
            ) : null}
          </span>
        ) : null}

        {/* ⚠️⚠️ **O GRUPO DA SEGUNDA VIAGEM — 📗 «nos detalhes dos dias de rotação tem de ter as
            duas viagens».**
            📌 Só aparece nos dias de rotação (~1 % das células). Abre o MESMO painel, com o
            intervalo **da viagem que parte** e a referência dela **explícita** — sem isso o
            servidor escolhia a `principal` e o gesto mexia na outra viagem em silêncio.
            ⚠️ Fechar e cancelar continuam de fora — são do PEDIDO e abrem-se pela lista de
            pedidos, onde não há ambiguidade de dia nenhuma. «Cor da viagem…» entrou porque,
            ao contrário desses dois, é uma escolha da CORRIDA (migração 055) e não do pedido. */}
        {fixo && natureza === 'servico' && alvo !== null &&
        (editarQuePartem !== undefined || corDaViagemQuePartem !== undefined) ? (
          <span className="painel-grupo faixa-quebra">
            <span className="painel-grupo-rot">{rotuloDaQuePartem}</span>
            {editarQuePartem !== undefined ? (
              <button
                type="button"
                className="btn btn--peq painel-editar-parte"
                data-gesto="editarQuePartem"
                onClick={editarQuePartem}
                title="mover ou mudar o condutor da SEGUNDA viagem deste dia — a que parte"
              >
                Viatura e condutor…
              </button>
            ) : null}
            {corDaViagemQuePartem !== undefined ? (
              <button
                type="button"
                className="btn btn--peq"
                data-gesto="corDaViagemQuePartem"
                onClick={corDaViagemQuePartem}
                title="escolher uma cor só para a SEGUNDA viagem deste dia — a que parte"
              >
                Cor da viagem…
              </button>
            ) : null}
          </span>
        ) : null}

        {/* ⚠️ **SÓ COM SERVIÇO**, e não é arrumação: `Corrigir dia…` e `Nota…` escrevem
            numa linha de `alocacao` que **tem de existir**. Num dia livre não existe, e os
            dois botões devolviam erro — a definição de um botão que ensina a desconfiar. */}
        {fixo && natureza === 'servico' &&
        (corrigir !== undefined || anotar !== undefined || corDaViagem !== undefined) &&
        alvo !== null ? (
          <span className="painel-grupo faixa-quebra">
            {/* ⚠️⚠️ **O RÓTULO DIZ QUE DIA É, e antes não dizia.**
                📗 Cliente, 2026-08-08: *«quando clico em cima da viagem completa, primeira
                linha da marcação onde aparece o cliente, tem de permitir editar não "este
                dia de viagem" — pois nenhum dia está seleccionado — mas sim editar todos os
                dias da viagem»*.
                📌 Ele tem razão sobre a AMBIGUIDADE e não sobre o facto: clicar na barra
                selecciona sempre um dia — o que está por baixo do cursor —, mas o ecrã não
                dizia qual, e «este dia» sobre uma barra que abrange oito lê-se como «o
                serviço». Passa a nomear a data. O que apanha a viagem inteira está no grupo
                de cima, e agora os dois âmbitos distinguem-se sem se adivinhar. */}
            <span className="painel-grupo-rot">{dataCurtaDoDia(dados, alvo.d)}</span>
            {corrigir !== undefined ? (
              <button
                type="button"
                className="btn btn--peq painel-corrigir"
                data-gesto="corrigir"
                onClick={corrigir}
                title="onde pernoita, o itinerário, as horas e o estado deste dia"
              >
                Itinerário e pernoita…
              </button>
            ) : null}
            {/* 📗 fonte 6.8. ⚠️ UM DIA, ao contrário do «Trocar viatura»: a nota é
                daquele dia («saida htl 07:50»), não da viagem toda. */}
            {anotar !== undefined ? (
              <button
                type="button"
                className="btn btn--peq painel-anotar"
                data-gesto="anotar"
                onClick={anotar}
                title="escrever uma nota breve neste dia (fonte 6.8)"
              >
                Nota…
              </button>
            ) : null}
            {/* ⚠️⚠️ **PARTIR A VIAGEM — 📗 «muitas vezes têm de partir viagens a meio».** Estava só
                no menu de contexto: um gesto frequente que se descobria por acidente. 📌 Aqui não
                há ambiguidade de dia, porque o rótulo do grupo é a própria data — e o menu de
                contexto continua a existir para quem já o usa. */}
            {partir !== undefined ? (
              <button
                type="button"
                className="btn btn--peq painel-partir"
                data-gesto="partir"
                onClick={partir}
                title="partir a viagem neste dia — a segunda metade passa a ser um serviço próprio"
              >
                Partir a viagem…
              </button>
            ) : null}
            {/* ⚠️⚠️ **ORGANIZAR EM TROÇOS — GARANTIA INVERSA do botão de cima.** Reaproveita a
                classe `painel-partir` do botão irmão — mesmo grupo, mesmo espaçamento — mas
                NUNCA o rótulo «partir»: aqui a referência não muda. */}
            {organizarEmTrocos !== undefined ? (
              <button
                type="button"
                className="btn btn--peq painel-partir"
                data-gesto="organizarEmTrocos"
                onClick={organizarEmTrocos}
                title="organizar a viagem em troços de N dias, para reporte — a referência não muda"
              >
                Organizar em troços…
              </button>
            ) : null}
            {/* ⚠️ **JUNTAR OS TROÇOS — o inverso do botão de cima**, só quando há fronteira
                para apagar (ver `juntarTrocos` em `Painel`). */}
            {juntarTrocos !== undefined ? (
              <button
                type="button"
                className="btn btn--peq painel-partir"
                data-gesto="juntarTrocos"
                onClick={juntarTrocos}
                title="apagar as fronteiras de troço desta viagem — volta a ser um troço só"
              >
                Juntar os troços…
              </button>
            ) : null}
            {/* ⚠️ A VIAGEM, não o dia — sobrepõe-se à cor do cliente em todas as
                células desta corrida, mesmo que atravesse mais de uma viatura. */}
            {corDaViagem !== undefined ? (
              <button
                type="button"
                className="btn btn--peq"
                data-gesto="corDaViagem"
                onClick={corDaViagem}
                title="escolher uma cor só para esta viagem, em vez da cor do cliente"
              >
                Cor da viagem…
              </button>
            ) : null}
          </span>
        ) : null}

        {fixo && marcarIndisponivel !== undefined && alvo !== null ? (
          <span className="painel-grupo faixa-quebra">
            <span className="painel-grupo-rot">
              {natureza === 'livre' ? 'este dia livre' : 'a viatura'}
            </span>
            {/* ⚠️⚠️ **ONDE ESTÁ PARADA — e vem ANTES do «Marcar oficina».**
                📗 Cliente, 2026-08-10: *«nos dias sem viagem o "parado" e o "na base" está
                correcto, mas tem de poder ser editado manualmente… selecionar o período e
                alterar o estado»*.
                📌 **A ordem é a frequência.** Um dia parado é o caso comum (17 877
                dias-viatura sem linha nenhuma, medidos); uma avaria é a excepção. E são
                perguntas diferentes: esta diz **onde ela está**, aquela diz que **não pode
                sair** — a primeira deixa-a disponível para o motor, a segunda não. */}
            {onde !== undefined ? (
              <button
                type="button"
                className="btn btn--peq painel-onde"
                data-gesto="onde"
                onClick={onde}
                title="dizer onde a viatura está parada — na base ou fora, e em que período"
              >
                Onde está parada…
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn--peq painel-indisp"
              data-gesto="marcarIndisponivel"
              onClick={marcarIndisponivel}
              title="marcar oficina, avaria ou sinistro em vários dias de uma vez"
            >
              {natureza === 'indisponivel' ? 'Alterar ou levantar…' : 'Marcar oficina/avaria…'}
            </button>
            {/* ⚠️ **A PERGUNTA QUE UM DIA VAZIO FAZ.** Um buraco no meio do ano é um custo:
                📗 D-083, «a partir de quantos dias parados compensa mandar voltar». O motor
                invertido já responde — o que faltava era perguntar-lhe **onde a pergunta
                se faz**, que é em cima do dia vazio, e não num separador ao lado. */}
            {natureza === 'livre' && encaixar !== undefined ? (
              <button
                type="button"
                className="btn btn--peq painel-encaixar"
                data-gesto="encaixar"
                onClick={encaixar}
                title="que pedidos por planear cabem nesta viatura, nestes dias livres"
              >
                O que encaixa aqui?
              </button>
            ) : null}
          </span>
        ) : null}
      </div>

      {aberto ? (
        <div className="painel-corpo" aria-live="polite">
          {alvo === null && alvoSub === undefined ? (
            <div className="painel-vazio">Passe o rato por um dia; clique para fixar.</div>
          ) : (
            // 🔴 2026-10-26 · 📗 «os detalhes são uma confusão de texto». Os mesmos pares, em blocos
            //    que respondem a uma pergunta cada (`seccoes-do-detalhe.ts`) — nada sai, muda de sítio.
            seccoesDoDetalhe(pares).map((sec) => (
              <section key={sec.titulo} className="painel-seccao">
                <h4 className="painel-seccao-tit">{sec.titulo}</h4>
                <dl>
                  {sec.pares.map(([chave, valor]) => (
                    <div key={chave} className="par">
                      <dt>{chave}</dt>
                      <dd>{valor}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ))
          )}
        </div>
      ) : null}

      {/* ⚠️⚠️ **A RECOMENDACAO DO INTERVALO, no dia livre.** 📗 Cliente, 2026-08-11: *«nos detalhes do
          intervalo, nos dias livres, nao fazia sentido aparecer la uma recomendacao? Da melhor
          solucao? Dependendo do contexto, proxima viagem etc.»* — e tem mais razao do que a lista que
          eu tinha feito na fila: aqui responde-se onde a pergunta nasce.
          ⚠️ **So com a seleccao FIXA e so num dia livre.** 📌 Com o rato a passar seriam centenas de
          pedidos por minuto, e num dia com serviço nao ha intervalo nenhum para explicar. */}
      {aberto && fixo && alvo !== null && diaSemItinerario(dados, alvo.v, alvo.d) ? (
        <RecomendacaoDoIntervalo
          viaturaId={dados.viaturas[alvo.v]?.id ?? ''}
          dia={alvo.d}
          nomeLocalidade={(codigo) => {
            const i = dados.localidades.findIndex((l) => l[0] === codigo);
            return i < 0 ? codigo : `${codigo} — ${dados.localidades[i]?.[1] ?? ''}`;
          }}
          nomeDoDia={(d) => dataCurtaDoDia(dados, d)}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Puxador de redimensionamento
// ---------------------------------------------------------------------------

/**
 * Arrasta e redimensiona.
 *
 * Durante o arrasto escreve **directamente na variável CSS** e só fixa o estado do
 * React ao largar. Passar por estado a cada movimento do ponteiro voltaria a
 * desenhar as linhas visíveis em cada pixel — e o arrasto é justamente onde isso
 * se sentiria. O layout responde igual porque a variável é a mesma que o CSS usa.
 */
function Puxador({
  eixo,
  className,
  valor,
  minimo,
  maximo,
  variavel,
  alvo,
  aoFixar,
  invertido = false,
  rotulo,
}: {
  eixo: 'x' | 'y';
  className: string;
  valor: number;
  minimo: number;
  maximo: number;
  variavel: string;
  alvo: React.RefObject<HTMLDivElement | null>;
  aoFixar: (n: number) => void;
  invertido?: boolean;
  rotulo: string;
}) {
  const inicio = useRef<{ pos: number; base: number } | null>(null);
  const ultimoValor = useRef(valor);

  const aoDescer = (e: EventoPonteiro<HTMLDivElement>): void => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    inicio.current = { pos: eixo === 'x' ? e.clientX : e.clientY, base: valor };
  };

  const aoMover = (e: EventoPonteiro<HTMLDivElement>): void => {
    const i = inicio.current;
    if (i === null) return;
    const delta = (eixo === 'x' ? e.clientX : e.clientY) - i.pos;
    const bruto = i.base + (invertido ? -delta : delta);
    const novo = Math.max(minimo, Math.min(maximo, bruto));
    alvo.current?.style.setProperty(variavel, `${novo}px`);
    ultimoValor.current = novo;
  };

  const aoSubir = (e: EventoPonteiro<HTMLDivElement>): void => {
    if (inicio.current === null) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    inicio.current = null;
    aoFixar(ultimoValor.current);
  };

  return (
    <div
      className={className}
      role="separator"
      aria-label={rotulo}
      aria-orientation={eixo === 'x' ? 'vertical' : 'horizontal'}
      onPointerDown={aoDescer}
      onPointerMove={aoMover}
      onPointerUp={aoSubir}
      onPointerCancel={aoSubir}
    />
  );
}

// ---------------------------------------------------------------------------

/**
 * Tema activo, seguido em tempo real.
 *
 * A cor das barras é calculada em JavaScript (depende do cliente), logo mudar de
 * tema no sistema tem de chegar aqui. Sem isto, quem alterna o tema fica com um
 * mapa de cores erradas até recarregar.
 */
function useTema(): Tema {
  const [tema, setTema] = useState<Tema>(() =>
    typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
      ? 'escuro'
      : 'claro',
  );

  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const consulta = matchMedia('(prefers-color-scheme: dark)');
    const aoMudar = (): void => setTema(consulta.matches ? 'escuro' : 'claro');
    consulta.addEventListener('change', aoMudar);
    return () => consulta.removeEventListener('change', aoMudar);
  }, []);

  return tema;
}
